import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./home.module.css";
import {
  getOutletTiming,
  getTherapistList, 
  getTherapistTimings,
  getSelectedOutlet,
  getRoomsForCalendar,
  getCalendarFilters,
  setCalendarFilters,
  getTherapistBreaks,
  deleteHoldBooking,
  getPublicHolidayList,
  getCalendarCards,
} from "../../APICalling/APICalling";
import { adaptCalendarCardsToBookingsList } from "../../Utils/adaptCalendarCardsToBookingsList";
import { useGlobalContext } from "../../Context/GlobalContext";
import Loader from "../../Components/Loader/Loader";
import {
  getCellKey,
  convertTo24HourFormat,
  convertTo12Hour,
  formatDateForBackend,
  convertToMinutes,
  generateFullDaySlotsWithOutletStatus,
  formatTimeWithoutZeroMinutes,
  calculateEndTime,
  formatDateToDDMMYYYY,
  parseCalendarRedirectDate,
  formatDateToYMD,
  getOutletExtendedFlagForDate,
  extractOutletFromApiResponse,
  timeToMinutes,
  normalizeTime,
  buildTimeSelection,
  scrollToBookingCard,
  scrollToNewBookingCard,
} from "../../Utils/utils.js";
import { buildProjectedOverridePayload, getProjectedColumnKey, resolveProjectedColumnIndex } from "../../Utils/calendarProjection.js";
import {
  scheduleServiceMove,
  ensureServiceLinkedFlags,
  sortAndRelinkChainByAbsoluteMinutes,
  buildChainServicesFromBookingItems,
  findBookingItemGroupForItemId,
  getServiceAbsoluteStartMinutes,
  absoluteMinutesToClock,
} from "../../Utils/serviceScheduleChain.js";
import { ToastContainer, toast, cssTransition } from "react-toastify";
import RightSidebarWrapper from "../../Components/RightSidebar/RightSidebarWrapper.js";
import Header from "./Header.js";
import { useBookingTableCurrentTime, getCellHeight } from "./useBookingTableCurrentTime.js";
import DiscardPopup from "../../Components/Popups/DiscardPopup/DiscardPopup.js";
// Removed ConfirmSavePopup as edits now happen directly in the Edit sidebar
import EventListner from "../../Components/Sockect/EventListner.js"
import TherapistCard from "./TherapistCard/TherapistCard.js";
import { buildTagIcons, normalizeTags } from "../../Utils/TagIcons.js";
import { BOOKING_TAG_ICONS } from "../../Utils/bookingTagIconRegistry.js";
import RoomCard from "./RoomCard/RoomCard.js";
import Placeholder from "../../Components/Placeholder/Placeholder.js";
import StaffIcon from "../../Components/Icons/StaffIcon.js";
import { defaultStatus, withDefaultStatus } from "../../Utils/Constants/defaultStatus.js";
import HoldingCardTimer from "../../Components/Timer/HoldingCardTimer.js";
import CustomerDetailsSidebar from "../../Components/CustomerDetailsModal/CustomerDetailsModal.js";
import HardQueueSidebar from "../../Components/HardQueueSidebar/HardQueueSidebar.js";
import { CLOSE_TAGGING_DROPDOWNS } from "../../Components/DropDown/TaggingDropdown/TaggingDropdown.js";
import logger from "../../Utils/logger.js";
import { isReadOnlyBookingStatus } from "../../Utils/BookingPreventChanges.js";
import "../../Components/InternetStatusListener/InternetStatusListener.css";

const calendarCardsToastTransition = cssTransition({
  enter: "Toastify__linear-enter",
  exit: "Toastify__linear-exit",
});

const facilityLabel = (facility) => {
  if (facility == null) return "";
  if (typeof facility === "string" || typeof facility === "number") return String(facility).trim();
  if (typeof facility === "object") {
    const value = facility.name ?? facility.facility ?? facility.label ?? facility.title ?? facility.value ?? facility.type;
    return value == null ? "" : String(value).trim();
  }
  return String(facility).trim();
};

const isShowerFacility = (facility) => facilityLabel(facility).toLowerCase() === "shower";

const asFacilityList = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    return value.split(",").map((part) => part.trim()).filter(Boolean);
  }
  return null;
};

const firstNonEmptyFacilityList = (candidates) => {
  let sawList = false;
  for (const candidate of candidates) {
    const list = asFacilityList(candidate);
    if (!list) continue;
    sawList = true;
    if (list.length > 0) return { list, sawList: true };
  }
  return { list: null, sawList };
};

/** Live couple/shower room tags for calendar cards during edit/room change. */
const getRoomTagFields = (roomLikes, fallback = {}) => {
  const sources = (Array.isArray(roomLikes) ? roomLikes : [roomLikes]).filter(Boolean);
  let roomType = "";
  let facilities = null;
  let sawFacilities = false;

  const readSource = (source) => {
    if (!source || typeof source !== "object") return;
    const nested = source.room_segments?.[0] || source.room_items?.[0] || null;
    if (!roomType) {
      const typeVal = source.room_type ?? source.capacity_type ?? nested?.room_type ?? nested?.capacity_type;
      if (typeVal != null && String(typeVal).trim() !== "") {
        roomType = String(typeVal);
      }
    }
    if (Array.isArray(facilities) && facilities.length > 0) return;
    const itemFacilities = Array.isArray(source.items)
      ? source.items.map((item) => item?.facilities)
      : [];
    const { list, sawList } = firstNonEmptyFacilityList([
      source.facilities,
      nested?.facilities,
      source.room?.facilities,
      ...itemFacilities,
    ]);
    if (sawList) sawFacilities = true;
    if (list) facilities = list;
  };

  sources.forEach(readSource);

  return {
    coupleRoom: roomType
      ? roomType.toLowerCase() === "double"
      : Boolean(fallback.coupleRoom),
    showerRoom: Array.isArray(facilities)
      ? facilities.some(isShowerFacility)
      : (sawFacilities ? false : Boolean(fallback.showerRoom)),
    resolved: Boolean(roomType) || sawFacilities,
  };
};

const getCatalogRoomMetaFromList = (homeRoomList, roomId) => {
  const id = Number(roomId);
  if (!Number.isFinite(id) || !Array.isArray(homeRoomList)) return null;
  for (const room of homeRoomList) {
    const item = (Array.isArray(room?.items) ? room.items : []).find((it) => Number(it.item_id) === id);
    if (!item) continue;
    const { list } = firstNonEmptyFacilityList([
      item.facilities,
      room.facilities,
      ...(Array.isArray(room.items) ? room.items.map((it) => it?.facilities) : []),
    ]);
    return {
      room_type: item.room_type ?? room.room_type ?? room.capacity_type ?? null,
      facilities: list || [],
    };
  }
  return null;
};

const getDraftCardColorTags = ({
  matchingService,
  slot,
  selectedRooms,
  homeRoomList,
} = {}) => {
  const selectedRoom =
    matchingService?.counterKey != null
      ? selectedRooms?.[matchingService.counterKey]
      : (slot?.counterKey != null ? selectedRooms?.[slot.counterKey] : null);
  const roomId =
    matchingService?.roomId ??
    matchingService?.room_segments?.[0]?.room_id ??
    matchingService?.room_items?.[0]?.item_id ??
    selectedRoom?.value ??
    selectedRoom?.item_id ??
    slot?.roomId ??
    slot?.roomData?.id ??
    slot?.roomData?.item_id ??
    slot?.roomData?.matchedItem?.item_id;
  const categoryFields = getServiceCategoryFields(matchingService, {
    serviceName: matchingService?.name || matchingService?.service_name || slot?.serviceName || "",
    serviceCategory:
      matchingService?.service_category_name ||
      matchingService?.service_category ||
      slot?.serviceCategoryName ||
      slot?.tags?.service_category ||
      "",
    serviceId: matchingService?.id ?? matchingService?.service ?? matchingService?.service_id ?? slot?.serviceId,
  });
  const roomTags = getRoomTagFields(
    [
      matchingService,
      selectedRoom,
      getCatalogRoomMetaFromList(homeRoomList, roomId),
      slot?.roomData?.matchedItem,
      slot?.roomData,
      slot?.tags,
    ],
    {
      coupleRoom: slot?.tags?.coupleRoom,
      showerRoom: slot?.tags?.showerRoom,
    }
  );
  const isRequested =
    matchingService?.requested_person === 1 ||
    matchingService?.requested_therapist === true ||
    slot?.requested_person === 1 ||
    slot?.tags?.isRequested === true;
  const hasRequestedRoom =
    matchingService?.requested_room === 1 ||
    slot?.requested_room === 1 ||
    slot?.tags?.hasRequestedRoom === true;
  const requestedCoupleRoom = !!roomTags.coupleRoom && !!hasRequestedRoom;
  const requestedShowerRoom = !!roomTags.showerRoom && !!hasRequestedRoom;
  const scrubService = !!categoryFields.scrubService;
  const pinkCard = !!isRequested;

  // console.log("card color >> requestedCoupleRoom", requestedCoupleRoom);
  console.log("card color >> requestedShowerRoom", requestedShowerRoom);
  // console.log("card color >> scrubService", scrubService);
  // console.log("card color >> pinkCard", pinkCard);
  console.log("card color >> roomTags", roomTags);

  return {
    coupleRoom: !!roomTags.coupleRoom,
    showerRoom: !!roomTags.showerRoom,
    isRequested: pinkCard,
    hasRequestedRoom: !!hasRequestedRoom,
    requestedCoupleRoom,
    requestedShowerRoom,
    scrubService,
    pinkCard,
    orangeCard: requestedCoupleRoom || (pinkCard && requestedCoupleRoom),
    yellowCard: scrubService || requestedShowerRoom || (pinkCard && requestedShowerRoom),
  };
};

/** Live service name/category/scrub fields for calendar cards during edit/replace. */
const getServiceCategoryFields = (serviceLike, fallback = {}) => {
  const serviceName =
    serviceLike?.name ||
    serviceLike?.service_name ||
    serviceLike?.service ||
    fallback.serviceName ||
    "";
  const explicitCategory = [
    serviceLike?.service_category_name,
    serviceLike?.service_category,
    serviceLike?.serviceCategoryName,
    serviceLike?.category_name,
  ].find((value) => value != null && String(value).trim() !== "");
  const incomingId = serviceLike?.id ?? serviceLike?.service_id ?? null;
  const fallbackId = fallback.serviceId ?? null;
  const serviceIdChanged =
    incomingId != null &&
    fallbackId != null &&
    String(incomingId) !== String(fallbackId);
  const serviceCategory = explicitCategory != null
    ? String(explicitCategory)
    : (serviceIdChanged ? "" : (fallback.serviceCategory || ""));
  const serviceId = incomingId ?? fallbackId ?? null;
  const categoryStr = String(serviceCategory || "").toLowerCase();
  const nameStr = String(serviceName || "").toLowerCase();
  return {
    serviceName,
    serviceCategory,
    serviceId,
    scrubService: categoryStr.includes("scrub") || nameStr.includes("scrub"),
  };
};

/** Match FilterPanel: red-dot / applied state from live roster vs selection. */
const computeTherapistFilterApplied = (filters, staffList, roomMode) => {
  if (roomMode) return true;

  if (filters?.gender && filters.gender.length > 0) return true;

  if (filters?.status && filters.status.length > 0) {
    const hasNonDefaultStatus = filters.status.some((s) => !defaultStatus.includes(s));
    if (hasNonDefaultStatus) return true;
  }

  if (staffList && staffList.length > 0) {
    const selectedStaffIdSet = new Set((filters?.staffIds || []).map((id) => String(id)));
    const allStaffSelected = staffList.every((staff) =>
      selectedStaffIdSet.has(String(staff.id))
    );
    if (!allStaffSelected) return true;
  }

  return false;
};

const Home = () => {
  const {
    loggedUser,
    outletList,
    setBookingData,
    filteredBookings,
    setFilteredBookings,
    bookingsList,
    setBookingsList,
    newAppointmentSlots,
    setNewAppointmentSlots,
    pendingBookings,
    setPendingBookings,
    selectedOutlet,
    homeStaffList,
    setHomeStaffList,
    setTriggerCancel,
    paxes,
    rightSidebarOpen,
    deletedBookingID,
    isNewBookingSlotActive, setIsNewBookingSlotActive,
    selectedBookingId, setSelectedBookingId,
    selectedTime,
    isEditBookingActive,
    setIsEditBookingActive,
    createdBooking, setCreatedBooking,
    setSelectedOutletName,
    intervalClass,
    tableInterval,
    selectedDate, setSelectedDate,
    timeSlots, setTimeSlots,
    bookingFilters,
    selectedOutletTiming, setSelectedOutletTiming,
    setSelectedOutletData,
    selectedOutletData,
    isViewOnlyOutlet,
    isCustomerSelected,
    setIsCustomerSelected,
    setSelectedTherapistIDFromRow,
    setSelectedTherapistFromRow,
    isSidebarVisible, setIsSidebarVisible,
    showBookingSummary, setShowBookingSummary,
    customerData,
    selectedServices,
    setSelectedServices,
    setPaxes,
    setSelectedTime,
    setServiceTotal,
    setAmountDue,
    setReference,
    setPaymentSummary,
    setPaymentHistory,
    setCurrentPaymentMethod,
    setCurrentPaymentMethodTitle,
    setVoucherStates,
    setVoucherDetail,
    editPrefill, setEditPrefill,
    showConflictModal, setShowConflictModal,
    setCompleteBookingWarning,
    setShowPaymentInProgressNotice,
    setIsGreyBox,
    isRoomFilterActive,
    setIsRoomFilterActive,
    homeRoomList,
    setHomeRoomList,
    selectedRooms,
    setSelectedRooms,
    setCustomerData,
    setSelectedSource,
    filteredItemIds,
    filteredRoomIds,
    setFilteredItemIds,
    setFilteredRoomIds,
    setBookingFilters,
    setIsFilterApplied,
    selectedRoomFilter,
    setSelectedRoomFilter,
    temporaryBookingId, setTemporaryBookingId,
    setTempBookingCreated,
    setHitTemporaryBookingApi,
    setPublicHoliday,
    setActiveSlotId,
    editableBooking,
    setEditableBooking,
    showFilterPanel,
    isBookingSidebar, setIsBookingSidebar,
   setShowCancelPopup,
   setShowFilterPanel,
   filterPanelRef,
   sidebarLoading,
   setSidebarLoading,
   itemData,
   editedItemData,
   setNewBookingActive,
   deletedBookingItemIds,
   setBookingDetails,
   setDiscountItemToPost,
   setNewSlot,
   setDeletedBookingItemIds,
   setDateSyncState,
  } = useGlobalContext();

  

  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isSelectedOutletLoaded, setIsSelectedOutletLoaded] = useState(false);
  const [isRoomFilterTransitionLoading, setIsRoomFilterTransitionLoading] = useState(false);
  // Suppress filter red-dot until get-filters has applied (avoids flash while staffIds still empty).
  const [calendarFiltersReady, setCalendarFiltersReady] = useState(false);
  const prevIsRoomFilterActiveRef = useRef(undefined);
  const prevFilterModeScrollRef = useRef(undefined);
  const pendingFilterModeScrollRef = useRef(null);
  const prevEmptyDraftDateScrollRef = useRef(null);
  const suppressRoomFilterTransitionRef = useRef(false);
  const scrollRetries = useRef(0);
  const previousHomeStaffIdsRef = useRef([]);
  const pendingAutoPersistFiltersRef = useRef(null);
  // Always holds the latest persistTherapistFilters callback so it can be invoked
  // immediately (without waiting for the next render/effect cycle) right when a
  // new therapist is detected — this shrinks the window in which another tab
  // (e.g. a duplicated tab) could load the calendar filters before this save lands.
  const persistTherapistFiltersRef = useRef(null);
  // Tracks the outlet/date key for which we've already scheduled the one-time
  // "self-heal" therapist resync below, so it only runs once per selection.
  const staffResyncKeyRef = useRef(null);
  const [searchQuery] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCells, setSelectedCells] = useState(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [startCell, setStartCell] = useState(null);
  const [bookingIdToScroll, setBookingIdToScroll] = useState(null);
  const homeStaffListRef = useRef(homeStaffList);
  homeStaffListRef.current = homeStaffList;

  useEffect(() => {
    const paramBookingId = searchParams.get("bookingId");
    if (!paramBookingId && typeof window !== "undefined") {
      sessionStorage.removeItem("skipCurrentTimeAutoScroll");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const paramBookingId = searchParams.get('bookingId');
    const paramDate = searchParams.get('date');

    if (paramBookingId && paramDate) {
      const newDate = parseCalendarRedirectDate(paramDate);
      if (newDate) {
        const isSameSelectedDay =
          selectedDate instanceof Date &&
          !isNaN(selectedDate.getTime()) &&
          selectedDate.getFullYear() === newDate.getFullYear() &&
          selectedDate.getMonth() === newDate.getMonth() &&
          selectedDate.getDate() === newDate.getDate();

        if (!isSameSelectedDay) {
          setSelectedDate(newDate);
        }
        sessionStorage.setItem("skipCurrentTimeAutoScroll", "1");
        setSelectedBookingId(paramBookingId);
        setShowBookingSummary(true);
        setIsBookingSidebar(false);
        setIsSidebarVisible(true);
        scrollRetries.current = 0;
        setBookingIdToScroll(paramBookingId);

        // Clear params to avoid loop/re-trigger
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('bookingId');
        newParams.delete('date');
        setSearchParams(newParams);
      }
    }
  }, [searchParams, selectedDate, setSelectedDate, setSelectedBookingId, setIsSidebarVisible, setIsBookingSidebar, setShowBookingSummary, setSearchParams]);
  const [staffTiming, setStaffTiming] = useState([]);
  const [hasRoomsLoaded, setHasRoomsLoaded] = useState(false);
  const [hasStaffLoaded, setHasStaffLoaded] = useState(false);
  const [breaksData, setBreaksData] = useState([]);
  const [activeDropdownId, setActiveDropdownId] = useState(null);
  
  // Customer details modal state
  const [isCustomerDetailsOpen, setIsCustomerDetailsOpen] = useState(false);
  const [selectedCustomerForDetails, setSelectedCustomerForDetails] = useState(null);
  const [isBookingSubmitting, setIsBookingSubmitting] = useState(false);
  const [isHardQueueSidebarOpen, setIsHardQueueSidebarOpen] = useState(false);
  // Track the *specific* card that should show the black border (booking_id + item_id).
  // This avoids highlighting multiple cards that share the same booking_id (e.g., group bookings / multiple items).
  const [activeCardKey, setActiveCardKey] = useState(null);
  
  // Ref to track previous selectedTime value to detect actual time changes
  const prevSelectedTimeRef = useRef(null);
  
  
  // Drag and drop state for booking cards
  const [isDraggingCard, setIsDraggingCard] = useState(false);
  // Hide sidebar during drag on mobile only (matches sidebar CSS max-width: 600px)
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 600
  );
  const [draggedCard, setDraggedCard] = useState(null);
  const [dragKey, setDragKey] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [ghostCard, setGhostCard] = useState(null);
  const [dropTime, setDropTime] = useState(null); // { value, absoluteMinutes } while dragging
  // Prefill data for Edit sidebar when dropping existing bookings
  // const [editPrefill, setEditPrefill] = useState(null);
  // Persist visual overrides across multiple drags (per booking-item)
  const [dragOverrides, setDragOverrides] = useState({}); // { [itemId]: { bookingId, start, end, therapistId, therapistName } }
  // Protects calendar-drag schedule from stale edit-hydrate editOverrideUpdate reverts
  // (e.g. 12:50 drag overwritten by API 10:00). Keyed by booking_item_id.
  const lastCalendarDragScheduleRef = useRef({});
  // Refs for synchronous drag state tracking
  const isDraggingRef = useRef(false);
  const dropTargetRef = useRef(null);
  const dragUpdateInProgressRef = useRef(false);
  // Generation token so overlapping setTimeout clears cannot unlock a newer drag update early
  const dragUpdateGenerationRef = useRef(0);
  // Mirror of dragOverrides for drop handlers (avoids stale closure on rapid re-drags)
  const dragOverridesRef = useRef({});
  const skipNextDiscardCheckRef = useRef(false);
  const hasCleanedUpTemporaryBookingRef = useRef(false);
  const dragPositionRef = useRef({ x: 0, y: 0 }); // For immediate position updates
  const dragOffsetRef = useRef({ x: 0, y: 0 }); // For immediate offset access
  const dragStartPosRef = useRef({ x: 0, y: 0 }); // For drag threshold detection
  const rafIdRef = useRef(null); // For requestAnimationFrame
  const dragCursorStyleRef = useRef(null); // For global cursor style during drag
  const isCreatingBookingRef = useRef(false); // Prevents sidebar from closing when booking is being created
  const draggedCardRef = useRef(null); // For immediate access to dragged card 
  const dragAutoScrollIntervalRef = useRef(null); // For auto-scroll during drag
  const dragScrollSpeedRef = useRef({ x: 0, y: 0 }); // Store current scroll speeds
  const dragScrollTargetSpeedRef = useRef({ x: 0, y: 0 }); // Store target scroll speeds for smooth interpolation
  const viewOnlyToastShownRef = useRef(false);
  const cardClickHandledViaPointerRef = useRef(false); // Prevents double-open when pointerup and click both fire
  const blankTouchTap = () => ({
    key: null,
    time: 0,
    x: 0,
    y: 0,
    rowIndex: null,
    colIndex: null,
    completed: false,
  });
  const lastTouchTapRef = useRef(blankTouchTap()); // Completed touch tap, used for double-tap
  const touchHoldTimerRef = useRef(null); // Cleared with the active touch gesture
  const touchGestureRef = useRef(null); // Active touch gesture listeners
  const touchSelectedKeyRef = useRef(null); // Card last selected by touch
  const suppressNextCardClickRef = useRef(false); // Blocks browser click after touch tap
  const touchSuppressTokenRef = useRef(0); // Ignores an older suppress timeout after a newer tap
  const touchDragArmedRef = useRef(false); // Legacy guard so a touch release never opens the sidebar

  const clearActiveTouchGesture = () => {
    if (touchHoldTimerRef.current) {
      clearTimeout(touchHoldTimerRef.current);
      touchHoldTimerRef.current = null;
    }
    const gesture = touchGestureRef.current;
    touchGestureRef.current = null;
    if (typeof gesture?.detach === "function") {
      gesture.detach();
    }
  };

  const clearTouchCardSelection = () => {
    touchSelectedKeyRef.current = null;
    lastTouchTapRef.current = blankTouchTap();
  };

  const armTouchClickSuppress = () => {
    suppressNextCardClickRef.current = true;
    const token = touchSuppressTokenRef.current + 1;
    touchSuppressTokenRef.current = token;
    setTimeout(() => {
      if (touchSuppressTokenRef.current === token) {
        suppressNextCardClickRef.current = false;
      }
    }, 500);
  };

  useEffect(() => {
    return () => {
      clearActiveTouchGesture();
    };
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobileViewport(window.innerWidth <= 600);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const normalizeComparableClock = useCallback((value) => {
    if (value == null || value === "") return null;
    const raw = String(value).trim();
    if (!raw) return null;
    if (/(AM|PM)/i.test(raw)) {
      try {
        return convertTo24HourFormat(raw).slice(0, 5);
      } catch {
        return null;
      }
    }
    const match = raw.match(/^(\d{1,2}):(\d{2})/);
    if (!match) return null;
    return `${String(match[1]).padStart(2, "0")}:${match[2]}`;
  }, []);

  const rememberCalendarDragSchedule = useCallback((itemId, schedule) => {
    if (itemId == null || !schedule) return;
    const key = String(itemId);
    const prev = lastCalendarDragScheduleRef.current[key] || null;
    const fromCalendarDrag = schedule?.fromCalendarDrag === true;
    lastCalendarDragScheduleRef.current[key] = {
      ...schedule,
      // Only calendar drops stamp a fresh protect window; sidebar updates keep the prior one.
      calendarDragAt: fromCalendarDrag
        ? Date.now()
        : (prev?.calendarDragAt ?? schedule?.calendarDragAt ?? null),
      rememberedAt: Date.now(),
    };
  }, []);

  const clearCalendarDragSchedule = useCallback((itemId = null) => {
    if (itemId == null) {
      lastCalendarDragScheduleRef.current = {};
      return;
    }
    delete lastCalendarDragScheduleRef.current[String(itemId)];
  }, []);

  const applyDragOverrides = useCallback((updater) => {
    setDragOverrides((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      dragOverridesRef.current = next && typeof next === "object" ? next : {};
      return next;
    });
  }, []);

  const beginDragUpdate = useCallback(() => {dragUpdateInProgressRef.current = true; dragUpdateGenerationRef.current += 1; return dragUpdateGenerationRef.current;}, []);

  const endDragUpdateAfter = useCallback((generation, delayMs = 150) => {
    setTimeout(() => {
      if (dragUpdateGenerationRef.current === generation) {
        dragUpdateInProgressRef.current = false;
      }
    }, delayMs);
  }, []);

  const normalizeNumericId = useCallback((value) => {
    if (value == null || value === "") return null;
    if (typeof value === "object") {
      const nestedValue = value?.value ?? value?.id ?? value?.item_id ?? value?.room_id ?? null;
      if (nestedValue == null || nestedValue === "") return null;
      const normalizedNested = Number(nestedValue);
      return Number.isNaN(normalizedNested) ? null : normalizedNested;
    }
    const normalized = Number(value);
    return Number.isNaN(normalized) ? null : normalized;
  }, []);

  const getServiceTherapistId = useCallback((service) => (
    normalizeNumericId(
      service?.therapist ??
      service?.therapist_id ??
      service?.therapistId ??
      service?.selected_therapist?.value ??
      null
    )
  ), [normalizeNumericId]);

  const getServiceRoomItemId = useCallback((service) => (
    normalizeNumericId(
      service?.roomId ??
      service?.room ??
      service?.room_segments?.[0]?.room_id ??
      service?.room_items?.[0]?.item_id ??
      service?.room_items?.[0]?.room_id ??
      null
    )
  ), [normalizeNumericId]);

  const getServiceStartTime = useCallback((service) => (
    service?.start_time || service?.service_at?.split(" ")?.[1]?.slice(0, 5) || null
  ), []);

  const getServiceEndTime = useCallback((service) => {
    const explicitEndTime = service?.end_time || null;
    if (explicitEndTime) return explicitEndTime;
    const startTime = getServiceStartTime(service);
    const duration = Number(service?.duration) || 0;
    return startTime && duration > 0 ? calculateEndTime(startTime, duration) : null;
  }, [getServiceStartTime]);

  const normalizeComparableTime = useCallback((timeValue) => {
    if (!timeValue) return null;
    const parts = String(timeValue).trim().split(":");
    if (parts.length < 2) return null;
    const hours = String(parseInt(parts[0], 10) || 0).padStart(2, "0");
    const minutes = String(parseInt(parts[1], 10) || 0).padStart(2, "0");
    return `${hours}:${minutes}`;
  }, []);

  const normalizeComparableDate = useCallback((dateValue) => {
    if (!dateValue) return null;
    const raw = String(dateValue).trim().split(" ")[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
      const [day, month, year] = raw.split("-");
      return `${year}-${month}-${day}`;
    }
    return null;
  }, []);

  const allDraftServices = useMemo(() => ([
    ...(Array.isArray(selectedServices) ? selectedServices : []),
    ...(Array.isArray(paxes) ? paxes.flatMap((pax) => Array.isArray(pax.selectedServices) ? pax.selectedServices : []) : []),
  ]), [selectedServices, paxes]);

  const totalDraftServiceCount = allDraftServices.length;
  const singleDraftService = totalDraftServiceCount === 1 ? allDraftServices[0] : null;

  const pageRef = useRef();
  const sidebarRef = useRef();
  const newBookingCardRef = useRef();
  const HOME_BOOKINGS_REFRESH_FLAG = 'forceHomeCalendarRefresh';
  // Initialize from sessionStorage to persist across navigation
  const initializeLastBookingsFetchKey = () => {
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem('lastBookingsFetchKey');
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch (e) {
          return { outlet: null, date: null };
        }
      }
    }
    return { outlet: null, date: null };
  };
  const lastBookingsFetchKeyRef = useRef(initializeLastBookingsFetchKey());
  const bookingsFetchRequestIdRef = useRef(0);
  const bookingsListLengthRef = useRef(0);
  // Outlet+date already fetched for this Home visit. Blocks socket/state updates from refetching.
  const homeVisitSelectionRef = useRef(null);
  bookingsListLengthRef.current = bookingsList.length;
  // Initialize therapist list fetch key from sessionStorage
  const initializeLastTherapistListFetchKey = () => {
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem('lastTherapistListFetchKey');
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch (e) {
          return { outlet: null, date: null };
        }
      }
    }
    return { outlet: null, date: null };
  };
  const lastTherapistListFetchKeyRef = useRef(initializeLastTherapistListFetchKey());
  const prevCalendarSelectionRef = useRef({ outlet: null, date: null });
  const calendarDataLoadedRef = useRef({
    selectedOutlet: false,
    outletTiming: false,
    therapistList: false,
    therapistTimings: false,
    rooms: false,
    calendarFilters: false,
    bookingList: false,
  });
  const checkAllCalendarDataLoaded = useCallback(() => {
    const r = calendarDataLoadedRef.current;
    if (r.selectedOutlet && r.outletTiming && r.therapistList && r.therapistTimings && r.rooms && r.calendarFilters && r.bookingList) {
      setIsTableLoading(false);
    }
  }, []);
  const bookingWrapperRef = useRef(null);
  const bookingTableRef = useRef(null);

  // Table is mounted and time rows exist — scroll even when filters leave no room/therapist columns.
  const isCalendarGridReady = useMemo(() => {
    if (isTableLoading || isRoomFilterTransitionLoading) return false;
    return (timeSlots?.length ?? 0) > 0;
  }, [isTableLoading, isRoomFilterTransitionLoading, timeSlots]);

  const { linePosition, currentTimeRowRef } = useBookingTableCurrentTime({
    selectedDate,
    tableInterval,
    timeSlots,
    isCalendarGridReady,
    selectedOutlet,
    bookingFilters,
    selectedRoomFilter,
    filteredRoomIds,
    filteredItemIds,
    newAppointmentSlotsLength: newAppointmentSlots.length,
    isRoomFilterActive,
    isSidebarVisible,
    isTableLoading,
    bookingWrapperRef,
    bookingTableRef,
  });

// GET SELECTED OUTLET START 
const selectedOutletRequestIdRef = useRef(0);
const fetchData = useCallback(async () => {
  if (!selectedOutlet) return;
  const reqId = ++selectedOutletRequestIdRef.current;
  const outletIdForRequest = selectedOutlet;
  try {
    // setLoading(true);
    let item = {
      "is_image": 1,
    }
    const response = await getSelectedOutlet(outletIdForRequest, item);
    // Ignore stale responses from a previous outlet after a quick switch.
    if (reqId !== selectedOutletRequestIdRef.current) return;
    const data = extractOutletFromApiResponse(response);
    if (!data) {
      throw new Error("Selected outlet response did not include outlet data");
    }
    if (Number(data.id) !== Number(outletIdForRequest)) return;
    setSelectedOutletData(data);
    setSelectedOutletName(data.name);
    // fetchCalendarFilters removed - called by useEffect when staff list is loaded (avoids duplicate API call)
  } catch (error) {
    if (reqId !== selectedOutletRequestIdRef.current) return;
    console.error("Error:", error);
  } finally {
    if (reqId !== selectedOutletRequestIdRef.current) return;
    setIsSelectedOutletLoaded(true);
    calendarDataLoadedRef.current.selectedOutlet = true;
    checkAllCalendarDataLoaded();
    // setLoading(false);
  }
// eslint-disable-next-line react-hooks/exhaustive-deps -- fetchCalendarFilters intentionally omitted to avoid cascade
}, [selectedOutlet]);
useEffect(() => {
  fetchData();
}, [fetchData]);
// GET SELECTED OUTLET END

// GET OUTLET TIMING START 
const fetchOutletTimings = useCallback(async (outletId) => {
  if (!outletId) return;
  const startDateObj = selectedDate;
  const endDateObj = new Date(startDateObj);

  let item = {
    outlet: outletId,
    start_date: formatDateForBackend(startDateObj),
    end_date: formatDateForBackend(endDateObj),
    pagination: 0,
    status: 1,
  };
  try {
    const response = await getOutletTiming(item);
    const listData = response?.data?.data?.list || {};
    setSelectedOutletTiming(listData?.outlet_timing || {});
  } catch (err) {
    console.error("Error fetching outlet timings:", err);
  } finally {
    calendarDataLoadedRef.current.outletTiming = true;
    checkAllCalendarDataLoaded();
  }
}, [selectedDate, checkAllCalendarDataLoaded, setSelectedOutletTiming]);
useEffect(() => {
  if (selectedOutlet && selectedDate) {
    fetchOutletTimings(selectedOutlet);
  }
}, [selectedOutlet, selectedDate, fetchOutletTimings]);
const isOutletExtended = useMemo(
  () => getOutletExtendedFlagForDate(selectedOutletData?.timing, selectedDate),
  [selectedOutletData?.timing, selectedDate]
);
// GET OUTLET TIMING END
 
  

// GET THERAPIST LIST START 
  const fetchTherapists = async (options = {}) => {
    const force = options && options.force === true;
    const silent = options && options.silent === true;
    if (!selectedOutlet || !selectedDate) return;
    
    const currentFetchKey = {
      outlet: selectedOutlet || null,
      date: selectedDate ? formatDateForBackend(selectedDate) : null,
    };
    const lastFetchKey = lastTherapistListFetchKeyRef.current;
    const isSameSelection = lastFetchKey.outlet === currentFetchKey.outlet && lastFetchKey.date === currentFetchKey.date;
    
    // Skip fetch if same selection AND not forcing AND data exists
    // Note: homeStaffList is accessed via closure, so it gets the current value
    if (!force && isSameSelection && homeStaffList.length > 0) {
      setHasStaffLoaded(true);
      calendarDataLoadedRef.current.therapistList = true;
      checkAllCalendarDataLoaded();
      return;
    }
    
    if (!silent) {
      setHasStaffLoaded(false); // Reset flag before fetching
      setHomeStaffList([]);
    }
    let item = {
      outlet: selectedOutlet,
      service_at: formatDateForBackend(selectedDate),
      availability: 0,
      pagination: 0,
      status: 1,
      role: 2,
      leave: 0,
      receptionist_id: loggedUser || null,
      calendar: 1,
    };

    try {
      const response = await getTherapistList(item);
      // Handle both response structures: responseData.data.list.staffs or responseData.data.data.list.staffs
      const responseData = response?.data;
      const allApiStaffs = responseData?.data?.list?.staffs || responseData?.data?.data?.list?.staffs || [];
      setHomeStaffList(allApiStaffs);
      setHasStaffLoaded(true);
      // fetchCalendarFilters();
      // Update last fetched key and persist to sessionStorage
      lastTherapistListFetchKeyRef.current = currentFetchKey;
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('lastTherapistListFetchKey', JSON.stringify(currentFetchKey));
      }
    } catch (error) {
      console.error("Error fetching therapists:", error);
      if (!silent) {
        setHomeStaffList([]);
      }
      setHasStaffLoaded(true);
    } finally {
      calendarDataLoadedRef.current.therapistList = true;
      checkAllCalendarDataLoaded();
    }
  };
  useEffect(() => {
    if (selectedOutlet && selectedDate) {
      const prev = prevCalendarSelectionRef.current;
      const outletUnchanged = prev.outlet === selectedOutlet;
      prevCalendarSelectionRef.current = { outlet: selectedOutlet, date: selectedDate };
      // When only date changes, keep selectedOutlet true (outlet API is not re-fetched)
      calendarDataLoadedRef.current = {
        selectedOutlet: outletUnchanged,
        outletTiming: false,
        therapistList: false,
        therapistTimings: false,
        rooms: false,
        calendarFilters: false,
        bookingList: false,
      };
      // Only reset selected outlet readiness when outlet actually changes.
      // On date change, selectedOutletData.timing is still valid and needed for list API flag.
      setIsSelectedOutletLoaded(outletUnchanged);
      setIsTableLoading(true);
      setCalendarFiltersReady(false);
      setIsFilterApplied(false);
      // Suppress room filter transition loader when outlet changes (fetchCalendarFilters will set mode)
      suppressRoomFilterTransitionRef.current = true;
    } else {
      setIsTableLoading(false);
    }
  }, [selectedOutlet, selectedDate]);

  useEffect(() => {
    // Fetch therapists when:
    // 1. selectedOutlet and selectedDate are available
    if (selectedOutlet && selectedDate) {
      // Always call fetchTherapists - it will check internally if fetch is needed
      // Force fetch if no data exists (handles reload case)
      const shouldForce = homeStaffList.length === 0;
      fetchTherapists({ force: shouldForce });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOutlet, selectedDate]);
  useEffect(() => {
    if (!selectedOutlet || !selectedDate || !hasStaffLoaded) return;
    const key = `${selectedOutlet}_${formatDateForBackend(selectedDate)}`;
    if (staffResyncKeyRef.current === key) return;
    staffResyncKeyRef.current = key;
    const timer = setTimeout(() => {
      fetchTherapists({ force: true, silent: true });
    }, 3000);
    return () => clearTimeout(timer);
  }, [selectedOutlet, selectedDate, hasStaffLoaded]);
// GET THERAPIST LIST END

// GET THERAPIST TIMING START 
  const fetchTherapistTimings = useCallback(async () => {
    const startDateObj = selectedDate;
    const endDateObj = new Date(startDateObj);
    let item = {
      outlet: selectedOutlet,
      start_date: formatDateForBackend(startDateObj),
      end_date: formatDateForBackend(endDateObj),
      leave: 0,
    };
    try {
      const response = await getTherapistTimings(item);
      if(response?.success === true) {
        setStaffTiming(response?.data?.list || {});
      } else {
        setStaffTiming({});
      }
    } catch (error) {
      console.error("Error fetching timing:", error);
      setStaffTiming({});
    } finally {
      calendarDataLoadedRef.current.therapistTimings = true;
      checkAllCalendarDataLoaded();
    }
  }, [selectedOutlet, selectedDate, checkAllCalendarDataLoaded]);
  useEffect(() => {
    fetchTherapistTimings();
  }, [fetchTherapistTimings]);
// GET THERAPIST TIMING END 


// GET ROOM LIST START 
  useEffect(() => {
    if (selectedOutlet && selectedDate) {
      setHasRoomsLoaded(false); // Reset flag before fetching
      setHomeRoomList([]);
      const fetchRooms = async () => {
        let item = {
          date: formatDateToDDMMYYYY(selectedDate),
        };

        try {
          const response = await getRoomsForCalendar(selectedOutlet, item);
          if(response?.success === true) {
            const AllRooms = Array.isArray(response?.data) ? response.data : [];
            setHomeRoomList(AllRooms);
            setHasRoomsLoaded(true);
          } else {
            setHomeRoomList([]);
            setHasRoomsLoaded(true);
          }
        } catch (error) {
          console.error("Error fetching rooms:", error);
          setHomeRoomList([]);
          setHasRoomsLoaded(true);
        } finally {
          calendarDataLoadedRef.current.rooms = true;
          checkAllCalendarDataLoaded();
        }
      };

      fetchRooms();
    } else {
      // Reset flag when switching to therapist mode
      setHasRoomsLoaded(false);
    }
  }, [selectedOutlet, selectedDate, setHomeRoomList, checkAllCalendarDataLoaded]); 
// GET ROOM LIST END


// GET SAVED CALENDAR FILTERS START 
 const fetchCalendarFilters = async () => {
  try {
    let params = {
      outlet_id: selectedOutlet,
      user_id: loggedUser,
    };
    const response = await getCalendarFilters(params);
    const data = response.data.data;


    let updatedDataResponse = {};
    if (Array.isArray(data) && data.length === 0) {
      // Get all staff IDs when homeStaffList is not blank
      const allStaffIds = homeStaffList && Array.isArray(homeStaffList) && homeStaffList.length > 0 
      ? homeStaffList.map((staff) => staff.id)
      : [];

      const allRoomIds = homeRoomList.length > 0 
            ? homeRoomList.map((room) => room.room_id)
            : [];
      const defaultFilters = {
        gender: [],
        status: defaultStatus, // Default: all statuses checked except 'Cancelled' and 'No-show'
        staffIds: allStaffIds, // All staff IDs
        roomItem: [],
        roomIds: allRoomIds,
        allTherapist: true, // No saved record means all therapists are selected
        isFilterApplied: false,
      }
  
      updatedDataResponse = {
        id: null,
        user_id: loggedUser,
        outlet_id: selectedOutlet,
        mode: "therapist",
        filters: defaultFilters,
      }
      // Don't return here - continue to set the filters below
    }else{
      updatedDataResponse = data;
    }


    // Use mode from updatedDataResponse (handles both saved filters and default filters)
    const mode = updatedDataResponse.mode || (Array.isArray(updatedDataResponse) && updatedDataResponse.length === 0 ? "therapist" : updatedDataResponse?.mode);
    setIsRoomFilterActive(mode === "room" ? true : false);


    const savedFilters = updatedDataResponse.filters;
    // Prefer latest roster (ref) so a delayed filters response cannot drop newly loaded staff.
    const staffListForMerge = homeStaffListRef.current?.length
      ? homeStaffListRef.current
      : homeStaffList;
    // Normalize saved staff filters for current outlet when not in room mode
    let mergedFilters = { ...savedFilters };
    if (mode !== "room" && staffListForMerge.length > 0) {
      const savedStaffIds = savedFilters.staffIds || [];
      const allCurrentStaffIds = staffListForMerge.map(staff => staff.id);
      const allCurrentStaffIdSet = new Set(allCurrentStaffIds.map((id) => String(id)));

      if (savedFilters.allTherapist === true) {
        // Union API staffIds with the current roster so we never drop IDs the
        // server already saved (e.g. newly added therapist) when the in-memory
        // list briefly lags, and we still pick up anyone now on duty.
        const staffIdByKey = new Map();
        savedStaffIds.forEach((id) => {
          staffIdByKey.set(String(id), id);
        });
        allCurrentStaffIds.forEach((id) => {
          staffIdByKey.set(String(id), id);
        });
        mergedFilters = {
          ...savedFilters,
          staffIds: Array.from(staffIdByKey.values()),
        };
      } else {
        // Filter out staff IDs that no longer exist in current outlet (e.g. after outlet switch)
        const validSavedStaffIds = savedStaffIds.filter((id) =>
          allCurrentStaffIdSet.has(String(id))
        );
        const shouldUseAllCurrentStaff =
          validSavedStaffIds.length === 0 &&
          savedFilters.allTherapist !== false &&
          (!savedFilters.isFilterApplied || savedStaffIds.length === 0);
        const normalizedStaffIds = shouldUseAllCurrentStaff ? allCurrentStaffIds : validSavedStaffIds;

        if (
          shouldUseAllCurrentStaff ||
          normalizedStaffIds.length !== savedStaffIds.length
        ) {
          mergedFilters = {
            ...savedFilters,
            staffIds: normalizedStaffIds,
          };
        }
      }
    }

    mergedFilters = {
      ...mergedFilters,
      status: withDefaultStatus(mergedFilters?.status),
    };

    const computedFilterApplied = computeTherapistFilterApplied(
      mergedFilters,
      staffListForMerge,
      mode === "room"
    );
    mergedFilters = {
      ...mergedFilters,
      isFilterApplied: computedFilterApplied,
    };
    
    setBookingFilters(mergedFilters);
    setIsFilterApplied(computedFilterApplied);
    
    // Convert roomItem to selectedRoomFilter and compute filtered IDs
    if (mode === "room" && savedFilters.roomItem) {
      const roomItem = savedFilters.roomItem.length > 0 ? savedFilters.roomItem[0] : "";
      
      let filterType = "resources"; // default
      if (roomItem === "Room") {
        filterType = "rooms";
      } else if (roomItem === "Sofa") {
        filterType = "sofa";
      } else if (roomItem === "Monkey Chair") {
        filterType = "monkeyChair";
      }
      
      setSelectedRoomFilter(filterType);
      
      // Compute filtered item IDs and room IDs
      if (homeRoomList && homeRoomList.length > 0) {
        const filteredRooms = getFilteredRoomsForType(filterType, homeRoomList);
        const selectedRoomIds = savedFilters.roomIds || [];
        
        // Get selected rooms
        const selectedRooms = filteredRooms.filter(room => 
          selectedRoomIds.includes(room.room_id)
        );
        
        // Collect all item_id values from selected rooms
        const allItemIds = selectedRooms.reduce((itemIds, room) => {
          if (room.items && Array.isArray(room.items)) {
            const roomItemIds = room.items.map(item => item.item_id).filter(id => id !== undefined && id !== null);
            return [...itemIds, ...roomItemIds];
          }
          return itemIds;
        }, []);
        
        setFilteredItemIds(allItemIds);
        setFilteredRoomIds(selectedRoomIds);
      }
    } else {
      // Clear filtered IDs when in therapist mode
      setFilteredItemIds([]);
      setFilteredRoomIds([]);
    }
  } catch (error) {
    console.error("Error:", error);
  } finally {
    calendarDataLoadedRef.current.calendarFilters = true;
    setCalendarFiltersReady(true);
    checkAllCalendarDataLoaded();
  }
};
// Call fetchCalendarFilters when staff list has finished loading (single source of truth - avoids duplicate call from fetchData)
useEffect(() => {
  if (selectedOutlet && loggedUser && hasStaffLoaded) {
    const timer = setTimeout(() => {
      fetchCalendarFilters();
    }, 500); // Small delay to ensure state is updated

    // Cleanup timeout when dependencies change or component unmounts
    return () => clearTimeout(timer);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [hasStaffLoaded, checkAllCalendarDataLoaded]);

// Recompute filtered IDs when homeRoomList is loaded
// useEffect(() => {
//   if (isRoomFilterActive && selectedRoomFilter && homeRoomList && homeRoomList.length > 0 && bookingFilters && Array.isArray(bookingFilters.roomIds)) {
//     const filteredRooms = getFilteredRoomsForType(selectedRoomFilter, homeRoomList);
//     const selectedRoomIds = bookingFilters.roomIds || [];
    
//     // Get selected rooms
//     const selectedRooms = filteredRooms.filter(room => 
//       selectedRoomIds.includes(room.room_id)
//     );
    
//     // Collect all item_id values from selected rooms
//     const allItemIds = selectedRooms.reduce((itemIds, room) => {
//       if (room.items && Array.isArray(room.items)) {
//         const roomItemIds = room.items.map(item => item.item_id).filter(id => id !== undefined && id !== null);
//         return [...itemIds, ...roomItemIds];
//       }
//       return itemIds;
//     }, []);
    
//     setFilteredItemIds(allItemIds);
//     setFilteredRoomIds(selectedRoomIds);
//   } else if (!isRoomFilterActive) {
//     // Clear filtered IDs when in therapist mode
//     setFilteredItemIds([]);
//     setFilteredRoomIds([]);
//   }
// }, [isRoomFilterActive, selectedRoomFilter, homeRoomList, bookingFilters, setFilteredItemIds, setFilteredRoomIds]);
// GET SAVED CALENDAR FILTERS END


// GET BOOKING LIST START
  // APICalling returns the Axios error instead of throwing, so a failed cards call
  // must not be stored as an empty calendar.
  const isCalendarCardsRequestError = (value) => {
    if (!value || typeof value !== 'object') return false;
    if (value.isAxiosError === true || value instanceof Error) return true;
    if (value.status === false) return true;
    const status = value.response?.status;
    return typeof status === 'number' && status >= 400;
  };

  const calendarCardsRequestStatus = (value) => value?.response?.status ?? null;

  const getCalendarCardsErrorMessage = (value) => {
    const candidates = [value?.response?.data?.message,value?.response?.data?.error,value?.data?.message,
      // Successful HTTP body shaped as { status: false, message }
      value?.status === false ? value?.message : null,
    ];
    for (const candidate of candidates) {
      const normalized = candidate != null ? String(candidate).trim() : '';
      if (normalized) return normalized;
    }
    return 'Error in loading calendar bookings.';
  };

  const fetchBookingsData = useCallback(async (options = {}) => {
    const silent = options && options.silent === true;
    const force = options && options.force === true;
    if (!isSelectedOutletLoaded) {
      return;
    }
    if (!silent) setIsTableLoading(true);
    // Build a unique key from outlet + date to avoid redundant calls
    const currentFetchKey = {
      outlet: selectedOutlet || null,
      date: selectedDate ? formatDateToDDMMYYYY(selectedDate) : null,
    };
    const lastFetchKey = lastBookingsFetchKeyRef.current;
    const isSameSelection = lastFetchKey.outlet === currentFetchKey.outlet && lastFetchKey.date === currentFetchKey.date;
    // Skip fetch only if same selection AND not forcing AND bookingsList has data.
    // Length is read from a ref so socket/list updates do not recreate this callback.
    if (!silent && !force && isSameSelection && bookingsListLengthRef.current > 0) {
      calendarDataLoadedRef.current.bookingList = true;
      checkAllCalendarDataLoaded();
      return;
    }
    if (!silent) calendarDataLoadedRef.current.bookingList = false;
    const requestPayload = {
      outlet_id: selectedOutlet,
      date: formatDateToYMD(selectedDate),
    };
    const requestId = bookingsFetchRequestIdRef.current + 1;
    bookingsFetchRequestIdRef.current = requestId;

    const isCurrentRequest = () => requestId === bookingsFetchRequestIdRef.current;

    const applyCalendarCards = (response) => {
      const cards = response?.data?.data?.cards || response?.data?.cards || [];
      const allBookings = adaptCalendarCardsToBookingsList(cards);
      setBookingsList(allBookings);
      lastBookingsFetchKeyRef.current = currentFetchKey;
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('lastBookingsFetchKey', JSON.stringify(currentFetchKey));
      }
      toast.dismiss("calendar-cards-error");
      calendarDataLoadedRef.current.bookingList = true;
      checkAllCalendarDataLoaded();
    };

    const failCalendarCards = (error) => {
      console.error("Error fetching bookings:", error);
      if (!silent) {
        const errorMessage = getCalendarCardsErrorMessage(error);
        toast.dismiss("calendar-cards-error");
        toast.error(errorMessage, {
          position: "bottom-center",
          autoClose: false,
          toastId: "calendar-cards-error",
          closeOnClick: false,
          transition: calendarCardsToastTransition,
        });
      }
      calendarDataLoadedRef.current.bookingList = true;
      checkAllCalendarDataLoaded();
    };

    try {
      let response = await getCalendarCards(requestPayload);
      if (!isCurrentRequest()) {
        return;
      }
      // 401 is already refreshed once inside APICalling. Call cards once more
      // with the new token. Any other status is a failure — do not clear the list.
      if (isCalendarCardsRequestError(response) && calendarCardsRequestStatus(response) === 401) {
        response = await getCalendarCards(requestPayload);
        if (!isCurrentRequest()) {
          return;
        }
      }
      if (isCalendarCardsRequestError(response)) {
        failCalendarCards(response);
        return;
      }
      applyCalendarCards(response);
    } catch (error) {
      if (!isCurrentRequest()) {
        return;
      }
      failCalendarCards(error);
    }
  }, [selectedOutlet, selectedDate, setBookingsList, setIsTableLoading, checkAllCalendarDataLoaded, isSelectedOutletLoaded]);

  // Fetch on entering Home (reload or navigation) and when date/outlet changes.
  // Does not run for socket events or other bookingsList updates.
  useEffect(() => {
    if (!selectedDate || !selectedOutlet || !isSelectedOutletLoaded) {
      return;
    }

    const selectionKey = `${selectedOutlet}|${formatDateToDDMMYYYY(selectedDate)}`;
    if (homeVisitSelectionRef.current === null) {
      homeVisitSelectionRef.current = selectionKey;
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(HOME_BOOKINGS_REFRESH_FLAG);
      }
      fetchBookingsData({ force: true });
      return;
    }

    if (homeVisitSelectionRef.current === selectionKey) {
      return;
    }

    homeVisitSelectionRef.current = selectionKey;
    fetchBookingsData({ force: true });
  }, [selectedDate, selectedOutlet, isSelectedOutletLoaded, fetchBookingsData]);
// GET BOOKING LIST END 

  // GET PUBLIC HOLIDAY LIST START 
  const fetchPublicHolidayList = useCallback(async () => {
    try {
      // setLoading(true);
      let item = {
        pagination: 0,
        daterange: `${formatDateToDDMMYYYY(selectedDate)} / ${formatDateToDDMMYYYY(selectedDate)}`,
        type: 'Public Holiday',
      };
      const response = await getPublicHolidayList(item);
      const publicHolidayList = response.data.data.list.holiday;
      
      // Format selectedDate to YYYY-MM-DD to match API response format
      const formatToYYYYMMDD = (dateInput) => {
        if (!dateInput) return "";
        const d = new Date(dateInput);
        if (Number.isNaN(d.getTime())) return "";
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      };
      
      const formattedSelectedDate = formatToYYYYMMDD(selectedDate);
      
      // Check if selectedDate matches any date in the list
      const isPublicHoliday = publicHolidayList && publicHolidayList.length > 0 
        ? publicHolidayList.some(holiday => holiday.date === formattedSelectedDate)
        : false;
      setPublicHoliday(isPublicHoliday);

    } catch (error) {
      console.error("Error:", error);
    } finally {
      // setLoading(false);
    }
  }, [selectedDate, setPublicHoliday]);
  useEffect(() => {
    if (selectedDate) {
      fetchPublicHolidayList();
    }
  }, [selectedDate, fetchPublicHolidayList]);
  // GET PUBLIC HOLIDAY LIST END

  // DELETE TEMPORARY BOOKING ON PAGE RELOAD START
  useEffect(() => {
    if (!selectedOutlet || hasCleanedUpTemporaryBookingRef.current) return;
    // Check both context state and localStorage for temporaryBookingId
    const storedTemporaryBookingId = localStorage.getItem('tempBookingID');
    const currentTemporaryBookingId = temporaryBookingId || storedTemporaryBookingId;
    
    if (currentTemporaryBookingId) {
      hasCleanedUpTemporaryBookingRef.current = true;
      const cleanupTemporaryBooking = async () => {
        try {
          const bookingId = currentTemporaryBookingId;
          const response = await deleteHoldBooking(bookingId, selectedOutlet);
          if (response?.success || response?.data?.success || response?.status) {
            setBookingsList((prev) => prev.filter((b) => String(b.booking_id || b.id) !== String(bookingId)));
				    setTemporaryBookingId(null);
				    setTempBookingCreated(false);
				    setHitTemporaryBookingApi(false);
            localStorage.removeItem('tempBookingID');
          }
        } catch (error) {
          console.error("Error deleting temporary booking on page reload:", error);
          // Still remove from localStorage even if API call fails
				  setTemporaryBookingId(null);
				  setTempBookingCreated(false);
					setHitTemporaryBookingApi(false);
          localStorage.removeItem('tempBookingID');
        }
      };
      
      cleanupTemporaryBooking();
    } else {
      // No temporary booking found, mark as cleaned up
      hasCleanedUpTemporaryBookingRef.current = true;
    }
  }, [selectedOutlet, temporaryBookingId, setBookingsList, setTemporaryBookingId, setTempBookingCreated, setHitTemporaryBookingApi]);
  // DELETE TEMPORARY BOOKING ON PAGE RELOAD END

  // GET BREAKS LIST START
  const fetchBreaks = useCallback(async () => {
    if (!selectedOutlet || !selectedDate) return;
    
    const formattedDate = formatDateToDDMMYYYY(selectedDate);
    const dateRange = `${formattedDate} 00:00 / ${formattedDate} 23:59`;
    const isCalendarExpansion = getOutletExtendedFlagForDate(selectedOutletData?.timing, selectedDate) ? 1 : 0;
    
    let item = {
      panel: 'outlet',
      outlet: selectedOutlet,
      pagination: 0,
      daterange: dateRange,
      selected_date: formattedDate,
      is_calendar_expansion: isCalendarExpansion,
      // Don't pass therapist_id to fetch all therapists' breaks
    };

    try {
      const response = await getTherapistBreaks(item);
      const breaksFromAPI = response?.data?.data?.list?.leave || [];
      
      
      // Format breaks data for calendar display
      const formattedBreaks = breaksFromAPI.map(breakItem => {
        const selectedTimelineDate = new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate(),
          0,0,0,0
        );
        const startTimeParts = String(breakItem.start_at || '').split(' ');
        const startTime = startTimeParts[1] || '';
        const endTimeParts = String(breakItem.end_at || '').split(' ');
        const endTime = endTimeParts[1] || '';

        const convertBreakTimeTo24Hour = (time) => {
          if (!time || typeof time !== 'string') return '';

          const [hourMin, period] = time.split(/(AM|PM)/);
          const [hour, min] = String(hourMin || '').split(':');
          let hour24 = parseInt(hour, 10);

          if (Number.isNaN(hour24) || min == null) return '';

          if (hour24 >= 13) {
            return `${hour24.toString().padStart(2, '0')}:${min}`;
          }

          if (period === 'PM' && hour24 !== 12) {
            hour24 += 12;
          } else if (period === 'AM' && hour24 === 12) {
            hour24 = 0;
          }

          return `${hour24.toString().padStart(2, '0')}:${min}`;
        };

        const parseDatePart = (datePart) => {
          if (!datePart || typeof datePart !== 'string') return null;

          if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
            const [year, month, day] = datePart.split('-').map(Number);
            const parsed = new Date(year, month - 1, day);
            return Number.isNaN(parsed.getTime()) ? null : parsed;
          }

          if (/^\d{2}-\d{2}-\d{4}$/.test(datePart)) {
            const [day, month, year] = datePart.split('-').map(Number);
            const parsed = new Date(year, month - 1, day);
            return Number.isNaN(parsed.getTime()) ? null : parsed;
          }

          return null;
        };

        const parseAbsoluteBreakMinutes = (dateTimeValue, fallbackTime24, isExpandedFlag = 0) => {
          const fallbackMinutes = convertToMinutes(fallbackTime24);

          if (!dateTimeValue || typeof dateTimeValue !== 'string') {
            if (Number.isNaN(fallbackMinutes)) return null;
            return Number(isExpandedFlag) === 1 ? fallbackMinutes + 1440 : fallbackMinutes;
          }

          const [datePart] = dateTimeValue.trim().split(' ');
          const parsedDate = parseDatePart(datePart);
          if (!parsedDate || Number.isNaN(fallbackMinutes)) {
            return Number(isExpandedFlag) === 1 ? fallbackMinutes + 1440 : fallbackMinutes;
          }

          const normalizedBreakDate = new Date(
            parsedDate.getFullYear(),
            parsedDate.getMonth(),
            parsedDate.getDate(),
            0,0,0,0
          );

          const dayOffset = Math.round(
            (normalizedBreakDate.getTime() - selectedTimelineDate.getTime()) / (24 * 60 * 60 * 1000)
          );

          if (dayOffset === 0 && Number(isExpandedFlag) === 1) {
            return fallbackMinutes + 1440;
          }

          return (Math.max(0, dayOffset) * 1440) + fallbackMinutes;
        };

        const formatBreakDisplayTime = (time24) => {
          if (!time24) return '';
          return convertTo12Hour(time24);
        };

        const startTime24 = convertBreakTimeTo24Hour(startTime);
        const endTime24 = convertBreakTimeTo24Hour(endTime);
        const absoluteStartMinutes = parseAbsoluteBreakMinutes(
          breakItem.start_at,
          startTime24,
          breakItem.is_break_calendar_expenssion
        );
        let absoluteEndMinutes = parseAbsoluteBreakMinutes(
          breakItem.end_at,
          endTime24,
          breakItem.is_break_calendar_expenssion
        );

        if (
          typeof absoluteStartMinutes === 'number' && typeof absoluteEndMinutes === 'number' && absoluteEndMinutes <= absoluteStartMinutes
        ) {
          absoluteEndMinutes += 1440;
        }

        return {
          id: breakItem.id,
          therapist_id: breakItem.therapist_id,
          therapist_name: breakItem.user_name,
          start_time: startTime24,
          end_time: endTime24,
          start_time_display: formatBreakDisplayTime(startTime24),
          end_time_display: formatBreakDisplayTime(endTime24),
          absoluteStartMinutes,
          absoluteEndMinutes,
          is_break_calendar_expenssion: breakItem.is_break_calendar_expenssion || 0,
          remark: breakItem.remark,
          status_name: breakItem.status_name,
          status_color: breakItem.status_color,
        };
      });
      
      setBreaksData(formattedBreaks);
    } catch (error) {
      console.error("Error fetching breaks:", error);
      setBreaksData([]);
    }
  }, [selectedOutlet, selectedDate, selectedOutletData?.timing]);

  useEffect(() => {
    if (selectedOutlet && selectedDate) {
      fetchBreaks();
    }
  }, [selectedOutlet, selectedDate, fetchBreaks]);
  // GET BREAKS LIST END






  




  const handleScheduleUpdated = useCallback((event) => {
    if (!event?.therapist_id || !event?.dates) {
      fetchTherapistTimings();
      return;
    }

    if (event.outlet_id && String(event.outlet_id) !== String(selectedOutlet)) {
      fetchTherapistTimings();
      return;
    }

    const selectedDateStr = formatDateForBackend(selectedDate);
    const [d, m, y] = selectedDateStr.split('-');
    const selectedDateYYYYMMDD = `${y}-${m}-${d}`;
    const matchesSelectedDate =
      event.dates.includes(selectedDateStr) || event.dates.includes(selectedDateYYYYMMDD);

    if (!matchesSelectedDate) {
      fetchTherapistTimings();
      return;
    }

    const schedules = Array.isArray(event.schedules) ? event.schedules : [];
    const hasSchedule = schedules.length > 0;
    const isProfileChange = event.update_scope === 'profile_change';
    const isNewTherapistEvent =
      event.hide !== 1 &&
      !isProfileChange &&
      hasSchedule &&
      !!event.therapist?.id &&
      !homeStaffList.some((staff) => String(staff.id) === String(event.therapist_id));

    setHomeStaffList((prev) => {
      const therapistExists = prev.some(
        (staff) => String(staff.id) === String(event.therapist_id)
      );

      if (event.hide === 1) {
        return prev.filter(
          (staff) => String(staff.id) !== String(event.therapist_id)
        );
      }

      if (isProfileChange || !hasSchedule) {
        if (!therapistExists || !event.therapist?.id) return prev;
        return prev.map((staff) =>
          String(staff.id) === String(event.therapist_id)
            ? { ...staff, ...event.therapist }
            : staff
        );
      }

      if (!therapistExists && event.therapist?.id) {
        return [event.therapist, ...prev];
      }

      if (therapistExists && event.therapist?.id) {
        return prev.map((staff) =>
          String(staff.id) === String(event.therapist_id)
            ? { ...staff, ...event.therapist }
            : staff
        );
      }

      return prev;
    });

    if (isNewTherapistEvent && !isRoomFilterActive && event.therapist?.id) {
      setBookingFilters((prev) => {
        // Only auto-check when "All Therapist" is selected
        if (
          prev.allTherapist !== true ||
          (prev.gender && prev.gender.length > 0)
        ) {
          return prev;
        }

        const selectedStaffIds = prev.staffIds || [];
        const selectedStaffIdSet = new Set(selectedStaffIds.map((id) => String(id)));
        const therapistId = event.therapist.id;

        if (selectedStaffIdSet.has(String(therapistId))) {
          return prev;
        }

        const updatedFiltersToPersist = {
          ...prev,
          staffIds: [...selectedStaffIds, therapistId],
          allTherapist: true,
        };
        if (persistTherapistFiltersRef.current) {
          persistTherapistFiltersRef.current(updatedFiltersToPersist);
        } else {
          pendingAutoPersistFiltersRef.current = updatedFiltersToPersist;
        }
        return updatedFiltersToPersist;
      });
    }

    fetchTherapistTimings();
  }, [selectedOutlet, selectedDate, fetchTherapistTimings, homeStaffList, isRoomFilterActive, setBookingFilters]);
  
  // Auto-check newly added therapists only when "All Therapist" is selected.
  // Partial / gender filters: new therapists stay unchecked.
  useEffect(() => {
    if (!isRoomFilterActive && homeStaffList.length > 0) {
      setBookingFilters((prev) => {
        const currentStaffIds = prev.staffIds || [];
        const allCurrentStaffIds = homeStaffList.map((staff) => staff.id);
        const previousHomeStaffIds = previousHomeStaffIdsRef.current || [];
        const allCurrentStaffIdSet = new Set(allCurrentStaffIds.map((id) => String(id)));
        const previousHomeStaffIdSet = new Set(previousHomeStaffIds.map((id) => String(id)));
        const selectedStaffIdSet = new Set(currentStaffIds.map((id) => String(id)));

        const validCurrentStaffIds = currentStaffIds.filter((id) =>
          allCurrentStaffIdSet.has(String(id))
        );

        const brandNewTherapistIds = allCurrentStaffIds.filter(
          (id) => !previousHomeStaffIdSet.has(String(id))
        );

        const isAllTherapistMode =
          prev.allTherapist === true &&
          (!prev.gender || prev.gender.length === 0);

        let idsToAutoCheck = [];

        // First roster load with empty selection → default to full roster
        if (
          previousHomeStaffIds.length === 0 &&
          currentStaffIds.length === 0 &&
          (!prev.gender || prev.gender.length === 0)
        ) {
          idsToAutoCheck = allCurrentStaffIds;
        } else if (isAllTherapistMode) {
          // All Therapist selected → check therapists that just appeared on duty
          idsToAutoCheck = brandNewTherapistIds.filter(
            (id) => !selectedStaffIdSet.has(String(id))
          );
        }
        // else: partial / gender selection → leave new therapists unchecked

        if (
          idsToAutoCheck.length > 0 ||
          validCurrentStaffIds.length !== currentStaffIds.length
        ) {
          const updatedStaffIds = [
            ...new Set([...validCurrentStaffIds, ...idsToAutoCheck]),
          ];
          const updatedStaffIdSet = new Set(updatedStaffIds.map((id) => String(id)));
          const allTherapist =
            (!prev.gender || prev.gender.length === 0) &&
            homeStaffList.length > 0 &&
            homeStaffList.every((staff) => updatedStaffIdSet.has(String(staff.id)));

          const nextFilters = {
            ...prev,
            staffIds: updatedStaffIds,
            allTherapist,
          };
          const updatedFiltersToPersist = {
            ...nextFilters,
            isFilterApplied: computeTherapistFilterApplied( nextFilters, homeStaffList,false ),
          };
          const isFullRosterReload = previousHomeStaffIds.length === 0;
          if (!isFullRosterReload && idsToAutoCheck.length > 0) {
            pendingAutoPersistFiltersRef.current = updatedFiltersToPersist;
          }
          return updatedFiltersToPersist;
        }

        return prev;
      });
    }
    previousHomeStaffIdsRef.current = homeStaffList.map((staff) => staff.id);
  }, [homeStaffList, isRoomFilterActive, setBookingFilters]);

  // Keep header red-dot in sync without requiring FilterPanel to open.
  // Wait until staff + saved filters are ready so empty staffIds mid-load does not flash the dot.
  useEffect(() => {
    if (!hasStaffLoaded || !calendarFiltersReady) {
      setIsFilterApplied(false);
      return;
    }
    const next = computeTherapistFilterApplied( bookingFilters,homeStaffList,isRoomFilterActive );
    setIsFilterApplied((prev) => (prev === next ? prev : next));
  }, [ bookingFilters,homeStaffList,isRoomFilterActive,hasStaffLoaded,calendarFiltersReady,setIsFilterApplied, ]);

  const persistTherapistFilters = useCallback(async (filtersToPersist) => {
    if (!filtersToPersist || !selectedOutlet || !loggedUser) return;

    const normalizedGender = (filtersToPersist.gender || []).map((g) =>
      typeof g === "string" ? g.toLowerCase() : g
    );

    // Keep the saved "all therapist" intent in sync with the persisted selection
    const persistedStaffIdSet = new Set(
      (filtersToPersist.staffIds || []).map((id) => String(id))
    );
    const allTherapist =
      normalizedGender.length === 0 &&
      homeStaffList.length > 0 &&
      homeStaffList.every((staff) => persistedStaffIdSet.has(String(staff.id)));

    try {
      await setCalendarFilters({
        outlet_id: selectedOutlet,
        user_id: loggedUser,
        mode: "therapist",
        filters: {
          ...filtersToPersist,
          gender: normalizedGender,
          allTherapist: allTherapist,
          status: withDefaultStatus(filtersToPersist.status),
          roomItem: [],
          roomIds: [],
        },
      });
    } catch (error) {
      console.error("Failed to persist therapist filters:", error?.message || error);
    }
  }, [selectedOutlet, loggedUser, homeStaffList]);

  useEffect(() => {
    persistTherapistFiltersRef.current = persistTherapistFilters;
  }, [persistTherapistFilters]);

  useEffect(() => {
    if (isRoomFilterActive) return;

    const pendingFilters = pendingAutoPersistFiltersRef.current;
    if (!pendingFilters) return;

    pendingAutoPersistFiltersRef.current = null;
    persistTherapistFilters(pendingFilters);
  }, [bookingFilters, isRoomFilterActive, persistTherapistFilters]);

  const handleManualReload = async () => {
    // Reload all associated data
    window.location.reload();
  };

  
  // GENERATE FULL DAY SLOTS WITH OUTLET STATUS START
  // Use only the selected date's outlet timings. Do not copy the next day's
  // cross-day end onto this date, or early morning hours show as open.
  useEffect(() => {
    if (selectedOutletTiming && Object.keys(selectedOutletTiming).length > 0) {
      const fullSlots = generateFullDaySlotsWithOutletStatus(
        selectedDate,
        tableInterval,
        selectedOutletTiming,
        isOutletExtended
      );
      setTimeSlots(fullSlots);
    }
  }, [selectedOutletTiming, selectedDate, tableInterval, isOutletExtended, setTimeSlots]);

  useEffect(() => {
    if (!selectedDate || !selectedOutletTiming) return;
  }, [selectedDate, selectedOutletTiming]);



  // Helper function to get filtered rooms based on filter type
  const getFilteredRoomsForType = (filterType, rooms) => {
    switch (filterType) {
      case "resources":
        return rooms; // Show all rooms
      case "rooms":
        return rooms.filter((room) => room.item_category === "Single Bed");
      case "sofa":
        return rooms.filter((room) => room.item_category === "Sofa");
      case "monkeyChair":
        return rooms.filter((room) => room.item_category === "Monkey Chair");
      default:
        return rooms;
    }
  };

  // Sync filteredRoomIds and filteredItemIds from bookingFilters when in room mode
  // This ensures the table headers work on initial load without opening FilterPanel
  // Clear stale room IDs when outlet changes and new outlet has no rooms
  useEffect(() => {
    if (isRoomFilterActive && homeRoomList.length > 0 && bookingFilters && Array.isArray(bookingFilters.roomIds)) {
      const filterType = selectedRoomFilter || "resources";
      const filteredRooms = getFilteredRoomsForType(filterType, homeRoomList);
      const selectedRoomIds = bookingFilters.roomIds || [];
      const selectedRooms = filteredRooms.filter(room => selectedRoomIds.includes(room.room_id));
      const allItemIds = selectedRooms.reduce((itemIds, room) => {
        if (room.items && Array.isArray(room.items)) {
          const roomItemIds = room.items.map(item => item.item_id).filter(id => id !== undefined && id !== null);
          return [...itemIds, ...roomItemIds];
        }
        return itemIds;
      }, []);
      setFilteredItemIds(allItemIds);
      setFilteredRoomIds(selectedRoomIds);
    } else if (!isRoomFilterActive) {
      setFilteredItemIds([]);
      setFilteredRoomIds([]);
    } else if (isRoomFilterActive && homeRoomList.length === 0) {
      // Clear stale filteredRoomIds when outlet has no rooms (e.g. after outlet change)
      setFilteredItemIds([]);
      setFilteredRoomIds([]);
    }
  }, [isRoomFilterActive, selectedRoomFilter, homeRoomList, bookingFilters, setFilteredItemIds, setFilteredRoomIds]);


  // CLICK OUTSIDE TO CLOSE RIGHTSIDEBAR START
    useEffect(() => {
      const handleClickOutside = (event) => {
        // Don't trigger if customer details modal is open
        if (isCustomerDetailsOpen || showFilterPanel) return;
        
        // Don't trigger if we're in the process of creating/showing a new booking
        if (isCreatingBookingRef.current) return;
        
        // Don't trigger if showing booking summary (viewing an existing booking)
        if (showBookingSummary) return;
        
        // Don't show popup while dragging
        if (isDraggingCard || isDraggingRef.current) return;
        
        // Don't trigger if clicking on modal overlays (toast, custom modal, or customer details sidebar overlay)
        if (event.target.closest(".Toastify__toast-container, .customModalOverlay, [class*='sidebarOverlay'], .prevent-outside-click")) return;

        // Don't trigger if clicking inside bookingWrapper (prevents scrollbar interactions triggering close)
        if (bookingWrapperRef.current && bookingWrapperRef.current.contains(event.target)) return;

        const clickedBooking = event.target.closest("[data-booking-id]");
        if (clickedBooking) {
          const bookingId = clickedBooking.getAttribute("data-booking-id");
          const bookingItemId = clickedBooking.getAttribute("data-item-id");
          handleBookedSlotClick(null, bookingId, bookingItemId);
          return;
        }

        // if click is outside sidebar
        if (
          !newBookingCardRef.current &&
          sidebarRef.current &&
          !sidebarRef.current.contains(event.target)
        ) {
          const clickedCell = event.target.closest("td[data-row][data-col]");
          const clickedEmptySlot = event.target.closest(".emptySlot");
          const clickedOutletOption = event.target.closest(".outlet-option");
          setSelectedCells(new Set());

          if ((clickedCell && !event.target.closest("[data-booking-id]")) || clickedEmptySlot || clickedOutletOption) {
            if (!isViewOnlyOutlet) {
              // Check if user has filled any data before showing conflict modal
              const hasFilledData = isCustomerSelected || selectedServices.length > 0;
              
              // If there's an existing new booking slot with no data filled
              if (newAppointmentSlots.length > 0 && !hasFilledData) {
                // Get the clicked cell's row and col
                const rowAttr = clickedCell?.getAttribute('data-row');
                const colAttr = clickedCell?.getAttribute('data-col');
                const clickedRow = rowAttr !== null ? parseInt(rowAttr) : null;
                const clickedCol = colAttr !== null ? parseInt(colAttr) : null;
                
                const existingSlot = newAppointmentSlots[0];
                
                // Check if clicking on the same cell
                if (existingSlot && clickedRow !== null && clickedCol !== null &&
                    existingSlot.rowIndex === clickedRow && existingSlot.colIndex === clickedCol) {
                  // Same cell - do nothing, keep the booking slot and sidebar open
                  return;
                }
                
                // Different cell - handleMouseDown will handle creating new booking
                // So just return here to let handleMouseDown take over
                return;
              } else if (hasFilledData) {
                // If data is filled, show conflict modal
                setShowConflictModal(true);
              } else {
                // No existing booking, handleMouseDown will create new one
                // Just return to let handleMouseDown handle it
                return;
              }
            }
          } else {
            // Check if user has filled any data
            // const hasFilledData = isCustomerSelected || selectedServices.length > 0;
            
            // if (hasFilledData) {
            //   setShowConflictModal(true);
            // } else {
            //   handleCloseSidebar();
            // }
          }
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [newAppointmentSlots, isCustomerSelected, selectedServices, isViewOnlyOutlet, isCustomerDetailsOpen, isDraggingCard, showBookingSummary]);

  // CLICK OUTSIDE TO CLOSE RIGHTSIDEBAR END

  useEffect(() => {
    const handleScrollToBooking = (e) => {
      const bookingDetail = e.detail;
      if (!bookingDetail?.booking_id) return;
      if (bookingDetail.source === "search") {
        setActiveCardKey(null);
        clearTouchCardSelection();
        setActiveSlotId(bookingDetail.booking_id);
        scrollRetries.current = 0;
        setBookingIdToScroll(bookingDetail.booking_id);
        return;
      }
      // Check if this is a newly created booking (not an existing one)
      const isNewBooking = !bookingDetail.cancelled && !bookingsList.some(
        (b) => b.booking_id === bookingDetail.booking_id || b.id === bookingDetail.booking_id
      );
      if (isNewBooking) {
        // Only add if it's a new booking
        setBookingsList((prev) => {
          const alreadyExists = prev.some(
            (b) => b.booking_id === bookingDetail.booking_id || b.id === bookingDetail.booking_id
          );
          return alreadyExists ? prev : [...prev, bookingDetail];
        });

        // Set ref flag to prevent handleClickOutside from interfering (synchronous)
        isCreatingBookingRef.current = true;
        
        // Set ALL sidebar states FIRST before any other operations
        // This prevents handleClickOutside from interfering
        setShowBookingSummary(true);
        setIsBookingSidebar(false);
        setIsSidebarVisible(true);
        setIsNewBookingSlotActive(false);
        setSelectedBookingId(bookingDetail.booking_id);
        
        // Clear new appointment slots AFTER setting sidebar states
        setNewAppointmentSlots([]);
        setNewSlot(null);

        // Only scroll for newly created bookings
        setBookingIdToScroll(bookingDetail.booking_id);
        setTimeout(() => {
          setActiveSlotId(bookingDetail.booking_id);
          // Clear the flag after state updates have had time to apply
          setTimeout(() => {
            isCreatingBookingRef.current = false;
          }, 300);
        }, 100);
      } else if (bookingDetail.cancelled) {

        // Handle cancelled bookings with visual flash but no scroll
        setTimeout(() => {
          setActiveSlotId(bookingDetail.booking_id);
          const el = bookingTableRef.current?.querySelector(
            `[data-booking-id='${bookingDetail.booking_id}']`
          );
          if (el) {
            el.classList.add("highlightedCancelled");
            setTimeout(() => {
              el.classList.remove("highlightedCancelled");
            }, 2000);
          }
        }, 100);
      }
      // For existing bookings, do nothing (no scroll)
    };

    window.addEventListener("scrollToBooking", handleScrollToBooking);
    // Silent refresh listener to normalize newly socket-inserted bookings
    const handleSilentRefresh = () => {
      if (selectedDate && selectedOutlet) {
        fetchBookingsData({ silent: true });
      }
    };
    window.addEventListener("silentBookingRefresh", handleSilentRefresh);
    
    // Customer details modal event listener
    const handleOpenCustomerDetails = (e) => {
      const customer = e.detail?.customer;
      if (customer) {
        setSelectedCustomerForDetails(customer);
        setIsCustomerDetailsOpen(true);
      }
    };
    window.addEventListener("openCustomerDetails", handleOpenCustomerDetails);
    
    return () =>
      {
        window.removeEventListener("scrollToBooking", handleScrollToBooking);
        window.removeEventListener("silentBookingRefresh", handleSilentRefresh);
        window.removeEventListener("openCustomerDetails", handleOpenCustomerDetails);
      };
  }, [bookingsList, selectedDate, selectedOutlet]);

  // Remove placeholder bookings when real bookings appear in bookingsList
  useEffect(() => {
    if (pendingBookings.length === 0) return;
    
    setPendingBookings((prevPending) => {
      return prevPending.filter((placeholder) => {
        // Check if a real booking with matching booking_id exists
        const realBookingExists = bookingsList.some((booking) => {
          const bookingIdMatch = 
            Number(booking.booking_id) === Number(placeholder.booking_id) ||
            Number(booking.id) === Number(placeholder.booking_id);
          
          // If booking exists, check if it's in the same position (same time/date)
          if (bookingIdMatch) {
            // Additional check: verify it's for the same date/time slot
            // This prevents removing placeholder prematurely if booking appears elsewhere
            return true; // Real booking found, remove placeholder
          }
          return false;
        });
        
        // Also remove placeholders older than 10 seconds as a safety measure
        const isOld = Date.now() - placeholder.createdAt > 10000;
        
        return !realBookingExists && !isOld;
      });
    });
  }, [bookingsList, pendingBookings.length]);

  useEffect(() => {
    if (!bookingIdToScroll || !bookingsList.length) {
      return;
    }
    // Wait until Home calendar finished loading so booking cards exist in the DOM
    if (isTableLoading || !isCalendarGridReady)return;
    let cancelled = false;
    scrollRetries.current = 0;

    const tryScroll = () => {
      if (cancelled) return;
      const el = bookingTableRef.current?.querySelector(
        `[data-booking-id='${bookingIdToScroll}']`
      );

      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center",
        });
        el.classList.add("highlightedCard");
        setTimeout(() => el.classList.remove("highlightedCard"), 500);

        const itemId = el.getAttribute("data-item-id") || "";
        setActiveCardKey(`${bookingIdToScroll}:${itemId}`);
        setBookingIdToScroll(null);
      } else if (scrollRetries.current < 60) {
        scrollRetries.current++;
        setTimeout(tryScroll, 100);
      } else {
        setBookingIdToScroll(null);
      }
    };
    const startTimer = setTimeout(tryScroll, 100);
    return () => {
      cancelled = true;
      clearTimeout(startTimer);
    };
  }, [bookingIdToScroll, bookingsList, isTableLoading, isCalendarGridReady]);



  // 6️⃣ Common function to open sidebar (used in both handleMouseDown & openSidebar)
  const openSidebarWithData = (rowIndex, colIndex) => {
    const selectedTime = timeSlots[rowIndex]?.time; // Get the time from the row
    // Use filteredRoomHeaders when room mode is active to match the rendered columns
    const currentHeaders = isRoomFilterActive ? (filteredRoomHeaders.length > 0 ? filteredRoomHeaders : headers) : headers;
    const selectedHeader = currentHeaders[colIndex];

    if (!selectedTime || typeof selectedTime !== "string") {
      console.error("Invalid selected time:", selectedTime);
      return;
    }

    try {
      const convertedTime = convertTo24HourFormat(selectedTime);
      const selectedSlot = timeSlots[rowIndex];
      const newTime = buildSelectedTimePayload(
        convertedTime,
        getSlotAbsoluteMinutes(selectedSlot),
        getSlotTimeLabel(selectedSlot)
      );
      
      setBookingData((prevData) => {
        if (isRoomFilterActive) {
          // Room mode: set room data instead of therapist data
          if (
            prevData.time?.value !== newTime.value ||
            prevData.room?.value !== selectedHeader.id
          ) {
            return {
              ...prevData,
              date: selectedDate,
              time: newTime,
              room: { 
                label: selectedHeader?.name, 
                value: selectedHeader?.id,
                room_id: selectedHeader?.room_id,
                room_name: selectedHeader?.room_name,
                room_code: selectedHeader?.room_code,
                item_type: selectedHeader?.item_type
              },
              therapist: null, // Clear therapist data in room mode
            };
          }
        } else {
          // Therapist mode: set therapist data
          if (
            prevData.time?.value !== newTime.value ||
            prevData.therapist?.value !== selectedHeader.id
          ) {
            return {
              ...prevData,
              date: selectedDate,
              time: newTime,
              therapist: { label: selectedHeader?.alias, value: selectedHeader?.id, pager: selectedHeader?.pager_number },
              room: null, // Clear room data in therapist mode
            };
          }
        }
        return prevData;
      });

      if (isRoomFilterActive) {
        // Room mode: set room-related state
        setSelectedTherapistIDFromRow(null);
        setSelectedTherapistFromRow(null);
      } else {
        // Therapist mode: set therapist-related state
        setSelectedTherapistIDFromRow(selectedHeader.id);
        setSelectedTherapistFromRow(selectedHeader);
      }
    } catch (error) {
      console.error("Error converting time:", error);
    }

    if (!isSidebarVisible) {
      setIsSidebarVisible(true);

      // Scroll sidebar to top when it opens for better UX
      setTimeout(() => {
        const sidebarContent = document.querySelector('.sideBarContent');
        if (sidebarContent) {
          sidebarContent.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }, 100); // Delay to ensure sidebar is fully rendered
    }
  };


  // GET ROW INDEX START 
  const getRowIndexFromTime = useCallback((time) => {
    if (!time) return -1;
    const normalizeTimeString = (timeStr) => {
      const [hours, minutes] = timeStr.split(":");
      return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
    };
    const normalizedTime = normalizeTimeString(time);
    
    // First try exact match
    let rowIndex = timeSlots.findIndex(
      (slot) => normalizeTimeString(slot.time24) === normalizedTime
    );
    // If no exact match, find the closest time slot
    if (rowIndex === -1) {
      const [targetHour, targetMinute] = normalizedTime.split(":").map(Number);
      const targetMinutes = targetHour * 60 + targetMinute;
      
      let closestIndex = -1;
      let minDifference = Infinity;
      
      timeSlots.forEach((slot, index) => {
        const [slotHour, slotMinute] = normalizeTimeString(slot.time24).split(":").map(Number);
        const slotMinutes = slotHour * 60 + slotMinute;
        const difference = Math.abs(slotMinutes - targetMinutes);
        
        if (difference < minDifference) {
          minDifference = difference;
          closestIndex = index;
        }
      });
      
      rowIndex = closestIndex;
    }
    
    return rowIndex;
  }, [timeSlots]);
  // GET ROW INDEX END

  const getSlotAbsoluteMinutes = useCallback((slot) => {
    if (!slot) return 0;
    if (typeof slot.absoluteMinutes === "number") return slot.absoluteMinutes;
    if (typeof slot.time24 === "string" && slot.time24.includes(":")) {
      const [hours, minutes] = slot.time24.split(":").map(Number);
      if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
        return hours * 60 + minutes;
      }
    }
    return normalizeTime(slot.time);
  }, []);

  const getSlotTimeLabel = useCallback((slot) => {
    if (!slot) return "";
    const baseLabel = formatTimeWithoutZeroMinutes(slot.time24);
    return slot.isExtended ? `${baseLabel} +1` : baseLabel;
  }, []);

  const OVERNIGHT_EXTENSION_START_MINUTES = 23 * 60;
  const MAX_NEXT_DAY_RENDER_MINUTES = 30 * 60;

  const resolveDraftAbsoluteStartMinutes = useCallback((timeValue, referenceAbsoluteMinutes = null) => {
    if (!timeValue) return null;
    const timeMinutes = convertToMinutes(timeValue);
    if (Number.isNaN(timeMinutes)) return null;

    if (typeof referenceAbsoluteMinutes === "number" && !Number.isNaN(referenceAbsoluteMinutes)) {
      const referenceClockMinutes = ((referenceAbsoluteMinutes % 1440) + 1440) % 1440;
      const maxNextDayClockMinutes = MAX_NEXT_DAY_RENDER_MINUTES - 1440;
      if (referenceAbsoluteMinutes >= 1440) {
        if (timeMinutes <= maxNextDayClockMinutes) {
          return 1440 + timeMinutes;
        }
        return timeMinutes;
      }
      if (
        referenceAbsoluteMinutes >= OVERNIGHT_EXTENSION_START_MINUTES &&
        timeMinutes < referenceClockMinutes
      ) {
        return 1440 + timeMinutes;
      }
    }

    return timeMinutes;
  }, [MAX_NEXT_DAY_RENDER_MINUTES]);

  const clampTimelineStartMinutes = useCallback((absoluteMinutes) => {
  //   if (typeof absoluteMinutes !== "number" || Number.isNaN(absoluteMinutes)) return 0;
  //   return Math.max(0, Math.min(absoluteMinutes - 5, absoluteMinutes));
  // }, []);
    const dayBoundaryMinutes = isOutletExtended ? MAX_NEXT_DAY_RENDER_MINUTES : 1440;
    return Math.max(0, Math.min(dayBoundaryMinutes - 5, absoluteMinutes));
  }, [isOutletExtended]);

  const formatAbsoluteMinutesToTime24 = useCallback((absoluteMinutes) => {
    const normalizedMinutes = ((Number(absoluteMinutes) || 0) % 1440 + 1440) % 1440;
    const hours = Math.floor(normalizedMinutes / 60);
    const minutes = normalizedMinutes % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  }, []);

  /** End clock for draft cards on the extended grid (+1 segment uses absolute minutes; clock-only calculateEndTime is wrong there). */
  const getDraftEndTimeForSlot = useCallback((absoluteStartMinutes, durationMin, fallbackStartTime24) => {
    const dur = Number(durationMin) || 0;
    if (typeof absoluteStartMinutes === "number" && !Number.isNaN(absoluteStartMinutes)) {
      const endAbs = absoluteStartMinutes + dur;
      const normalized = ((endAbs % 1440) + 1440) % 1440;
      const hours = Math.floor(normalized / 60);
      const minutes = normalized % 60;
      const clock = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
      if (clock === "00:00" && dur > 0) {
        return "23:59";
      }
      return clock;
    }
    const start = fallbackStartTime24 || "00:00";
    const calculated = calculateEndTime(start, dur);
    return calculated === "00:00" ? "23:59" : calculated;
  }, []);

  const buildSelectedTimePayload = useCallback((timeValue, absoluteMinutes = null, fallbackLabel = null) => {
    if (!timeValue) return null;
    const resolvedAbsoluteMinutes = typeof absoluteMinutes === "number"
      ? absoluteMinutes
      : convertToMinutes(timeValue);
    const dayOffset = Math.max(0, Math.floor((resolvedAbsoluteMinutes || 0) / 1440));
    return buildTimeSelection(timeValue, dayOffset, fallbackLabel || convertTo12Hour(timeValue), resolvedAbsoluteMinutes);
  }, []);

  const getProjectedGridTargetFromPointer = useCallback((clientX, clientY) => {
    const table = bookingTableRef.current;
    if (!table || !Array.isArray(timeSlots) || timeSlots.length === 0) return null;

    const rowElements = Array.from(table.querySelectorAll("tbody tr"));
    if (rowElements.length === 0) return null;

    let rowIndex = rowElements.findIndex((row) => {
      const rect = row.getBoundingClientRect();
      return clientY >= rect.top && clientY < rect.bottom;
    });

    if (rowIndex < 0) {
      const firstRowRect = rowElements[0].getBoundingClientRect();
      const lastRowRect = rowElements[rowElements.length - 1].getBoundingClientRect();
      if (clientY < firstRowRect.top) {
        rowIndex = 0;
      } else if (clientY >= lastRowRect.bottom) {
        rowIndex = rowElements.length - 1;
      } else {
        return null;
      }
    }

    const rowRect = rowElements[rowIndex]?.getBoundingClientRect();
    if (!rowRect) return null;

    const columnEntries = [];
    const seenCols = new Set();
    Array.from(table.querySelectorAll("tbody td[data-col]")).forEach((cell) => {
      const colIndex = Number(cell.dataset.col);
      if (Number.isNaN(colIndex) || seenCols.has(colIndex)) return;
      const rect = cell.getBoundingClientRect();
      if (rect.width <= 0) return;
      seenCols.add(colIndex);
      columnEntries.push({
        colIndex,
        left: rect.left,
        right: rect.right,
      });
    });

    if (columnEntries.length === 0) return null;

    columnEntries.sort((a, b) => a.colIndex - b.colIndex);

    let columnEntry = columnEntries.find((entry) => clientX >= entry.left && clientX < entry.right);
    if (!columnEntry) {
      if (clientX < columnEntries[0].left) {
        columnEntry = columnEntries[0];
      } else if (clientX >= columnEntries[columnEntries.length - 1].right) {
        columnEntry = columnEntries[columnEntries.length - 1];
      } else {
        columnEntry = columnEntries.reduce((closest, entry) => {
          const entryCenter = (entry.left + entry.right) / 2;
          const closestCenter = (closest.left + closest.right) / 2;
          return Math.abs(entryCenter - clientX) < Math.abs(closestCenter - clientX) ? entry : closest;
        }, columnEntries[0]);
      }
    }

    return {
      rowIndex,
      colIndex: columnEntry.colIndex,
      rowRect,
    };
  }, [timeSlots]);

  const getSelectedTimeAbsoluteMinutes = useCallback((timeSelection, referenceAbsoluteMinutes = null) => {
    if (!timeSelection?.value) return null;
    if (typeof timeSelection.absoluteMinutes === "number") {
      return timeSelection.absoluteMinutes;
    }
    if (typeof timeSelection.dayOffset === "number") {
      return (timeSelection.dayOffset * 1440) + convertToMinutes(timeSelection.value);
    }
    return resolveDraftAbsoluteStartMinutes(timeSelection.value, referenceAbsoluteMinutes);
  }, [resolveDraftAbsoluteStartMinutes]);

  const getDateStringForAbsoluteMinutes = useCallback((absoluteMinutes, fallbackDate = selectedDate) => {
    const baseDate = fallbackDate instanceof Date ? new Date(fallbackDate) : new Date(fallbackDate || selectedDate);
    if (Number.isNaN(baseDate.getTime())) {
      return formatDateToDDMMYYYY(selectedDate);
    }

    // Signed day offset so previous-day services (e.g. 10:40 PM on 16th while viewing 17th) keep the correct date.
    const dayOffset = typeof absoluteMinutes === "number" && !Number.isNaN(absoluteMinutes)
      ? Math.floor(absoluteMinutes / 1440)
      : 0;
    baseDate.setDate(baseDate.getDate() + dayOffset);
    return formatDateToDDMMYYYY(baseDate);
  }, [selectedDate]);

  const getRowIndexFromAbsoluteMinutes = useCallback((absoluteMinutes, fallbackTime = null) => {
    if (typeof absoluteMinutes === "number" && !Number.isNaN(absoluteMinutes)) {
      const exactIndex = timeSlots.findIndex(
        (slot) => getSlotAbsoluteMinutes(slot) === absoluteMinutes
      );
      if (exactIndex >= 0) return exactIndex;

      // Prefer the slot that contains this absolute minute (floor), not nearest clock.
      let containingIndex = -1;
      for (let i = 0; i < timeSlots.length; i += 1) {
        const slotAbs = getSlotAbsoluteMinutes(timeSlots[i]);
        if (slotAbs <= absoluteMinutes) containingIndex = i;
        else break;
      }
      if (containingIndex >= 0) return containingIndex;
    }

    return fallbackTime ? getRowIndexFromTime(fallbackTime) : -1;
  }, [timeSlots, getSlotAbsoluteMinutes, getRowIndexFromTime]);

  const getDraftSlotAbsoluteStartMinutes = useCallback((draftSlot) => {
    if (!draftSlot) return 0;
    if (typeof draftSlot.absoluteStartMinutes === "number") return draftSlot.absoluteStartMinutes;
    if (typeof draftSlot.absoluteMinutes === "number") return draftSlot.absoluteMinutes;
    if (
      typeof draftSlot?.dayOffset === "number" &&
      !Number.isNaN(draftSlot.dayOffset) &&
      draftSlot?.start_time
    ) {
      return (Math.max(0, draftSlot.dayOffset) * 1440) + convertToMinutes(draftSlot.start_time);
    }
    return convertToMinutes(draftSlot.start_time);
  }, []);

  const getDraftSlotAbsoluteEndMinutes = useCallback((draftSlot) => {
    const startMinutes = getDraftSlotAbsoluteStartMinutes(draftSlot);
    const duration = Number(draftSlot?.duration) || 0;
    return startMinutes + duration;
  }, [getDraftSlotAbsoluteStartMinutes]);

  const getBreakAbsoluteStartMinutes = useCallback((breakItem) => {
    if (typeof breakItem?.absoluteStartMinutes === "number") return breakItem.absoluteStartMinutes;

    const startMinutes = convertToMinutes(breakItem?.start_time);
    if (Number.isNaN(startMinutes)) return 0;

    const isExpanded =breakItem?.is_break_calendar_expenssion === 1 || breakItem?.is_break_calendar_expenssion === "1" || breakItem?.is_break_calendar_expenssion === true || breakItem?.is_break_calendar_expenssion === "true";

    return isExpanded ? startMinutes + 1440 : startMinutes;
  }, []);

  const getBreakAbsoluteEndMinutes = useCallback((breakItem) => {
    if (typeof breakItem?.absoluteEndMinutes === "number") return breakItem.absoluteEndMinutes;

    const startAbsoluteMinutes = getBreakAbsoluteStartMinutes(breakItem);
    const endMinutes = convertToMinutes(breakItem?.end_time);
    if (Number.isNaN(endMinutes)) return startAbsoluteMinutes;

    const startMinutes = ((startAbsoluteMinutes % 1440) + 1440) % 1440;
    const adjustedEndMinutes = endMinutes < startMinutes ? endMinutes + 1440 : endMinutes;
    return startAbsoluteMinutes - startMinutes + adjustedEndMinutes;
  }, [getBreakAbsoluteStartMinutes]);

  const isDraftSlotOnSelectedTimeline = useCallback((draftSlot) => {
    if (!draftSlot) return false;

    const absoluteStartMinutes = getDraftSlotAbsoluteStartMinutes(draftSlot);
    if (typeof absoluteStartMinutes === "number" && !Number.isNaN(absoluteStartMinutes)) {
      return absoluteStartMinutes >= 0 && absoluteStartMinutes < MAX_NEXT_DAY_RENDER_MINUTES;
    }

    return true;
  }, [getDraftSlotAbsoluteStartMinutes]);

  const getTherapistScheduleBounds = useCallback((therapistDayData) => {
    if (!therapistDayData || !selectedDate) return null;
    const checkinMinutes = timeToMinutes(therapistDayData.checkin);
    const checkoutMinutes = timeToMinutes(therapistDayData.checkout);
    
    const parseScheduleDateTime = (value) => {
      if (!value || typeof value !== "string") return null;

      const [datePart, timePart = "00:00:00"] = value.trim().split(" ");
      if (!datePart) return null;

      let parsedDate = null;
      if (/^\d{2}-\d{2}-\d{4}$/.test(datePart)) {
        const [day, month, year] = datePart.split("-").map(Number);
        parsedDate = new Date(year, month - 1, day);
      } else if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        const [year, month, day] = datePart.split("-").map(Number);
        parsedDate = new Date(year, month - 1, day);
      }

      if (!parsedDate || Number.isNaN(parsedDate.getTime())) return null;
      
      const [hours = "0", minutes = "0", seconds = "0"] = timePart.split(":");
      parsedDate.setHours(Number(hours) || 0, Number(minutes) || 0, Number(seconds) || 0, 0);
      return parsedDate;
    };

    const scheduleStartDateTime = parseScheduleDateTime(therapistDayData.schedule_start_time);
    const scheduleEndDateTime = parseScheduleDateTime(therapistDayData.schedule_end_time);
    const selectedTimelineDate = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      0,0,0,0
    );
    const isScheduleExpanded = therapistDayData.is_schedule_calendar_expansion === 1 ||therapistDayData.is_schedule_calendar_expansion === "1" ||therapistDayData.is_schedule_calendar_expansion === true ||therapistDayData.is_schedule_calendar_expansion === "true";

    const scheduleStartAbsoluteMinutes = scheduleStartDateTime
      ? Math.round((scheduleStartDateTime.getTime() - selectedTimelineDate.getTime()) / 60000)
      : null;
    const scheduleEndAbsoluteMinutes = scheduleEndDateTime
      ? Math.round((scheduleEndDateTime.getTime() - selectedTimelineDate.getTime()) / 60000)
      : null;

    const resolvedStartAbsoluteMinutes =
      typeof scheduleStartAbsoluteMinutes === "number" && !Number.isNaN(scheduleStartAbsoluteMinutes)
        ? scheduleStartAbsoluteMinutes
        : (!Number.isNaN(checkinMinutes) ? checkinMinutes : null);

    let resolvedEndAbsoluteMinutes =
      typeof scheduleEndAbsoluteMinutes === "number" && !Number.isNaN(scheduleEndAbsoluteMinutes)
        ? scheduleEndAbsoluteMinutes
        : (
            !Number.isNaN(checkoutMinutes)
              ? (isScheduleExpanded || checkoutMinutes <= checkinMinutes
                  ? checkoutMinutes + 1440
                  : checkoutMinutes)
              : null
          );

    if (
      typeof resolvedStartAbsoluteMinutes === "number" &&
      !Number.isNaN(resolvedStartAbsoluteMinutes) &&
      typeof resolvedEndAbsoluteMinutes === "number" &&
      !Number.isNaN(resolvedEndAbsoluteMinutes)
    ) {
      // Same-minute start/end (e.g. 14:26:21–14:26:23) must not get +1440 —
      // that made slots after checkout stay white. Only roll overnight when expanded.
      if (
        resolvedEndAbsoluteMinutes <= resolvedStartAbsoluteMinutes &&
        isScheduleExpanded
      ) {
        resolvedEndAbsoluteMinutes += 1440;
      }

      return {
        startAbsoluteMinutes: resolvedStartAbsoluteMinutes,
        endAbsoluteMinutes: resolvedEndAbsoluteMinutes,
      };
    }

    return {
      startAbsoluteMinutes: !Number.isNaN(checkinMinutes) ? checkinMinutes : 0,
      endAbsoluteMinutes: !Number.isNaN(checkoutMinutes)
        ? (isScheduleExpanded || checkoutMinutes <= checkinMinutes ? checkoutMinutes + 1440 : checkoutMinutes)
        : 0,
    };
  }, [selectedDate]);

  const isSlotWithinTherapistAvailability = useCallback((slotAbsoluteMinutes, therapistDayData) => {
    if (!therapistDayData) {
      return {
        therapistIsOnLeave: true,
        therapistIsAvailable: false,
      };
    }

    const isLeave = therapistDayData.leave === 1;
    if (isLeave) {
      return {
        therapistIsOnLeave: true,
        therapistIsAvailable: false,
      };
    }

    const scheduleBounds = getTherapistScheduleBounds(therapistDayData);

    if (
      !scheduleBounds ||
      typeof slotAbsoluteMinutes !== "number" ||
      Number.isNaN(slotAbsoluteMinutes)
    ) {
      return {
        therapistIsOnLeave: false,
        therapistIsAvailable: false,
      };
    }

    return {
      therapistIsOnLeave: false,
      therapistIsAvailable:
        slotAbsoluteMinutes >= scheduleBounds.startAbsoluteMinutes &&
        slotAbsoluteMinutes < scheduleBounds.endAbsoluteMinutes,
    };
  }, [getTherapistScheduleBounds]);

  const shouldIgnoreOutletDisabledForExpandedSchedule = useCallback(
    (slotAbsoluteMinutes, therapistDayData, therapistIsAvailable) => {
      if (
        isRoomFilterActive ||
        !therapistIsAvailable ||
        typeof slotAbsoluteMinutes !== "number" ||
        Number.isNaN(slotAbsoluteMinutes)
      ) {
        return false;
      }

      const hasExplicitScheduleBounds =
        !!therapistDayData?.schedule_start_time ||
        !!therapistDayData?.schedule_end_time;
      if (hasExplicitScheduleBounds) {
        return true;
      }

      if (slotAbsoluteMinutes < 1440) {
        return false;
      }

      const isScheduleExpanded =therapistDayData?.is_schedule_calendar_expansion === 1 ||therapistDayData?.is_schedule_calendar_expansion === "1" ||therapistDayData?.is_schedule_calendar_expansion === true ||therapistDayData?.is_schedule_calendar_expansion === "true";
      return isScheduleExpanded;
    },
    [isRoomFilterActive]
  );

  const isExpandedTherapistScheduleActive = useCallback((slotAbsoluteMinutes, therapistDayData, therapistIsAvailable) => {
    if (
      isRoomFilterActive ||
      !therapistIsAvailable ||
      typeof slotAbsoluteMinutes !== "number" ||
      Number.isNaN(slotAbsoluteMinutes) ||
      slotAbsoluteMinutes < 1440
    ) {
      return false;
    }

    return therapistDayData?.is_schedule_calendar_expansion === 1 ||
      therapistDayData?.is_schedule_calendar_expansion === "1" ||
      therapistDayData?.is_schedule_calendar_expansion === true ||
      therapistDayData?.is_schedule_calendar_expansion === "true";
  }, [isRoomFilterActive]);

  const getMergedCellBackgroundStyle = useCallback(({rowIndex,rowspan,therapistDayData,therapistIsOnLeave,}) => {
    if (typeof rowIndex !== "number" || typeof rowspan !== "number" || rowIndex < 0 || rowspan <= 0 || !Array.isArray(timeSlots) || !timeSlots[rowIndex] ) {
      return null;
    }

    const disabledColor = "#e8e8e8";
    const availableColor = "#ffffff";
    const cellStartMinutes = getSlotAbsoluteMinutes(timeSlots[rowIndex]);
    const cellEndMinutes = rowIndex + rowspan < timeSlots.length ? getSlotAbsoluteMinutes(timeSlots[rowIndex + rowspan]) : cellStartMinutes + (rowspan * tableInterval);
    if (typeof cellStartMinutes !== "number" ||typeof cellEndMinutes !== "number" ||Number.isNaN(cellStartMinutes) ||Number.isNaN(cellEndMinutes) ||cellEndMinutes <= cellStartMinutes) {
      return null;
    }
    const unavailableRanges = [];
    for (let idx = rowIndex; idx < Math.min(timeSlots.length, rowIndex + rowspan); idx++) {
      const rowSlot = timeSlots[idx];
      if (!rowSlot?.disabled) continue;

      const rowStartMinutes = getSlotAbsoluteMinutes(rowSlot);
      const rowEndMinutes = idx + 1 < timeSlots.length? getSlotAbsoluteMinutes(timeSlots[idx + 1]): rowStartMinutes + tableInterval;

      if (isRoomFilterActive) {
        unavailableRanges.push([rowStartMinutes, rowEndMinutes]);
        continue;
      }
      const availabilityState = isSlotWithinTherapistAvailability(rowStartMinutes, therapistDayData);
      const shouldIgnoreOutletDisabled = shouldIgnoreOutletDisabledForExpandedSchedule(rowStartMinutes,therapistDayData,availabilityState.therapistIsAvailable);

      if (!shouldIgnoreOutletDisabled) {
        unavailableRanges.push([rowStartMinutes, rowEndMinutes]);
      }
    }
    if (!isRoomFilterActive) {
      if (!therapistDayData || therapistIsOnLeave) {
        unavailableRanges.push([cellStartMinutes, cellEndMinutes]);
      } else {
        const scheduleBounds = getTherapistScheduleBounds(therapistDayData);
        if (!scheduleBounds) {
          unavailableRanges.push([cellStartMinutes, cellEndMinutes]);
        } else {
          if (scheduleBounds.startAbsoluteMinutes > cellStartMinutes) {
            unavailableRanges.push([cellStartMinutes,Math.min(cellEndMinutes, scheduleBounds.startAbsoluteMinutes),]);
          }
          if (scheduleBounds.endAbsoluteMinutes < cellEndMinutes) {
            unavailableRanges.push([Math.max(cellStartMinutes, scheduleBounds.endAbsoluteMinutes),cellEndMinutes,]);
          }
        }
      }
    }

    const normalizedRanges = unavailableRanges.filter(([start, end]) => end > start).sort((a, b) => a[0] - b[0]);
    if (normalizedRanges.length === 0) return null;
    const mergedRanges = [];
    normalizedRanges.forEach(([start, end]) => {
      const lastRange = mergedRanges[mergedRanges.length - 1];
      if (!lastRange || start > lastRange[1]) {
        mergedRanges.push([start, end]);
      } else {
        lastRange[1] = Math.max(lastRange[1], end);
      }
    });

    const segments = [];
    let cursor = cellStartMinutes;

    mergedRanges.forEach(([start, end]) => {
      if (start > cursor) {
        segments.push({ start: cursor, end: start, color: availableColor });
      }
      segments.push({ start, end, color: disabledColor });
      cursor = end;
    });
    if (cursor < cellEndMinutes) {
      segments.push({ start: cursor, end: cellEndMinutes, color: availableColor });
    }
    if (segments.length === 1) {
      return segments[0].color === disabledColor? { backgroundColor: disabledColor }: null;
    }

    const totalDuration = cellEndMinutes - cellStartMinutes;
    const gradientStops = segments.flatMap((segment) => {
      const startPct = ((segment.start - cellStartMinutes) / totalDuration) * 100;
      const endPct = ((segment.end - cellStartMinutes) / totalDuration) * 100;
      return [
        `${segment.color} ${startPct}%`,
        `${segment.color} ${endPct}%`,
      ];
    });

    return {
      backgroundColor: availableColor,
      backgroundImage: `linear-gradient(to bottom, ${gradientStops.join(", ")})`,
    };
  }, [getSlotAbsoluteMinutes,getTherapistScheduleBounds,isRoomFilterActive,isSlotWithinTherapistAvailability,shouldIgnoreOutletDisabledForExpandedSchedule,tableInterval,timeSlots,]);

  // Helper function to check if a cell / booking start is greyed out.
  // Pass absoluteMinutes when the real start time can fall inside a larger grid cell
  // (e.g. schedule from 11:05 inside an 11:00 / 1hr or 15min slot).
  const checkIfCellIsGreyedOut = useCallback((rowIndex, colIndex, absoluteMinutes = null) => {
    if (!timeSlots || !timeSlots[rowIndex]) return false;
    
    // Compute headers inline to avoid forward reference issues
    let currentHeaders = [];
    if (isRoomFilterActive) {
      // Room-based headers
      const roomHeaders = [];
      homeRoomList.forEach((room) => {
        if (room.items && room.items.length > 0) {
          room.items.forEach((item) => {
            roomHeaders.push({
              id: item.item_id,
              name: item.item_name,
              room_id: item.item_id,
              room_name: room.room_name,
              room_code: room.room_code,
              item_type: item.item,
              type: 'room_item'
            });
          });
        }
      });
      
      // Apply search filter
      const filteredRoomHeadersList = roomHeaders.filter((header) => {
        const matchesSearch =
          (searchQuery || "").trim() === "" ||
          header.name?.toLowerCase().includes((searchQuery || "").toLowerCase()) ||
          header.room_code?.toLowerCase().includes((searchQuery || "").toLowerCase()) ||
          String(header.id).includes(searchQuery || "");
        return matchesSearch;
      });
      
      // Filter by selected room IDs
      if (filteredRoomIds.length === 0) {
        currentHeaders = [];
      } else {
        currentHeaders = filteredRoomHeadersList.filter((header) => {
          return filteredItemIds.length > 0 && filteredItemIds.includes(header.id);
        });
      }
    } else {
      // Therapist-based headers
      if (bookingFilters.staffIds && bookingFilters.staffIds.length === 0) {
        currentHeaders = [];
      } else {
        currentHeaders = homeStaffList.filter((staff) => {
          const matchesGender =
            !bookingFilters.gender || bookingFilters.gender.length === 0 ||
            bookingFilters.gender.includes(staff.gender?.toLowerCase());
          const matchesSelected =
            !bookingFilters.staffIds || bookingFilters.staffIds.length === 0 ||
            bookingFilters.staffIds.includes(staff.id);
          const matchesSearch =
            (searchQuery || "").trim() === "" ||
            staff.name?.toLowerCase().includes((searchQuery || "").toLowerCase()) ||
            String(staff.id).includes(searchQuery || "");
          return matchesGender && matchesSelected && matchesSearch;
        });
      }
    }
    
    if (!currentHeaders || !currentHeaders[colIndex]) return false;
    
    const slot = timeSlots[rowIndex];
    const header = currentHeaders[colIndex];
    const headerId = header.id;
    const formattedDate = formatDateToDDMMYYYY(selectedDate);
    const therapistDayData = staffTiming?.[headerId]?.[formattedDate]?.[0];
    const slotTimeInMinutes = getSlotAbsoluteMinutes(slot);
    const checkMinutes = typeof absoluteMinutes === "number" && !Number.isNaN(absoluteMinutes) ? absoluteMinutes: slotTimeInMinutes;

    let therapistIsOnLeave = true;
    let therapistIsAvailable = false;

    // For room mode, we don't check therapist availability
    if (!isRoomFilterActive && therapistDayData) {
      const availabilityState = isSlotWithinTherapistAvailability(checkMinutes, therapistDayData);
      therapistIsOnLeave = availabilityState.therapistIsOnLeave;
      therapistIsAvailable = availabilityState.therapistIsAvailable;
    } else if (isRoomFilterActive) {
      // In room mode, always consider available (no therapist timing restrictions)
      therapistIsOnLeave = false;
      therapistIsAvailable = true;
    }
    
    const shouldIgnoreOutletDisabled = shouldIgnoreOutletDisabledForExpandedSchedule(
      checkMinutes,
      therapistDayData,
      therapistIsAvailable
    );
    const hasExpandedTherapistSchedule = isExpandedTherapistScheduleActive(
      checkMinutes,
      therapistDayData,
      therapistIsAvailable
    );

    if (hasExpandedTherapistSchedule) {
      return therapistIsOnLeave || !therapistIsAvailable;
    }

    // Outlet closed is still based on the grid slot; therapist availability uses checkMinutes.
    return (slot.disabled && !shouldIgnoreOutletDisabled) || therapistIsOnLeave || !therapistIsAvailable;
  }, [timeSlots, isRoomFilterActive, homeRoomList, homeStaffList, bookingFilters, searchQuery, filteredRoomIds, filteredItemIds, selectedDate, staffTiming, getSlotAbsoluteMinutes, isSlotWithinTherapistAvailability, shouldIgnoreOutletDisabledForExpandedSchedule, isExpandedTherapistScheduleActive]);

  // NEW BOOKING CLICKED START
  const handleMouseDown = (event, rowIndex, colIndex, isGreyedOut) => {
    // iPad Safari still emits a compatibility mousedown on the cell for the
    // second tap of a double-tap. That mousedown was creating a new slot and
    // clearing the touch selection before pointerup could open the service sidebar.
    const hit = event?.clientX != null ? document.elementFromPoint(event.clientX, event.clientY) : null;
    const hitCard = event.target?.closest?.("[data-booking-id], [data-counter-key]")
      || hit?.closest?.("[data-booking-id], [data-counter-key]");
    const lastTap = lastTouchTapRef.current;
    const recentCardTapOnThisCell =
      lastTap?.completed &&
      Date.now() - lastTap.time <= 500 &&
      lastTap.rowIndex === rowIndex &&
      lastTap.colIndex === colIndex;

    if (hitCard || touchGestureRef.current || recentCardTapOnThisCell) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (isBookingSubmitting) return;

    // Don't allow clicking on new booking slots when sidebar is loading
    if (sidebarLoading) {
      return;
    }

    if (showFilterPanel) {
      filterPanelRef?.current?.saveAndClose?.();
      setShowFilterPanel(false);
      return;
    }

  
    if (isViewOnlyOutlet) {
      event.stopPropagation();
      event.preventDefault();
      const shouldShowToast = !viewOnlyToastShownRef.current;
      if (shouldShowToast) {
        toast.warning("You have view-only access for this outlet.");
        viewOnlyToastShownRef.current = true;
      }
      if (isSidebarVisible || !shouldShowToast) {
        handleCloseSidebar();
      }
      return;
    }
    
    // Don't show discard popup while dragging
    if (isDraggingCard || isDraggingRef.current) {
      return;
    }
    
    // If the previous action was cancel edit, allow one empty-slot click without discard popup
    if (skipNextDiscardCheckRef.current) {
      skipNextDiscardCheckRef.current = false;
    } else {
    // Guard BEFORE create-booking state: otherwise Keep Editing leaves newBookingActive=true
    // and bookingDetails=null, which flashes create UI on save and sticks header date defer.
    if (isEditBookingActive) {
      event.stopPropagation();
      event.preventDefault();
      setShowConflictModal(true);
      return;
    }
      
    // Check if user has filled any data (customer selected OR services selected)
    const hasFilledData = isCustomerSelected || selectedServices.length > 0;
    
    // If there's an existing new booking slot with no data filled
    if (newAppointmentSlots.length > 0 && !hasFilledData) {
      const existingSlot = newAppointmentSlots[0];
      // Check if clicking on the same cell
      if (existingSlot && existingSlot.rowIndex === rowIndex && existingSlot.colIndex === colIndex) {
        // Same cell - do nothing, keep the booking slot and sidebar open
        return;
      }

      if(temporaryBookingId) {
        deleteHoldBooking(temporaryBookingId, selectedOutlet);
        setTemporaryBookingId(null);
        setTempBookingCreated(false);
        setHitTemporaryBookingApi(false);
        localStorage.removeItem('tempBookingID');
      }
      // Different cell - close old one and clear all related states, then return
      setNewAppointmentSlots([]);
      setNewSlot(null);
      setSelectedServices([]);
      setPaxes([]);
      setSelectedRooms({});
      setNewBookingActive(false);
      setIsSidebarVisible(false);
      setIsBookingSidebar(false);
      setIsNewBookingSlotActive(false);
      setSelectedCells(new Set());
      setActiveSlotId(null);
      setSelectedBookingId(null);
      setActiveCardKey(null);
      clearTouchCardSelection();
      return; // Stop here - don't create a new booking
    }
      
      // If data is filled, show conflict modal
      if (hasFilledData && newAppointmentSlots.length > 0) {
        setShowConflictModal(true);
        return;
      }

      setEditableBooking(null);
      setCustomerData({});
      setSelectedRooms({});
      setActiveSlotId(null);
      setSelectedBookingId(null);
    // Starting a new booking (empty slot) should clear any previously selected booking border
    try { setActiveCardKey(null); clearTouchCardSelection(); } catch {}
      setShowBookingSummary(false);
      setIsGreyBox(isGreyedOut);
    }

    // Only enter create-booking mode after all discard/edit guards above.
    setBookingDetails(null);
    setNewBookingActive(true);
    setDiscountItemToPost([]);

    // If any booking has isVoided true, set all to false when clicking on new booking slot
    setBookingsList((prev) => {
      let hasChanges = false;
      const updatedBookings = prev.map((booking) => {
        const bookingItems = booking.booking_item || {};
        const updatedBookingItems = {};
        let bookingHasChanges = false;

        // Iterate through all customer groups in booking_item
        Object.entries(bookingItems).forEach(([customerName, items]) => {
          updatedBookingItems[customerName] = items.map((item) => {
            // If item has isVoided true, set it to false
            if (item.isVoided === true) {
              bookingHasChanges = true;
              hasChanges = true;
              return { ...item, isVoided: false };
            }
            return item;
          });
        });

        if (bookingHasChanges) {
          return {
            ...booking,
            booking_item: updatedBookingItems,
          };
        }
        return booking;
      });

      return hasChanges ? updatedBookings : prev;
    });

    // Logic to close sidebar if already open when clicking on empty slot
    if (rightSidebarOpen) {
      const hasPendingDraft = isNewBookingSlotActive || newAppointmentSlots.length > 0 || isEditBookingActive;
      if ((isCustomerSelected || selectedServices.length > 0) && hasPendingDraft) {
        setShowConflictModal(true);
        return;
      }
      handleCloseSidebar();
      return;
    }

    // Conditionally assign therapistId or roomId based on filter mode
    let therapistId, roomId, roomData;
    if (isRoomFilterActive) {
      // Use filteredRoomHeaders when room mode is active to match the rendered columns
      const currentHeaders = filteredRoomHeaders.length > 0 ? filteredRoomHeaders : headers;
      const headerItem = currentHeaders[colIndex]; // flattened room item header
      roomId = headerItem?.id;
      if (roomId != null) {
        const parentRoom = Array.isArray(homeRoomList)
          ? homeRoomList.find(r => Array.isArray(r.items) && r.items.some(it => it.item_id === roomId))
          : null;
        const matchedItem = parentRoom?.items?.find(it => it.item_id === roomId);
        roomData = {
          id: roomId,
          name: headerItem?.name ?? matchedItem?.item_name ?? "",
          item_type: headerItem?.item_type ?? matchedItem?.item ?? "",
          room_id: roomId,
          room_name: headerItem?.room_name ?? parentRoom?.room_name ?? "",
          room_code: headerItem?.room_code ?? parentRoom?.room_code ?? "",
          type: 'room_item'
        };
      } else {
        roomData = null;
      }
    } else {
      therapistId = headers[colIndex].id;
    }
    
    const selectedTimeString = timeSlots[rowIndex].time; // Use .time for 12-hour format
    const convertedTime = convertTo24HourFormat(selectedTimeString);
    const clickedSlotAbsoluteMinutes = getSlotAbsoluteMinutes(timeSlots[rowIndex]);
    const newTime = buildSelectedTimePayload(
      convertedTime,
      clickedSlotAbsoluteMinutes,
      getSlotTimeLabel(timeSlots[rowIndex])
    );
    setSelectedTime(newTime);

    // Get duration and service name from selected services, default to 15 if no services selected
    const serviceDuration = singleDraftService
      ? Number(singleDraftService?.duration) || 15
      : 15;
    const serviceName = singleDraftService
      ? (singleDraftService?.name || singleDraftService?.service_name || '')
      : '';
    // Get start_date and start_time from the service if available, otherwise use selectedDate and clickedTime
    const serviceStartDate = singleDraftService?.start_date
      ? singleDraftService.start_date
      : getDateStringForAbsoluteMinutes(clickedSlotAbsoluteMinutes);
    const serviceStartTime = getServiceStartTime(singleDraftService)
      ? getServiceStartTime(singleDraftService)
      : convertedTime;
    const calculatedEndTime = getDraftEndTimeForSlot(
      clickedSlotAbsoluteMinutes,
      serviceDuration,
      serviceStartTime
    );
    const newSlot = {
      rowIndex,
      colIndex,
      absoluteStartMinutes: clickedSlotAbsoluteMinutes,
      duration: serviceDuration,
      start_time: serviceStartTime,
      end_time: calculatedEndTime,
      service_at: `${serviceStartDate} ${serviceStartTime}`,
      therapistId: isRoomFilterActive ? null : therapistId,
      roomId: isRoomFilterActive ? roomId : null,
      roomData: isRoomFilterActive ? roomData : null,
      serviceName: serviceName,
      isPlaceholder: true,
      counterKey: 1,
      uid: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}_${Math.random().toString(36).slice(2)}`,
    };

    setNewSlot(newSlot);
    setNewAppointmentSlots([newSlot]);

    setActiveSlotId(null);
    setSelectedBookingId(null);
    setIsNewBookingSlotActive(true);
    setCreatedBooking(null);
    setShowBookingSummary(false);
    setBookingData((prev) => {
      if (prev?.counterKey) return prev;
      return { ...prev, counterKey: 1 };
    });
    setIsDragging(true);
    setStartCell({ rowIndex, colIndex });
    setSelectedCells(new Set([getCellKey(rowIndex, colIndex)]));
    openSidebarWithData(rowIndex, colIndex);

    //  Check if this is a NEW booking creation (empty cell) and apply horizontal scroll for last 5 visible columns
    setTimeout(() => {
      const tableContainer = bookingWrapperRef.current;
      if (!tableContainer) return;

      const table = bookingTableRef.current;
      if (!table) return;

      // Get the visible width of the table container
      const containerWidth = tableContainer.clientWidth;
      const tableWidth = table.scrollWidth;

      // If table is wider than container, check if we need to scroll
      if (tableWidth > containerWidth) {
        const clickedCell = table?.querySelector(`[data-row="${rowIndex}"][data-col="${colIndex}"]`);
        if (!clickedCell) return;

        // Check if this cell contains an existing booking (has data-booking-id attribute)
        const existingBooking = clickedCell.querySelector('[data-booking-id]');

        if (!existingBooking) {
          // This is a NEW booking creation (empty cell) - apply horizontal scroll


          // Get the position of the clicked cell relative to the table
          const cellRect = clickedCell.getBoundingClientRect();
          const tableRect = table.getBoundingClientRect();
          const cellPosition = cellRect.left - tableRect.left;

          // Calculate how many columns are visible on screen
          const columnWidth = cellRect.width;
          const visibleColumns = Math.floor(containerWidth / columnWidth);

          // Check if clicked column is in the last 5 visible columns
          const cellColumnIndex = Math.floor(cellPosition / columnWidth);
          const isInLastFiveVisible = cellColumnIndex >= visibleColumns - 5;



          if (isInLastFiveVisible) {

            // Scroll horizontally to center the clicked cell
            clickedCell.scrollIntoView({
              behavior: "smooth",
              block: "center",
              inline: "center"
            });

            // Add highlight animation to show the new booking slot
            clickedCell.classList.remove('highlightedCard');
            void clickedCell.offsetWidth; // Force reflow
            clickedCell.classList.add('highlightedCard');

            // Remove highlight after animation
            setTimeout(() => {
              clickedCell.classList.remove('highlightedCard');
            }, 1500);
          }
        }
      }
    }, 100); // Delay to ensure DOM is updated
  };

  // useEffect to update newAppointmentSlots when selectedTime ACTUALLY changes
  // CRITICAL FIX: Removed selectedServices from dependencies - this effect should ONLY run when time changes
  useEffect(() => {
    // Don't run this effect if we're currently dragging a card or updating from drag
    if (isDraggingCard || dragUpdateInProgressRef.current) return;
    if (isEditBookingActive || !isNewBookingSlotActive) return;
    if (totalDraftServiceCount > 1) return;
    if (!selectedTime?.value) return;
    
    // CRITICAL FIX: Only proceed if selected time/date actually changed
    const currentTimeValue = selectedTime.value;
    const currentDateKey = selectedDate ? formatDateToDDMMYYYY(selectedDate) : "";
    const currentTimeKey = `${currentDateKey}|${selectedTime.value}|${selectedTime.dayOffset ?? ""}|${selectedTime.absoluteMinutes ?? ""}`;
    const prevTimeValue = prevSelectedTimeRef.current;
    
    // Skip if time/date hasn't actually changed (this prevents running when services are added/removed)
    if (prevTimeValue === currentTimeKey && newAppointmentSlots.length > 0) {
      // Update the ref but don't update slots
      prevSelectedTimeRef.current = currentTimeKey;
      return;
    }
    
    // Update ref for next comparison
    prevSelectedTimeRef.current = currentTimeKey;
    
    const currentMainSlot = newAppointmentSlots[0];
    const currentMainAbsoluteMinutes = typeof currentMainSlot?.absoluteStartMinutes === "number"
      ? currentMainSlot.absoluteStartMinutes
      : getSlotAbsoluteMinutes(timeSlots[currentMainSlot?.rowIndex]);
    const resolvedAbsoluteStartMinutes = getSelectedTimeAbsoluteMinutes(selectedTime, currentMainAbsoluteMinutes);
    const newRowIndex = getRowIndexFromAbsoluteMinutes(
      resolvedAbsoluteStartMinutes,
      currentTimeValue
    );
    if (newRowIndex >= 0) {
      let currentColIndex = 0;
      const newSlotAbsoluteMinutes = typeof resolvedAbsoluteStartMinutes === "number"
        ? resolvedAbsoluteStartMinutes
        : getSlotAbsoluteMinutes(timeSlots[newRowIndex]);
      
      setNewAppointmentSlots((prev) => {
        if (!Array.isArray(prev) || prev.length === 0) return prev;
        const mainSlot = prev[0];
        
        // CRITICAL FIX: Check if mainSlot exists and is valid
        if (!mainSlot || typeof mainSlot !== 'object') return prev;
        
        // Store colIndex for use outside the setState callback
        currentColIndex = mainSlot.colIndex ?? 0;
        
        // Update isGreyBox based on new time slot (use exact minutes, not grid cell start)
        if (mainSlot && typeof mainSlot.colIndex === 'number') {
          const isGreyedOut = checkIfCellIsGreyedOut(newRowIndex, mainSlot.colIndex, newSlotAbsoluteMinutes);
          setIsGreyBox(isGreyedOut);
        }
        
        // Get duration and service name from selected services, fallback to existing values
        const serviceDuration = singleDraftService
          ? Number(singleDraftService?.duration) || mainSlot.duration
          : mainSlot.duration;
        const serviceName = singleDraftService
          ? (singleDraftService?.name || singleDraftService?.service_name || mainSlot.serviceName || '')
          : (mainSlot.serviceName || '');

        // Get start_time - use selectedTime.value since time actually changed
        const serviceStartTime = getServiceStartTime(singleDraftService) || (selectedTime?.value || mainSlot.start_time);
        
        const updatedMain = {
          ...mainSlot,
          absoluteStartMinutes: newSlotAbsoluteMinutes,
          // Time changed, so update rowIndex
          rowIndex: newRowIndex,
          start_time: serviceStartTime,
          end_time: getDraftEndTimeForSlot(newSlotAbsoluteMinutes, serviceDuration, serviceStartTime),
          duration: serviceDuration,
          serviceName: serviceName,
          // CRITICAL FIX: Always preserve colIndex - never change it here
          colIndex: mainSlot.colIndex ?? 0,
          // Use conditional assignment based on filter mode
          therapistId: isRoomFilterActive ? null : (mainSlot.therapistId ?? null),
          therapistName: isRoomFilterActive ? null : (mainSlot.therapistName ?? null),
          roomId: isRoomFilterActive ? (mainSlot.roomId ?? null) : null,
          roomData: isRoomFilterActive ? (mainSlot.roomData ?? null) : null,
          // CRITICAL FIX: Preserve customer from customerData if available, otherwise use mainSlot.customer
          customer: (customerData && Object.keys(customerData).length > 0) ? customerData : (mainSlot.customer || null),
          services: singleDraftService ? [singleDraftService] : mainSlot.services,
          service_at: `${getDateStringForAbsoluteMinutes(newSlotAbsoluteMinutes)} ${serviceStartTime}`,
        };

        // Update only main slot, keep others reference-identical
        const next = [...prev];
        next[0] = updatedMain;
        return next;
      });
      
      // Update startCell since time changed
      setStartCell((prev) => ({ ...prev, rowIndex: newRowIndex }));
      setSelectedCells(
        new Set([getCellKey(newRowIndex, currentColIndex)])
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTime, isDraggingCard, getRowIndexFromTime, selectedDate, isRoomFilterActive, isEditBookingActive, isNewBookingSlotActive, totalDraftServiceCount, singleDraftService, getServiceStartTime, getSlotAbsoluteMinutes, timeSlots, getSelectedTimeAbsoluteMinutes, getRowIndexFromAbsoluteMinutes, getDateStringForAbsoluteMinutes, getDraftEndTimeForSlot]);

  // Update duration and service name when selectedServices changes
  // CRITICAL FIX: This effect ONLY updates service data (duration, name, etc.) and NEVER touches rowIndex/colIndex
  useEffect(() => {
    if (isDraggingCard || dragUpdateInProgressRef.current) return;
    if (isEditBookingActive) return;
    if (!isNewBookingSlotActive) return; // Only run for new bookings
    if (totalDraftServiceCount > 1) return;
    if (newAppointmentSlots.length === 0) return; // Need at least one slot
    
    setNewAppointmentSlots((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const mainSlot = prev[0];
      
      // CRITICAL FIX: Check if mainSlot exists and is valid
      if (!mainSlot || typeof mainSlot !== 'object') return prev;
      
      // Get service data from first service if available
      const serviceDuration = singleDraftService
        ? Number(singleDraftService?.duration) || mainSlot.duration
        : mainSlot.duration;
      const serviceName = singleDraftService
        ? (singleDraftService?.name || singleDraftService?.service_name || mainSlot.serviceName || '')
        : (mainSlot.serviceName || '');
      
      // CRITICAL FIX: Preserve existing start_time - only use service start_time if it's different AND explicitly set
      // This prevents the card from jumping when services are added/removed
      let serviceStartTime = mainSlot.start_time; // Default to existing
      if (getServiceStartTime(singleDraftService)) {
        // Only update if service has an explicit start_time
        serviceStartTime = getServiceStartTime(singleDraftService);
      } else if (!mainSlot.start_time && selectedTime?.value) {
        // Only set if slot doesn't have a start_time yet
        serviceStartTime = selectedTime.value;
      }
      
      // Check if anything actually changed
      const durationChanged = mainSlot.duration !== serviceDuration;
      const nameChanged = mainSlot.serviceName !== serviceName;
      const timeChanged = mainSlot.start_time !== serviceStartTime;
      
      // If nothing changed, return prev to avoid unnecessary re-render
      if (!durationChanged && !nameChanged && !timeChanged) {
        return prev;
      }

      const mainSlotAbsoluteMinutes = typeof mainSlot.absoluteStartMinutes === "number"
        ? mainSlot.absoluteStartMinutes
        : getSlotAbsoluteMinutes(timeSlots[mainSlot.rowIndex]);
      const updatedMain = {
        ...mainSlot,
        absoluteStartMinutes: mainSlotAbsoluteMinutes,
        // CRITICAL FIX: ALWAYS preserve grid position - NEVER change rowIndex or colIndex here
        rowIndex: mainSlot.rowIndex,
        colIndex: mainSlot.colIndex,
        duration: serviceDuration,
        serviceName: serviceName,
        start_time: serviceStartTime,
        end_time: getDraftEndTimeForSlot(mainSlotAbsoluteMinutes, serviceDuration, serviceStartTime),
        // CRITICAL FIX: Preserve customer from customerData if available, otherwise use mainSlot.customer
        customer: (customerData && Object.keys(customerData).length > 0) ? customerData : (mainSlot.customer || null),
        service_at: `${getDateStringForAbsoluteMinutes(mainSlotAbsoluteMinutes)} ${serviceStartTime}`,
      };

      // Update only main slot, keep others reference-identical
      const next = [...prev];
      next[0] = updatedMain;
      return next;
    });
  }, [selectedServices, paxes, isDraggingCard, isNewBookingSlotActive, selectedDate, selectedTime, isEditBookingActive, totalDraftServiceCount, singleDraftService, getServiceStartTime, getSlotAbsoluteMinutes, timeSlots, getDateStringForAbsoluteMinutes, getDraftEndTimeForSlot]);

  // Show pax cards during new booking creation only when a therapist is selected (not Auto-Assign)
  useEffect(() => {
    if (isEditBookingActive) return;
    if (!isNewBookingSlotActive) return;
    if (isDraggingCard || dragUpdateInProgressRef.current) return;
    if (totalDraftServiceCount > 0) return;
    if (!Array.isArray(newAppointmentSlots) || newAppointmentSlots.length === 0) return;

    const baseSlot = newAppointmentSlots[0];
    if (!baseSlot) return;

    // Helper to extract therapist id from various shapes
    const extractTherapistId = (svc) => {
      const directId = getServiceTherapistId(svc);
      if (directId != null) return directId;

      const nameLike = svc?.therapist_name || (svc?.therapist && typeof svc.therapist === 'object' ? (svc.therapist?.label || svc.therapist?.name) : undefined);
      if (nameLike && Array.isArray(homeStaffList)) {
        const lower = String(nameLike).trim().toLowerCase();
        const match = homeStaffList.find((h) => String(h?.name || '').toLowerCase() === lower || String(h?.alias || '').toLowerCase() === lower);
        if (match && match.id != null) return Number(match.id);
      }

      return null;
    };

    // Use filteredRoomHeaders when room mode is active to match the rendered columns
    // This ensures colIndex matches the actual column position in the table
    const currentHeaders = isRoomFilterActive
      ? (filteredRoomHeaders.length > 0 ? filteredRoomHeaders : headers)
      : (Array.isArray(homeStaffList)
          ? homeStaffList.filter((staff) => {
              const matchesGender =
                !bookingFilters.gender || bookingFilters.gender.length === 0 ||
                bookingFilters.gender.includes(staff.gender?.toLowerCase());
              const matchesSelected =
                !bookingFilters.staffIds || bookingFilters.staffIds.length === 0 ||
                bookingFilters.staffIds.includes(staff.id);
              const matchesSearch =
                (searchQuery || "").trim() === "" ||
                staff.name?.toLowerCase().includes((searchQuery || "").toLowerCase()) ||
                String(staff.id).includes(searchQuery || "");
              return matchesGender && matchesSelected && matchesSearch;
            })
          : []);

    const baseSlotAbsoluteMinutes = typeof baseSlot?.absoluteStartMinutes === "number"
      ? baseSlot.absoluteStartMinutes
      : getSlotAbsoluteMinutes(timeSlots[baseSlot.rowIndex]);

    // Collect pax services only (not main selectedServices)
    const paxServices = Array.isArray(paxes)
      ? paxes.flatMap((p) => (Array.isArray(p.selectedServices) ? p.selectedServices : []))
      : [];

    // Also handle main selectedServices for room mode
    const mainServices = Array.isArray(selectedServices) ? selectedServices : [];
    const allServices = [...paxServices, ...mainServices];

    // Try also to resolve therapist/room from object shape on services (value/label)
    const normalizedAllServices = allServices.map((svc) => {
      if (isRoomFilterActive) {
        // In room mode, extract room information
        const roomId = getServiceRoomItemId(svc);
        return roomId != null ? { ...svc, roomId: roomId } : svc;
      } else {
        // In therapist mode, extract therapist information
        const tid = extractTherapistId(svc);
        return tid != null ? { ...svc, therapist: tid } : svc;
      }
    });

    // Only services with an explicitly selected therapist/room (not Auto-Assign)
    const servicesWithSelection = isRoomFilterActive 
      ? normalizedAllServices
          .map((svc) => ({ svc, selectionId: svc.roomId }))
          .filter(({ selectionId }) => selectionId != null && !Number.isNaN(Number(selectionId)))
      : normalizedAllServices
          .map((svc) => ({ svc, selectionId: extractTherapistId(svc) }))
          .filter(({ selectionId }) => selectionId != null && !Number.isNaN(Number(selectionId)));

    const desiredKeys = new Set(servicesWithSelection.map(({ svc }) => String(svc.counterKey)));


    setNewAppointmentSlots((prev) => {
      const existingByKey = new Map(prev.map((s) => [String(s.counterKey), s]));
      let changed = false;
      
      // CRITICAL FIX: Preserve main slot (counterKey: 1) reference - never modify it in this effect
      const mainSlotKey = prev.find(s => s.counterKey === 1 || (!s.isPax && !s.isPlaceholder))?.counterKey;
      
      // Remove previously added pax slots whose therapist/room was cleared back to Auto-Assign
      // Keep all non-pax slots (including main slot) and pax slots that are still desired
      const next = prev.filter((s) => {
        // Always keep non-pax slots (main slot)
        if (!s.isPax) return true;
        // For pax slots, only keep if still in desiredKeys
        return desiredKeys.has(String(s.counterKey));
      });

      servicesWithSelection.forEach(({ svc, selectionId }) => {
        const key = String(svc.counterKey);
        const timeVal =
          (svc.start_time && typeof svc.start_time === 'string' ? svc.start_time : (selectedTime?.value || baseSlot.start_time));
        const durationVal = Number(svc.duration) || baseSlot.duration || 15;
        const endVal = calculateEndTime(timeVal, durationVal);
        const selectionIdVal = Number(selectionId);
        const colIdx = currentHeaders.findIndex((h) => Number(h.id) === selectionIdVal);
        const paxServiceStartTime = svc.start_time || timeVal;
        const existingSlotAbsoluteMinutes = existingByKey.get(key)?.absoluteStartMinutes;
        const serviceAbsoluteMinutes =
          typeof svc?.absoluteMinutes === "number" && !Number.isNaN(svc.absoluteMinutes)
            ? svc.absoluteMinutes
            : (
                typeof svc?.dayOffset === "number" &&
                !Number.isNaN(svc.dayOffset) &&
                paxServiceStartTime
                  ? (Math.max(0, svc.dayOffset) * 1440) + convertToMinutes(paxServiceStartTime)
                  : null
              );
        const absoluteStartMinutes = typeof serviceAbsoluteMinutes === "number" && !Number.isNaN(serviceAbsoluteMinutes)
          ? serviceAbsoluteMinutes
          : (
              typeof existingSlotAbsoluteMinutes === "number" && !Number.isNaN(existingSlotAbsoluteMinutes)
                ? existingSlotAbsoluteMinutes
                : resolveDraftAbsoluteStartMinutes(
                    paxServiceStartTime,
                    baseSlotAbsoluteMinutes
                  )
            );
        const rowIdx = getRowIndexFromAbsoluteMinutes(absoluteStartMinutes, timeVal);
        
        // Get the appropriate name based on mode
        const selectionNameVal = isRoomFilterActive 
          ? (svc.roomName || currentHeaders.find((h) => Number(h.id) === selectionIdVal)?.name || baseSlot.roomName || '')
          : (svc.therapist_name || currentHeaders.find((h) => Number(h.id) === selectionIdVal)?.name || baseSlot.therapistName || '');

        // If the therapist/room column is not visible, skip rendering this pax slot
        if (colIdx < 0) {
          return;
        }

        const slotShape = {
          rowIndex: rowIdx >= 0 ? rowIdx : baseSlot.rowIndex,
          colIndex: colIdx,
          absoluteStartMinutes,
          duration: durationVal,
          start_time: paxServiceStartTime,
          end_time: endVal,
          service_at: `${getDateStringForAbsoluteMinutes(absoluteStartMinutes)} ${paxServiceStartTime}`,
          // Use conditional assignment based on filter mode
          therapistId: isRoomFilterActive ? null : selectionIdVal,
          therapistName: isRoomFilterActive ? null : selectionNameVal,
          roomId: isRoomFilterActive ? selectionIdVal : null,
          roomData: isRoomFilterActive ? currentHeaders.find((h) => Number(h.id) === selectionIdVal) : null,
          isPlaceholder: false,
          isPax: true,
          counterKey: svc.counterKey,
          customer: svc.customer || null,
          serviceName: svc.name || svc.service_name || svc.service || ''
        };

        if (existingByKey.has(key)) {
          const existing = existingByKey.get(key);
          // In room mode, only check room-relevant fields (skip therapist comparison)
          // In therapist mode, include therapist in the comparison
          const needsUpdate = isRoomFilterActive
            ? (existing.rowIndex !== slotShape.rowIndex ||
               existing.colIndex !== slotShape.colIndex ||
               existing.start_time !== slotShape.start_time ||
               existing.end_time !== slotShape.end_time ||
               existing.service_at !== slotShape.service_at ||
               Number(existing.roomId) !== Number(slotShape.roomId))
            : (existing.rowIndex !== slotShape.rowIndex ||
               existing.colIndex !== slotShape.colIndex ||
               existing.start_time !== slotShape.start_time ||
               existing.end_time !== slotShape.end_time ||
               existing.service_at !== slotShape.service_at ||
               Number(existing.therapistId) !== Number(slotShape.therapistId));
          if (needsUpdate) {
            const idx = next.findIndex((s) => String(s.counterKey) === key);
            if (idx >= 0) {
              // CRITICAL FIX: Never modify the main slot (counterKey: 1) in this effect
              if (String(key) === String(mainSlotKey)) {
                // Skip updating main slot - it's handled by other useEffects
                return;
              }
              next[idx] = { ...next[idx], ...slotShape };
              changed = true;
            }
          }
        } else {
          // CRITICAL FIX: Never add a new main slot here - only add pax slots
          if (String(key) === String(mainSlotKey)) {
            // Skip - main slot should already exist
            return;
          }
          next.push(slotShape);
          changed = true;
        }
      });

      // CRITICAL FIX: Only return new array if something actually changed
      // Compare arrays to avoid unnecessary re-renders
      if (!changed) {
        return prev; // Return same reference if nothing changed
      }
      
      // Check if the arrays are actually different
      if (next.length === prev.length) {
        const arraysEqual = next.every((slot, idx) => {
          const prevSlot = prev[idx];
          return prevSlot && 
                 String(prevSlot.counterKey) === String(slot.counterKey) &&
                 prevSlot.rowIndex === slot.rowIndex &&
                 prevSlot.colIndex === slot.colIndex &&
                 prevSlot.start_time === slot.start_time;
        });
        if (arraysEqual) {
          return prev; // Arrays are identical, return same reference
        }
      }
      
      return next;
    });
  }, [
    isNewBookingSlotActive,
    paxes,
    selectedServices,
    selectedTime,
    newAppointmentSlots,
    isDraggingCard,
    homeStaffList,
    homeRoomList,
    bookingFilters,
    searchQuery,
    getRowIndexFromTime,
    setNewAppointmentSlots,
    isRoomFilterActive,
    selectedDate,
    isEditBookingActive,
    totalDraftServiceCount,
    getServiceTherapistId,
    getServiceRoomItemId,
    getDateStringForAbsoluteMinutes,
  ]);

  // Update calendar slot when customer is selected or cleared
  useEffect(() => {
    if (newAppointmentSlots.length > 0) {
      setNewAppointmentSlots((prev) => {
        const updated = prev.map((slot) => {
          // Update ALL slots (main booking slot and service slots) when customer is selected or cleared
          const updatedSlot = {
            ...slot,
            customer: customerData && Object.keys(customerData).length > 0 ? customerData : null,
          };
          return updatedSlot;
        });
        return updated;
      });
    }
  }, [customerData, setNewAppointmentSlots, newAppointmentSlots.length]);
  // NEW BOOKING CLICKED END

  const handleMouseOver = (rowIndex, colIndex) => {
    if (!isDragging || !startCell) return;
    if (colIndex !== startCell.colIndex) return;
    const newSelection = new Set(selectedCells);
    const cellKey = getCellKey(rowIndex, colIndex);
    newSelection.add(cellKey);
    setSelectedCells(newSelection);
  };

  // Helper function to clamp ghost card position within container bounds
  const getClampedGhostCardPosition = (leftPos, topPos, cardWidth, cardHeight) => {
    const container = bookingWrapperRef.current;
    const containerRect = container?.getBoundingClientRect();
    
    if (!containerRect) {
      return { left: leftPos, top: topPos };
    }
    
    const minLeftOffset = window?.innerWidth && window.innerWidth < 600 ? 48 : 68;
    const minLeft = containerRect.left + minLeftOffset;
    const maxLeft = containerRect.right - cardWidth;
    const minTop = containerRect.top + 40;
    const maxTop = containerRect.bottom - cardHeight;
    
    return {
      left: Math.max(minLeft, Math.min(maxLeft, leftPos)),
      top: Math.max(minTop, Math.min(maxTop, topPos))
    };
  };

  const CHECK_IN_IN_PROGRESS_STATUS = 'Check-in (In Progress)';
  const OPEN_IN_PROGRESS_PAYMENT_STATUS = 'Open(In Progress)';
  const COMPLETED_STATUS = 'Completed';
  const CLOSED_STATUS = 'Closed';

  const normalizeBookingStatusKey = (value) =>
    String(value || '').trim().toLowerCase().replace(/\s+/g, '').replace(/[()]/g, '');

  const isOpenInProgressPayment = ({ paymentStatus }) => {
    const normalizedPaymentStatus = normalizeBookingStatusKey(paymentStatus);
    if (
      paymentStatus === OPEN_IN_PROGRESS_PAYMENT_STATUS ||
      normalizedPaymentStatus === 'open(inprogress)'
    ) {
      return true;
    }
  };

  const resolveSlotBookingContext = (slot) => {
    const sourceBooking = (bookingsList || []).find(
      (booking) => String(booking.id) === String(slot?.booking_id)
    );

    const status = slot?.status ?? sourceBooking?.status ?? null;
    const paymentStatus =slot?.payment_status ?? slot?.sale?.payment_status ?? sourceBooking?.sale?.payment_status ?? sourceBooking?.payment_status ?? null;
    const paymentStatusId = slot?.payment_status_id ?? slot?.sale?.payment_status_id ?? sourceBooking?.payment_status_id ?? sourceBooking?.sale?.payment_status_id ?? null;

    return { status, paymentStatus, paymentStatusId };
  };

  const getBookingMoveBlockedMessage = (slot) => {
    if (!slot?.booking_id) return null;

    const { status, paymentStatus } = resolveSlotBookingContext(slot);

    if (isReadOnlyBookingStatus(status)) {
      return `This booking is ${status}; it can't be moved.`;
    }

    if (status === COMPLETED_STATUS || status === CLOSED_STATUS) {
      setCompleteBookingWarning(true);
    } else {
      setCompleteBookingWarning(false);
    }
    if (
      status === CHECK_IN_IN_PROGRESS_STATUS && paymentStatus === OPEN_IN_PROGRESS_PAYMENT_STATUS) {
      setShowPaymentInProgressNotice(true);
    } else {
      setShowPaymentInProgressNotice(false);
    }

    return null;
  };

  // Keeps a card press from bubbling to the cell, which would create a new slot.
  // pointerdown stopPropagation does not stop the separate mousedown iPad synthesizes.
  const stopCardMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  // Drag and drop handlers for booking cards
  const handleCardMouseDown = (event, slot) => {
    // Prevent empty-slot handler from firing when clicking a card
    event.preventDefault();
    event.stopPropagation();
    window.dispatchEvent(new Event(CLOSE_TAGGING_DROPDOWNS));

    // Touch and Apple Pencil. Mice and trackpads report pointerType "mouse" and keep the path below.
    if (event.pointerType === "touch" || event.pointerType === "pen") {
      handleTouchCardPointerDown(event, slot);
      return;
    }

    // Allow dragging existing bookings even when edit sidebar is open; do not trigger discard popup
    if (isViewOnlyOutlet) {
      touchDragArmedRef.current = false;
      return;
    }

    const isCustomerSelected = customerData && Object.keys(customerData).length > 0;
    
    // show discard pupup when starts drag new booking card
    if ((newAppointmentSlots.length > 0) || selectedServices.length > 0) {
      
      const isNewSlot = newAppointmentSlots.some(s => String(s.counterKey) === String(slot.counterKey));
      const isEditingCurrentBooking = selectedBookingId && String(selectedBookingId) === String(slot.booking_id);
      if (!isNewSlot && !isEditingCurrentBooking) {
         if (isCustomerSelected || selectedServices.length > 0) {
            touchDragArmedRef.current = false;
            setShowConflictModal(true);
            return;
         }
      }
    }

    // Make this the most recently interacted card (fixes "two cards show selected border")
    try {
      if (slot?.booking_id) {
        setActiveCardKey(`${slot.booking_id}:${slot?.item_id ?? ""}`);
      }
    } catch {}
    


    const rect = event.currentTarget.getBoundingClientRect();
    const offset = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
    
    // Create ghost card data
    const ghostStatusRaw = (slot?.status || '').toString().toLowerCase();
    const ghostStatus = ghostStatusRaw.replace(/\s+/g, '').replace(/-/g, '').replace(/[()]/g, '');
    const ghostRequested = slot?.requested_person === 1 || slot?.requested_therapist === true || slot?.tags?.isRequested;
    const ghostUser = slot?.user || {};
    const ghostServiceName = slot?.serviceName || '';
    const ghostServiceId = slot?.service_id || slot?.serviceId || null;

    const ghostCardData = {
      ...slot,
      rect: rect,
      offset: offset,
      ghostMeta: {
        statusClass: ghostStatus ? `${styles['status' + ghostStatus]}` : '',
        isRequested: !!ghostRequested,
        userName: ghostUser?.name,
        userContact: ghostUser?.contact,
        serviceName: ghostServiceName,
        serviceId: ghostServiceId,
      }
    };

    draggedCardRef.current = slot;
    dragPositionRef.current = { x: event.clientX, y: event.clientY };
    dragStartPosRef.current = { x: event.clientX, y: event.clientY };
    dragOffsetRef.current = offset; // Store offset in ref for immediate access
    setDraggedCard(slot);
    if (slot?.booking_id && slot?.item_id) {
      setDragKey(`${slot.booking_id}:${slot.item_id}`);
    } else {
      setDragKey(null);
    }
    setGhostCard(ghostCardData);
    setDragOffset(offset);
    setDragPosition({ x: event.clientX, y: event.clientY });
    setIsDraggingCard(false); // Start with false, will be set to true on movement
    
    // Capture pointer for consistent drag on touch devices
    try {
      if (event?.pointerId != null && event.currentTarget?.setPointerCapture) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
    } catch {}

    // Add global mouse/pointer event listeners with capture
    // document.addEventListener('mousemove', handleCardMouseMove, { passive: false });
    // document.addEventListener('mouseup', handleCardMouseUp, { passive: false });
    document.addEventListener('pointermove', handleCardMouseMove, { passive: false });
    document.addEventListener('pointerup', handleCardMouseUp, { passive: false });
    document.addEventListener('pointercancel', handleCardMouseCancel, { passive: false });
    window.addEventListener('blur', handleCardMouseCancel);
  };


  const handleCardMouseMove = (event) => {
    if (event?.cancelable) {
      event.preventDefault();
    }
    if (!draggedCardRef.current) return;
    
    // Start dragging only after a small movement threshold
    if (!isDraggingRef.current) {
      const startPos = dragStartPosRef.current || { x: 0, y: 0 };
      const dx = (event.clientX || 0) - (startPos.x || 0);
      const dy = (event.clientY || 0) - (startPos.y || 0);
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance < 6) {
        return;
      }
      const slot = draggedCardRef.current;
      
      // Allow dragging requested therapist/room to change time in same column.
      // Column change validation is handled on drop.
      
      event.preventDefault(); // Prevent default browser behavior
      isDraggingRef.current = true;
      setIsDraggingCard(true);
      // Set cursor to grabbing on document body and html with !important
      document.body.style.setProperty('cursor', 'grabbing', 'important');
      document.documentElement.style.setProperty('cursor', 'grabbing', 'important');
      document.body.style.userSelect = 'none';
      
      // Add global style tag to force cursor on all elements
      if (!dragCursorStyleRef.current) {
        const style = document.createElement('style');
        style.id = 'drag-cursor-style';
        style.textContent = '* { cursor: grabbing !important; }';
        document.head.appendChild(style);
        dragCursorStyleRef.current = style;
      }
    }
    
    // Update position ref immediately for smooth cursor following
    const newPosition = { 
      x: event.clientX, 
      y: event.clientY 
    };
    dragPositionRef.current = newPosition;
    
    // Update ghost card positions directly via DOM for instant response (no React re-render delay)
    const currentOffset = dragOffsetRef.current;
    let leftPos = newPosition.x - (currentOffset.x || 0);
    let topPos = newPosition.y - (currentOffset.y || 0);
    
    const ghostElements = document.querySelectorAll(`[data-ghost-overlay="true"]`);
    
    // Get card dimensions from ghost card state or element
    let cardWidth = 160;
    let cardHeight = 60;
    if (ghostElements.length > 0) {
      const firstGhostElement = ghostElements[0];
      const cardRect = firstGhostElement.getBoundingClientRect();
      cardWidth = cardRect.width || 160;
      cardHeight = cardRect.height || 60;
    } else if (ghostCard) {
      // Fallback to ghostCard state if element not found yet
      cardWidth = ghostCard.rect?.width || 160;
      cardHeight = ghostCard.rect?.height || 60;
    }
    
    // Clamp position to stay within container bounds
    const clamped = getClampedGhostCardPosition(leftPos, topPos, cardWidth, cardHeight);
    leftPos = clamped.left;
    topPos = clamped.top;
    
    ghostElements.forEach((element) => {
      if (element && element.style) {
        element.style.setProperty('left', `${leftPos}px`, 'important');
        element.style.setProperty('top', `${topPos}px`, 'important');
        element.style.setProperty('cursor', 'grabbing', 'important');
      }
    });
    
    // Auto-scroll when dragging near edges
    const container = bookingWrapperRef.current;
    if (container && isDraggingRef.current) {
      const containerRect = container.getBoundingClientRect();
      const scrollThreshold = 44; // Distance from edge to trigger scroll (in pixels)
      // Slightly faster auto-scroll (px per frame; tuned for ~60fps)
      const maxScrollSpeed = 1060; // Maximum scroll speed (pixels per frame)
      const minScrollSpeed = 65; // Minimum scroll speed (pixels per frame)
      
      let scrollX = 0;
      let scrollY = 0;
      
      // Check if container can scroll horizontally
      const canScrollX = container.scrollWidth > container.clientWidth;
      // Check if container can scroll vertically
      const canScrollY = container.scrollHeight > container.clientHeight;
      
        // Check horizontal scroll (left/right edges)
        if (canScrollX) {
          const distanceFromLeft = newPosition.x - containerRect.left;
          const distanceFromRight = containerRect.right - newPosition.x;
          
          const maxScrollLeft = container.scrollWidth - container.clientWidth;
          const scrollTolerance = 1;
          
          if (distanceFromLeft < scrollThreshold && container.scrollLeft > scrollTolerance) {
            // Near left edge - scroll left
            const speed = Math.max(minScrollSpeed, Math.min(maxScrollSpeed, (scrollThreshold - distanceFromLeft) / scrollThreshold * maxScrollSpeed));
            scrollX = -speed;
          } else if (distanceFromRight < scrollThreshold && container.scrollLeft < maxScrollLeft - scrollTolerance) {
            // Near right edge - scroll right (only if not already at right edge)
            const speed = Math.max(minScrollSpeed, Math.min(maxScrollSpeed, (scrollThreshold - distanceFromRight) / scrollThreshold * maxScrollSpeed));
            scrollX = speed;
          } else {
            // At scroll limits, ensure we don't try to scroll
            scrollX = 0;
          }
        }
      
      // Check vertical scroll (top/bottom edges)
      if (canScrollY) {
        const distanceFromTop = newPosition.y - containerRect.top;
        const distanceFromBottom = containerRect.bottom - newPosition.y;
        
        // Calculate max scroll position with small tolerance for floating point precision
        const maxScrollTop = container.scrollHeight - container.clientHeight;
        const scrollTolerance = 1; // Small tolerance for floating point comparison
        
        if (distanceFromTop < scrollThreshold && container.scrollTop > scrollTolerance) {
          // Near top edge - scroll up
          const speed = Math.max(minScrollSpeed, Math.min(maxScrollSpeed, (scrollThreshold - distanceFromTop) / scrollThreshold * maxScrollSpeed));
          scrollY = -speed;
        } else if (distanceFromBottom < scrollThreshold && container.scrollTop < maxScrollTop - scrollTolerance) {
          // Near bottom edge - scroll down (only if not already at bottom)
          const speed = Math.max(minScrollSpeed, Math.min(maxScrollSpeed, (scrollThreshold - distanceFromBottom) / scrollThreshold * maxScrollSpeed));
          scrollY = speed;
        } else {
          // At scroll limits, ensure we don't try to scroll
          scrollY = 0;
        }
      }
      
      // Update target scroll speeds (will be smoothly interpolated in animation loop)
      dragScrollTargetSpeedRef.current = { x: scrollX, y: scrollY };
      
      // Apply scrolling if needed
      if (scrollX !== 0 || scrollY !== 0) {
        // Only start animation frame loop if it doesn't exist
        if (!dragAutoScrollIntervalRef.current) {
          const scrollLoop = () => {
            const currentContainer = bookingWrapperRef.current;
            if (currentContainer && dragAutoScrollIntervalRef.current) {
              const targetSpeeds = dragScrollTargetSpeedRef.current;
              const currentSpeeds = dragScrollSpeedRef.current;
              
              // Smooth interpolation factor (lower = smoother but slower response)
              // 0.1 = very smooth, 0.15 = smooth, 0.2 = balanced, 0.3+ = faster response
              const lerpFactor = 0.42;
              
              // Smoothly interpolate current speed towards target speed
              const newSpeedX = currentSpeeds.x + (targetSpeeds.x - currentSpeeds.x) * lerpFactor;
              const newSpeedY = currentSpeeds.y + (targetSpeeds.y - currentSpeeds.y) * lerpFactor;
              
              // Update current speeds with smaller threshold for smoother deceleration
              dragScrollSpeedRef.current = { 
                x: Math.abs(newSpeedX) < 0.05 ? 0 : newSpeedX, 
                y: Math.abs(newSpeedY) < 0.05 ? 0 : newSpeedY 
              };
              
              const speeds = dragScrollSpeedRef.current;
              
              // Only scroll if there's room to scroll
              const scrollTolerance = 1; // Small tolerance for floating point comparison
              const maxScrollLeft = currentContainer.scrollWidth - currentContainer.clientWidth;
              const maxScrollTop = currentContainer.scrollHeight - currentContainer.clientHeight;
              
              if (speeds.x !== 0) {
                const newScrollLeft = currentContainer.scrollLeft + speeds.x;
                // Clamp to bounds
                if (newScrollLeft < 0) {
                  currentContainer.scrollLeft = 0;
                  // Stop horizontal scrolling if at limit
                  if (speeds.x < 0) {
                    dragScrollSpeedRef.current.x = 0;
                    dragScrollTargetSpeedRef.current.x = 0;
                  }
                } else if (newScrollLeft > maxScrollLeft + scrollTolerance) {
                  currentContainer.scrollLeft = maxScrollLeft;
                  // Stop horizontal scrolling if at limit
                  if (speeds.x > 0) {
                    dragScrollSpeedRef.current.x = 0;
                    dragScrollTargetSpeedRef.current.x = 0;
                  }
                } else {
                  currentContainer.scrollLeft = newScrollLeft;
                }
              }
              
              if (speeds.y !== 0) {
                const newScrollTop = currentContainer.scrollTop + speeds.y;
                // Clamp to bounds
                if (newScrollTop < 0) {
                  currentContainer.scrollTop = 0;
                  // Stop vertical scrolling if at limit
                  if (speeds.y < 0) {
                    dragScrollSpeedRef.current.y = 0;
                    dragScrollTargetSpeedRef.current.y = 0;
                  }
                } else if (newScrollTop > maxScrollTop + scrollTolerance) {
                  currentContainer.scrollTop = maxScrollTop;
                  // Stop vertical scrolling if at limit
                  if (speeds.y > 0) {
                    dragScrollSpeedRef.current.y = 0;
                    dragScrollTargetSpeedRef.current.y = 0;
                  }
                } else {
                  currentContainer.scrollTop = newScrollTop;
                }
              }
              
              // Continue scrolling if speeds are non-zero or still interpolating
              if (speeds.x !== 0 || speeds.y !== 0 || targetSpeeds.x !== 0 || targetSpeeds.y !== 0) {
                dragAutoScrollIntervalRef.current = requestAnimationFrame(scrollLoop);
              } else {
                dragAutoScrollIntervalRef.current = null;
              }
            } else {
              dragAutoScrollIntervalRef.current = null;
            }
          };
          dragAutoScrollIntervalRef.current = requestAnimationFrame(scrollLoop);
        }
      } else {
        // Stop scrolling if not near edges (target speed is 0, will smoothly decelerate)
        dragScrollTargetSpeedRef.current = { x: 0, y: 0 };
        // Don't cancel immediately - let it smoothly decelerate
        if (dragScrollSpeedRef.current.x === 0 && dragScrollSpeedRef.current.y === 0) {
          if (dragAutoScrollIntervalRef.current) {
            cancelAnimationFrame(dragAutoScrollIntervalRef.current);
            dragAutoScrollIntervalRef.current = null;
          }
        }
      }
    }
    
    // Cancel any pending animation frame for drop target calculation
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }
    
    // Use requestAnimationFrame only for drop target calculation (can be slightly delayed)
    rafIdRef.current = requestAnimationFrame(() => {
      const currentOffset = dragOffsetRef.current;
      const ghostCardTop = dragPositionRef.current.y - (currentOffset.y || 0);
      const ghostCardProbeY = ghostCardTop + 1;
      const hoverX = dragPositionRef.current.x;
      const gridTarget = getProjectedGridTargetFromPointer(hoverX, ghostCardProbeY);
      const currentHeaders = isRoomFilterActive ? filteredRoomHeaders : headers;

      if (!gridTarget) {
        return;
      }

      const { rowIndex: computedRowIndex, colIndex, rowRect } = gridTarget;

      if (!currentHeaders || colIndex >= currentHeaders.length || colIndex < 0) {
        dropTargetRef.current = null;
        setDropTime(null);
        return;
      }

      dropTargetRef.current = { rowIndex: computedRowIndex, colIndex };

      if (timeSlots && timeSlots[computedRowIndex] && rowRect) {
        const rowAbsoluteMinutes = getSlotAbsoluteMinutes(timeSlots[computedRowIndex]);
        const rowHeight = rowRect.height || getCellHeight(tableInterval) || 1;
        const relativePositionInRow = (ghostCardProbeY - rowRect.top) / rowHeight;
        const minutesInRow = relativePositionInRow * tableInterval;
        const totalMinutes = rowAbsoluteMinutes + minutesInRow;
        const roundedMinutes = Math.round(totalMinutes / 5) * 5;
        const clampedMinutes = clampTimelineStartMinutes(roundedMinutes);

        setDropTime({
          value: formatAbsoluteMinutesToTime24(clampedMinutes),
          absoluteMinutes: clampedMinutes,
        });
      }
    });
  };

  const getTouchCardKey = (slot) => {
    if (slot?.booking_id != null && slot.booking_id !== "") {
      return `${slot.booking_id}:${slot?.item_id ?? ""}`;
    }
    if (slot?.counterKey != null && slot.counterKey !== "") {
      return `draft:${slot.counterKey}`;
    }
    return null;
  };

  // Touch only:
  // - the first press selects the card and does not drag
  // - a later press on that selected card drags once the finger slides
  // - a second tap within the double-tap window opens the sidebar
  const handleTouchCardPointerDown = (event, slot) => {
    const DOUBLE_TAP_MS = 450;
    const DOUBLE_TAP_SLOP_PX = 48;
    const SLIDE_START_PX = 8;

    clearActiveTouchGesture();
    armTouchClickSuppress();

    const key = getTouchCardKey(slot);
    const now = Date.now();
    const last = lastTouchTapRef.current;
    const isDoubleTap =
      !!key &&
      last.completed === true &&
      last.key === key &&
      now - last.time <= DOUBLE_TAP_MS &&
      Math.abs(event.clientX - last.x) <= DOUBLE_TAP_SLOP_PX &&
      Math.abs(event.clientY - last.y) <= DOUBLE_TAP_SLOP_PX;

    const wasSelected = !!key && touchSelectedKeyRef.current === key;
    if (key) {
      touchSelectedKeyRef.current = key;
    }
    if (slot?.booking_id != null && slot.booking_id !== "") {
      try {
        setActiveCardKey(`${slot.booking_id}:${slot?.item_id ?? ""}`);
      } catch {}
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const gesture = {
      pointerId: event.pointerId,
      key,
      slot,
      startX: event.clientX,
      startY: event.clientY,
      downTime: now,
      offset: {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      },
      captureEl: event.currentTarget,
      // Drag only when this card was already selected before this press.
      canDrag: wasSelected && !isViewOnlyOutlet,
      // A quick second tap opens the sidebar. Sliding a selected card drags instead.
      doubleTapCandidate: isDoubleTap,
      slid: false,
      dragStarted: false,
      detach: null,
    };

    try {
      if (event.pointerId != null && event.currentTarget?.setPointerCapture) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
    } catch {}

    const distanceFromStart = (e) => {
      const dx = (e.clientX || 0) - gesture.startX;
      const dy = (e.clientY || 0) - gesture.startY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const detach = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onUp);
    };
    gesture.detach = detach;

    const finishTap = (e, reason) => {
      if (e && gesture.pointerId != null && e.pointerId !== gesture.pointerId) return;
      if (touchGestureRef.current !== gesture) return;
      if (touchHoldTimerRef.current) {
        clearTimeout(touchHoldTimerRef.current);
        touchHoldTimerRef.current = null;
      }
      touchGestureRef.current = null;
      detach();
      if (gesture.dragStarted) return;
      // Discard or sidebar close cleared this card while the finger was down.
      if (gesture.key && touchSelectedKeyRef.current !== gesture.key) return;

      const completedTap = reason === "up" && !gesture.slid;
      if (completedTap && gesture.doubleTapCandidate) {
        lastTouchTapRef.current = blankTouchTap();
        const bookingId = gesture.slot?.booking_id ?? gesture.slot?.id;
        if (bookingId != null && bookingId !== "") {
          try {
            handleBookedSlotClick(null, bookingId, gesture.slot?.item_id ?? null);
          } catch {}
        } else {
          setIsSidebarVisible(true);
        }
        return;
      }

      if (completedTap && gesture.key) {
        lastTouchTapRef.current = {
          key: gesture.key,
          time: Date.now(),
          x: gesture.startX,
          y: gesture.startY,
          rowIndex: gesture.slot?.rowIndex ?? null,
          colIndex: gesture.slot?.colIndex ?? null,
          completed: true,
        };
      } else {
        lastTouchTapRef.current = blankTouchTap();
      }
    };

    const onMove = (e) => {
      if (gesture.pointerId != null && e.pointerId !== gesture.pointerId) return;
      if (touchGestureRef.current !== gesture || gesture.dragStarted) return;
      if (e.cancelable) e.preventDefault();

      const distance = distanceFromStart(e);
      if (distance < SLIDE_START_PX) return;

      gesture.slid = true;
      if (!gesture.canDrag) return;

      gesture.dragStarted = true;
      touchGestureRef.current = null;
      detach();
      lastTouchTapRef.current = blankTouchTap();

      const fakeEvent = {
        pointerType: "mouse",
        pointerId: e.pointerId,
        clientX: e.clientX,
        clientY: e.clientY,
        currentTarget: gesture.captureEl,
        target: gesture.captureEl,
        cancelable: true,
        preventDefault() {},
        stopPropagation() {},
      };
      handleCardMouseDown(fakeEvent, gesture.slot);
      if (!draggedCardRef.current) return;

      dragStartPosRef.current = { x: gesture.startX, y: gesture.startY };
      dragOffsetRef.current = gesture.offset;
      setDragOffset(gesture.offset);
      handleCardMouseMove(e);
    };

    const onUp = (e) => finishTap(e, e?.type === "pointercancel" ? "cancel" : "up");

    touchGestureRef.current = gesture;
    document.addEventListener("pointermove", onMove, { passive: false });
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onUp);
  };

  const handleCardMouseUp = (event) => {
    if (event?.cancelable) {
      event.preventDefault();
    }
    if (!draggedCardRef.current) return;
    const draggedCard = draggedCardRef.current;
    const wasDragging = isDraggingRef.current;

    // Stop auto-scroll
    if (dragAutoScrollIntervalRef.current) {
      cancelAnimationFrame(dragAutoScrollIntervalRef.current);
      dragAutoScrollIntervalRef.current = null;
    }
    dragScrollSpeedRef.current = { x: 0, y: 0 };
    dragScrollTargetSpeedRef.current = { x: 0, y: 0 };
    
    // Cancel any pending animation frame
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    
    // Restore cursor and user selection
    document.body.style.removeProperty('cursor');
    document.documentElement.style.removeProperty('cursor');
    document.body.style.userSelect = '';
    
    // Stop auto-scroll
    if (dragAutoScrollIntervalRef.current) {
      cancelAnimationFrame(dragAutoScrollIntervalRef.current);
      dragAutoScrollIntervalRef.current = null;
    }
    dragScrollSpeedRef.current = { x: 0, y: 0 };
    dragScrollTargetSpeedRef.current = { x: 0, y: 0 };
    
    // Remove global cursor style
    if (dragCursorStyleRef.current) {
      dragCursorStyleRef.current.remove();
      dragCursorStyleRef.current = null;
    }
    
    // Remove global event listeners
    document.removeEventListener('pointermove', handleCardMouseMove, { passive: false });
    document.removeEventListener('pointerup', handleCardMouseUp, { passive: false });
    document.removeEventListener('pointercancel', handleCardMouseCancel, { passive: false });
    window.removeEventListener('blur', handleCardMouseCancel);
    
    // Use ref position for immediate response
    const finalPosition = dragPositionRef.current;

    // Only process drop if we were actually dragging.
    // Resolve grid target synchronously — dropTargetRef is filled in rAF during move, which is
    // cancelled on pointerup, so rapid drags often left it null and the card snapped back.
    if (wasDragging) {
      const currentOffset = dragOffsetRef.current;
      const ghostCardTop = finalPosition.y - (currentOffset.y || 0);
      const ghostCardProbeY = ghostCardTop + 1;
      // Use pointer X for column (same as move preview). Ghost-center X is biased right when
      // overlapping cards are half-width, so drops near mid/right of a column snap to the next one.
      let gridTarget = getProjectedGridTargetFromPointer(finalPosition.x, ghostCardProbeY);

      if (!gridTarget && dropTargetRef.current) {
        gridTarget = { ...dropTargetRef.current, rowRect: null };
      }

      if (gridTarget) {
      const { rowIndex, colIndex } = gridTarget;
      dropTargetRef.current = { rowIndex, colIndex };
      
      // Use filtered headers when room filter is active to match table rendering
      const currentHeaders = isRoomFilterActive ? filteredRoomHeaders : headers;
      
      if (!currentHeaders || currentHeaders.length === 0 || colIndex >= currentHeaders.length || colIndex < 0) {
        // Invalid drop target - clear drag state
        isDraggingRef.current = false;
        dropTargetRef.current = null;
        draggedCardRef.current = null;
        setIsDraggingCard(false);
        setDraggedCard(null);
        setDragKey(null);
        setGhostCard(null);
        setDragOffset({ x: 0, y: 0 });
        setDragPosition({ x: 0, y: 0 });
        setDropTime(null);
        return;
      }
      
      const newTherapistId = currentHeaders[colIndex]?.id;
      const newTherapistName = currentHeaders[colIndex]?.name || currentHeaders[colIndex]?.alias;
      
      // Calculate time with 5-minute precision based on absolute position in the table
      let droppedAbsoluteStartMinutes = clampTimelineStartMinutes(getSlotAbsoluteMinutes(timeSlots[rowIndex]));
      let newTime = formatAbsoluteMinutesToTime24(droppedAbsoluteStartMinutes);

      const rowRect = gridTarget.rowRect;
      if (rowRect && timeSlots?.length) {
        const projectedRowIndex = rowIndex;

        if (projectedRowIndex >= 0 && projectedRowIndex < timeSlots.length) {
          const rowHeight = rowRect.height || getCellHeight(tableInterval) || 1;
          const relativePositionInRow = (ghostCardProbeY - rowRect.top) / rowHeight;
          const rowAbsoluteMinutes = getSlotAbsoluteMinutes(timeSlots[projectedRowIndex]);
          const minutesInRow = relativePositionInRow * tableInterval;
          const totalMinutes = rowAbsoluteMinutes + minutesInRow;
          const roundedMinutes = Math.round(totalMinutes / 5) * 5;
          const clampedMinutes = clampTimelineStartMinutes(roundedMinutes);
          droppedAbsoluteStartMinutes = clampedMinutes;
          newTime = formatAbsoluteMinutesToTime24(clampedMinutes);
        }
      }
      
      if (newTherapistId && newTime) {
        // Update isGreyBox based on drop target (exact drop minute, not grid cell start)
        const isGreyedOut = checkIfCellIsGreyedOut(rowIndex, colIndex, droppedAbsoluteStartMinutes);
        setIsGreyBox(isGreyedOut);
        
        // New booking draft (no persisted booking / item): update slot + selectedServices via updateCardPosition.
        // Use !item_id (not only isPlaceholder) so main slot still syncs after a service is attached.
        if (!draggedCard.booking_id && !draggedCard.item_id) {
          updateCardPosition(draggedCard, rowIndex, colIndex, newTherapistId, newTherapistName, newTime, droppedAbsoluteStartMinutes);
        } else {
          // Prefer latest visual override (ref) so rapid re-drags don't use stale card props
          const liveOverride =
            draggedCard.item_id != null
              ? (dragOverridesRef.current?.[draggedCard.item_id] ||
                  dragOverridesRef.current?.[String(draggedCard.item_id)] ||
                  null)
              : null;
          // No-op when dropping at the same therapist/room and time (use normalized time to avoid format mismatches)
          const originalTherapistIdSameCheck = Number(
            liveOverride?.therapistId ?? draggedCard.therapistId ?? draggedCard.therapist
          );
          // In room mode, prefer room/item identifiers over booking item id
          const originalRoomItemIdSameCheck = (() => {
            const candidates = [ liveOverride?.roomId,  draggedCard.roomId, draggedCard.room?.value, draggedCard.room_item_id,  draggedCard.room_id, draggedCard.item_id,];
            const resolved = candidates.find((val) => val != null && val !== "");
            return resolved != null && !Number.isNaN(Number(resolved)) ? Number(resolved) : null;
          })();
          const sameColumn =typeof draggedCard.colIndex === "number" ? Number(colIndex) === Number(draggedCard.colIndex) : null;
          const originalStartTime = (() => {
            if (liveOverride?.start) {
              return String(liveOverride.start).slice(0, 5);
            }
            const fromServiceAt = (liveOverride?.service_at || draggedCard?.service_at)?.split?.(" ")?.[1];
            if (fromServiceAt && typeof fromServiceAt === "string") {
              return fromServiceAt.slice(0, 5);
            }
            const raw = draggedCard?.start_time || draggedCard?.startTime || "";
            if (!raw) return "";
            const trimmed = String(raw).trim();
            if (!trimmed) return "";
            if (/(AM|PM)/i.test(trimmed)) {
              try {
                return convertTo24HourFormat(trimmed);
              } catch {
              }
            }
            const m = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
            if (m) {
              return `${String(m[1]).padStart(2, "0")}:${m[2]}`;
            }
            if (typeof draggedCard.rowIndex === "number") {
              const slotTime = timeSlots?.[draggedCard.rowIndex]?.time24;
              if (slotTime) return slotTime;
            }
            return trimmed;
          })();
          
          // Check if it's the same location based on current filter mode
          // In room mode, newTherapistId is actually the item_id from headers[colIndex]
          const sameLocation = !!draggedCard.booking_id && (sameColumn !== null ? sameColumn : (isRoomFilterActive ? (!!originalRoomItemIdSameCheck && Number(newTherapistId) === Number(originalRoomItemIdSameCheck)) : (!!originalTherapistIdSameCheck && Number(newTherapistId) === originalTherapistIdSameCheck)));
          const originalMinutes = normalizeTime(originalStartTime);
          const newMinutes = normalizeTime(newTime);
          const sameTime = newMinutes === originalMinutes;
          const isUnderFiveMinutes = Math.abs(newMinutes - originalMinutes) < 5;
          
          if (sameLocation && (sameTime || isUnderFiveMinutes)) {
            isDraggingRef.current = false;
            dropTargetRef.current = null;
            draggedCardRef.current = null; // Clear the ref
            setIsDraggingCard(false);
            setDraggedCard(null);
            setDragKey(null);
            setGhostCard(null);
            setDragOffset({ x: 0, y: 0 });
            setDragPosition({ x: 0, y: 0 });
            setDropTime(null);
            // Treat as a normal click to keep selection consistent.
            // A touch release never opens the sidebar; double-tap does that.
            try {
              if (touchDragArmedRef.current || event?.pointerType === "touch") {
                touchDragArmedRef.current = false;
                if (event?.pointerType !== "touch") {
                  cardClickHandledViaPointerRef.current = true;
                }
              } else {
                const clickedBookingId = draggedCard?.booking_id || draggedCard?.id;
                const clickedItemId = draggedCard?.item_id || null;
                if (clickedBookingId != null) {
                  cardClickHandledViaPointerRef.current = true;
                  handleBookedSlotClick(null, clickedBookingId, clickedItemId);
                }
              }
            } catch {}
            return;
          }
          const bookingMoveBlockedMessage = getBookingMoveBlockedMessage(draggedCard);
          if (bookingMoveBlockedMessage) {
            toast.warning(bookingMoveBlockedMessage);
            isDraggingRef.current = false;
            dropTargetRef.current = null;
            draggedCardRef.current = null;
            setIsDraggingCard(false);
            setDraggedCard(null);
            setDragKey(null);
            setGhostCard(null);
            setDragOffset({ x: 0, y: 0 });
            setDragPosition({ x: 0, y: 0 });
            setDropTime(null);
            return;
          }
          // Block therapist/room change when requested, but allow time change in same column
          const isRequestedTherapist = draggedCard?.requested_person === 1 || draggedCard?.requested_therapist === true || draggedCard?.tags?.isRequested === true;
          const isRequestedRoom = draggedCard?.requested_room === 1 || draggedCard?.requested_room === true || draggedCard?.tags?.hasRequestedRoom === true;
          const originalTherapistId = Number(draggedCard.therapistId || draggedCard.therapist);
          const originalRoomItemId = (() => {
            const candidates = [draggedCard.roomId,draggedCard.room?.value, draggedCard.room_item_id,draggedCard.room_id,draggedCard.item_id,];
            const resolved = candidates.find((val) => val != null && val !== "");
            return resolved != null && !Number.isNaN(Number(resolved)) ? Number(resolved) : null;
          })();
          
          if (!isRoomFilterActive && isRequestedTherapist && originalTherapistId && Number(newTherapistId) !== originalTherapistId) {
            toast.warning("This booking has a requested therapist; therapist can't be changed.");
            // Disallow column change; do not apply move
            isDraggingRef.current = false;
            dropTargetRef.current = null;
            draggedCardRef.current = null; // Clear the ref
            setIsDraggingCard(false);
            setDraggedCard(null);
            setDragKey(null);
            setGhostCard(null);
            setDragOffset({ x: 0, y: 0 });
            setDragPosition({ x: 0, y: 0 });
            setDropTime(null);
            return;
          }

          if (isRoomFilterActive && isRequestedRoom && originalRoomItemId && Number(newTherapistId) !== originalRoomItemId) {
            toast.warning("This booking has a requested room; room can't be changed.");
            // Disallow column change; do not apply move
            isDraggingRef.current = false;
            dropTargetRef.current = null;
            draggedCardRef.current = null; // Clear the ref
            setIsDraggingCard(false);
            setDraggedCard(null);
            setDragKey(null);
            setGhostCard(null);
            setDragOffset({ x: 0, y: 0 });
            setDragPosition({ x: 0, y: 0 });
            setDropTime(null);
            return;
          }
          // Existing booking item dropped → open Edit sidebar with prefilled therapist/time
          // Apply shared chain algorithm so linked same-pax services move together.
          const duration = draggedCard.duration || 15;
          const rowAbsoluteMinutes = getSlotAbsoluteMinutes(timeSlots[rowIndex]);
          const droppedAbsoluteMinutes = typeof droppedAbsoluteStartMinutes === "number"
            ? droppedAbsoluteStartMinutes
            : resolveDraftAbsoluteStartMinutes(newTime, rowAbsoluteMinutes);
          const bookingIdForDrop = draggedCard.booking_id || draggedCard.id;
          const movedItemId = draggedCard.item_id || null;
          const newHeader = currentHeaders[colIndex];
          const accumulatedBookingOverrides = Object.entries(dragOverridesRef.current || {}).reduce((acc, [itemId, override]) => {
            if (override && String(override.bookingId) === String(bookingIdForDrop)) {
              acc[itemId] = { ...override };
            }
            return acc;
          }, {});

          const resolveLiveChainGroup = () => {
            if (
              Array.isArray(selectedServices) &&
              selectedServices.some((svc) => String(svc?.booking_item_id) === String(movedItemId))
            ) {
              return { group: selectedServices, isPax: false, paxIndex: -1 };
            }
            for (let i = 0; i < (paxes || []).length; i += 1) {
              const paxServices = paxes[i]?.selectedServices;
              if (
                Array.isArray(paxServices) &&
                paxServices.some((svc) => String(svc?.booking_item_id) === String(movedItemId))
              ) {
                return { group: paxServices, isPax: true, paxIndex: i };
              }
            }
            return null;
          };

          const liveGroup = resolveLiveChainGroup();
          // Same key for prep + scheduleServiceMove (booking_item_id over counterKey).
          const resolveDragChainKey = (svc) =>
            svc?.booking_item_id ?? svc?.item_id ?? svc?.counterKey ?? svc?.id ?? null;
          let chainInput = null;
          if (liveGroup) {
            const preserveUnlinkKeys = new Set(
              (liveGroup.group || [])
                .filter((svc) => svc?.linkedToPrevious === false)
                .map((svc) => resolveDragChainKey(svc))
                .filter((key) => key != null && key !== "")
                .map(String)
            );
            // Sort + force-relink by true abs so overnight parents stay root, and
            // rewrite stale counterKey parents ("1"/"2") to booking_item_id keys.
            chainInput = sortAndRelinkChainByAbsoluteMinutes(
              ensureServiceLinkedFlags(liveGroup.group, resolveDragChainKey),
              resolveDragChainKey,
              { forceRelink: true, preserveUnlinkKeys }
            );
          } else {
            const sourceBooking = (bookingsList || []).find(
              (booking) => String(booking?.id || booking?.booking_id) === String(bookingIdForDrop)
            );
            const itemGroup = findBookingItemGroupForItemId(sourceBooking, movedItemId);
            chainInput = buildChainServicesFromBookingItems(
              itemGroup || [],
              accumulatedBookingOverrides,
              (item, override) => {
                if (typeof override?.absoluteStartMinutes === "number") {
                  return override.absoluteStartMinutes;
                }
                return getBookingAbsoluteStartMinutes({
                  service_at: item?.service_at,
                  start_time: item?.start_time,
                  absoluteStartMinutes: undefined,
                  crossDayBooking: item?.cross_day_booking_item,
                  cross_day_booking_item: item?.cross_day_booking_item,
                  is_calendar_expansion: item?.is_calendar_expansion,
                });
              }
            );
          }

          if (!Array.isArray(chainInput) || chainInput.length === 0) {
            const abs = getBookingAbsoluteStartMinutes(draggedCard);
            chainInput = ensureServiceLinkedFlags(
              [
                {
                  booking_item_id: movedItemId,
                  item_id: movedItemId,
                  counterKey: movedItemId,
                  duration,
                  start_time: (() => {
                    const fromServiceAt = draggedCard?.service_at?.split(" ")?.[1];
                    if (fromServiceAt) return String(fromServiceAt).slice(0, 5);
                    return newTime;
                  })(),
                  end_time: getDraftEndTimeForSlot(abs, duration, newTime),
                  absoluteMinutes: abs,
                  dayOffset: Math.floor(abs / 1440),
                  linkedToPrevious: false,
                },
              ],
              resolveDragChainKey
            );
          }

          const movedKey = movedItemId != null
            ? movedItemId
            : resolveDragChainKey(chainInput.find((svc) => String(svc?.booking_item_id) === String(movedItemId)) || draggedCard);

          // Therapist drag (same clock time): keep chain links. Force the
          // schedule abs to the moved service's current abs so grid rounding cannot
          // create a non-zero delta and trigger Rule 3 unlink.
          const movedChainSvc =
            (chainInput || []).find(
              (svc) =>
                String(svc?.booking_item_id ?? svc?.item_id) === String(movedItemId) || String(resolveDragChainKey(svc)) === String(movedKey)
            ) || null;
          const movedChainAbs = movedChainSvc != null ? getServiceAbsoluteStartMinutes(movedChainSvc) : null;
          const scheduleTargetAbs = sameTime && typeof movedChainAbs === "number" && !Number.isNaN(movedChainAbs) ? movedChainAbs : droppedAbsoluteMinutes;

          const { services: scheduledChain, affectedIndices } = scheduleServiceMove({
            services: chainInput,
            movedKey: movedKey || movedItemId,
            newAbsoluteStartMinutes: scheduleTargetAbs,
            interactionMode: "drag",
            resolveKey: resolveDragChainKey,
          });

          const movedScheduled =
            scheduledChain.find((svc) => String(svc?.booking_item_id) === String(movedItemId)) ||
            scheduledChain[affectedIndices[0]] ||
            null;
          const movedStart = movedScheduled?.start_time || newTime;
          const movedEnd = movedScheduled?.end_time || getDraftEndTimeForSlot(droppedAbsoluteMinutes, duration, newTime);
          const movedAbs =
            typeof movedScheduled?.absoluteMinutes === "number"
              ? movedScheduled.absoluteMinutes
              : droppedAbsoluteMinutes;
          const movedServiceAt = `${getDateStringForAbsoluteMinutes(movedAbs)} ${movedStart}`;
          const originalAbsoluteStartMinutes = typeof liveOverride?.absoluteStartMinutes === "number" ? liveOverride.absoluteStartMinutes : getBookingAbsoluteStartMinutes(draggedCard);
          const deltaMinutes = movedAbs - originalAbsoluteStartMinutes;

          const chainBookingOverrides = { ...accumulatedBookingOverrides };
          const chainInputById = new Map(
            (chainInput || []).map((svc) => [
              String(svc?.booking_item_id ?? svc?.item_id),
              svc,
            ])
          );
          const touchedOverrideIds = new Set();
          affectedIndices.forEach((idx) => {
            const svc = scheduledChain[idx];
            const itemId = svc?.booking_item_id ?? svc?.item_id;
            if (itemId == null) return;
            const abs =
              typeof svc.absoluteMinutes === "number"
                ? svc.absoluteMinutes
                : getServiceAbsoluteStartMinutes(svc);
            const start = svc.start_time;
            const end = svc.end_time || absoluteMinutesToClock(abs + (Number(svc.duration) || 0)).start_time;
            const isMovedItem = String(itemId) === String(movedItemId);
            const prevSvc = chainInputById.get(String(itemId));
            const prevAbs =
              prevSvc != null ? getServiceAbsoluteStartMinutes(prevSvc) : null;
            const absChanged =
              typeof prevAbs === "number" && !Number.isNaN(prevAbs)
                ? Math.round(abs) !== Math.round(prevAbs)
                : true;
            // Detach marks left-behind siblings as affected for link flags only —
            // do not restamp their service_at/abs (negative overnight abs hides day-2 cards).
            const shouldWriteSchedule = isMovedItem || absChanged;
            touchedOverrideIds.add(String(itemId));
            chainBookingOverrides[itemId] = {
              ...(chainBookingOverrides[itemId] || {}),
              bookingId: bookingIdForDrop,
              linkedToPrevious: svc.linkedToPrevious,
              scheduleLinkParentKey: svc.scheduleLinkParentKey ?? null,
              ...(shouldWriteSchedule
                ? {
                    start,
                    end,
                    absoluteStartMinutes: abs,
                    service_at: `${getDateStringForAbsoluteMinutes(abs)} ${start}`,
                    duration: Number(svc.duration) || duration,
                  }
                : {}),
              ...(isMovedItem
                ? {
                    therapistId: isRoomFilterActive ? null : Number(newTherapistId),
                    therapistName: isRoomFilterActive ? null : newTherapistName,
                    roomId: isRoomFilterActive ? Number(newTherapistId) : null,
                    roomName: isRoomFilterActive ? newTherapistName : null,
                  }
                : {}),
            };
          });

          // Clear stale day-2 projection overrides on untouched overnight parents
          // (e.g. 23:15 true start overridden as 00:00-00:45 when a sibling was dragged).
          (scheduledChain || []).forEach((svc) => {
            const itemId = svc?.booking_item_id ?? svc?.item_id;
            if (itemId == null || touchedOverrideIds.has(String(itemId))) return;
            const trueAbs = getServiceAbsoluteStartMinutes(svc);
            const trueStart = String(svc?.start_time || "").slice(0, 5);
            const ov =
              chainBookingOverrides[itemId] ||
              chainBookingOverrides[String(itemId)] ||
              null;
            if (!ov?.start) return;
            const ovStart = String(ov.start).slice(0, 5);
            const hasCrossDay =
              Array.isArray(svc?.cross_day_booking_item) &&
              svc.cross_day_booking_item.length > 1;
            const stillOvernightParent =
              trueAbs < 0 ||
              hasCrossDay ||
              (trueStart && convertToMinutes(trueStart) >= 18 * 60);
            if (!stillOvernightParent || !ovStart || ovStart === trueStart) return;
            delete chainBookingOverrides[itemId];
            delete chainBookingOverrides[String(itemId)];
            touchedOverrideIds.add(String(itemId));
          });

          const droppedRoomId = isRoomFilterActive ? Number(newTherapistId) : null;
          const droppedRoomName = isRoomFilterActive
            ? (newTherapistName || newHeader?.name || newHeader?.room_name || null)
            : null;
          const droppedRoomSelection = isRoomFilterActive && (droppedRoomId != null || newHeader)
            ? {
                value: newHeader?.id ?? droppedRoomId,
                label: droppedRoomName || newHeader?.name || newHeader?.room_name || "Room Selected",
                room_id: newHeader?.room_id ?? droppedRoomId,
                room_name: newHeader?.room_name || droppedRoomName,
                room_code: newHeader?.room_code,
                item_type: newHeader?.item_type ?? null,
                item_id: newHeader?.id ?? droppedRoomId,
                item_name: droppedRoomName || newHeader?.name || newHeader?.room_name || "Room Selected",
                room_type: newHeader?.room_type ?? null,
                facilities: Array.isArray(newHeader?.facilities) ? newHeader.facilities : [],
              }
            : null;

          const scheduledChainWithColumn = scheduledChain.map((svc) => {
            const isMovedItem = String(svc?.booking_item_id) === String(movedItemId);
            if (!isMovedItem) return svc;
            if (isRoomFilterActive) {
              return {
                ...svc,
                roomId: droppedRoomId,
                room: droppedRoomId,
                roomName: droppedRoomName,
                room_name: droppedRoomName,
                room_segments: Array.isArray(svc.room_segments) && svc.room_segments.length > 0
                  ? svc.room_segments.map((segment) => ({
                      ...segment,
                      room_id: newHeader?.room_id ?? droppedRoomId,
                      item_type: newHeader?.item_type ?? segment.item_type,
                      room_type: newHeader?.room_type ?? null,
                      facilities: Array.isArray(newHeader?.facilities) ? newHeader.facilities : [],
                    }))
                  : svc.room_segments,
              };
            }
            return {
              ...svc,
              therapist: Number(newTherapistId),
              therapist_name: newTherapistName,
              selected_therapist: {
                ...(svc.selected_therapist || {}),
                value: Number(newTherapistId),
                label: newTherapistName,
              },
            };
          });

          // Keep live sidebar group in sync when already editing this booking.
          // Merge schedule fields into existing rows so overnight cross_day metadata is not dropped.
          if (liveGroup) {
            const mergeScheduledIntoGroup = (groupServices) => {
              const scheduledById = new Map(
                (scheduledChainWithColumn || []).map((svc) => [
                  String(svc?.booking_item_id ?? svc?.item_id ?? svc?.counterKey),
                  svc,
                ])
              );
              return (groupServices || []).map((svc) => {
                const key = String(svc?.booking_item_id ?? svc?.item_id ?? svc?.counterKey);
                const updated = scheduledById.get(key);
                if (!updated) return svc;
                const prevAbs = getServiceAbsoluteStartMinutes(svc);
                const nextAbs = getServiceAbsoluteStartMinutes(updated);
                const timeChanged = Math.round(prevAbs) !== Math.round(nextAbs);
                return {
                  ...svc,
                  ...updated,
                  // Preserve genuine overnight split segments unless this item's time moved.
                  cross_day_booking_item: timeChanged
                    ? (updated.cross_day_booking_item ?? null)
                    : (svc.cross_day_booking_item ?? updated.cross_day_booking_item ?? null),
                  service_at: timeChanged
                    ? (updated.service_at ?? svc.service_at)
                    : (svc.service_at ?? updated.service_at),
                };
              });
            };
            if (liveGroup.isPax) {
              setPaxes((prev) =>
                (prev || []).map((pax, index) =>
                  index === liveGroup.paxIndex
                    ? { ...pax, selectedServices: mergeScheduledIntoGroup(pax.selectedServices) }
                    : pax
                )
              );
            } else {
              setSelectedServices((prev) => mergeScheduledIntoGroup(prev));
            }
          }

          if (isRoomFilterActive && droppedRoomSelection) {
            const roomStateKeys = new Set();
            const movedLiveService = (liveGroup?.group || scheduledChainWithColumn).find(
              (svc) => String(svc?.booking_item_id) === String(movedItemId)
            );
            if (movedLiveService?.counterKey != null) {
              roomStateKeys.add(movedLiveService.counterKey);
            }
            if (movedItemId != null) {
              roomStateKeys.add(movedItemId);
            }
            if (roomStateKeys.size > 0) {
              setSelectedRooms((prevRooms) => {
                const next = { ...prevRooms };
                roomStateKeys.forEach((key) => {
                  next[key] = droppedRoomSelection;
                });
                return next;
              });
            }
          }

          const payload = {
            bookingId: bookingIdForDrop,
            itemId: movedItemId,
            serviceId: draggedCard.service_id || draggedCard.serviceId || null,
            therapistId: isRoomFilterActive ? null : Number(newTherapistId),
            therapistName: isRoomFilterActive ? null : newTherapistName,
            roomId: isRoomFilterActive ? Number(newTherapistId) : null,
            roomName: isRoomFilterActive ? newTherapistName : null,
            roomData: isRoomFilterActive ? newHeader : null,
            startTime: movedStart,
            endTime: movedEnd,
            duration: Number(movedScheduled?.duration) || duration,
            originalAbsoluteStartMinutes,
            absoluteStartMinutes: movedAbs,
            deltaMinutes,
            service_at: movedServiceAt,
            linkedToPrevious: movedScheduled?.linkedToPrevious,
            scheduleLinkParentKey: movedScheduled?.scheduleLinkParentKey ?? null,
            bookingOverrides: chainBookingOverrides,
          };

          // Set selected booking and open the sidebar directly in Edit mode
          // Persist visual overrides only for items touched by this drop (and stale overnight clears).
          try {
            applyDragOverrides((prev) => {
              const next = { ...prev };
              touchedOverrideIds.forEach((itemId) => {
                const override =
                  chainBookingOverrides[itemId] ||
                  chainBookingOverrides[Number(itemId)] ||
                  null;
                if (!override) {
                  delete next[itemId];
                  delete next[Number(itemId)];
                  return;
                }
                const projectedOverride = buildProjectedOverridePayload({
                  bookingId: bookingIdForDrop,
                  itemId,
                  start: override.start,
                  end: override.end,
                  therapistId: override.therapistId,
                  therapistName: override.therapistName,
                  roomId: override.roomId,
                  roomName: override.roomName,
                  duration: override.duration,
                  absoluteStartMinutes: override.absoluteStartMinutes,
                  service_at: override.service_at,
                  linkedToPrevious: override.linkedToPrevious,
                  scheduleLinkParentKey: override.scheduleLinkParentKey,
                });
                next[itemId] = {
                  ...(next[itemId] || {}),
                  ...projectedOverride,
                };
                // Remember moved item schedule so stale edit-hydrate emits cannot revert it.
                if (String(itemId) === String(movedItemId)) {
                  const written = next[itemId];
                  rememberCalendarDragSchedule(itemId, {
                    fromCalendarDrag: true,
                    start: written?.start ?? null,
                    end: written?.end ?? null,
                    abs:
                      typeof written?.absoluteStartMinutes === "number"
                        ? written.absoluteStartMinutes
                        : null,
                    service_at: written?.service_at ?? null,
                    therapistId: written?.therapistId ?? null,
                    therapistName: written?.therapistName ?? null,
                    roomId: written?.roomId ?? null,
                    roomName: written?.roomName ?? null,
                    originalAbs:
                      typeof originalAbsoluteStartMinutes === "number"
                        ? originalAbsoluteStartMinutes
                        : originalMinutes,
                    originalStart: originalStartTime || null,
                    originalTherapistId: originalTherapistIdSameCheck ?? null,
                  });
                }
              });
              return next;
            });
          } catch {}

          setSelectedBookingId(payload.bookingId);
          setIsSidebarVisible(true);
          setIsBookingSidebar(false);
          setShowBookingSummary(false);
          setIsNewBookingSlotActive(false);
          setIsEditBookingActive(true);
          setNewAppointmentSlots([]);
          setNewSlot(null);
          setEditPrefill(payload);
        }
      }
      } // end gridTarget
    }

    // Tap-to-open for mouse/trackpad. Touch never opens the sidebar on release.
    if (!wasDragging && draggedCard) {
      if (touchDragArmedRef.current || event?.pointerType === "touch") {
        touchDragArmedRef.current = false;
        if (event?.pointerType !== "touch") {
          cardClickHandledViaPointerRef.current = true;
        }
      } else {
        const clickedBookingId = draggedCard.booking_id || draggedCard.id;
        const clickedItemId = draggedCard.item_id || null;
        if (clickedBookingId != null) {
          cardClickHandledViaPointerRef.current = true;
          try {
            handleBookedSlotClick(null, clickedBookingId, clickedItemId);
          } catch {}
        }
      }
    } else if (touchDragArmedRef.current) {
      touchDragArmedRef.current = false;
    }
    
    // Reset drag state immediately for faster response (removed requestAnimationFrame delay)
    isDraggingRef.current = false;
    dropTargetRef.current = null;
    draggedCardRef.current = null; // Clear the ref
    setIsDraggingCard(false);
    setDraggedCard(null);
    setDragKey(null);
    setGhostCard(null);
    setDragOffset({ x: 0, y: 0 });
    setDragPosition({ x: 0, y: 0 });
    setDropTime(null); // Clear drop time
  };



  const handleCardMouseCancel = () => {
    clearActiveTouchGesture();
    if (!draggedCardRef.current && !isDraggingRef.current && !isDraggingCard) return;

    if (dragAutoScrollIntervalRef.current) {
      cancelAnimationFrame(dragAutoScrollIntervalRef.current);
      dragAutoScrollIntervalRef.current = null;
    }
    dragScrollSpeedRef.current = { x: 0, y: 0 };
    dragScrollTargetSpeedRef.current = { x: 0, y: 0 };

    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    document.body.style.removeProperty('cursor');
    document.documentElement.style.removeProperty('cursor');
    document.body.style.userSelect = '';

    if (dragCursorStyleRef.current) {
      dragCursorStyleRef.current.remove();
      dragCursorStyleRef.current = null;
    }

    document.removeEventListener('pointermove', handleCardMouseMove, { passive: false });
    document.removeEventListener('pointerup', handleCardMouseUp, { passive: false });
    document.removeEventListener('pointercancel', handleCardMouseCancel, { passive: false });
    window.removeEventListener('blur', handleCardMouseCancel);

    isDraggingRef.current = false;
    dropTargetRef.current = null;
    draggedCardRef.current = null;
    touchDragArmedRef.current = false;
    setIsDraggingCard(false);
    setDraggedCard(null);
    setDragKey(null);
    setGhostCard(null);
    setDragOffset({ x: 0, y: 0 });
    setDragPosition({ x: 0, y: 0 });
    setDropTime(null);
  };

  // Clear drag state when filters change to prevent cards from vanishing or becoming invalid
  useEffect(() => {
    if (isDraggingRef.current || isDraggingCard) {
      // Clear drag state when filter mode changes or headers change
      isDraggingRef.current = false;
      dropTargetRef.current = null;
      draggedCardRef.current = null;
      setIsDraggingCard(false);
      setDraggedCard(null);
      setDragKey(null);
      setGhostCard(null);
      setDragOffset({ x: 0, y: 0 });
      setDragPosition({ x: 0, y: 0 });
      setDropTime(null);
      
      // Cancel any pending animation frames
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    }
  }, [isRoomFilterActive, filteredRoomIds.length, filteredItemIds.length]);

  // Helper function to handle midnight wrap-around in time comparisons
  const adjustEndTimeForMidnightWrap = (startMinutes, endMinutes) => {
    // If end time is less than start time, it means the appointment wraps past midnight
    // Add 1440 minutes (24 hours) to the end time to represent the next day
    return endMinutes < startMinutes ? endMinutes + 1440 : endMinutes;
  };

  const parseTimelineDatePart = useCallback((datePart) => {
    if (!datePart || typeof datePart !== "string") return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const [year, month, day] = datePart.split("-").map(Number);
      const parsed = new Date(year, month - 1, day);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    if (/^\d{2}-\d{2}-\d{4}$/.test(datePart)) {
      const [day, month, year] = datePart.split("-").map(Number);
      const parsed = new Date(year, month - 1, day);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
  }, []);

  const getServiceAtSortValue = useCallback((serviceAt) => {
    if (!serviceAt || typeof serviceAt !== "string") return 0;

    const [datePart, timePart = "00:00:00"] = serviceAt.split(" ");
    const parsedDate = parseTimelineDatePart(datePart);
    if (!parsedDate) return 0;

    const [hours = "0", minutes = "0", seconds = "0"] = timePart.split(":");
    parsedDate.setHours(Number(hours) || 0, Number(minutes) || 0, Number(seconds) || 0, 0);
    return parsedDate.getTime();
  }, [parseTimelineDatePart]);

  const getCalendarExpansionBoolean = useCallback((booking) => {
    const rawValue =
      booking?.is_calendar_expansion ??
      booking?.isCalendarExpansion ??
      booking?.calendar_expansion;

    if (
      rawValue === 1 ||
      rawValue === "1" ||
      rawValue === true ||
      rawValue === "true"
    ) {
      return true;
    }

    if (
      rawValue === 0 ||
      rawValue === "0" ||
      rawValue === false ||
      rawValue === "false"
    ) {
      return false;
    }

    return null;
  }, []);

  const isDualContextCalendarExpansionItem = useCallback((booking) => {
    const segments = Array.isArray(booking?.crossDayBooking)
      ? booking.crossDayBooking
      : (Array.isArray(booking?.cross_day_booking_item) ? booking.cross_day_booking_item : []);
    if (segments.length < 2) {
      return false;
    }
    const firstStart = segments[0]?.start_time || null;
    const firstEnd = segments[0]?.end_time || null;
    const allSameTimes = segments.every((segment) =>
      (segment?.start_time || null) === firstStart &&
      (segment?.end_time || null) === firstEnd
    );
    if (!allSameTimes) {
      return false;
    }
    const startMinutes = convertToMinutes(firstStart);
    const endMinutes = convertToMinutes(firstEnd);
    if (
      Number.isNaN(startMinutes) ||
      Number.isNaN(endMinutes) ||
      startMinutes >= 6 * 60 ||
      endMinutes >= 6 * 60
    ) {
      return false;
    }
    const uniqueDates = new Set(
      segments
        .map((segment) => segment?.start_date || null)
        .filter(Boolean)
    );
    return uniqueDates.size >= 2;
  }, []);

  const getExpandedSplitSelectedDayPosition = useCallback((booking, selectedDateStr) => {
    if (!selectedDateStr) {
      return null;
    }
    const segments = Array.isArray(booking?.crossDayBooking)
      ? booking.crossDayBooking
      : (Array.isArray(booking?.cross_day_booking_item) ? booking.cross_day_booking_item : []);
    if (segments.length < 2) {
      return null;
    }
    const firstSegment = segments[0];
    const secondSegment = segments[1];
    const firstStart = firstSegment?.start_time || null;
    const firstEnd = firstSegment?.end_time || null;
    const secondStart = secondSegment?.start_time || null;
    const secondEnd = secondSegment?.end_time || null;
    if (!firstStart || !firstEnd || !secondStart || !secondEnd) {
      return null;
    }
    const firstStartMinutes = convertToMinutes(firstStart);
    const secondStartMinutes = convertToMinutes(secondStart);
    if (
      Number.isNaN(firstStartMinutes) ||
      Number.isNaN(secondStartMinutes) ||
      firstStartMinutes >= 6 * 60
    ) {
      return null;
    }
    const allSameTimes = segments.every((segment) =>
      (segment?.start_time || null) === firstStart &&
      (segment?.end_time || null) === firstEnd
    );
    if (allSameTimes) {
      return null;
    }
    const isExtendedBoundarySplit = secondStartMinutes === 6 * 60;
    if (!isExtendedBoundarySplit && getCalendarExpansionBoolean(booking) !== true) {
      return null;
    }
    const toYMD = (dateStr) => {
      if (!dateStr || typeof dateStr !== "string") return null;
      const [day, month, year] = dateStr.split("-");
      if (!day || !month || !year) return null;
      return `${year}-${month}-${day}`;
    };
    const firstDate = toYMD(firstSegment?.start_date);
    const secondDate = toYMD(secondSegment?.start_date);
    if (firstDate === selectedDateStr) {
      return "first";
    }
    if (secondDate === selectedDateStr) {
      return "second";
    }
    return null;
  }, [getCalendarExpansionBoolean]);

  const shouldRenderFullOnExtendedBoundarySecondDay = useCallback((booking) => {
    const segments = Array.isArray(booking?.crossDayBooking)
      ? booking.crossDayBooking
      : (Array.isArray(booking?.cross_day_booking_item) ? booking.cross_day_booking_item : []);
    if (segments.length < 2) {
      return false;
    }

    const firstStart = segments[0]?.start_time || null;
    const firstEnd = segments[0]?.end_time || null;
    const secondStart = segments[1]?.start_time || null;
    const secondEnd = segments[1]?.end_time || null;
    if (!firstStart || !firstEnd || !secondStart || !secondEnd) {
      return false;
    }

    const firstStartMinutes = convertToMinutes(firstStart);
    const secondStartMinutes = convertToMinutes(secondStart);
    if (
      Number.isNaN(firstStartMinutes) ||
      Number.isNaN(secondStartMinutes) ||
      firstStartMinutes >= 6 * 60
    ) {
      return false;
    }

    const allSameTimes = segments.every((segment) =>
      (segment?.start_time || null) === firstStart &&
      (segment?.end_time || null) === firstEnd
    );
    if (allSameTimes) {
      return false;
    }

    return secondStartMinutes === 6 * 60;
  }, []);

  // Expanded outlets keep 23:xx -> 00:xx within the same business-day card.
  // UI should split only on the 06:00 boundary, not at midnight.
  const isExpandedMidnightCarrySplit = useCallback((bookingLike) => {
    if (!isOutletExtended) return false;
    const segments = Array.isArray(bookingLike?.crossDayBooking)
      ? bookingLike.crossDayBooking
      : (Array.isArray(bookingLike?.cross_day_booking_item) ? bookingLike.cross_day_booking_item : []);
    if (segments.length < 2) return false;

    const firstSegment = segments[0];
    const secondSegment = segments[1];
    const firstStartMinutes = convertToMinutes(firstSegment?.start_time);
    const firstEndMinutes = convertToMinutes(firstSegment?.end_time);
    const secondStartMinutes = convertToMinutes(secondSegment?.start_time);
    if (
      Number.isNaN(firstStartMinutes) ||
      Number.isNaN(firstEndMinutes) ||
      Number.isNaN(secondStartMinutes)
    ) {
      return false;
    }

    if (firstEndMinutes !== 0 || secondStartMinutes !== 0) return false;
    if (firstStartMinutes < 6 * 60) return false;

    const toYMD = (dateStr) => {
      if (!dateStr || typeof dateStr !== "string") return null;
      const [day, month, year] = dateStr.split("-");
      if (!day || !month || !year) return null;
      return `${year}-${month}-${day}`;
    };
    const firstDate = toYMD(firstSegment?.start_date);
    const secondDate = toYMD(secondSegment?.start_date);
    return !!firstDate && !!secondDate && firstDate !== secondDate;
  }, [isOutletExtended]);

  const getDualContextSelectedDayPosition = useCallback((booking, selectedDateStr) => {
    if (!selectedDateStr || !isDualContextCalendarExpansionItem(booking)) {
      return null;
    }
    const segments = Array.isArray(booking?.crossDayBooking)
      ? booking.crossDayBooking
      : (Array.isArray(booking?.cross_day_booking_item) ? booking.cross_day_booking_item : []);
    if (segments.length < 2) {
      return null;
    }
    const toYMD = (dateStr) => {
      if (!dateStr || typeof dateStr !== "string") return null;
      const [day, month, year] = dateStr.split("-");
      if (!day || !month || !year) return null;
      return `${year}-${month}-${day}`;
    };
    const firstDate = toYMD(segments[0]?.start_date);
    const secondDate = toYMD(segments[1]?.start_date);
    if (firstDate === selectedDateStr) {
      return "first";
    }
    if (secondDate === selectedDateStr) {
      return "second";
    }
    return null;
  }, [isDualContextCalendarExpansionItem]);

  const getBookingTimelineDayOffset = useCallback((booking) => {
    if (!selectedDate) return 0;

    const baseDate = new Date(selectedDate);
    if (Number.isNaN(baseDate.getTime())) return 0;
    const selectedDateStr = `${baseDate.getFullYear()}-${String(baseDate.getMonth() + 1).padStart(2, "0")}-${String(baseDate.getDate()).padStart(2, "0")}`;

    const serviceAtDatePart = typeof booking?.service_at === "string"
      ? booking.service_at.split(" ")[0]
      : "";
    const bookingDate = parseTimelineDatePart(serviceAtDatePart);
    if (!bookingDate) return 0;

    const normalizedBase = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
    const normalizedBooking = new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate());
    const diffDays = Math.round((normalizedBooking.getTime() - normalizedBase.getTime()) / (24 * 60 * 60 * 1000));
    // Keep signed offset (previous calendar day vs selectedDate → negative).
    const normalizedDiffDays = diffDays;

    const expansionFlag = getCalendarExpansionBoolean(booking);
    const boundarySplitSelectedDayPosition = booking?.extended_boundary_split_selected_day_position || null;
    const expandedSplitSelectedDayPosition = getExpandedSplitSelectedDayPosition(booking, selectedDateStr);

    if (boundarySplitSelectedDayPosition === "first") {
      return 1;
    }

    if (boundarySplitSelectedDayPosition === "second") {
      return 0;
    }

    if (expandedSplitSelectedDayPosition === "first") {
      return 1;
    }

    if (expandedSplitSelectedDayPosition === "second") {
      return 0;
    }

    const serviceAtTimePart = typeof booking?.service_at === "string"
      ? (booking.service_at.split(" ")[1] || "").slice(0, 5)
      : "";
    const startMinutes = serviceAtTimePart ? convertToMinutes(serviceAtTimePart) : Number.NaN;

    // is_calendar_expansion=1 → +1 extended band on the selected day; otherwise normal timeline.
    if (
      expansionFlag === true &&
      isOutletExtended &&
      normalizedDiffDays === 0 &&
      !Number.isNaN(startMinutes) &&
      startMinutes < 6 * 60
    ) {
      return 1;
    }

    return normalizedDiffDays;
  }, [getCalendarExpansionBoolean, getExpandedSplitSelectedDayPosition, isOutletExtended, parseTimelineDatePart, selectedDate]);

  const getBookingAbsoluteStartMinutes = useCallback((booking) => {
    if (typeof booking?.absoluteStartMinutes === "number" && !Number.isNaN(booking.absoluteStartMinutes)) {
      return booking.absoluteStartMinutes;
    }

    const dayOffset = getBookingTimelineDayOffset(booking);
    const serviceAtTimePart = typeof booking?.service_at === "string"
      ? booking.service_at.split(" ")[1] || ""
      : "";

    if (/^\d{2}:\d{2}/.test(serviceAtTimePart)) {
      return (dayOffset * 1440) + convertToMinutes(serviceAtTimePart.slice(0, 5));
    }

    if (booking?.startTime instanceof Date && !Number.isNaN(booking.startTime.getTime())) {
      return (dayOffset * 1440) + (booking.startTime.getHours() * 60) + booking.startTime.getMinutes();
    }

    if (typeof booking?.startTime === "string" && booking.startTime) {
      return (dayOffset * 1440) + normalizeTime(booking.startTime);
    }

    return dayOffset * 1440;
  }, [getBookingTimelineDayOffset]);

  const getBookingAbsoluteEndMinutes = useCallback((booking) => {
    const absoluteStart = getBookingAbsoluteStartMinutes(booking);
    const duration = Number(booking?.duration);

    if (duration > 0) {
      return absoluteStart + duration;
    }

    const startMinutes = ((absoluteStart % 1440) + 1440) % 1440;
    const endMinutes = booking?.endTime ? normalizeTime(booking.endTime) : startMinutes;
    return absoluteStart - startMinutes + adjustEndTimeForMidnightWrap(startMinutes, endMinutes);
  }, [getBookingAbsoluteStartMinutes]);

  const formatTimelineTimeLabel = useCallback((timeValue, absoluteMinutes = null) => {
    const resolvedTimeValue = timeValue || (
      typeof absoluteMinutes === "number" && !Number.isNaN(absoluteMinutes)
        ? formatAbsoluteMinutesToTime24(absoluteMinutes)
        : null
    );

    if (!resolvedTimeValue) return "";

    const baseLabel = convertTo12Hour(resolvedTimeValue);
    if (
      isOutletExtended &&
      typeof absoluteMinutes === "number" &&
      !Number.isNaN(absoluteMinutes) &&
      absoluteMinutes >= 1440
    ) {
      return /\+\d+/.test(baseLabel) ? baseLabel : `${baseLabel} +1`;
    }

    return baseLabel;
  }, [formatAbsoluteMinutesToTime24, isOutletExtended]);

  const getDraftCardTimeLabel = useCallback((slot) => {
    if (!slot) return "";
    const absoluteStartMinutes = getDraftSlotAbsoluteStartMinutes(slot);
    return formatTimelineTimeLabel(slot?.start_time, absoluteStartMinutes);
  }, [formatTimelineTimeLabel, getDraftSlotAbsoluteStartMinutes]);

  const getBookingCardTimeRangeLabel = useCallback((booking) => {
    if (!booking) return "";

    const absoluteStartMinutes = getBookingAbsoluteStartMinutes(booking);
    const absoluteEndMinutes = getBookingAbsoluteEndMinutes(booking);
    const isExpandedTimelineBooking =
      isOutletExtended &&
      typeof absoluteStartMinutes === "number" &&
      !Number.isNaN(absoluteStartMinutes) &&
      absoluteStartMinutes >= 1440;

    if (booking?.timeRange) {
      if (!isExpandedTimelineBooking) {
        return booking.timeRange;
      }

      const rangeParts = String(booking.timeRange).split(" - ");
      if (rangeParts.length === 2) {
        return rangeParts
          .map((part) => (/\+\d+/.test(part) ? part : `${part} +1`))
          .join(" - ");
      }

      return /\+\d+/.test(String(booking.timeRange))
        ? booking.timeRange
        : `${booking.timeRange} +1`;
    }

    return `${formatTimelineTimeLabel(null, absoluteStartMinutes)} - ${formatTimelineTimeLabel(null, absoluteEndMinutes)}`;
  }, [
    formatTimelineTimeLabel,
    getBookingAbsoluteEndMinutes,
    getBookingAbsoluteStartMinutes,
    isOutletExtended,
  ]);

  const updateCardPosition = (slot, newRowIndex, newColIndex, newTherapistId, newTherapistName, newTime, explicitAbsoluteStartMinutes = null) => {
    const duration = slot.duration || 15;
    const newSlotAbsoluteMinutes = typeof explicitAbsoluteStartMinutes === "number" && !Number.isNaN(explicitAbsoluteStartMinutes)
      ? clampTimelineStartMinutes(explicitAbsoluteStartMinutes)
      : getSlotAbsoluteMinutes(timeSlots[newRowIndex]);

    // Update isGreyBox based on new position (exact start minute, not grid cell start)
    const isGreyedOut = checkIfCellIsGreyedOut(newRowIndex, newColIndex, newSlotAbsoluteMinutes);
    setIsGreyBox(isGreyedOut);

    const durNum = Number(duration) || 0;
    const effectiveStartTime24 =
      isOutletExtended && !slot?.booking_id
        ? formatAbsoluteMinutesToTime24(newSlotAbsoluteMinutes)
        : newTime;
    // Use absolute timeline for end clock and calendar end date (matches time-dropdown path;
    // calculateEndTime + duration-1 breaks extended +1 / midnight spans and stale cross_day_booking_item
    // from ...originalService can poison create payload via hasExpandedCoverageSegments).
    const newEndTime = getDraftEndTimeForSlot(newSlotAbsoluteMinutes, durNum, effectiveStartTime24);
    const resolvedServiceStartDate = getDateStringForAbsoluteMinutes(newSlotAbsoluteMinutes);
    const resolvedServiceEndDate = getDateStringForAbsoluteMinutes(newSlotAbsoluteMinutes + durNum);
    const serviceDayOffset = Math.max(0, Math.floor(newSlotAbsoluteMinutes / 1440));
    
    // Get the correct headers based on filter mode
    const currentHeaders = isRoomFilterActive ? filteredRoomHeaders : headers;
    // Get the header data for the new column (use currentHeaders, not headers)
    const newHeader = currentHeaders[newColIndex];
    
    // If no customer/services selected yet and this is the placeholder (first card),
    // move the placeholder and sync only the RightSidebar (global) time
    if (slot?.isPlaceholder && (!selectedServices || selectedServices.length === 0)) {
      // Set flag to prevent useEffect from overriding our changes
      const dragUpdateGeneration = beginDragUpdate();
      
      // Get start_date from the service if available, otherwise use selectedDate
      const placeholderStartDate = resolvedServiceStartDate;
      
      // Update placeholder position in calendar
      setNewAppointmentSlots(prevSlots => prevSlots.map(s =>
        s.counterKey === slot.counterKey
          ? {
              ...s,
              rowIndex: newRowIndex,
              colIndex: newColIndex,
              absoluteStartMinutes: newSlotAbsoluteMinutes,
              start_time: effectiveStartTime24,
              end_time: newEndTime,
              service_at: `${placeholderStartDate} ${effectiveStartTime24}`,
              // Use conditional assignment based on filter mode
              therapistId: isRoomFilterActive ? null : Number(newTherapistId),
              therapistName: isRoomFilterActive ? null : newTherapistName,
              roomId: isRoomFilterActive ? newHeader?.id : null,
              roomData: isRoomFilterActive ? newHeader : null,
            }
          : s
      ));

      // Update RightSidebar time selection (header/DateTime) and therapist/room
      const formattedTime = buildSelectedTimePayload(effectiveStartTime24, newSlotAbsoluteMinutes);
      try {
        setSelectedTime(formattedTime);
        
        // Update bookingData with time and therapist/room info (matching openSidebarWithData structure)
        setBookingData((prevData) => {
          if (isRoomFilterActive) {
            // Room mode: set room data instead of therapist data
            const roomData = newHeader ? { 
              label: newHeader?.name, 
              value: newHeader?.id,
              room_id: newHeader?.room_id,
              room_name: newHeader?.room_name,
              room_code: newHeader?.room_code,
              item_type: newHeader?.item_type
            } : null;
            
            // ✅ Update selectedRooms when dragging placeholder in room mode
            if (newHeader && slot.counterKey) {
              setSelectedRooms(prevRooms => ({
                ...prevRooms,
                [slot.counterKey]: {
                  value: newHeader.id,
                  label: newHeader.name || newHeader.room_name,
                  room_id: newHeader.room_id,
                  room_name: newHeader.room_name,
                  room_code: newHeader.room_code,
                  item_type: newHeader.item_type,
                  item_id: newHeader.id,
                  item_name: newHeader.name || newHeader.room_name,
                  room_type: newHeader.room_type ?? null,
                  facilities: Array.isArray(newHeader.facilities) ? newHeader.facilities : [],
                }
              }));
            }
            
            return {
              ...prevData,
              date: selectedDate,
              time: formattedTime,
              room: roomData,
              therapist: null, // Clear therapist data in room mode
            };
          } else {
            // Therapist mode: set therapist data
            return {
              ...prevData,
              date: selectedDate,
              time: formattedTime,
              therapist: { 
                label: newHeader?.alias || newHeader?.name, 
                value: newHeader?.id, 
                pager: newHeader?.pager_number 
              },
              room: null, // Clear room data in therapist mode
            };
          }
        });
        
        // Update selectedTherapistFromRow and selectedTherapistIDFromRow (matching openSidebarWithData)
        if (isRoomFilterActive) {
          // Room mode: clear therapist-related state
          setSelectedTherapistIDFromRow(null);
          setSelectedTherapistFromRow(null);
        } else {
          // Therapist mode: set therapist-related state
          setSelectedTherapistIDFromRow(newHeader?.id);
          setSelectedTherapistFromRow(newHeader);
        }
      } catch {}

      // Clear the flag after a short delay to allow useEffect to run normally for other changes
      endDragUpdateAfter(dragUpdateGeneration, 100);
      return;
    }
    
    
    // Find the original service data
    const matchingService = selectedServices?.find(service => service.counterKey === slot.counterKey);
    let originalService = null;
    let isPaxService = false;
    let paxIndex = -1;
    
    if (matchingService) {
      originalService = matchingService;
    } else {
      // Check in paxes
      for (let i = 0; i < paxes.length; i++) {
        const pax = paxes[i];
        const paxService = pax.selectedServices?.find(service => service.counterKey === slot.counterKey);
        if (paxService) {
          originalService = paxService;
          isPaxService = true;
          paxIndex = i;
          break;
        }
      }
    }
    
    if (!originalService) {
      return;
    }
    
    // Get start_date from the service if available, otherwise use selectedDate
    const serviceStartDate = resolvedServiceStartDate || originalService?.start_date || formatDateToDDMMYYYY(selectedDate);
    
    // Create selectedTherapist object if therapist exists (for therapist mode)
    let selectedTherapist = null;
    if (!isRoomFilterActive && newTherapistId) {
      const therapistFromList = Array.isArray(homeStaffList) && homeStaffList.length > 0
        ? homeStaffList.find((staff) => Number(staff.id) === Number(newTherapistId))
        : null;
      const therapistName = newTherapistName || therapistFromList?.name || therapistFromList?.alias || "";
      selectedTherapist = {
        value: Number(newTherapistId),
        label: therapistName,
        pager: therapistFromList?.pager_number || "",
        gender: therapistFromList?.gender || "",
      };
    }
    
    // Create new service with updated position and therapist/room
    // Note: currentHeaders and newHeader are already declared at the top of the function
    const therapistOrRoomPatch = isRoomFilterActive ? {
        roomId: newHeader?.id,
        roomName: newHeader?.name || newHeader?.room_name,
        room: newHeader ? {
          value: newHeader.id,
          label: newHeader.name || newHeader.room_name,
          room_id: newHeader.room_id,
          room_name: newHeader.room_name,
          room_code: newHeader.room_code,
          item_type: newHeader.item_type
        } : null,
      } : {
        therapist: Number(newTherapistId),
        therapist_name: newTherapistName,
        selected_therapist: selectedTherapist,
        isAutoAssignedTherapist: false,
        hasManualTherapistSelection: true,
        roomId: null,
        roomName: null,
        room: null,
      };

    // Do NOT pre-write the drop time onto the moved service before scheduleServiceMove —
    // that zeroes the delta and leaves linked siblings unmoved. Pass original times in;
    // the algorithm applies newAbsoluteStartMinutes and shifts descendants.
    const applyDraftChainSchedule = (groupServices) => {
      const { services: scheduled, affectedIndices } = scheduleServiceMove({
        services: ensureServiceLinkedFlags(groupServices),
        movedKey: slot.counterKey,
        newAbsoluteStartMinutes: newSlotAbsoluteMinutes,
        interactionMode: "drag",
      });
      const affectedSet = new Set(affectedIndices);
      return scheduled.map((svc, index) => {
        if (!affectedSet.has(index) && String(svc.counterKey) !== String(slot.counterKey)) {
          return svc;
        }
        const abs =
          typeof svc.absoluteMinutes === "number" && !Number.isNaN(svc.absoluteMinutes)
            ? svc.absoluteMinutes
            : getServiceAbsoluteStartMinutes(svc);
        const start = svc.start_time || formatAbsoluteMinutesToTime24(abs);
        const end = svc.end_time || getDraftEndTimeForSlot(abs, Number(svc.duration) || 0, start);
        const startDate = getDateStringForAbsoluteMinutes(abs);
        const endDate = getDateStringForAbsoluteMinutes(abs + (Number(svc.duration) || 0));
        const isMoved = String(svc.counterKey) === String(slot.counterKey);
        const keptTherapistId = svc?.therapist ?? svc?.selected_therapist?.value ?? svc?.selected_therapist?.id ?? null;
        return {
          ...svc,
          start_time: start,
          end_time: end,
          start_date: startDate,
          end_date: endDate,
          dayOffset: Math.floor(abs / 1440),
          absoluteMinutes: abs,
          service_at: `${startDate} ${start}`,
          cross_day_booking_item: null,
          ...(isMoved ? therapistOrRoomPatch : {}),
          // Cascaded siblings keep their therapist — mark as intentional so create
          // date/time sync does not wipe them when options omit that therapist.
          ...(!isMoved && !isRoomFilterActive && keptTherapistId != null ? {
                hasManualTherapistSelection: true,
                isAutoAssignedTherapist: false,
              }
            : {}),
          room_segments: svc?.room_segments && Array.isArray(svc.room_segments) && svc.room_segments.length > 0
            ? svc.room_segments.map((segment) => ({
                ...segment,
                start_time: start,
                end_time: end,
                service_at: `${startDate} ${start}`,
                start_date: startDate,
                ...(isMoved && isRoomFilterActive && newHeader
                  ? {
                      room_id: newHeader.room_id,
                      item_type: newHeader.item_type,
                      room_type: newHeader.room_type ?? null,
                      facilities: Array.isArray(newHeader.facilities) ? newHeader.facilities : [],
                    }
                  : {}),
              }))
            : svc?.room_segments,
        };
      });
    };

    const dragUpdateGeneration = beginDragUpdate();

    const scheduledGroupForSlots = isPaxService
      ? applyDraftChainSchedule(paxes[paxIndex]?.selectedServices || [])
      : applyDraftChainSchedule(selectedServices || []);

    try {
      if (!isRoomFilterActive) {
        (scheduledGroupForSlots || []).forEach((svc) => {
          const therapistId =
            svc?.therapist ?? svc?.selected_therapist?.value ?? svc?.selected_therapist?.id ?? null;
          if (svc?.counterKey == null || therapistId == null) return;
          window.dispatchEvent(
            new CustomEvent("stampManualTherapistSelection", {
              detail: {
                counterKey: svc.counterKey,
                therapistId: Number(therapistId),
              },
            })
          );
        });
      }
    } catch {}

    // Remove the original service and add the new one
    if (isPaxService) {
      // Update pax service chain (reuse scheduled result — do not re-apply or delta becomes 0)
      setPaxes(prevPaxes => {
        const updatedPaxes = prevPaxes.map((pax, index) => {
          if (index === paxIndex) {
            return {
              ...pax,
              selectedServices: scheduledGroupForSlots,
            };
          }
          return pax;
        });
        return updatedPaxes;
      });
      
      // ✅ Update selectedRooms when dragging in room mode
      if (isRoomFilterActive && newHeader && slot.counterKey) {
        setSelectedRooms(prevRooms => ({
          ...prevRooms,
          [slot.counterKey]: {
            value: newHeader.id,
            label: newHeader.name || newHeader.room_name,
            room_id: newHeader.room_id,
            room_name: newHeader.room_name,
            room_code: newHeader.room_code,
            item_type: newHeader.item_type,
            item_id: newHeader.id,
            item_name: newHeader.name || newHeader.room_name,
            room_type: newHeader.room_type ?? null,
            facilities: Array.isArray(newHeader.facilities) ? newHeader.facilities : [],
          }
        }));
      }
    } else {
      // Update main service chain (reuse scheduled result — do not re-apply or delta becomes 0)
      setSelectedServices(() => {
        const updatedServices = scheduledGroupForSlots;
        // If the first chain service is being moved, sync the global selected time
        try {
          const movedIsFirst =
            updatedServices.length > 0 &&
            String(updatedServices[0]?.counterKey) === String(slot.counterKey);
          if (movedIsFirst) {
            const formattedTime = buildSelectedTimePayload(effectiveStartTime24, newSlotAbsoluteMinutes);
            setSelectedTime(formattedTime);
            
            // Update bookingData with time and therapist/room info (matching openSidebarWithData structure)
            setBookingData((prevData) => {
              if (isRoomFilterActive) {
                // Room mode: set room data
                return {
                  ...prevData,
                  date: selectedDate,
                  time: formattedTime,
                  room: newHeader ? { 
                    label: newHeader.name, 
                    value: newHeader.id,
                    room_id: newHeader.room_id,
                    room_name: newHeader.room_name,
                    room_code: newHeader.room_code,
                    item_type: newHeader.item_type
                  } : null,
                  therapist: null, // Clear therapist data in room mode
                };
              } else {
                // Therapist mode: set therapist data
                return {
                  ...prevData,
                  date: selectedDate,
                  time: formattedTime,
                  therapist: { 
                    label: newHeader?.alias || newHeader?.name, 
                    value: newHeader?.id, 
                    pager: newHeader?.pager_number 
                  },
                  room: null, // Clear room data in therapist mode
                };
              }
            });
            
            // Update selectedTherapistFromRow and selectedTherapistIDFromRow
            if (isRoomFilterActive) {
              setSelectedTherapistIDFromRow(null);
              setSelectedTherapistFromRow(null);
            } else {
              setSelectedTherapistIDFromRow(newHeader?.id);
              setSelectedTherapistFromRow(newHeader);
            }
          }
        } catch {}
        return updatedServices;
      });
      
      // ✅ Update selectedRooms when dragging in room mode
      if (isRoomFilterActive && newHeader && slot.counterKey) {
        setSelectedRooms(prevRooms => ({
          ...prevRooms,
          [slot.counterKey]: {
            value: newHeader.id,
            label: newHeader.name || newHeader.room_name,
            room_id: newHeader.room_id,
            room_name: newHeader.room_name,
            room_code: newHeader.room_code,
            item_type: newHeader.item_type,
            item_id: newHeader.id,
            item_name: newHeader.name || newHeader.room_name,
            room_type: newHeader.room_type ?? null,
            facilities: Array.isArray(newHeader.facilities) ? newHeader.facilities : [],
          }
        }));
      }
    }
    
    // Update calendar slots for the whole affected chain (not only the dragged card)
    const scheduledByKey = new Map(
      (scheduledGroupForSlots || []).map((svc) => [String(svc.counterKey), svc])
    );
    setNewAppointmentSlots((prevSlots) => {
      const updatedSlots = (prevSlots || []).map((s) => {
        const svc = scheduledByKey.get(String(s.counterKey));
        if (!svc) return s;

        const isMoved = String(s.counterKey) === String(slot.counterKey);
        const abs =
          typeof svc.absoluteMinutes === "number" && !Number.isNaN(svc.absoluteMinutes)
            ? svc.absoluteMinutes
            : getServiceAbsoluteStartMinutes(svc);
        const start = svc.start_time || formatAbsoluteMinutesToTime24(abs);
        const end = svc.end_time || getDraftEndTimeForSlot(abs, Number(svc.duration) || 0, start);
        const startDate = getDateStringForAbsoluteMinutes(abs);
        const rowIdx = isMoved
          ? newRowIndex
          : getRowIndexFromAbsoluteMinutes(abs, start);
        const resolvedRow = rowIdx >= 0 ? rowIdx : s.rowIndex;

        return {
          ...s,
          rowIndex: resolvedRow,
          colIndex: isMoved ? newColIndex : s.colIndex,
          absoluteStartMinutes: abs,
          start_time: start,
          end_time: end,
          service_at: `${startDate} ${start}`,
          duration: Number(svc.duration) || s.duration,
          ...(isMoved
            ? {
                therapistId: isRoomFilterActive ? null : Number(newTherapistId),
                therapistName: isRoomFilterActive ? null : newTherapistName,
                roomId: isRoomFilterActive ? newHeader?.id : null,
                roomData: isRoomFilterActive ? newHeader : null,
              }
            : {}),
        };
      });
      return updatedSlots;
    });

    endDragUpdateAfter(dragUpdateGeneration, 150);
  };


  // Helper function to generate room classes based on room_name and item_category
  const getRoomClasses = (room) => {
    const classes = ['random-color'];
    const lowerName = String(room?.capacity_type ?? "").toLowerCase();
    
    // Add classes based on room_name content
    if (lowerName.includes('single')) {
      classes.push('single');
    } else if (lowerName.includes('double')) {
      classes.push('double');
    } else if (lowerName.includes('hall')) {
      classes.push('hall');
    }

    const hasShower = Array.isArray(room.items) && room.items.some((item) =>
      Array.isArray(item.facilities) &&
      item.facilities.some((facility) => String(facility).toLowerCase() === 'shower')
    );
    if (hasShower) {
      classes.push('shower');
    }

    return classes.join(' ');
  };  
  

  // Cleanup drag event listeners on unmount
  useEffect(() => {
    return () => {
      // Cancel any pending animation frame
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      // Restore cursor and user selection
      document.body.style.removeProperty('cursor');
      document.documentElement.style.removeProperty('cursor');
      document.body.style.userSelect = '';
      
      // Stop auto-scroll
      if (dragAutoScrollIntervalRef.current) {
        cancelAnimationFrame(dragAutoScrollIntervalRef.current);
        dragAutoScrollIntervalRef.current = null;
      }
      
      // Remove global cursor style
      if (dragCursorStyleRef.current) {
        dragCursorStyleRef.current.remove();
        dragCursorStyleRef.current = null;
      }
      
      document.removeEventListener('pointermove', handleCardMouseMove, { passive: false });
      document.removeEventListener('pointerup', handleCardMouseUp, { passive: false });
      document.removeEventListener('pointercancel', handleCardMouseCancel, { passive: false });
      window.removeEventListener('blur', handleCardMouseCancel);
      // Clear drop time on cleanup
      setDropTime(null);
    };
  }, []);

  useEffect(() => {
    if (outletList.length > 0 && selectedDate) {
      const selectedOutlet =
        outletList.find((outlet) => outlet.selected === 1) || null;

      setBookingData((prevData) => {
        if (
          prevData.outlet !== selectedOutlet?.id ||
          prevData.date !== selectedDate
        ) {
          return {
            ...prevData,
            outlet: selectedOutlet?.id || null,
            date: selectedDate,
          };
        }
        return prevData;
      });
    }
  }, [outletList, selectedDate, setBookingData]);

  useEffect(() => {
    viewOnlyToastShownRef.current = false;
  }, [selectedOutlet, selectedDate]);

  // Create headers based on current filter mode
  const headers = useMemo(() => {
    if (isRoomFilterActive) {
      // Room-based headers: create headers from homeRoomList items
      const roomHeaders = [];
      homeRoomList.forEach((room) => {
        if (room.items && room.items.length > 0) {
          room.items.forEach((item) => {
            roomHeaders.push({
              id: item.item_id,
              name: item.item_name,
              room_id: item.item_id,
              room_name: room.room_name,
              room_code: room.room_code,
              item_type: item.item,
              room_type: item.room_type ?? room.room_type ?? room.capacity_type ?? null,
              facilities: Array.isArray(item.facilities) ? item.facilities : [],
              type: 'room_item' // to distinguish from therapist headers
            });
          });
        }
      });
      
      // Apply search filter for room items
      const filteredHeaders = roomHeaders.filter((header) => {
        const matchesSearch =
          searchQuery.trim() === "" ||
          header.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          header.room_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          String(header.id).includes(searchQuery || "");
        
        return matchesSearch;
      });
      
      return filteredHeaders;
    } else {
      // Therapist-based headers (existing logic)
      // If staffIds is an empty array, all staff are unchecked - show no headers
      if (bookingFilters.staffIds && bookingFilters.staffIds.length === 0) {
        return [];
      }
      
      return homeStaffList.filter((staff) => {
        const matchesGender =
          !bookingFilters.gender || bookingFilters.gender.length === 0 ||
          bookingFilters.gender.includes(staff.gender?.toLowerCase());
        const matchesSelected =
          !bookingFilters.staffIds || bookingFilters.staffIds.length === 0 ||
          bookingFilters.staffIds.includes(staff.id);

        const matchesSearch =
          searchQuery.trim() === "" ||
          staff.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          String(staff.id).includes(searchQuery || "");

        return matchesGender && matchesSelected && matchesSearch;
      });
    }
  }, [isRoomFilterActive, homeRoomList, homeStaffList, bookingFilters, searchQuery]);

  // Separate useMemo for room filtering with filteredItemIds dependency
  const filteredRoomHeaders = useMemo(() => {
    if (!isRoomFilterActive) return headers;
    
    // If no rooms are selected, return empty array (no headers, no booking cards)
    if (filteredRoomIds.length === 0) {
      return [];
    }
    
    return headers.filter((header) => {
      // Check if this item belongs to a selected room
      return filteredItemIds.length > 0 && filteredItemIds.includes(header.id);
    });
  }, [headers, filteredItemIds, filteredRoomIds, isRoomFilterActive]);

  const currentDraftHeaders = useMemo(() => {
    return isRoomFilterActive
      ? (filteredRoomHeaders.length > 0 ? filteredRoomHeaders : headers)
      : headers;
  }, [filteredRoomHeaders, headers, isRoomFilterActive]);

  const draftSlotsForUI = useMemo(() => {
    if (!Array.isArray(newAppointmentSlots) || newAppointmentSlots.length === 0) {
      return [];
    }

    return newAppointmentSlots
      .map((slot) => {
        // A slot tied to a therapist/room whose column is filtered out must stay hidden;
        // its stored colIndex may point at an unrelated visible column.
        const hasOwnColumnKey = getProjectedColumnKey(slot, isRoomFilterActive) != null;
        const fallbackIndex = !hasOwnColumnKey && typeof slot?.colIndex === "number" ? slot.colIndex : -1;
        const resolvedColIndex = resolveProjectedColumnIndex({
          item: slot,
          headers: currentDraftHeaders,
          isRoomFilterActive,
          fallbackIndex,
        });

        if (resolvedColIndex < 0) return null;

        return {
          ...slot,
          colIndex: resolvedColIndex,
        };
      })
      .filter(Boolean);
  }, [currentDraftHeaders, isRoomFilterActive, newAppointmentSlots]);

  // Re-check greyed-out state when therapist timings API succeeds and updates staffTiming
  useEffect(() => {
    let rowIndex;
    let colIndex;
    let absoluteMinutes = null;
    if (draftSlotsForUI.length > 0) {
      const mainSlot = draftSlotsForUI[0];
      rowIndex = mainSlot?.rowIndex;
      colIndex = mainSlot?.colIndex;
      absoluteMinutes = getDraftSlotAbsoluteStartMinutes(mainSlot);
    } else if (startCell) {
      rowIndex = startCell.rowIndex;
      colIndex = startCell.colIndex;
    }
    if (typeof rowIndex === "number" && typeof colIndex === "number") {
      const isGreyedOut = checkIfCellIsGreyedOut(rowIndex, colIndex, absoluteMinutes);
      setIsGreyBox(isGreyedOut);
    }
  }, [staffTiming, checkIfCellIsGreyedOut, setIsGreyBox, draftSlotsForUI, startCell, getDraftSlotAbsoluteStartMinutes]);

  const getDraftAvailabilityTargets = useCallback(() => {
    if (!Array.isArray(currentDraftHeaders) || currentDraftHeaders.length === 0) {
      return [];
    }

    if (draftSlotsForUI.length > 0) {
      return draftSlotsForUI
        .map((slot) => {
          if (typeof slot?.rowIndex !== "number" || typeof slot?.colIndex !== "number") {
            return null;
          }

          const header = currentDraftHeaders[slot.colIndex];
          if (!header) return null;

          return {
            rowIndex: slot.rowIndex,
            colIndex: slot.colIndex,
            absoluteMinutes: getDraftSlotAbsoluteStartMinutes(slot),
            header,
          };
        })
        .filter(Boolean);
    }

    const allServices = [
      ...(Array.isArray(selectedServices) ? selectedServices : []),
      ...(Array.isArray(paxes)
        ? paxes.flatMap((pax) => (Array.isArray(pax?.selectedServices) ? pax.selectedServices : []))
        : []),
    ];

    return allServices
      .map((service) => {
        const selectionId = isRoomFilterActive
          ? (
              getServiceRoomItemId(service) ??
              normalizeNumericId(selectedRooms?.[service?.counterKey]?.value) ??
              normalizeNumericId(selectedRooms?.[service?.counterKey]?.item_id)
            )
          : getServiceTherapistId(service);

        if (selectionId == null) return null;

        const colIndex = currentDraftHeaders.findIndex((header) => Number(header?.id) === Number(selectionId));
        if (colIndex < 0) return null;

        const serviceTime = getServiceStartTime(service) || selectedTime?.value;
        const serviceAbsoluteMinutes =
          typeof service?.absoluteMinutes === "number" && !Number.isNaN(service.absoluteMinutes)
            ? service.absoluteMinutes
            : (
                typeof service?.dayOffset === "number" && !Number.isNaN(service.dayOffset) && serviceTime
                  ? (Math.max(0, service.dayOffset) * 1440) + convertToMinutes(serviceTime)
                  : (serviceTime ? convertToMinutes(serviceTime) : null)
              );
        const rowIndex = getRowIndexFromAbsoluteMinutes(serviceAbsoluteMinutes, serviceTime);
        if (typeof rowIndex !== "number" || rowIndex < 0) return null;

        return {
          rowIndex,
          colIndex,
          absoluteMinutes: serviceAbsoluteMinutes,
          header: currentDraftHeaders[colIndex],
        };
      })
      .filter(Boolean);
  }, [
    currentDraftHeaders,
    draftSlotsForUI,
    getDraftSlotAbsoluteStartMinutes,
    getRowIndexFromAbsoluteMinutes,
    getServiceRoomItemId,
    getServiceStartTime,
    getServiceTherapistId,
    isRoomFilterActive,
    normalizeNumericId,
    paxes,
    selectedRooms,
    selectedServices,
    selectedTime?.value,
  ]);

  useEffect(() => {
    if (isDraggingRef.current || dragUpdateInProgressRef.current) return;

    const targets = getDraftAvailabilityTargets();
    if (targets.length === 0) {
      setIsGreyBox(false);
      return;
    }

    const unavailableTarget = targets.find((target) =>
      checkIfCellIsGreyedOut(target.rowIndex, target.colIndex, target.absoluteMinutes)
    );
    if (!unavailableTarget) {
      setIsGreyBox(false);
      return;
    }

    setIsGreyBox(true);

    if (!isRoomFilterActive && unavailableTarget.header) {
      setSelectedTherapistIDFromRow(unavailableTarget.header?.id ?? null);
      setSelectedTherapistFromRow(unavailableTarget.header);
    }
  }, [
    checkIfCellIsGreyedOut,
    getDraftAvailabilityTargets,
    isRoomFilterActive,
    setIsGreyBox,
    setSelectedTherapistFromRow,
    setSelectedTherapistIDFromRow,
  ]);

  // Recompute grey-box state when Edit sidebar changes therapist/time without drag.
  useEffect(() => {
    const handleEditSidebarGreyBoxRecheck = (event) => {
      const detail = event?.detail || {};
      const mode = detail?.mode || (isRoomFilterActive ? 'room' : 'therapist');
      const currentHeaders = mode === 'room' ? (filteredRoomHeaders.length > 0 ? filteredRoomHeaders : headers) : headers;
      const explicitTargets = Array.isArray(detail?.serviceTargets) && detail.serviceTargets.length > 0
        ? detail.serviceTargets
        : [{
            time: detail?.time || selectedTime?.value,
            absoluteStartMinutes: detail?.absoluteStartMinutes,
            therapistId: detail?.therapistId,
            roomItemId: detail?.roomItemId,
          }];

      if (!Array.isArray(currentHeaders) || currentHeaders.length === 0 || explicitTargets.length === 0) {
        setIsGreyBox(false);
        return;
      }

      const unavailableTarget = explicitTargets
        .map((target) => {
          const timeValue = target?.time || selectedTime?.value;
          if (!timeValue) return null;
          const rowIndex = getRowIndexFromAbsoluteMinutes(
            target?.absoluteStartMinutes,
            timeValue
          );
          if (typeof rowIndex !== 'number' || rowIndex < 0) return null;

          const targetId = mode === 'room' ? target?.roomItemId : target?.therapistId;
          if (targetId == null) return null;

          const colIndex = currentHeaders.findIndex((header) => Number(header?.id) === Number(targetId));
          if (colIndex < 0) return null;

          const absoluteMinutes =  typeof target?.absoluteStartMinutes === "number" && !Number.isNaN(target.absoluteStartMinutes)  ? target.absoluteStartMinutes: (
                  typeof target?.dayOffset === "number" && !Number.isNaN(target.dayOffset) && timeValue
                    ? (Math.max(0, target.dayOffset) * 1440) + convertToMinutes(timeValue)
                    : convertToMinutes(timeValue)
                );
          return {
            rowIndex,
            colIndex,
            absoluteMinutes,
            header: currentHeaders[colIndex],
          };
        })
        .filter(Boolean)
        .find((target) => checkIfCellIsGreyedOut(target.rowIndex, target.colIndex, target.absoluteMinutes));

      if (!unavailableTarget) {
        setIsGreyBox(false);
        return;
      }

      setIsGreyBox(true);
      if (mode !== 'room' && unavailableTarget.header) {
        setSelectedTherapistIDFromRow(unavailableTarget.header?.id ?? null);
        setSelectedTherapistFromRow(unavailableTarget.header);
      }
    };
    window.addEventListener('editSidebarGreyBoxRecheck', handleEditSidebarGreyBoxRecheck);
    return () => {
      window.removeEventListener('editSidebarGreyBoxRecheck', handleEditSidebarGreyBoxRecheck);
    };
  }, [isRoomFilterActive, selectedTime?.value, getRowIndexFromAbsoluteMinutes, filteredRoomHeaders, headers, checkIfCellIsGreyedOut, setIsGreyBox, setSelectedTherapistFromRow, setSelectedTherapistIDFromRow]);



  logger.log("bookingsList", bookingsList);
  // Transform bookings to expand cross_day_booking_item into separate items.
  // Expanded overnight items stay whole on covered dates so the next day can
  // render the full card on the normal top timeline.
  const transformedBookingsList = useMemo(() => {
    const formatSelectedDateToYYYYMMDD = (date) => {
      if (!date) return null;
      const parsed = new Date(date);
      if (Number.isNaN(parsed.getTime())) return null;
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, "0");
      const day = String(parsed.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };
    const formatCrossDayDateToYYYYMMDD = (dateStr) => {
      if (!dateStr || typeof dateStr !== "string") return null;
      const [day, month, year] = dateStr.split("-");
      if (!day || !month || !year) return null;
      return `${year}-${month}-${day}`;
    };
    const selectedDateStr = formatSelectedDateToYYYYMMDD(selectedDate);

    return bookingsList.map((booking) => {
      const bookingItems = booking.booking_item || {};
      const transformedBookingItems = {};
      
      // Process each customer's booking items
      Object.entries(bookingItems).forEach(([customerName, items]) => {
        const expandedItems = [];
        
        items.forEach((item) => {
          // If item has cross_day_booking_item, expand it (replace original with expanded items)
          if (item.cross_day_booking_item && Array.isArray(item.cross_day_booking_item) && item.cross_day_booking_item.length > 0) {
            const serviceAtDateStr = typeof item?.service_at === "string"
              ? item.service_at.split(" ")[0]
              : null;
            const expandedSplitSelectedDayPosition = getExpandedSplitSelectedDayPosition(item, selectedDateStr);
            const bookingCalendarExpansion = getCalendarExpansionBoolean(item);
            const shouldRenderWholeServiceAtDay =
              (isOutletExtended || bookingCalendarExpansion === true) &&
              !!selectedDateStr &&
              !!serviceAtDateStr &&
              serviceAtDateStr === selectedDateStr &&
              Array.isArray(item?.cross_day_booking_item) &&
              item.cross_day_booking_item.length >= 2 &&
              (() => {
                const actualDate = serviceAtDateStr;
                return item.cross_day_booking_item.some((segment) => {
                  const segmentDate = formatCrossDayDateToYYYYMMDD(segment?.start_date);
                  return !!segmentDate && segmentDate !== actualDate;
                });
              })();
            const shouldRenderDualContextExpansion =
              getDualContextSelectedDayPosition(item, selectedDateStr) != null;
            const shouldRenderExpandedMidnightCarryAsWhole =
              isExpandedMidnightCarrySplit(item) &&
              !!selectedDateStr &&
              !!serviceAtDateStr &&
              serviceAtDateStr === selectedDateStr;
            const isExpandedCoveredNextDate =
              bookingCalendarExpansion === true &&
              !isDualContextCalendarExpansionItem(item) &&
              !!selectedDateStr &&
              !!serviceAtDateStr &&
              serviceAtDateStr !== selectedDateStr &&
              (
                item.cross_day_booking_item.some(
                  (crossDayItem) => formatCrossDayDateToYYYYMMDD(crossDayItem?.start_date) === selectedDateStr
                )
              );

            if (shouldRenderExpandedMidnightCarryAsWhole) {
              const firstSegment = item.cross_day_booking_item[0];
              const lastSegment = item.cross_day_booking_item[item.cross_day_booking_item.length - 1];
              const totalCrossDayDuration = item.cross_day_booking_item.reduce((sum, segment) => {
                const segmentDuration = Number(segment?.duration);
                return sum + (!Number.isNaN(segmentDuration) && segmentDuration > 0 ? segmentDuration : 0);
              }, 0);
              const resolvedFullDuration = totalCrossDayDuration || Number(item.totalDuration) || Number(item.duration) || 0;
              let firstSegmentDate = null;
              if (firstSegment?.start_date) {
                const [day, month, year] = firstSegment.start_date.split('-');
                firstSegmentDate = `${year}-${month}-${day}`;
              }
              let formattedStartTime = firstSegment?.start_time || item.start_time;
              if (formattedStartTime) {
                const [hours = "0", minutes = "0", seconds = "0"] = String(formattedStartTime).split(":");
                formattedStartTime = `${String(Number(hours) || 0).padStart(2, "0")}:${String(Number(minutes) || 0).padStart(2, "0")}:${String(Number(seconds) || 0).padStart(2, "0")}`;
              }

              expandedItems.push({
                ...item,
                duration: resolvedFullDuration,
                totalDuration: resolvedFullDuration,
                start_time: firstSegment?.start_time || item.start_time,
                end_time: lastSegment?.end_time || item.end_time,
                start_date: firstSegment?.start_date || item.start_date,
                end_date: firstSegment?.start_date || item.end_date,
                service_at: firstSegmentDate && formattedStartTime
                  ? `${firstSegmentDate} ${formattedStartTime}`
                  : item.service_at,
                // Keep this booking on one business day in UI.
                cross_day_booking_item: null,
                extended_boundary_split_selected_day_position: null,
                render_full_expanded_cross_day: false,
              });
              return;
            }

            if (expandedSplitSelectedDayPosition === "first") {
              const firstSegment = item.cross_day_booking_item[0];
              let firstSegmentDate = null;
              if (firstSegment?.start_date) {
                const [day, month, year] = firstSegment.start_date.split('-');
                firstSegmentDate = `${year}-${month}-${day}`;
              }
              let formattedStartTime = firstSegment?.start_time || item.start_time;
              if (formattedStartTime) {
                const [hours = "0", minutes = "0", seconds = "0"] = String(formattedStartTime).split(":");
                formattedStartTime = `${String(Number(hours) || 0).padStart(2, "0")}:${String(Number(minutes) || 0).padStart(2, "0")}:${String(Number(seconds) || 0).padStart(2, "0")}`;
              }
              expandedItems.push({
                ...item,
                duration: firstSegment?.duration || item.duration,
                start_time: formattedStartTime,
                end_time: firstSegment?.end_time || item.end_time,
                start_date: firstSegment?.start_date || null,
                end_date: firstSegment?.end_date || null,
                service_at: firstSegmentDate && formattedStartTime
                  ? `${firstSegmentDate} ${formattedStartTime}`
                  : item.service_at,
                cross_day_booking_item: firstSegment ? [firstSegment] : null,
                extended_boundary_split_selected_day_position: shouldRenderFullOnExtendedBoundarySecondDay(item) ? "first" : null,
                render_full_expanded_cross_day: false,
              });
              return;
            }

            if (expandedSplitSelectedDayPosition === "second") {
              const firstSegment = item.cross_day_booking_item[0];
              const secondSegment = item.cross_day_booking_item[1];
              const shouldRenderFullOnSecondDay = shouldRenderFullOnExtendedBoundarySecondDay(item);
              let secondSegmentDate = null;
              if (secondSegment?.start_date) {
                const [day, month, year] = secondSegment.start_date.split('-');
                secondSegmentDate = `${year}-${month}-${day}`;
              }

              if (shouldRenderFullOnSecondDay) {
                const totalCrossDayDuration = item.cross_day_booking_item.reduce((sum, segment) => {
                  return sum + (Number(segment?.duration) || 0);
                }, 0);
                const resolvedFullDuration = totalCrossDayDuration || Number(item.totalDuration) || Number(item.duration) || 0;
                const lastSegment = item.cross_day_booking_item[item.cross_day_booking_item.length - 1];
                let formattedStartTime = firstSegment?.start_time || item.start_time;
                if (formattedStartTime) {
                  const [hours = "0", minutes = "0", seconds = "0"] = String(formattedStartTime).split(":");
                  formattedStartTime = `${String(Number(hours) || 0).padStart(2, "0")}:${String(Number(minutes) || 0).padStart(2, "0")}:${String(Number(seconds) || 0).padStart(2, "0")}`;
                }
                expandedItems.push({
                  ...item,
                  duration: resolvedFullDuration,
                  totalDuration: resolvedFullDuration,
                  start_time: firstSegment?.start_time || item.start_time,
                  end_time: lastSegment?.end_time || item.end_time,
                  start_date: secondSegment?.start_date || item.start_date,
                  end_date: secondSegment?.end_date || item.end_date,
                  service_at: secondSegmentDate && formattedStartTime
                    ? `${secondSegmentDate} ${formattedStartTime}`
                    : item.service_at,
                  extended_boundary_split_selected_day_position: "second",
                  render_full_expanded_cross_day: true,
                });
              } else {
                let formattedStartTime = secondSegment?.start_time || item.start_time;
                if (formattedStartTime) {
                  const [hours = "0", minutes = "0", seconds = "0"] = String(formattedStartTime).split(":");
                  formattedStartTime = `${String(Number(hours) || 0).padStart(2, "0")}:${String(Number(minutes) || 0).padStart(2, "0")}:${String(Number(seconds) || 0).padStart(2, "0")}`;
                }
                expandedItems.push({
                  ...item,
                  duration: secondSegment?.duration || item.duration,
                  start_time: secondSegment?.start_time || item.start_time,
                  end_time: secondSegment?.end_time || item.end_time,
                  start_date: secondSegment?.start_date || item.start_date,
                  end_date: secondSegment?.end_date || item.end_date,
                  service_at: secondSegmentDate && formattedStartTime
                    ? `${secondSegmentDate} ${formattedStartTime}`
                    : item.service_at,
                  cross_day_booking_item: secondSegment ? [secondSegment] : null,
                  extended_boundary_split_selected_day_position: null,
                  render_full_expanded_cross_day: false,
                });
              }
              return;
            }

            if (shouldRenderDualContextExpansion) {
              const dualContextSelectedDayPosition = getDualContextSelectedDayPosition(item, selectedDateStr);
              expandedItems.push({
                ...item,
                render_full_expanded_cross_day: dualContextSelectedDayPosition === "first",
              });
              return;
            }

            if (shouldRenderWholeServiceAtDay) {
              expandedItems.push({
                ...item,
                render_full_expanded_cross_day: true,
              });
              return;
            }

            if (isExpandedCoveredNextDate) {
              expandedItems.push({
                ...item,
                render_full_expanded_cross_day: true,
              });
              return;
            }

            // Expand cross_day_booking_item into separate items
            item.cross_day_booking_item.forEach((crossDayItem) => {
              // Convert DD-MM-YYYY to YYYY-MM-DD for service_at
              let serviceAtDate = null;
              if (crossDayItem.start_date) {
                const [day, month, year] = crossDayItem.start_date.split('-');
                serviceAtDate = `${year}-${month}-${day}`;
              }
              
              // Normalize cross-day segment time so absolute timeline parsing
              // works consistently for values like "5:30" and "05:30:00".
              let formattedStartTime = crossDayItem.start_time || item.start_time;
              if (formattedStartTime) {
                const [hours = "0", minutes = "0", seconds = "0"] = String(formattedStartTime).split(":");
                formattedStartTime = `${String(Number(hours) || 0).padStart(2, "0")}:${String(Number(minutes) || 0).padStart(2, "0")}:${String(Number(seconds) || 0).padStart(2, "0")}`;
              }
              
              // Create a new item based on the original item, overriding with cross_day data
              const expandedItem = {
                ...item,
                duration: crossDayItem.duration || item.duration,
                start_time: formattedStartTime,
                end_time: crossDayItem.end_time || item.end_time,
                start_date: crossDayItem.start_date || null,
                end_date: crossDayItem.end_date || null,
                // Build service_at from start_date and start_time (YYYY-MM-DD HH:MM:SS format)
                service_at: serviceAtDate && formattedStartTime
                  ? `${serviceAtDate} ${formattedStartTime}`
                  : item.service_at,
                // Keep cross_day_booking_item reference for tracking
                cross_day_booking_item: item.cross_day_booking_item,
                render_full_expanded_cross_day: false,
              };
              expandedItems.push(expandedItem);
            });
            // Don't add the original item when it has cross_day_booking_item
          } else {
            // No cross_day_booking_item, keep original item
            expandedItems.push(item);
          }
        });
        
        transformedBookingItems[customerName] = expandedItems;
      });
      
      return {
        ...booking,
        booking_item: transformedBookingItems,
      };
    });
  }, [bookingsList, getCalendarExpansionBoolean, getDualContextSelectedDayPosition, getExpandedSplitSelectedDayPosition, isDualContextCalendarExpansionItem, isExpandedMidnightCarrySplit, selectedDate, shouldRenderFullOnExtendedBoundarySecondDay]);

  const filteredBookingsByPanel = useMemo(() => {
    return transformedBookingsList.filter((booking) => {
      // Apply status filter in both room and therapist modes
      const matchStatus =
        Array.isArray(bookingFilters.status) &&
        bookingFilters.status.length > 0 &&
        bookingFilters.status.includes(booking.status);
      
      // If no status match, return false immediately
      if (!matchStatus) {
        return false;
      }
      
      // In room mode, show all bookings (room filtering happens at header level)
      if (isRoomFilterActive) {
        return true;
      }
      
      // In therapist mode, apply staff and gender filters along with status filter
      const bookingItems = booking.booking_item || {};
      const allServices = Object.values(bookingItems).flat();
      
      // Check gender filter - look up gender from staff list using therapist_id
      const matchGender = !bookingFilters.gender || bookingFilters.gender.length === 0 || (() => {
        return allServices.some(service => {
          if (!service.therapist_id) return false;
          
          // Look up the staff member's gender from homeStaffList
          const staffMember = homeStaffList.find(staff => staff.id === Number(service.therapist_id));
          if (!staffMember) return false;
          
          const staffGender = staffMember.gender?.toLowerCase();
          return staffGender && bookingFilters.gender.includes(staffGender);
        });
      })();
      
      // Check if any service in booking_item matches the selected staff
      const matchStaff = !bookingFilters.staffIds || bookingFilters.staffIds.length === 0 || (() => {
        // Check if any service has a matching therapist_id
        return allServices.some(service => 
          bookingFilters.staffIds.includes(Number(service.therapist_id))
        );
      })();
      
      // When gender filter is selected, it already filters staffIds in FilterPanel
      // So if gender is selected, matchStaff should be sufficient
      // But we still check both to be safe and handle edge cases
      return matchGender && matchStaff;
    });
  }, [transformedBookingsList, bookingFilters, isRoomFilterActive, homeStaffList]);
  

  useEffect(() => {
    const groupedBookings = [];

    // Format selectedDate to YYYY-MM-DD for comparison
    const formatSelectedDateToYYYYMMDD = (date) => {
      if (!date) return null;
      const d = new Date(date);
      if (isNaN(d.getTime())) return null;
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const selectedDateStr = formatSelectedDateToYYYYMMDD(selectedDate);

    (filteredBookingsByPanel || []).forEach((booking) => {
      const bookingItems = booking.booking_item || {};
      const bookingItemEntries = Object.entries(bookingItems);
      if (bookingItemEntries.length === 0) return;
      // Detect main customer based on `primary: 1`
      let mainCustomerEntry =
        bookingItemEntries.find(([_, services]) =>
          services.some((item) => item.primary === 1)
        ) || bookingItemEntries[0];
        
      const mainCustomerKey = mainCustomerEntry?.[0] || "";
      const guestEntries = bookingItemEntries.filter(
        ([name]) => name !== mainCustomerKey
      );
      const guestNames = guestEntries.map(([name]) => name);
      const allEntries = [mainCustomerEntry, ...guestEntries];

      allEntries.forEach(([memberName, services]) => {
        // Filter services by selectedDate - only include services that match the selected date
        const filteredServices = selectedDateStr 
          ? services.filter((item) => {
              const dualContextSelectedDayPosition = getDualContextSelectedDayPosition(item, selectedDateStr);
              if (dualContextSelectedDayPosition != null) {
                return true;
              }

              // For "Holding" items, construct service_at from start_date and start_time
              let serviceAt = item.service_at;
              if (item.status === "Holding" && item.start_time) {
                const holdingDate = item.end_date && item.start_date && item.start_date !== item.end_date
                  ? item.end_date
                  : item.start_date;
                const dateParts = holdingDate?.split('-') || [];
                if (dateParts.length === 3) {
                  const [day, month, year] = dateParts;
                  const timeWithSeconds = item.start_time.includes(':') && item.start_time.split(':').length === 2 
                    ? `${item.start_time}:00` 
                    : item.start_time;
                  serviceAt = `${year}-${month}-${day} ${timeWithSeconds}`;
                }
              }
              
              if (!serviceAt) return false;
              // Extract date part from service_at (format: "YYYY-MM-DD HH:MM:SS")
              const serviceDateStr = serviceAt.split(' ')[0];
              if (serviceDateStr === selectedDateStr) {
                return true;
              }

              if (
                item?.render_full_expanded_cross_day === true &&
                Array.isArray(item.cross_day_booking_item) &&
                item.cross_day_booking_item.length > 0
              ) {
                return item.cross_day_booking_item.some((crossDayItem) => {
                  if (!crossDayItem?.start_date || typeof crossDayItem.start_date !== "string") return false;
                  const [day, month, year] = crossDayItem.start_date.split('-');
                  if (!day || !month || !year) return false;
                  return `${year}-${month}-${day}` === selectedDateStr;
                });
              }

              return false;
            })
          : services;

        const sortedServices = [...filteredServices].sort(
          (a, b) => {
            // For "Holding" items, construct service_at from start_date and start_time for sorting
            let serviceAtA = a.service_at;
            let serviceAtB = b.service_at;
            
            if (a.status === "Holding" && a.start_time) {
              const holdingDateA = a.end_date && a.start_date && a.start_date !== a.end_date
                ? a.end_date
                : a.start_date;
              const datePartsA = holdingDateA?.split('-') || [];
              if (datePartsA.length === 3) {
                const [day, month, year] = datePartsA;
                const timeWithSecondsA = a.start_time.includes(':') && a.start_time.split(':').length === 2 
                  ? `${a.start_time}:00` 
                  : a.start_time;
                serviceAtA = `${year}-${month}-${day} ${timeWithSecondsA}`;
              }
            }
            
            if (b.status === "Holding" && b.start_time) {
              const holdingDateB = b.end_date && b.start_date && b.start_date !== b.end_date
                ? b.end_date
                : b.start_date;
              const datePartsB = holdingDateB?.split('-') || [];
              if (datePartsB.length === 3) {
                const [day, month, year] = datePartsB;
                const timeWithSecondsB = b.start_time.includes(':') && b.start_time.split(':').length === 2 
                  ? `${b.start_time}:00` 
                  : b.start_time;
                serviceAtB = `${year}-${month}-${day} ${timeWithSecondsB}`;
              }
            }
            
            return new Date(serviceAtA.replace(" ", "T")) - new Date(serviceAtB.replace(" ", "T"));
          }
        );

        sortedServices.forEach((item) => {
          const therapistType = item.soft_therapist;
          const therapistId = Number(item.therapist_id);
          const duration = Number(item.duration) || 15;
          const totalDuration = Number(item.totalDuration) || 15;

          // If status is "Holding", construct service_at from start_date and start_time
          let serviceAt = item.service_at;
          const dualContextSelectedDayPosition = getDualContextSelectedDayPosition(item, selectedDateStr);
          if (dualContextSelectedDayPosition != null && item.start_time && selectedDateStr) {
            const timeWithSeconds = item.start_time.includes(':') && item.start_time.split(':').length === 2
              ? `${item.start_time}:00`
              : item.start_time;
            serviceAt = `${selectedDateStr} ${timeWithSeconds}`;
          } else if (item.status === "Holding" && item.start_time) {
            const holdingDate = item.end_date && item.start_date && item.start_date !== item.end_date
              ? item.end_date
              : item.start_date;
            // Convert start_date from "DD-MM-YYYY" to "YYYY-MM-DD"
            const dateParts = holdingDate?.split('-') || [];
            if (dateParts.length === 3) {
              const [day, month, year] = dateParts;
              // Ensure start_time has seconds (format: "HH:MM" -> "HH:MM:SS")
              const timeWithSeconds = item.start_time.includes(':') && item.start_time.split(':').length === 2 
                ? `${item.start_time}:00` 
                : item.start_time;
              serviceAt = `${year}-${month}-${day} ${timeWithSeconds}`;
            }
          }
          
          const isDualContextExpansionItem = isDualContextCalendarExpansionItem(item);
          let timeRange;
          if (item.cross_day_booking_item && item.cross_day_booking_item.length > 0 && !isDualContextExpansionItem) {
            const firstItem = item.cross_day_booking_item[0];
            const lastItem = item.cross_day_booking_item[item.cross_day_booking_item.length - 1];
            timeRange = `${convertTo12Hour(firstItem.start_time)} - ${convertTo12Hour(lastItem.end_time)}`;
          } else {
            timeRange = `${convertTo12Hour(item.start_time)} - ${convertTo12Hour(item.end_time)}`;
          }

          const startDate = new Date(serviceAt.replace(" ", "T"));
          const endDate = new Date(startDate.getTime() + duration * 60000);
          // If endDate is exactly midnight, set it to 23:59:59 of that day
          if (
            endDate.getHours() === 0 &&
            endDate.getMinutes() === 0 &&
            endDate.getSeconds() === 0
          ) {
            endDate.setHours(23, 59, 59, 999);
          }
          const isPax = memberName !== mainCustomerKey;
          groupedBookings.push({
            booking_id: booking.id,
            item_id: item.id, // Add item.id here
            room_id: item?.room_items?.[0]?.item_id ?? item?.room_items?.[0]?.room_id ?? item?.room_id, // item id in room mode
            is_calendar_expansion:
              item?.is_calendar_expansion ??
              booking?.is_calendar_expansion ??
              0,
            extended_boundary_split_selected_day_position:
              item?.extended_boundary_split_selected_day_position ?? null,
            render_full_expanded_cross_day: item?.render_full_expanded_cross_day === true,
            service_id: item.service_id,
            service_name: item.service_name,
            service_category: item?.service_category_name,
            therapistId,
            therapistType,
            therapistName: item.therapist,
            therapistPager: item?.pager_number ||  item?.therapist_pager_number || "",
            therapistGender: item?.gender || item?.therapist_gender || "",
            service_at: serviceAt,
            startTime: startDate,
            endTime: endDate,
            serviceName: item.service,
            duration,
            totalDuration,
            timeRange: timeRange || "",
            createdBy: booking?.user_name || "",
            customerName: booking?.customer_name || booking?.user?.name || "",
            crossDayBooking: item?.cross_day_booking_item || null,
            memberName: isPax
              ? `${booking?.user?.[0]?.name || ""} → ${memberName || "Pax " + (guestNames.indexOf(memberName) + 1)
              }`
              : booking?.user?.[0]?.name || "",
            guestIndex: isPax ? guestNames.indexOf(memberName) : null,
            isGroupBooking: booking.booking_type_id === 2,
            user: {
              name: booking?.customer_name || booking?.user?.name || "",
              contact: booking?.mobile_number || booking?.user?.contact_number || "",
            },
            status: booking.status,
            payment_status: booking?.sale?.payment_status ?? booking?.payment_status ?? null,
            payment_status_id: booking?.payment_status_id ?? booking?.sale?.payment_status_id ?? null,
            panel: booking.panel,
            expires_at: booking.expires_at || booking.expires_At, // Preserve expires_at for timer (handle both cases)
            isVoided: item?.isVoided || false,
            isMemberRateApplied: item?.isMemberRateApplied || false,
            isMembershipApplied: item?.isMembershipApplied || false,
            // Calculate holdingCardTime from expires_at (same logic as EventListner.js)
            holdingCardTime: (() => {
              const expiresAtValue = booking.expires_at || booking.expires_At;
              if (!expiresAtValue) {
                return booking.holdingCardTime || 300; // Default 5 minutes
              }
              
              try {
                // Parse expires_at timestamp string to calculate remaining time
                let expiresAtTimestamp = null;
                let holdingCardTime = 300; // Default 5 minutes
                
                // Handle different formats: number (milliseconds) or string (ISO timestamp or "YYYY-MM-DD HH:MM:SS")
                if (typeof expiresAtValue === 'number') {
                  expiresAtTimestamp = expiresAtValue;
                } else if (typeof expiresAtValue === 'string') {
                  let dateString = expiresAtValue.trim();
                  
                  // If format is "YYYY-MM-DD HH:MM:SS" (API format), parse it manually to ensure local time
                  const dateTimeMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
                  if (dateTimeMatch) {
                    // Parse as local time: YYYY-MM-DD HH:MM:SS
                    const [, year, month, day, hour, minute, second] = dateTimeMatch;
                    const expiresAtDate = new Date(
                      parseInt(year, 10),
                      parseInt(month, 10) - 1, // Month is 0-indexed
                      parseInt(day, 10),
                      parseInt(hour, 10),
                      parseInt(minute, 10),
                      parseInt(second, 10)
                    );
                    if (!isNaN(expiresAtDate.getTime())) {
                      expiresAtTimestamp = expiresAtDate.getTime();
                    }
                  } else {
                    // Try standard Date parsing for other formats (ISO, etc.)
                    const expiresAtDate = new Date(dateString);
                    if (!isNaN(expiresAtDate.getTime())) {
                      expiresAtTimestamp = expiresAtDate.getTime();
                    }
                  }
                }
                
                if (expiresAtTimestamp) {
                  const remainingSeconds = Math.max(0, Math.floor((expiresAtTimestamp - Date.now()) / 1000));
                  holdingCardTime = remainingSeconds;
                }
                
                return holdingCardTime;
              } catch (error) {
                console.error("Error parsing expires_at in Home.js:", error);
                return booking.holdingCardTime || 300; // Fallback to existing value or default
              }
            })(),
            lastUpdated: booking.lastUpdated, // Preserve lastUpdated to track booking updates
            tags: {
              natal: ((typeof item?.service_name === 'string' && item.service_name.toLowerCase().includes("natal")) || (typeof item?.service === 'string' && item.service.toLowerCase().includes("natal"))) || false,
              slimming: ((typeof item?.service_name === 'string' && item.service_name.toLowerCase().includes("slimming")) || (typeof item?.service === 'string' && item.service.toLowerCase().includes("slimming"))) || false,
              scrubService: ((typeof item?.service_category_name === 'string' && item.service_category_name.toLowerCase().includes("scrub")) || (typeof item?.service_category === 'string' && item.service_category.toLowerCase().includes("scrub")) || (typeof item?.service === 'string' && item.service.toLowerCase().includes("scrub"))) || false,
              coupleRoom: (item?.room_items?.[0]?.room_type === "double") || false,
              showerRoom: (item?.room_items?.[0]?.facilities?.includes("shower")) || false,
              // repeat: (booking?.recurring_group_id !== null && booking?.recurring_group_id !== undefined && booking?.recurring_group_id !== '') || false,
              repeat: (booking?.is_recurring),
              // isNewUser: item.user_type === "new",
              isMember: booking?.user?.[0]?.membership === 1,
              noMembership: booking?.user?.[0]?.membership === 0,
              China: item.service_request
                ?.split(",")
                .map((req) => req.trim().toLowerCase())
                .includes("china"),
              thai: item.service_request
                ?.split(",")
                .map((req) => req.trim().toLowerCase())
                .includes("thai"),
              malaysian: item.service_request
                ?.split(",")
                .map((req) => req.trim().toLowerCase())
                .includes("malaysian"),
                singapore: item.service_request
                ?.split(",")
                .map((req) => req.trim().toLowerCase())
                .includes("singapore"),
              indonesian: item.service_request
                ?.split(",")
                .map((req) => req.trim().toLowerCase())
                .includes("indonesian"),
              male: item.service_request
                ?.split(",")
                .map((req) => req.trim().toLowerCase())
                .includes("male"),
              female: item.service_request
                ?.split(",")
                .map((req) => req.trim().toLowerCase())
                .includes("female"),
              isRequested: item.requested_person === 1,
              hasRequestedRoom: item.requested_room === 1,
              haveNotes: !!booking?.note,
              hasWebNotes: booking?.web_notes,
              bookingThrough: booking?.panel === "customer",
              roomName: item?.room_items?.[0]?.room_name || item?.room_name,
              note: booking?.note,
            },
            guest_checkin_id: item.guest_checkin_id,
            guest_checkin_name: item.guest_checkin_name,
          });
        });
      });
    });

    // Sort groupedBookings by service_at (date + time) before formatting
    groupedBookings.sort((a, b) => {
      return getServiceAtSortValue(a.service_at) - getServiceAtSortValue(b.service_at);
    });

    // Format time as readable strings and lock timeline placement for stable post-save rendering.
    const newFilteredBookings = groupedBookings.map((booking) => {
      const absoluteStartMinutes = getBookingAbsoluteStartMinutes(booking);
      return {
        ...booking,
        absoluteStartMinutes,
        startTime: booking.startTime.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }),
        endTime: booking.endTime.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }),
      };
    });
    setFilteredBookings(newFilteredBookings);
  }, [filteredBookingsByPanel, isRoomFilterActive, filteredItemIds, selectedDate, getBookingAbsoluteStartMinutes]);
  

  // Build visual bookings by applying persistent per-item overrides; also apply current prefill to that one item
  const adjustedFilteredBookings = useMemo(() => {
    try {
      
      // Get all services for tag updates (used in both drag override and edit mode)
      const allServicesForTags = [
        ...(Array.isArray(selectedServices) ? selectedServices : []),
        ...(Array.isArray(paxes) ? paxes.flatMap(p => Array.isArray(p.selectedServices) ? p.selectedServices : []) : [])
      ];

      const getCatalogRoomMeta = (roomId) => getCatalogRoomMetaFromList(homeRoomList, roomId);

      const applyRoomTagsFromService = (updatedTags, matchingService, bookingLike) => {
        if (!updatedTags || !matchingService) return updatedTags;
        const selectedRoom = matchingService?.counterKey != null
          ? selectedRooms?.[matchingService.counterKey]
          : null;
        const roomId =
          matchingService?.roomId ??
          matchingService?.room_segments?.[0]?.room_id ??
          matchingService?.room_items?.[0]?.item_id ??
          selectedRoom?.value ??
          selectedRoom?.item_id ??
          bookingLike?.room_id;
        const roomTags = getRoomTagFields(
          [matchingService, selectedRoom, getCatalogRoomMeta(roomId)],
          {
            coupleRoom: updatedTags.coupleRoom,
            showerRoom: updatedTags.showerRoom,
          }
        );
        if (roomTags.resolved) {
          updatedTags.coupleRoom = roomTags.coupleRoom;
          updatedTags.showerRoom = roomTags.showerRoom;
        }
        return updatedTags;
      };
      
      // Filter out deleted booking items in edit mode
      const deletedIdsSet = new Set((deletedBookingItemIds || []).map(id => String(id)));
      const bookingsToProcess = (filteredBookings || []).filter((b) => {
        // In edit mode, exclude items that are marked for deletion
        if (isEditBookingActive && b?.item_id && deletedIdsSet.has(String(b.item_id))) {
          return false;
        }
        return true;
      });

      const getProjectedCrossDayDisplayState = (calendarBooking, serviceLike) => {
        const segments = Array.isArray(serviceLike?.cross_day_booking_item)
          ? serviceLike.cross_day_booking_item
          : (Array.isArray(calendarBooking?.crossDayBooking) ? calendarBooking.crossDayBooking : []);
        if (segments.length === 0) {
          return null;
        }

        const buildDurationFromTimes = (startTime, endTime) => {
          const startMinutes = convertToMinutes(startTime);
          const endMinutes = convertToMinutes(endTime);
          if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) {
            return 0;
          }
          return adjustEndTimeForMidnightWrap(startMinutes, endMinutes) - startMinutes;
        };

        const currentBookingDate = normalizeComparableDate(calendarBooking?.service_at);
        const currentBookingTime = typeof calendarBooking?.service_at === "string"
          ? normalizeComparableTime((calendarBooking.service_at.split(" ")[1] || "").slice(0, 5))
          : null;
        const secondSegmentDate = normalizeComparableDate(segments[1]?.start_date);
        const renderFullOnBoundarySecondDay =
          shouldRenderFullOnExtendedBoundarySecondDay(serviceLike || { cross_day_booking_item: segments }) &&
          !!currentBookingDate &&
          !!secondSegmentDate &&
          currentBookingDate === secondSegmentDate;

        if (isExpandedMidnightCarrySplit(serviceLike || calendarBooking || { cross_day_booking_item: segments })) {
          const firstSegment = segments[0];
          const lastSegment = segments[segments.length - 1];
          const totalDuration = segments.reduce((sum, segment) => {
            const segmentDuration = Number(segment?.duration);
            if (!Number.isNaN(segmentDuration) && segmentDuration > 0) {
              return sum + segmentDuration;
            }
            return sum + buildDurationFromTimes(segment?.start_time, segment?.end_time);
          }, 0);
          return {
            duration: totalDuration,
            totalDuration,
            timeRange: `${convertTo12Hour(firstSegment?.start_time)} - ${convertTo12Hour(lastSegment?.end_time)}`,
            crossDayBooking: [firstSegment],
          };
        }

        if (calendarBooking?.render_full_expanded_cross_day === true || renderFullOnBoundarySecondDay) {
          const firstSegment = segments[0];
          const lastSegment = segments[segments.length - 1];
          const totalDuration = segments.reduce((sum, segment) => {
            const segmentDuration = Number(segment?.duration);
            if (!Number.isNaN(segmentDuration) && segmentDuration > 0) {
              return sum + segmentDuration;
            }
            return sum + buildDurationFromTimes(segment?.start_time, segment?.end_time);
          }, 0);
          return {
            duration: totalDuration,
            totalDuration,
            timeRange: `${convertTo12Hour(firstSegment?.start_time)} - ${convertTo12Hour(lastSegment?.end_time)}`,
            crossDayBooking: segments,
          };
        }

        const matchingSegment =
          segments.find((segment) => {
            const segmentDate = normalizeComparableDate(segment?.start_date);
            const segmentStart = normalizeComparableTime(segment?.start_time);
            return (
              (!!currentBookingDate && segmentDate === currentBookingDate && (!!currentBookingTime ? segmentStart === currentBookingTime : true)) ||
              (!currentBookingDate && !!currentBookingTime && segmentStart === currentBookingTime)
            );
          }) ||
          segments.find((segment) => normalizeComparableDate(segment?.start_date) === currentBookingDate) ||
          segments[0];

        if (!matchingSegment) {
          return null;
        }

        const segmentDuration = Number(matchingSegment?.duration);
        const resolvedDuration =
          !Number.isNaN(segmentDuration) && segmentDuration > 0
            ? segmentDuration
            : buildDurationFromTimes(matchingSegment?.start_time, matchingSegment?.end_time);

        return {
          duration: resolvedDuration,
          totalDuration: resolvedDuration,
          timeRange: `${convertTo12Hour(matchingSegment?.start_time)} - ${convertTo12Hour(matchingSegment?.end_time)}`,
          crossDayBooking: [matchingSegment],
        };
      };
      
      const safeDragOverrides = dragOverrides || {};
      const base = bookingsToProcess.map((b) => {
        const ov = b?.item_id != null ? safeDragOverrides[b.item_id] : null;
        
        // Find matching service for tag updates (used in both drag override and edit mode)
        const matchingService = allServicesForTags.find(svc => 
          (svc.booking_item_id && String(svc.booking_item_id) === String(b.item_id)) ||
          (svc.item_id && String(svc.item_id) === String(b.item_id))
        );
        const categoryFields = matchingService
          ? getServiceCategoryFields(matchingService, {
              serviceName: b.serviceName,
              serviceCategory: b.service_category,
              serviceId: b.service_id,
            })
          : null;
        
        // Build updated tags - preserve all original tags first (including haveNotes, repeat, etc.)
        // In edit mode, only check editedItemData for notes if this is the booking being edited
        const isBookingBeingEdited = isEditBookingActive && selectedBookingId && String(b.booking_id) === String(selectedBookingId);
        const currentNoteForBooking = isBookingBeingEdited && editedItemData?.note ? editedItemData.note : b?.tags?.note;
        const hasNotesForBooking = !!(currentNoteForBooking && currentNoteForBooking.trim() && currentNoteForBooking.length > 0);
        
        let updatedTags = { 
          ...(b?.tags || {}),
          // Explicitly preserve booking-level tags that don't come from service
          repeat: b?.tags?.repeat ?? false,
          isMember: b?.tags?.isMember ?? false,
          noMembership: b?.tags?.noMembership ?? false,
          bookingThrough: b?.tags?.bookingThrough ?? false,
          note: currentNoteForBooking,
          hasWebNotes: b?.tags?.hasWebNotes ?? false,
          haveNotes: hasNotesForBooking,
        };
        
        // Always update tags from matching service when in edit mode (regardless of drag override)
        // This ensures icons update immediately when requested_room or other properties change
        if (isEditBookingActive && matchingService) {
          // Update requested_person and requested_room from matching service
          if (typeof matchingService.requested_person !== 'undefined') {
            updatedTags.isRequested = matchingService.requested_person === 1;
          }
          
          if (typeof matchingService.requested_room !== 'undefined') {
            updatedTags.hasRequestedRoom = matchingService.requested_room === 1;
          }
          
          // Update service request tags (China, thai, malaysian, singapore, indonesian, male, female) from matching service
          // Always update these tags, even if service_request is empty (to clear them)
          const serviceRequestStr = matchingService.service_request 
            ? (typeof matchingService.service_request === 'string' 
                ? matchingService.service_request 
                : Array.isArray(matchingService.service_request) 
                  ? matchingService.service_request.join(',')
                  : '')
            : '';
          
          updatedTags.China = serviceRequestStr
            .split(",")
            .map((req) => req.trim().toLowerCase())
            .includes("china");
          updatedTags.thai = serviceRequestStr
            .split(",")
            .map((req) => req.trim().toLowerCase())
            .includes("thai");
          updatedTags.malaysian = serviceRequestStr
            .split(",")
            .map((req) => req.trim().toLowerCase())
            .includes("malaysian");
          updatedTags.singapore = serviceRequestStr
            .split(",")
            .map((req) => req.trim().toLowerCase())
            .includes("singapore");
          updatedTags.indonesian = serviceRequestStr
            .split(",")
            .map((req) => req.trim().toLowerCase())
            .includes("indonesian");
          updatedTags.male = serviceRequestStr
            .split(",")
            .map((req) => req.trim().toLowerCase())
            .includes("male");
          updatedTags.female = serviceRequestStr
            .split(",")
            .map((req) => req.trim().toLowerCase())
            .includes("female");
          // Update natal and slimming tags from service name
          const serviceNameForTags = matchingService?.name || matchingService?.service_name || "";
          const serviceNameForTagsStr = typeof serviceNameForTags === 'string' ? serviceNameForTags : String(serviceNameForTags || "");
          updatedTags.natal = serviceNameForTagsStr.toLowerCase().includes("natal") || false;
          updatedTags.slimming = serviceNameForTagsStr.toLowerCase().includes("slimming") || false;
          updatedTags.scrubService = categoryFields?.scrubService || false;
          applyRoomTagsFromService(updatedTags, matchingService, b);
          
          // Update roomName if available
          if (matchingService.roomName) {
            updatedTags.roomName = matchingService.roomName;
          }
        } else if (ov && String(ov.bookingId) === String(b.booking_id)) {
          // Drag override case: update tags from matching service if available, otherwise use override values
          if (matchingService) {
            if (typeof matchingService.requested_person !== 'undefined') {
              updatedTags.isRequested = matchingService.requested_person === 1;
            } else if (typeof ov.isRequested !== 'undefined') {
              updatedTags.isRequested = ov.isRequested;
            }
            
            if (typeof matchingService.requested_room !== 'undefined') {
              updatedTags.hasRequestedRoom = matchingService.requested_room === 1;
            }
            
            // Update service request tags from matching service
            const serviceRequestStr = matchingService.service_request 
              ? (typeof matchingService.service_request === 'string' 
                  ? matchingService.service_request 
                  : Array.isArray(matchingService.service_request) 
                    ? matchingService.service_request.join(',')
                    : '')
              : '';
            
            updatedTags.China = serviceRequestStr
              .split(",")
              .map((req) => req.trim().toLowerCase())
              .includes("china");
            updatedTags.thai = serviceRequestStr
              .split(",")
              .map((req) => req.trim().toLowerCase())
              .includes("thai");
            updatedTags.malaysian = serviceRequestStr
              .split(",")
              .map((req) => req.trim().toLowerCase())
              .includes("malaysian");
            updatedTags.singapore = serviceRequestStr
              .split(",")
              .map((req) => req.trim().toLowerCase())
              .includes("singapore");
            updatedTags.indonesian = serviceRequestStr
              .split(",")
              .map((req) => req.trim().toLowerCase())
              .includes("indonesian");
            updatedTags.male = serviceRequestStr
              .split(",")
              .map((req) => req.trim().toLowerCase())
              .includes("male");
            updatedTags.female = serviceRequestStr
              .split(",")
              .map((req) => req.trim().toLowerCase())
              .includes("female");
            
            const serviceNameForTags = matchingService?.name || matchingService?.service_name || "";
            const serviceNameForTagsStr = typeof serviceNameForTags === 'string' ? serviceNameForTags : String(serviceNameForTags || "");
            updatedTags.natal = serviceNameForTagsStr.toLowerCase().includes("natal") || false;
            updatedTags.slimming = serviceNameForTagsStr.toLowerCase().includes("slimming") || false;
            updatedTags.scrubService = categoryFields?.scrubService || false;
            applyRoomTagsFromService(updatedTags, matchingService, b);
            
            if (matchingService.roomName) {
              updatedTags.roomName = matchingService.roomName;
            } else if (ov.roomName) {
              updatedTags.roomName = ov.roomName;
            }
          } else {
            // No matching service, use override values
            if (typeof ov.isRequested !== 'undefined') {
              updatedTags.isRequested = ov.isRequested;
            }
            if (ov.roomName) {
              updatedTags.roomName = ov.roomName;
            }
          }
        }
        
        // Handle drag override logic (time, duration, etc.)
        if (ov && String(ov.bookingId) === String(b.booking_id)) {
          // Calculate duration, timeRange, and crossDayBooking based on cross_day_booking_item
          let updatedDuration = typeof ov.duration !== 'undefined' ? ov.duration : b.duration;
          // Initialize timeRange with fallback to original booking's timeRange
          // This handles cases where only requested therapist/room changes (no time change)
          let updatedTimeRange = b.timeRange || "";
          let updatedCrossDayBooking = b.crossDayBooking;
          let updatedTotalDuration = typeof ov.totalDuration !== 'undefined' ? ov.totalDuration : b.totalDuration;
          
          if (matchingService) {
            // Check if cross_day_booking_item exists and has segments
            const hasCrossDayBooking = matchingService.cross_day_booking_item && 
              Array.isArray(matchingService.cross_day_booking_item) && 
              matchingService.cross_day_booking_item.length > 0;
            
            if (hasCrossDayBooking && !isDualContextCalendarExpansionItem(matchingService)) {
              const projectedDisplayState = getProjectedCrossDayDisplayState(b, matchingService);
              if (projectedDisplayState) {
                updatedDuration = projectedDisplayState.duration;
                updatedTotalDuration = projectedDisplayState.totalDuration;
                updatedTimeRange = projectedDisplayState.timeRange;
                updatedCrossDayBooking = projectedDisplayState.crossDayBooking;
              }
            } else if (hasCrossDayBooking) {
              updatedDuration = Number(matchingService.duration) || updatedDuration;
              updatedTotalDuration = Number(matchingService.duration) || updatedTotalDuration;
              if (matchingService.start_time && matchingService.end_time) {
                updatedTimeRange = `${convertTo12Hour(matchingService.start_time)} - ${convertTo12Hour(matchingService.end_time)}`;
              }
              updatedCrossDayBooking = matchingService.cross_day_booking_item;
            } else {
              // No cross_day_booking_item (null or empty), use regular duration and timeRange
              const serviceDuration = Number(matchingService.duration) || 
                (matchingService.start_time && matchingService.end_time 
                  ? (() => {
                      const startMinutes = convertToMinutes(matchingService.start_time);
                      const endMinutes = convertToMinutes(matchingService.end_time);
                      if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) {
                        return null;
                      }
                      return adjustEndTimeForMidnightWrap(startMinutes, endMinutes) - startMinutes;
                    })()
                  : null) ||
                updatedDuration;
              updatedDuration = serviceDuration;
              updatedTotalDuration = serviceDuration;
              
              // Use override times if available, otherwise use matchingService times, otherwise keep original
              if (ov.start && ov.end) {
                updatedTimeRange = `${convertTo12Hour(ov.start)} - ${convertTo12Hour(ov.end)}`;
              } else if (matchingService.start_time && matchingService.end_time) {
                updatedTimeRange = `${convertTo12Hour(matchingService.start_time)} - ${convertTo12Hour(matchingService.end_time)}`;
              }
              // Otherwise keep the original timeRange (already set above)
              
              updatedCrossDayBooking = null;
            }
          } else if (ov.start && ov.end) {
            // No matching service found, but we have start/end times from override
            updatedTimeRange = `${convertTo12Hour(ov.start)} - ${convertTo12Hour(ov.end)}`;
            // Check if times cross days to determine if crossDayBooking should be cleared
            const startMins = convertToMinutes(ov.start);
            const endMins = convertToMinutes(ov.end);
            if (endMins > startMins && b.crossDayBooking && Array.isArray(b.crossDayBooking) && b.crossDayBooking.length > 0) {
              // No longer crossing days, clear crossDayBooking
              updatedCrossDayBooking = null;
              // Calculate duration from times
              updatedDuration = adjustEndTimeForMidnightWrap(startMins, endMins) - startMins;
              updatedTotalDuration = updatedDuration;
            }
          }
          // If neither matchingService nor ov.start/ov.end exist, keep original timeRange (already set above)
          
          const overrideAbs =
            typeof ov.absoluteStartMinutes === "number" && !Number.isNaN(ov.absoluteStartMinutes)
              ? ov.absoluteStartMinutes
              : null;
          // Negative abs = previous calendar day vs selectedDate. Applying it to a
          // day-2 overnight card (already placed with abs >= 0) pushes it off the grid.
          const keepVisibleOvernightPlacement =
            overrideAbs != null &&
            overrideAbs < 0 &&
            typeof b.absoluteStartMinutes === "number" &&
            !Number.isNaN(b.absoluteStartMinutes) &&
            b.absoluteStartMinutes >= 0;
          const resolvedAbsoluteStartMinutes = keepVisibleOvernightPlacement
            ? b.absoluteStartMinutes
            : overrideAbs != null
              ? overrideAbs
              : b.absoluteStartMinutes;
          const resolvedIsCalendarExpansion = resolvedAbsoluteStartMinutes >= 1440
            ? 1
            : (b.is_calendar_expansion ?? 0);

          return {
            ...b,
            therapistId: isRoomFilterActive
              ? normalizeNumericId(b.therapistId)
              : normalizeNumericId(typeof ov.therapistId !== 'undefined' ? ov.therapistId : b.therapistId),
            therapistName: isRoomFilterActive ? b.therapistName : (ov.therapistName || b.therapistName),
            room_id: isRoomFilterActive
              ? normalizeNumericId(typeof ov.roomId !== 'undefined' ? ov.roomId : b.room_id)
              : normalizeNumericId(b.room_id),
            roomName: isRoomFilterActive ? (ov.roomName || b.roomName) : b.roomName,
            startTime: ov.start ? convertTo12Hour(ov.start) : b.startTime,
            endTime: ov.end ? convertTo12Hour(ov.end) : b.endTime,
            serviceName: categoryFields?.serviceName || ov.serviceName || b.serviceName,
            service_category: categoryFields
              ? categoryFields.serviceCategory
              : (ov.service_category || b.service_category),
            service_id: categoryFields?.serviceId ?? b.service_id,
            duration: updatedDuration,
            absoluteStartMinutes: resolvedAbsoluteStartMinutes,
            is_calendar_expansion: resolvedIsCalendarExpansion,
            tags: updatedTags,
            service_at: keepVisibleOvernightPlacement
              ? b.service_at
              : (ov.service_at || b.service_at),
            totalDuration: updatedTotalDuration,
            timeRange: updatedTimeRange,
            crossDayBooking: updatedCrossDayBooking,
            guest_checkin_id: ov.guest_checkin_id || b.guest_checkin_id,
            guest_checkin_name: ov.guest_checkin_name || b.guest_checkin_name,
          };
        }
        
        // If in edit mode and we have a matching service, always update tags (even if no drag override)
        // This ensures icons update immediately when requested_room or other properties change
        if (isEditBookingActive && matchingService) {
          return {
            ...b,
            serviceName: categoryFields?.serviceName || b.serviceName,
            service_category: categoryFields?.serviceCategory ?? b.service_category,
            service_id: categoryFields?.serviceId ?? b.service_id,
            tags: updatedTags,
          };
        }
        
        // If tags were updated but we're not in edit mode or don't have matching service, still return updated tags
        // (This handles the case where tags were updated from drag override but we're not in edit mode)
        const tagsWereUpdated = JSON.stringify(updatedTags) !== JSON.stringify(b?.tags || {});
        if (tagsWereUpdated) {
          return {
            ...b,
            tags: updatedTags,
          };
        }
        
        return b;
      });

      let result = base;
      if (isEditBookingActive && editPrefill) {
        const newStart12 = convertTo12Hour(editPrefill.startTime);
        const newEnd12 = convertTo12Hour(editPrefill.endTime);
        
        // Find matching service for tag updates
        const allServices = [
          ...(Array.isArray(selectedServices) ? selectedServices : []),
          ...(Array.isArray(paxes) ? paxes.flatMap(p => Array.isArray(p.selectedServices) ? p.selectedServices : []) : [])
        ];
        
        result = base.map((b) => {
          const isSameBooking = String(b.booking_id) === String(editPrefill.bookingId);
          const isSameItem = !editPrefill.itemId || String(b.item_id || "") === String(editPrefill.itemId || "");
          const hasOverride = b?.item_id != null && safeDragOverrides[b.item_id] && String(safeDragOverrides[b.item_id].bookingId) === String(b.booking_id);
          
          // Always update tags from matching service if in edit mode (even without override)
          if (isSameBooking && isSameItem) {
            // Find matching service to get updated tags
            const matchingService = allServices.find(svc => 
              (svc.booking_item_id && String(svc.booking_item_id) === String(b.item_id)) ||
              (svc.item_id && String(svc.item_id) === String(b.item_id))
            );
            const categoryFields = matchingService
              ? getServiceCategoryFields(matchingService, {
                  serviceName: b.serviceName,
                  serviceCategory: b.service_category,
                  serviceId: b.service_id,
                })
              : null;
            
            // Build updated tags - preserve all original tags first (including haveNotes, repeat, etc.)
            // In edit mode, check editedItemData for notes, otherwise use booking tags
            const currentNoteForBooking = editedItemData?.note ? editedItemData.note : b?.tags?.note;
            const hasNotesForBooking = !!(currentNoteForBooking && currentNoteForBooking.trim() && currentNoteForBooking.length > 0);
            
            let updatedTags = { 
              ...(b?.tags || {}),
              // Explicitly preserve booking-level tags that don't come from service
              repeat: b?.tags?.repeat ?? false,
              isMember: b?.tags?.isMember ?? false,
              noMembership: b?.tags?.noMembership ?? false,
              bookingThrough: b?.tags?.bookingThrough ?? false,
              note: currentNoteForBooking,
              haveNotes: hasNotesForBooking,
              hasWebNotes: b?.tags?.hasWebNotes ?? false,
            };
            
            if (matchingService) {
              // Update requested_person and requested_room from matching service
              if (typeof matchingService.requested_person !== 'undefined') {
                updatedTags.isRequested = matchingService.requested_person === 1;
              }
              
              if (typeof matchingService.requested_room !== 'undefined') {
                updatedTags.hasRequestedRoom = matchingService.requested_room === 1;
              }
              
              // Update service request tags (China, thai, malaysian, singapore, indonesian, male, female) from matching service
              // Always update these tags, even if service_request is empty (to clear them)
              const serviceRequestStr = matchingService.service_request 
                ? (typeof matchingService.service_request === 'string' 
                    ? matchingService.service_request 
                    : Array.isArray(matchingService.service_request) 
                      ? matchingService.service_request.join(',')
                      : '')
                : '';
              
              updatedTags.China = serviceRequestStr
                .split(",")
                .map((req) => req.trim().toLowerCase())
                .includes("china");
              updatedTags.thai = serviceRequestStr
                .split(",")
                .map((req) => req.trim().toLowerCase())
                .includes("thai");
              updatedTags.malaysian = serviceRequestStr
                .split(",")
                .map((req) => req.trim().toLowerCase())
                .includes("malaysian");
              updatedTags.indonesian = serviceRequestStr
                .split(",")
                .map((req) => req.trim().toLowerCase())
                .includes("indonesian");
              updatedTags.singapore = serviceRequestStr
                .split(",")
                .map((req) => req.trim().toLowerCase())
                .includes("singapore");
              updatedTags.male = serviceRequestStr
                .split(",")
                .map((req) => req.trim().toLowerCase())
                .includes("male");
              updatedTags.female = serviceRequestStr
                .split(",")
                .map((req) => req.trim().toLowerCase())
                .includes("female");
              
              // Update natal and slimming tags from service name
              const serviceNameForTags = matchingService?.name || matchingService?.service_name || "";
              const serviceNameForTagsStr = typeof serviceNameForTags === 'string' ? serviceNameForTags : String(serviceNameForTags || "");
              updatedTags.natal = serviceNameForTagsStr.toLowerCase().includes("natal") || false;
              updatedTags.slimming = serviceNameForTagsStr.toLowerCase().includes("slimming") || false;
              updatedTags.scrubService = categoryFields?.scrubService || false;
              applyRoomTagsFromService(updatedTags, matchingService, b);
              
              // Update roomName if available
              if (matchingService.roomName) {
                updatedTags.roomName = matchingService.roomName;
              }
            }
            
            // Only update time/therapist/room if no override exists
            if (!hasOverride) {
              const projectedDisplayState =
                matchingService &&
                matchingService.cross_day_booking_item &&
                Array.isArray(matchingService.cross_day_booking_item) &&
                matchingService.cross_day_booking_item.length > 0 &&
                !isDualContextCalendarExpansionItem(matchingService)
                  ? getProjectedCrossDayDisplayState(b, matchingService)
                  : null;
              return {
                ...b,
                therapistId: isRoomFilterActive
                  ? normalizeNumericId(b.therapistId)
                  : normalizeNumericId(typeof editPrefill.therapistId !== 'undefined' ? editPrefill.therapistId : b.therapistId),
                therapistName: isRoomFilterActive ? b.therapistName : (editPrefill.therapistName || b.therapistName),
                room_id: isRoomFilterActive
                  ? normalizeNumericId(typeof editPrefill.roomId !== 'undefined' ? editPrefill.roomId : b.room_id)
                  : normalizeNumericId(b.room_id),
                roomName: isRoomFilterActive ? (editPrefill.roomName || b.roomName) : b.roomName,
                startTime: editPrefill.startTime ? newStart12 : b.startTime,
                endTime: editPrefill.endTime ? newEnd12 : b.endTime,
                serviceName: categoryFields?.serviceName || b.serviceName,
                service_category: categoryFields?.serviceCategory ?? b.service_category,
                service_id: categoryFields?.serviceId ?? b.service_id,
                duration: projectedDisplayState?.duration ?? b.duration,
                totalDuration: projectedDisplayState?.totalDuration ?? b.totalDuration,
                timeRange: projectedDisplayState?.timeRange ?? b.timeRange,
                crossDayBooking: projectedDisplayState?.crossDayBooking ?? b.crossDayBooking,
                tags: updatedTags,
              };
            } else {
              // Has override, but still update tags and replaced service category
              return {
                ...b,
                serviceName: categoryFields?.serviceName || b.serviceName,
                service_category: categoryFields?.serviceCategory ?? b.service_category,
                service_id: categoryFields?.serviceId ?? b.service_id,
                tags: updatedTags,
              };
            }
          }
          return b;
        });
      }

      if (isEditBookingActive && editableBooking?.id) {
        const existingItemIds = new Set(
          (result || [])
            .map((booking) => booking?.item_id)
            .filter((itemId) => itemId != null)
            .map((itemId) => String(itemId))
        );
        const originalBookingItems = Object.values(editableBooking?.booking_item || {}).flatMap((items) =>
          Array.isArray(items) ? items : []
        );
        const originalItemMap = new Map(
          originalBookingItems
            .filter((item) => item?.id != null)
            .map((item) => [String(item.id), item])
        );
        const editedServices = [
          ...(Array.isArray(selectedServices) ? selectedServices : []),
          ...(Array.isArray(paxes) ? paxes.flatMap((pax) => (Array.isArray(pax?.selectedServices) ? pax.selectedServices : [])) : []),
        ];

        const syntheticBookings = editedServices
          .map((service) => {
            const itemId = service?.booking_item_id ?? service?.item_id ?? null;
            if (itemId == null) return null;
            if (existingItemIds.has(String(itemId))) return null;
            if (deletedIdsSet.has(String(itemId))) return null;

            const startTime24 = typeof service?.start_time === "string" ? service.start_time.slice(0, 5) : "";
            const endTime24 = typeof service?.end_time === "string" ? service.end_time.slice(0, 5) : "";
            const startDateStr = service?.start_date || null;
            if (!startTime24 || !startDateStr) return null;

            // Only synthesize a preview card for the day currently being viewed/edited.
            // Without this check, an item whose real date is a different day (e.g. still
            // sitting on the previous day because it's untouched) would incorrectly get
            // rendered on every day it's "missing" from, since it never matches an
            // existing card in `result` for those other days.
            const selectedDateAsDate = selectedDate instanceof Date ? selectedDate : new Date(selectedDate);
            const selectedDateStr = Number.isNaN(selectedDateAsDate.getTime())
              ? null
              : `${selectedDateAsDate.getFullYear()}-${String(selectedDateAsDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDateAsDate.getDate()).padStart(2, "0")}`;
            const normalizedServiceStartDate = normalizeComparableDate(startDateStr);
            const crossDaySegments = Array.isArray(service?.cross_day_booking_item) ? service.cross_day_booking_item : [];
            const belongsToSelectedDate =
              normalizedServiceStartDate === selectedDateStr ||
              crossDaySegments.some((segment) => normalizeComparableDate(segment?.start_date) === selectedDateStr);
            if (!belongsToSelectedDate) return null;

            const originalItem = originalItemMap.get(String(itemId)) || {};
            const categoryFields = getServiceCategoryFields(service, {
              serviceName: originalItem?.service_name || originalItem?.service || "",
              serviceCategory: originalItem?.service_category_name || originalItem?.service_category || "",
              serviceId: originalItem?.service_id,
            });
            const serviceName = categoryFields.serviceName || originalItem?.service_name || originalItem?.service || "";
            const roomName = service?.roomName || service?.room_name || originalItem?.room_name || originalItem?.room_items?.[0]?.room_name || "";
            const serviceRequestStr = service?.service_request
              ? (typeof service.service_request === "string"
                  ? service.service_request
                  : Array.isArray(service.service_request)
                    ? service.service_request.join(",")
                    : "")
              : "";
            const startDateParts = String(startDateStr).split("-");
            const serviceAt = startDateParts.length === 3
              ? `${startDateParts[2]}-${startDateParts[1]}-${startDateParts[0]} ${startTime24}:00`
              : `${startDateStr} ${startTime24}:00`;
            const duration = Number(service?.duration || originalItem?.duration || 15);
            const noteValue = editedItemData?.note || editableBooking?.note || "";
            const roomTags = applyRoomTagsFromService(
              {
                coupleRoom: originalItem?.room_items?.[0]?.room_type === "double",
                showerRoom: Array.isArray(originalItem?.room_items?.[0]?.facilities)
                  ? originalItem.room_items[0].facilities.some((facility) => String(facility).toLowerCase() === "shower")
                  : false,
              },
              service,
              { room_id: service?.roomId ?? originalItem?.room_items?.[0]?.item_id ?? originalItem?.room_id }
            );

            return {
              booking_id: editableBooking.id,
              item_id: itemId,
              therapistId: normalizeNumericId(service?.therapist ?? service?.selected_therapist?.value ?? null),
              therapistName: service?.therapist_name || service?.selected_therapist?.label || "",
              room_id: normalizeNumericId(service?.roomId ?? service?.room?.value ?? service?.room ?? originalItem?.room_items?.[0]?.item_id ?? originalItem?.room_id),
              roomName,
              startTime: convertTo12Hour(startTime24),
              endTime: convertTo12Hour(endTime24),
              start_time: startTime24,
              end_time: endTime24,
              start_date: startDateStr,
              end_date: service?.end_date || startDateStr,
              service_at: serviceAt,
              absoluteStartMinutes: typeof service?.absoluteMinutes === "number" ? service.absoluteMinutes : undefined,
              serviceName,
              service_category: categoryFields.serviceCategory,
              service_id: categoryFields.serviceId ?? originalItem?.service_id ?? service?.service_id,
              duration,
              totalDuration: duration,
              timeRange: `${convertTo12Hour(startTime24)} - ${convertTo12Hour(endTime24)}`,
              customerName: customerData?.name || editableBooking?.user?.[0]?.name || "",
              customer_name: customerData?.name || editableBooking?.user?.[0]?.name || "",
              user: {
                name: customerData?.name || editableBooking?.user?.[0]?.name || "",
                contact: customerData?.contact_number || editableBooking?.user?.[0]?.contact_number || "",
              },
              status: editableBooking?.status || "Confirmed",
              panel: editableBooking?.panel,
              crossDayBooking: service?.cross_day_booking_item || null,
              guest_checkin_id: service?.guest_checkin_id ?? originalItem?.guest_checkin_id ?? null,
              guest_checkin_name: service?.guest_checkin_name ?? originalItem?.guest_checkin_name ?? null,
              tags: {
                repeat: !!editableBooking?.is_recurring,
                isMember: editableBooking?.user?.[0]?.membership === 1,
                noMembership: editableBooking?.user?.[0]?.membership === 0,
                bookingThrough: editableBooking?.panel === "customer",
                note: noteValue,
                haveNotes: !!(noteValue && String(noteValue).trim()),
                hasWebNotes: !!editableBooking?.user?.web_notes,
                isRequested: service?.requested_person === 1,
                hasRequestedRoom: service?.requested_room === 1,
                China: serviceRequestStr.split(",").map((req) => req.trim().toLowerCase()).includes("china"),
                thai: serviceRequestStr.split(",").map((req) => req.trim().toLowerCase()).includes("thai"),
                malaysian: serviceRequestStr.split(",").map((req) => req.trim().toLowerCase()).includes("malaysian"),
                singapore: serviceRequestStr.split(",").map((req) => req.trim().toLowerCase()).includes("singapore"),
                indonesian: serviceRequestStr.split(",").map((req) => req.trim().toLowerCase()).includes("indonesian"),
                male: serviceRequestStr.split(",").map((req) => req.trim().toLowerCase()).includes("male"),
                female: serviceRequestStr.split(",").map((req) => req.trim().toLowerCase()).includes("female"),
                natal: String(serviceName || "").toLowerCase().includes("natal"),
                slimming: String(serviceName || "").toLowerCase().includes("slimming"),
                scrubService: categoryFields.scrubService,
                coupleRoom: roomTags.coupleRoom,
                showerRoom: roomTags.showerRoom,
                roomName,
              },
            };
          })
          .filter(Boolean);

        if (syntheticBookings.length > 0) {
          result = [...result, ...syntheticBookings];
        }
      }

      // Sort by service_at (date + time) to maintain correct display order
      const sorted = [...result].sort((a, b) => {
        return getServiceAtSortValue(a.service_at) - getServiceAtSortValue(b.service_at);
      });
      
      return sorted;
    } catch {
      return filteredBookings;
    }
  }, [filteredBookings, dragOverrides, isEditBookingActive, editPrefill, isRoomFilterActive, selectedServices, paxes, editedItemData, selectedBookingId, deletedBookingItemIds, normalizeNumericId, editableBooking, customerData, normalizeComparableDate, normalizeComparableTime, isExpandedMidnightCarrySplit, shouldRenderFullOnExtendedBoundarySecondDay, selectedDate, selectedRooms, homeRoomList]);

  const adjustedFilteredBookingsForUI = useMemo(() => {
    if (!Array.isArray(adjustedFilteredBookings) || adjustedFilteredBookings.length === 0) {
      return [];
    }

    const projectedBookings = adjustedFilteredBookings
      .map((booking) => {
        const normalizedTherapistId = normalizeNumericId(
          booking?.therapistId ?? booking?.therapist_id ?? booking?.therapist ?? null
        );
        const normalizedRoomId = normalizeNumericId(
          booking?.room_id ?? booking?.roomId ?? booking?.room?.value ?? booking?.room ?? null
        );
        const fallbackKey = isRoomFilterActive ? normalizedRoomId : normalizedTherapistId;
        const fallbackIndex = filteredRoomHeaders.findIndex(
          (header) => Number(header?.id) === Number(fallbackKey)
        );
        const resolvedColIndex = resolveProjectedColumnIndex({
          item: {
            ...booking,
            therapistId: normalizedTherapistId,
            roomId: normalizedRoomId,
          },
          headers: filteredRoomHeaders,
          isRoomFilterActive,
          fallbackIndex,
        });

        if (resolvedColIndex < 0) return null;

        return {
          ...booking,
          therapistId: normalizedTherapistId,
          room_id: normalizedRoomId,
          colIndex: resolvedColIndex,
        };
      })
      .filter(Boolean);

    const bookingMap = new Map();
    projectedBookings.forEach((booking) => {
      const itemIdKey = booking?.item_id != null ? String(booking.item_id) : null;
      const bookingKey = itemIdKey? `${booking.booking_id}:${itemIdKey}`: [booking.booking_id,booking.service_id ?? "service",booking.service_at ?? "",booking.therapistId ?? "",booking.room_id ?? "",].join(":");
      bookingMap.set(bookingKey, booking);
    });
    return Array.from(bookingMap.values()).sort((a, b) => {
      return getServiceAtSortValue(a.service_at) - getServiceAtSortValue(b.service_at);
    });
  }, [adjustedFilteredBookings, filteredRoomHeaders, getServiceAtSortValue, isRoomFilterActive, normalizeNumericId]);

  const bookingsByColumnIndex = useMemo(() => {
    const groupedBookings = new Map();
    (adjustedFilteredBookingsForUI || []).forEach((booking) => {
      const columnKey = Number(booking?.colIndex);
      if (Number.isNaN(columnKey)) return;
      if (!groupedBookings.has(columnKey)) {
        groupedBookings.set(columnKey, []);
      }
      groupedBookings.get(columnKey).push(booking);
    });
    return groupedBookings;
  }, [adjustedFilteredBookingsForUI]);

  const breaksByTherapistId = useMemo(() => {
    const groupedBreaks = new Map();
    (breaksData || []).forEach((breakItem) => {
      const therapistKey = Number(breakItem?.therapist_id);
      if (Number.isNaN(therapistKey)) return;
      if (!groupedBreaks.has(therapistKey)) {
        groupedBreaks.set(therapistKey, []);
      }
      groupedBreaks.get(therapistKey).push(breakItem);
    });
    return groupedBreaks;
  }, [breaksData]);

  useEffect(() => {
    const onCancelOverrides = (e) => {
      const bid = e?.detail?.bookingId;
      if (!bid) return;
      const closeSidebar = Boolean(e?.detail?.closeSidebar);
      // Ensure edit mode is fully exited when edit is cancelled
      try { setIsEditBookingActive(false); } catch {}
      try { setEditableBooking(null); } catch {}
      try { setNewBookingActive(false); } catch {}
      try { setShowConflictModal(false); } catch {}
      try { setIsNewBookingSlotActive(false); } catch {}
      try { setNewAppointmentSlots([]); } catch {}
      try { setNewSlot(null); } catch {}
      try { setSelectedServices([]); } catch {}
      try { setPaxes([]); } catch {}
      // Do not trigger global cancel here; we want to stay in sidebar summary view
      try { setEditPrefill(null); } catch {}
      try { setDateSyncState({ requestId: 0, mode: null,status: "idle", date: null,});} catch {}
      // Bypass discard check on the very next empty-slot click after cancelling edit
      skipNextDiscardCheckRef.current = true;
      if (closeSidebar) {
        try { setIsSidebarVisible(false); } catch {}
        try { setIsBookingSidebar(false); } catch {}
        try { setShowBookingSummary(false); } catch {}
        try { setSelectedBookingId(null); } catch {}
        try { setActiveSlotId(null); } catch {}
        try { setActiveCardKey(null); } catch {}
        clearTouchCardSelection();
      } else {
        // Show appointment summary like clicking on a booking card
        try { setIsSidebarVisible(true); } catch {}
        try { setIsBookingSidebar(false); } catch {}
        try { setShowBookingSummary(true); } catch {}
      }
      applyDragOverrides(prev => {
        if (!prev || typeof prev !== 'object') return prev;
        const next = { ...prev };
        Object.entries(next).forEach(([key, ov]) => {
          if (ov && String(ov.bookingId) === String(bid)) {
            delete next[key];
            clearCalendarDragSchedule(key);
          }
        });
        return next;
      });
    };
    window.addEventListener('cancelEditOverride', onCancelOverrides);
    return () => window.removeEventListener('cancelEditOverride', onCancelOverrides);
  }, [applyDragOverrides, clearCalendarDragSchedule, setDateSyncState, setEditableBooking, setNewBookingActive]);

  useEffect(() => {
    applyDragOverrides((prev) => {
      if (!prev || typeof prev !== "object" || Object.keys(prev).length === 0) {
        return prev;
      }

      let changed = false;
      const next = { ...prev };

      Object.entries(prev).forEach(([itemId, override]) => {
        if (!override?.bookingId) return;

        const booking = (bookingsList || []).find(
          (entry) => String(entry?.id || entry?.booking_id) === String(override.bookingId)
        );
        if (!booking?.booking_item) return;

        const bookingItems = Object.values(booking.booking_item).flatMap((items) =>
          Array.isArray(items) ? items : []
        );
        const matchedItem = bookingItems.find(
          (entry) => String(entry?.id || entry?.booking_item_id) === String(itemId)
        );
        if (!matchedItem) return;

        const overrideStart = normalizeComparableTime(override.start);
        const overrideEnd = normalizeComparableTime(override.end);
        const overrideTherapistId = normalizeNumericId(override.therapistId);
        const overrideRoomId = normalizeNumericId(override.roomId);
        const overrideServiceDate = normalizeComparableDate(override.service_at);

        const itemStart = normalizeComparableTime(getServiceStartTime(matchedItem));
        const itemEnd = normalizeComparableTime(getServiceEndTime(matchedItem));
        const itemTherapistId = getServiceTherapistId(matchedItem);
        const itemRoomId = getServiceRoomItemId(matchedItem);
        const itemServiceDate = normalizeComparableDate(
          matchedItem?.service_at ||
          (matchedItem?.start_date && getServiceStartTime(matchedItem)
            ? `${matchedItem.start_date} ${getServiceStartTime(matchedItem)}`
            : matchedItem?.start_date)
        );

        const overrideExpansion = typeof override.absoluteStartMinutes === "number" && override.absoluteStartMinutes >= 1440
          ? 1
          : 0;
        const itemExpansionRaw =
          matchedItem?.is_calendar_expansion ??
          matchedItem?.isCalendarExpansion ??
          matchedItem?.calendar_expansion;
        const itemExpansion =
          itemExpansionRaw === 1 ||
          itemExpansionRaw === "1" ||
          itemExpansionRaw === true ||
          itemExpansionRaw === "true"
            ? 1
            : 0;

        // Sidebar explicitly cleared the therapist (e.g. off roster after a date change):
        // keep the override so the card does not fall back to the saved therapist.
        const overrideClearsSavedTherapist = override.therapistCleared === true && overrideTherapistId == null && itemTherapistId != null;
        const matchesOverride =
          !overrideClearsSavedTherapist &&
          (!overrideStart || overrideStart === itemStart) &&
          (!overrideEnd || overrideEnd === itemEnd) &&
          (overrideTherapistId == null || overrideTherapistId === itemTherapistId) &&
          (overrideRoomId == null || overrideRoomId === itemRoomId) &&
          (!overrideServiceDate || overrideServiceDate === itemServiceDate) &&
          overrideExpansion === itemExpansion;

        if (matchesOverride) {
          delete next[itemId];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [
    applyDragOverrides,
    bookingsList,
    getServiceEndTime,
    getServiceRoomItemId,
    getServiceStartTime,
    getServiceTherapistId,
    normalizeComparableDate,
    normalizeComparableTime,
    normalizeNumericId,
  ]);

  useEffect(() => {
    const handleClearCache = () => {
      setActiveCardKey(null);
      clearTouchCardSelection();
    };
    window.addEventListener('clearCacheRefresh', handleClearCache);
    return () => window.removeEventListener('clearCacheRefresh', handleClearCache);
  }, []);

  // Show loader briefly when room filter mode toggles (therapist <-> room)
  // Skip when change is from outlet switch (fetchCalendarFilters) to avoid loader blink
  useEffect(() => {
    if (prevIsRoomFilterActiveRef.current === undefined) {
      prevIsRoomFilterActiveRef.current = isRoomFilterActive;
      return;
    }
    if (prevIsRoomFilterActiveRef.current !== isRoomFilterActive) {
      if (suppressRoomFilterTransitionRef.current) {
        suppressRoomFilterTransitionRef.current = false;
        prevIsRoomFilterActiveRef.current = isRoomFilterActive;
        return;
      }
      setIsRoomFilterTransitionLoading(true);
      prevIsRoomFilterActiveRef.current = isRoomFilterActive;
      const t = setTimeout(() => setIsRoomFilterTransitionLoading(false), 600);
      return () => clearTimeout(t);
    }
  }, [isRoomFilterActive]);

  const getSelectedBookingItemIdForScroll = useCallback(() => {
    if (activeCardKey && String(activeCardKey).includes(":")) {
      const [, activeItemId = ""] = String(activeCardKey).split(":");
      if (activeItemId) return activeItemId;
    }

    if (isEditBookingActive) {
      const allDraftServices = [
        ...(Array.isArray(selectedServices) ? selectedServices : []),
        ...(Array.isArray(paxes)
          ? paxes.flatMap((pax) => (Array.isArray(pax?.selectedServices) ? pax.selectedServices : []))
          : []),
      ];
      const primaryDraftService =
        allDraftServices.find((service) => !service?.isSplitSegment && service?.booking_item_id != null) ||
        allDraftServices.find((service) => service?.booking_item_id != null);
      if (primaryDraftService?.booking_item_id != null) {
        return String(primaryDraftService.booking_item_id);
      }
    }

    if (selectedBookingId == null || !Array.isArray(bookingsList)) return null;

    const matchedBooking = bookingsList.find(
      (booking) => String(booking?.id || booking?.booking_id) === String(selectedBookingId)
    );
    if (!matchedBooking?.booking_item) return null;

    const allBookingItems = Object.values(matchedBooking.booking_item).flatMap((items) =>
      Array.isArray(items) ? items : []
    );
    const primaryItem =
      allBookingItems.find((item) => item?.primary === 1 && item?.id != null) ||
      allBookingItems.find((item) => item?.id != null);

    return primaryItem?.id != null ? String(primaryItem.id) : null;
  }, [activeCardKey, bookingsList, isEditBookingActive, paxes, selectedBookingId, selectedServices]);

  const getDraftBookingKeyForScroll = useCallback(() => {
    if (!isNewBookingSlotActive) return null;

    const draftServices = [
      ...(Array.isArray(selectedServices) ? selectedServices : []),
      ...(Array.isArray(paxes)
        ? paxes.flatMap((pax) => (Array.isArray(pax?.selectedServices) ? pax.selectedServices : []))
        : []),
    ];

    const primaryDraftService =
      draftServices.find((service) => !service?.isSplitSegment && service?.counterKey != null) ||
      draftServices.find((service) => service?.counterKey != null);
    if (primaryDraftService?.counterKey != null) {
      return String(primaryDraftService.counterKey);
    }

    const primaryDraftSlot = Array.isArray(newAppointmentSlots) ? newAppointmentSlots[0] : null;
    return primaryDraftSlot?.counterKey != null ? String(primaryDraftSlot.counterKey) : null;
  }, [isNewBookingSlotActive, newAppointmentSlots, paxes, selectedServices]);

  useEffect(() => {
    if (!isNewBookingSlotActive) {
      prevEmptyDraftDateScrollRef.current = null;
      return;
    }

    const hasDraftServices =
      (Array.isArray(selectedServices) && selectedServices.length > 0) ||
      (Array.isArray(paxes) && paxes.some((pax) => Array.isArray(pax?.selectedServices) && pax.selectedServices.length > 0));

    if (hasDraftServices) {
      prevEmptyDraftDateScrollRef.current = null;
      return;
    }

    const dateKey = selectedDate ? formatDateToDDMMYYYY(selectedDate) : "";
    if (!dateKey) return;

    if (prevEmptyDraftDateScrollRef.current === null) {
      prevEmptyDraftDateScrollRef.current = dateKey;
      return;
    }

    if (prevEmptyDraftDateScrollRef.current === dateKey) return;

    prevEmptyDraftDateScrollRef.current = dateKey;
    const draftBookingKey = getDraftBookingKeyForScroll();
    if (!draftBookingKey) return;

    pendingFilterModeScrollRef.current = { type: "draft", id: draftBookingKey };
  }, [getDraftBookingKeyForScroll, isNewBookingSlotActive, paxes, selectedDate, selectedServices]);

  useEffect(() => {
    if (prevFilterModeScrollRef.current === undefined) {
      prevFilterModeScrollRef.current = isRoomFilterActive;
      return;
    }

    if (prevFilterModeScrollRef.current === isRoomFilterActive) {
      return;
    }

    prevFilterModeScrollRef.current = isRoomFilterActive;

    const selectedBookingItemId = getSelectedBookingItemIdForScroll();
    const draftBookingKey = getDraftBookingKeyForScroll();
    if (!selectedBookingItemId && !draftBookingKey) return;
    pendingFilterModeScrollRef.current = selectedBookingItemId
      ? { type: "booking", id: selectedBookingItemId }
      : { type: "draft", id: draftBookingKey };
  }, [getDraftBookingKeyForScroll, getSelectedBookingItemIdForScroll, isRoomFilterActive]);

  useEffect(() => {
    const pendingScroll = pendingFilterModeScrollRef.current;
    if (!pendingScroll || isTableLoading || isRoomFilterTransitionLoading) {
      return;
    }

    const targetReady = pendingScroll.type === "booking"
      ? (adjustedFilteredBookingsForUI || []).some(
          (booking) => String(booking?.item_id || "") === String(pendingScroll.id)
        )
      : (draftSlotsForUI || []).some(
          (slot) => String(slot?.counterKey || "") === String(pendingScroll.id)
        );

    if (!targetReady) {
      return;
    }

    const timer = setTimeout(() => {
      if (pendingScroll.type === "booking") {
        scrollToBookingCard(pendingScroll.id, 150);
      } else {
        scrollToNewBookingCard(pendingScroll.id, 150);
      }
      pendingFilterModeScrollRef.current = null;
    }, 100);

    return () => clearTimeout(timer);
  }, [
    adjustedFilteredBookingsForUI,
    draftSlotsForUI,
    isRoomFilterTransitionLoading,
    isTableLoading,
  ]);

  // CALCULATE BOOKED SLOT HEIGHT START 
  const getBookingHeight = (duration, tableInterval) => {
    const cellHeight = getCellHeight(tableInterval);
    const interval = tableInterval;
    return (duration / interval) * cellHeight;
  };
  // CALCULATE BOOKED SLOT HEIGHT END 

  // BOOKED SLOT CLICKED START 
  const handleBookedSlotClick = (event, bookingId, bookingItemId) => {
    // Don't allow clicking on booked slots when sidebar is loading
    if (sidebarLoading) {
      return;
    }

    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (selectedBookingId && String(selectedBookingId) === String(bookingId)) {
      setActiveSlotId(bookingId);
      setShowBookingSummary(true);
      setIsBookingSidebar(false);
      setIsSidebarVisible(true);
      try {
        setActiveCardKey(`${bookingId}:${bookingItemId ?? ""}`);
      } catch {}
      return;
    }

    setShowCancelPopup(false);
    setDiscountItemToPost([]);

    // If booking has isVoided true, set it to false
    if (bookingId != null) {
      setBookingsList((prev) => {
        return prev.map((booking) => {
          if (String(booking.id || booking.booking_id) === String(bookingId)) {
            const bookingItems = booking.booking_item || {};
            const updatedBookingItems = {};
            let hasChanges = false;

            // Iterate through all customer groups in booking_item
            Object.entries(bookingItems).forEach(([customerName, items]) => {
              updatedBookingItems[customerName] = items.map((item) => {
                // If this is the clicked item, set isVoided to false
                if (String(item.id) === String(bookingItemId || "")) {
                  if (item.isVoided === true) {
                    hasChanges = true;
                    return { ...item, isVoided: false };
                  }
                }
                return item;
              });
            });

            if (hasChanges) {
              return {
                ...booking,
                booking_item: updatedBookingItems,
              };
            }
          }
          return booking;
        });
      });
    }

    // Don't show discard popup if customer details modal is open
    if (isCustomerDetailsOpen) {
      return;
    }
    
    // Don't show discard popup while dragging (ref is source of truth; state can be stale on click)
    if (isDraggingRef.current) {
      return;
    }
    
    if ((newAppointmentSlots.length > 0 && isCustomerSelected) || selectedServices.length > 0) {
				setShowConflictModal(true);
				return;
			}
		setNewAppointmentSlots([]);
    // When editing, clicking/touching any booked card should NOT show discard popup
    if (isEditBookingActive) {
      return;
    }

    // Keep the black border on the most recently interacted *card* (booking_id + item_id),
    // even if the same booking is already selected.
    try {
      if (bookingId != null) {
        setActiveCardKey(`${bookingId}:${bookingItemId ?? ""}`);
      }
    } catch {}

    // Clear any pending scroll operations before setting new booking
    setBookingIdToScroll(null);
    scrollToBookingCard(bookingItemId);
    setActiveSlotId(bookingId);
    setSelectedBookingId(bookingId);
    setShowBookingSummary(true);
    setIsBookingSidebar(false);
    setIsSidebarVisible(true);
    setIsNewBookingSlotActive(false);
    setNewAppointmentSlots([]);
    setNewSlot(null);
    setServiceTotal(0);
    setAmountDue(0);
    setReference("");
    setPaymentSummary({
      subtotal: 0,
      midtotal: 0,
      gst: 0,
      tip: "0.00",
      total: 0,
      payment_method: "",
    });
    setPaymentHistory([]);
    setCurrentPaymentMethod("");
    setCurrentPaymentMethodTitle("");
    setVoucherStates({});
    setVoucherDetail([]);
    setSelectedServices([]);
    setPaxes([]);
    setEditPrefill(null);
    setCustomerData({});
    setSelectedSource(null)
    localStorage.removeItem('tempBookingID');
    setTemporaryBookingId(null);
    setTempBookingCreated(false);
    setHitTemporaryBookingApi(false);
  };

  const handleBookingCardClick = (event, bookingId, bookingItemId) => {
    if (suppressNextCardClickRef.current) {
      suppressNextCardClickRef.current = false;
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      return;
    }
    if (cardClickHandledViaPointerRef.current) {
      cardClickHandledViaPointerRef.current = false;
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      return;
    }
    handleBookedSlotClick(event, bookingId, bookingItemId);
  };
  // BOOKED SLOT CLICKED END 

  // CLOSE SIDEBAR START
  const handleCloseSidebar = () => {
    // Clear the creating booking flag when manually closing sidebar
    isCreatingBookingRef.current = false;
    viewOnlyToastShownRef.current = false;
    
    // Revert any persisted visual overrides for the current booking when cancelling edit
    try {
      const bookingIdToClear = selectedBookingId;
      if (bookingIdToClear) {
        applyDragOverrides((prev) => {
          if (!prev || typeof prev !== 'object') return prev;
          const next = { ...prev };
          Object.entries(next).forEach(([key, ov]) => {
            if (ov && String(ov.bookingId) === String(bookingIdToClear)) {
              delete next[key];
            }
          });
          return next;
        });
      }
    } catch {}

    // Exit edit/create mode and clear any lingering conflict modal state
    try { setIsEditBookingActive(false); } catch {}
    try { setNewBookingActive(false); } catch {}
    try { setShowConflictModal(false); } catch {}
    try {
      setDateSyncState({
        requestId: 0,
        mode: null,
        status: "idle",
        date: null,
      });
    } catch {}

    setIsSidebarVisible(false);
    setIsBookingSidebar(false);
    setSelectedBookingId(null);
    setActiveSlotId(null);
    // Closing sidebar should clear selected booking border and the touch drag selection
    try { setActiveCardKey(null); clearTouchCardSelection(); } catch {}
    setSelectedBookingId(null);
    setNewAppointmentSlots([]);
    setNewSlot(null);
    try { setSelectedServices([]); } catch {}
    try { setPaxes([]); } catch {}
    try { setIsNewBookingSlotActive(false); } catch {}
    try { setTriggerCancel(true); } catch {}
    try { setSidebarLoading(false); } catch {}
    try { setIsCustomerSelected(false); } catch {}
    setEditPrefill(null);
    setServiceTotal(0);
    setAmountDue(0);
    setReference("");
    setPaymentSummary({
      subtotal: 0,
      midtotal: 0,
      gst: 0,
      tip: "0.00",
      total: 0,
      payment_method: "",
    });
    setPaymentHistory([]);
    setCurrentPaymentMethod("");
    setCurrentPaymentMethodTitle("");
    setVoucherStates({});
    setVoucherDetail([]);
    setSelectedServices([]);
    setPaxes([]);
    setSelectedRooms({});
    setEditPrefill(null);
    setCustomerData({});
    setSelectedSource(null);
    setTemporaryBookingId(null);
    setEditableBooking(null);
    setBookingDetails(null);
    setDeletedBookingItemIds([]);
  };

  const handleDateContainerChange = () => {
    try { applyDragOverrides({}); } catch {}
    try { clearCalendarDragSchedule(); } catch {}
    handleCloseSidebar();
  };
  // CLOSE SIDEBAR END 

  const clearSidebarVisualSelection = useCallback(() => {
    setSelectedBookingId(null);
    setActiveSlotId(null);
    setActiveCardKey(null);
    clearTouchCardSelection();
    setSelectedCells(new Set());
  }, [setActiveSlotId, setSelectedBookingId]);

  // Handle timer expiration - delete booking when timer reaches 0
  const handleTimerExpired = useCallback(async (bookingId) => {
    if (!bookingId) return;
    try {
      const response = await deleteHoldBooking(bookingId, selectedOutlet);
      if (response?.success || response?.data?.success || response?.status) {
        setTemporaryBookingId(null);
				setTempBookingCreated(false);
				setHitTemporaryBookingApi(false);
        localStorage.removeItem('tempBookingID');
        // setBookingsList((prev) => prev.filter((b) => String(b.booking_id || b.id) !== String(bookingId)));
      }
    } catch (error) {
      console.error("Error deleting booking on timer expiration:", error);
    }
  }, [setBookingsList, selectedOutlet]);

  const onDiscard = async () => {
    skipNextDiscardCheckRef.current = true;
    try { setSidebarLoading(false); } catch {}
    if(temporaryBookingId) {
      const response = await deleteHoldBooking(temporaryBookingId, selectedOutlet);
      if(response?.success || response?.data?.success || response?.status) {
				setTemporaryBookingId(null);
				setTempBookingCreated(false);
				setHitTemporaryBookingApi(false);
        localStorage.removeItem('tempBookingID');
      }
    }
    setNewAppointmentSlots([]);
    setNewSlot(null);
    setShowConflictModal(false);
    setSelectedCells(new Set());
    setIsNewBookingSlotActive(false);
    setIsEditBookingActive(false);
    setNewBookingActive(false);
    try {
      setDateSyncState({
        requestId: 0,
        mode: null,
        status: "idle",
        date: null,
      });
    } catch {}
    setServiceTotal(0);
    setAmountDue(0);
    setReference("");
    setPaymentSummary({
      subtotal: 0,
      midtotal: 0,
      gst: 0,
      tip: "0.00",
      total: 0,
      payment_method: "",
    });
    setPaymentHistory([]);
    setCurrentPaymentMethod("");
    setCurrentPaymentMethodTitle("");
    setVoucherStates({});
    setVoucherDetail([]);
    setSelectedServices([]);
    setPaxes([]);
    setSelectedRooms({});
    setEditPrefill(null);
    setCustomerData({});
    setSelectedSource(null)
    setEditableBooking(null);
    try { setIsCustomerSelected(false); } catch {}
    const discardedBookingId = selectedBookingId;
    try {
			window.dispatchEvent(new CustomEvent('cancelEditOverride', { detail: { bookingId: discardedBookingId, closeSidebar: true } }));
    } catch {}
    setEditableBooking(null);
    applyDragOverrides({});
    clearCalendarDragSchedule();
    setShowPaymentInProgressNotice(false);
    setCompleteBookingWarning(false);
    setDeletedBookingItemIds([]);
    handleCloseSidebar();
  }
  const onCancel = () => {
    setShowConflictModal(false);
    // Only for edit: undo calendar-click pollution.
    // For create booking, Keep Editing must leave newBookingActive true.
    if (isEditBookingActive) {
      setNewBookingActive(false);
      if (editableBooking) { setBookingDetails((prev) => prev || editableBooking); }
    }
  }

  useEffect(() => {
    const onEditOverrideUpdate = (e) => {
      const d = e?.detail;
      if (!d || !d.itemId || !d.bookingId) return;

      applyDragOverrides((prev) => {
        const existing =
          prev?.[d.itemId] ||
          prev?.[String(d.itemId)] ||
          prev?.[Number(d.itemId)] ||
          null;

        const incomingStart =
          typeof d.start !== "undefined" ? normalizeComparableClock(d.start) : null;
        const existingStart = normalizeComparableClock(existing?.start);
        const incomingAbs =
          typeof d.absoluteStartMinutes === "number" && !Number.isNaN(d.absoluteStartMinutes)
            ? d.absoluteStartMinutes
            : null;
        const existingAbs =
          typeof existing?.absoluteStartMinutes === "number" && !Number.isNaN(existing.absoluteStartMinutes)
            ? existing.absoluteStartMinutes
            : null;

        const dragMemory = lastCalendarDragScheduleRef.current[String(d.itemId)] || null;
        const dragAbs =
          typeof dragMemory?.abs === "number" && !Number.isNaN(dragMemory.abs)
            ? dragMemory.abs
            : null;
        const dragStart = normalizeComparableClock(dragMemory?.start);
        const originalAbs =
          typeof dragMemory?.originalAbs === "number" && !Number.isNaN(dragMemory.originalAbs)
            ? dragMemory.originalAbs
            : null;
        const originalStart = normalizeComparableClock(dragMemory?.originalStart);
        // Only protect briefly after a calendar drop so later intentional sidebar
        // edits (including setting time back to the original) still apply.
        const STALE_HYDRATE_GUARD_MS = 5000;
        const calendarDragAt =
          typeof dragMemory?.calendarDragAt === "number" ? dragMemory.calendarDragAt : null;
        const guardAgeMs =
          calendarDragAt != null ? Date.now() - calendarDragAt : null;
        const withinPostDragGuardWindow =
          calendarDragAt != null && guardAgeMs < STALE_HYDRATE_GUARD_MS;

        const existingStillMatchesDrag =
          dragMemory != null &&
          ((dragAbs != null && existingAbs != null && Number(existingAbs) === Number(dragAbs)) ||
            (dragStart != null && existingStart != null && dragStart === existingStart));

        const incomingDiffersFromExisting =
          (incomingAbs != null && existingAbs != null && Number(incomingAbs) !== Number(existingAbs)) ||
          (incomingStart != null && existingStart != null && incomingStart !== existingStart);

        const incomingMatchesOriginal =
          (originalAbs != null && incomingAbs != null && Number(incomingAbs) === Number(originalAbs)) ||
          (originalStart != null && incomingStart != null && incomingStart === originalStart);

        // Stale edit-hydrate emit: keep drag time (+ column), still allow tag merges.
        const blockStaleTimeRevert =
          withinPostDragGuardWindow &&
          existingStillMatchesDrag &&
          incomingDiffersFromExisting &&
          incomingMatchesOriginal;

        const incomingTherapistId =typeof d.therapistId !== "undefined" ? d.therapistId : undefined;
        const existingTherapistId = existing?.therapistId ?? null;
        const dragTherapistId = dragMemory?.therapistId ?? null;
        const originalTherapistId = dragMemory?.originalTherapistId ?? null;
        const therapistWouldRevert = typeof incomingTherapistId !== "undefined" &&  existingTherapistId != null && incomingTherapistId != null && Number(incomingTherapistId) !== Number(existingTherapistId);
        const incomingTherapistMatchesOriginal =  originalTherapistId != null && incomingTherapistId != null && Number(incomingTherapistId) === Number(originalTherapistId);
        // Minimal fix: keep drop therapist/room when hydrate restores pre-drag column
        // (either bundled with stale time, or therapist-only).
        const blockStaleTherapistRevert = withinPostDragGuardWindow && therapistWouldRevert && (blockStaleTimeRevert || incomingTherapistMatchesOriginal);
        const projectedOverride = buildProjectedOverridePayload({
          bookingId: d.bookingId,
          itemId: d.itemId,
          start: blockStaleTimeRevert
            ? existing?.start
            : (typeof d.start !== "undefined" ? d.start : existing?.start),
          end: blockStaleTimeRevert
            ? existing?.end
            : (typeof d.end !== "undefined" ? d.end : existing?.end),
          therapistId: blockStaleTherapistRevert? existingTherapistId  : (typeof d.therapistId !== "undefined" ? d.therapistId : existing?.therapistId),
          therapistName: blockStaleTherapistRevert  ? existing?.therapistName : (typeof d.therapistName !== "undefined" ? d.therapistName : existing?.therapistName),
          roomId: blockStaleTherapistRevert ? existing?.roomId  : (typeof d.roomId !== "undefined" ? d.roomId : existing?.roomId),
          roomName: blockStaleTherapistRevert ? existing?.roomName  : (typeof d.roomName !== "undefined" ? d.roomName : existing?.roomName),
          serviceName: typeof d.serviceName !== "undefined" ? d.serviceName : existing?.serviceName,
          service_category: typeof d.service_category !== "undefined" ? d.service_category : existing?.service_category,
          duration: blockStaleTimeRevert
            ? existing?.duration
            : (typeof d.duration !== "undefined" ? d.duration : existing?.duration),
          isRequested: typeof d.isRequested !== "undefined" ? d.isRequested : existing?.isRequested,
          hasRequestedRoom: typeof d.hasRequestedRoom !== "undefined" ? d.hasRequestedRoom : existing?.hasRequestedRoom,
          service_at: blockStaleTimeRevert
            ? existing?.service_at
            : (typeof d.service_at !== "undefined" ? d.service_at : existing?.service_at),
          absoluteStartMinutes: blockStaleTimeRevert
            ? existing?.absoluteStartMinutes
            : (typeof d.absoluteStartMinutes === "number" ? d.absoluteStartMinutes : existing?.absoluteStartMinutes),
          guest_checkin_id: typeof d.guest_checkin_id !== "undefined" ? d.guest_checkin_id : existing?.guest_checkin_id,
          guest_checkin_name: typeof d.guest_checkin_name !== "undefined" ? d.guest_checkin_name : existing?.guest_checkin_name,
          linkedToPrevious: typeof d.linkedToPrevious !== "undefined" ? d.linkedToPrevious : existing?.linkedToPrevious,
          scheduleLinkParentKey: typeof d.scheduleLinkParentKey !== "undefined" ? d.scheduleLinkParentKey : existing?.scheduleLinkParentKey,
          therapistCleared: typeof d.therapistCleared !== "undefined" ? d.therapistCleared : existing?.therapistCleared,
        });

        const nextEntry = {
          ...(existing || {}),
          ...projectedOverride,
        };
        // Sidebar selectedServices can still hold stale hydrate values even when Home
        // keeps the drag override — that makes Save write the old time/therapist.
        if (blockStaleTimeRevert || blockStaleTherapistRevert) {
          try {
            window.dispatchEvent(
              new CustomEvent("reapplyCalendarDragPrefill", {
                detail: {
                  itemId: d.itemId,
                  bookingId: d.bookingId,
                  start: nextEntry?.start ?? null,
                  end: nextEntry?.end ?? null,
                  absoluteStartMinutes: nextEntry?.absoluteStartMinutes ?? null,
                  therapistId: nextEntry?.therapistId ?? null,
                  therapistName: nextEntry?.therapistName ?? null,
                },
              })
            );
          } catch {}
        }

        // User (or chain) moved to a new non-original time — treat that as the new protected schedule.
        if (
          !blockStaleTimeRevert &&
          incomingDiffersFromExisting &&
          !incomingMatchesOriginal &&
          (incomingAbs != null || incomingStart != null)
        ) {
          rememberCalendarDragSchedule(d.itemId, {
            start: nextEntry?.start ?? incomingStart,
            end: nextEntry?.end ?? null,
            abs: typeof nextEntry?.absoluteStartMinutes === "number"
              ? nextEntry.absoluteStartMinutes
              : incomingAbs,
            service_at: nextEntry?.service_at ?? null,
            therapistId: nextEntry?.therapistId ?? dragTherapistId ?? null,
            therapistName: nextEntry?.therapistName ?? dragMemory?.therapistName ?? null,
            roomId: nextEntry?.roomId ?? dragMemory?.roomId ?? null,
            roomName: nextEntry?.roomName ?? dragMemory?.roomName ?? null,
            originalAbs: originalAbs ?? dragMemory?.originalAbs ?? null,
            originalStart: originalStart ?? dragMemory?.originalStart ?? null,
            originalTherapistId: dragMemory?.originalTherapistId ?? null,
          });
        }

        return {
          ...prev,
          [d.itemId]: nextEntry,
        };
      });
    };
    window.addEventListener('editOverrideUpdate', onEditOverrideUpdate);
    return () => window.removeEventListener('editOverrideUpdate', onEditOverrideUpdate);
  }, [
    applyDragOverrides,
    normalizeComparableClock,
    rememberCalendarDragSchedule,
  ]);

  // Helper function to find transitive overlap groups (clusters of connected bookings)
  const getTransitiveOverlapGroup = (seedBookings, allColumnBookings) => {
    let group = [...seedBookings];
    // Use string IDs to ensure uniqueness and proper Set behavior
    let visitedIds = new Set(group.map(b => `${b.booking_id}:${b.item_id || 'main'}`));
    let queue = [...group];

    while (queue.length > 0) {
      const current = queue.shift();
      const currentStart = getBookingAbsoluteStartMinutes(current);
      const currentAdjustedEnd = getBookingAbsoluteEndMinutes(current);

      // Find all bookings that overlap with current
      const overlaps = allColumnBookings.filter(other => {
        const otherId = `${other.booking_id}:${other.item_id || 'main'}`;
        if (visitedIds.has(otherId)) return false;
        
        const otherStart = getBookingAbsoluteStartMinutes(other);
        const otherAdjustedEnd = getBookingAbsoluteEndMinutes(other);
        
        // Check overlap: Start A < End B && End A > Start B
        return currentStart < otherAdjustedEnd && currentAdjustedEnd > otherStart;
      });

      overlaps.forEach(b => {
        const bId = `${b.booking_id}:${b.item_id || 'main'}`;
        visitedIds.add(bId);
        group.push(b);
        queue.push(b);
      });
    }
    
    // Sort group by start time, then booking ID for consistent order
    return group.sort((a, b) => {
      const startA = getBookingAbsoluteStartMinutes(a);
      const startB = getBookingAbsoluteStartMinutes(b);
      if (startA !== startB) return startA - startB;
      return String(a.booking_id).localeCompare(String(b.booking_id));
    });
  };

  const getBookingOwnerSlotStartMinutes = (booking) => {
    const bookingStartMinutes = getBookingAbsoluteStartMinutes(booking);
    if (typeof bookingStartMinutes !== "number" || Number.isNaN(bookingStartMinutes)) {
      return null;
    }
    return Math.max(0, Math.floor(bookingStartMinutes / tableInterval) * tableInterval);
  };

  const getNextColumnBookingOwnerSlotStartMinutes = (seedBookings, allColumnBookings) => {
    if (!Array.isArray(seedBookings) || seedBookings.length === 0 || !Array.isArray(allColumnBookings)) {
      return null;
    }

    const seedBookingIds = new Set(
      seedBookings.map((booking) => `${booking.booking_id}:${booking.item_id || "main"}`)
    );
    const renderedGroupEndMinutes = Math.max(
      ...seedBookings.map((booking) => getBookingAbsoluteEndMinutes(booking))
    );

    const nextBooking = allColumnBookings
      .filter((booking) => {
        const bookingId = `${booking.booking_id}:${booking.item_id || "main"}`;
        if (seedBookingIds.has(bookingId)) return false;
        return getBookingAbsoluteStartMinutes(booking) >= renderedGroupEndMinutes;
      })
      .sort((a, b) => getBookingAbsoluteStartMinutes(a) - getBookingAbsoluteStartMinutes(b))[0];

    return nextBooking ? getBookingOwnerSlotStartMinutes(nextBooking) : null;
  };

  // Compute headers explicitly locally since 'headers' variable seems elusive or stale
  // const displayHeaders = useMemo(() => {
  //   if (isRoomFilterActive) {
  //     return filteredRoomHeaders;
  //   }
  //   return homeStaffList.filter((staff) => {
  //     const matchesGender = !bookingFilters.gender || bookingFilters.gender.length === 0 || bookingFilters.gender.includes(staff.gender?.toLowerCase());
  //     const matchesSelected = !bookingFilters.staffIds || bookingFilters.staffIds.length === 0 || bookingFilters.staffIds.includes(staff.id);
  //     const matchesSearch = (searchQuery || "").trim() === "" || staff.name?.toLowerCase().includes((searchQuery || "").toLowerCase()) || String(staff.id).includes(searchQuery || "");
  //     return matchesGender && matchesSelected && matchesSearch;
  //   });
  // }, [homeStaffList, bookingFilters, searchQuery, isRoomFilterActive, filteredRoomHeaders]);

  // const currentHeaders = displayHeaders;
  // const isSmallWidth = currentHeaders?.length <= 9;
  // const sidebarClass = isSidebarVisible 
  //   ? (isSmallWidth ? styles.rightSidebarOpenSmall : styles.rightSidebarOpen)
  //   : "";
  

  logger.log("newAppointmentSlots", newAppointmentSlots);


  const shouldShowMultiServiceBorder =
    isNewBookingSlotActive && newAppointmentSlots.length > 1;

  return (
    <div className={styles.home}>
      {/* Ghost card for drag preview (only for new booking placeholders) */}
      {isDraggingCard && ghostCard && (!ghostCard?.booking_id && ghostCard?.isPlaceholder === true) && (() => {
        const slot = ghostCard;
        const duration = slot.duration || 15;
        const matchingService = selectedServices?.find(service => service.counterKey === slot.counterKey) ||
          paxes?.flatMap((p) => p.selectedServices || [])?.find((s) => s.counterKey === slot.counterKey);
        
        // Build icon tags for the ghost card
        // Check service name for natal and slimming tags
        const serviceNameForTags = matchingService?.name || matchingService?.service_name || slot?.serviceName || slot?.service?.name || "";
        const serviceNameForTagsStr = typeof serviceNameForTags === 'string' ? serviceNameForTags : String(serviceNameForTags || "");
        const rawRequest = {
          // isNewUser: slot?.customer?.isNewUser || false,
          isMember: slot?.customer?.isMember || false,
          noMembership: slot?.customer?.noMembership || false,
          China: slot?.customer?.China || false,
          thai: slot?.customer?.thai || false,
          malaysian: slot?.customer?.malaysian || false,
          singapore: slot?.customer?.singapore || false,
          indonesian: slot?.customer?.indonesian || false,
          male: slot?.customer?.male || false,
          female: slot?.customer?.female || false,
          requested_therapist: slot?.requested_therapist || false,
          requested_person: slot?.requested_person || 0,  
          natal: serviceNameForTagsStr.toLowerCase().includes("natal") || false,
          slimming: serviceNameForTagsStr.toLowerCase().includes("slimming") || false,
        };
        
        const room = slot?.room;
        const roomName = room?.name || '';
        const hasRequestedRoom = matchingService?.requested_room === 1 || slot?.requested_room === 1;
        const currentNote = isEditBookingActive ? editedItemData?.note : itemData?.note;
        const hasNotes = !!(currentNote && currentNote.trim() && currentNote.length > 0);
        const currentWebNotes = isEditBookingActive ? editedItemData?.web_notes : itemData?.web_notes;
        const hasWebNotes = !!(currentWebNotes && currentWebNotes.trim() && currentWebNotes.length > 0);
        
        const requestIconTags = buildTagIcons(rawRequest, BOOKING_TAG_ICONS);
        const roomIconTags = buildTagIcons(hasRequestedRoom ? { hasRequestedRoom: true } : {}, BOOKING_TAG_ICONS, { room: roomName });
        const notesIconTags = buildTagIcons(hasNotes ? { haveNotes: true } : {}, BOOKING_TAG_ICONS, { note: currentNote });
        const webNotesIconTags = buildTagIcons(hasWebNotes ? { hasWebNotes: true } : {}, BOOKING_TAG_ICONS, { webNotes: currentWebNotes });
        const hasIcon = requestIconTags.length > 0 || roomIconTags.length > 0 || notesIconTags.length > 0 || webNotesIconTags.length > 0;

        const {
          pinkCard,
          orangeCard,
          yellowCard,
        } = getDraftCardColorTags({
          matchingService,
          slot,
          selectedRooms,
          homeRoomList,
        });

        return (
          <div
            className={`
              ${styles.newAppointmentBlock}
               ${duration <= 30 ? styles.hoverCard : ''}
               ${duration <= 15 ? styles.card15 : ''}
               ${duration <= 30 ? styles.card30 : ''}
               ${hasIcon && styles.cardHasIcon}
               ${pinkCard && styles.pinkCard}
               ${orangeCard && styles.orangeCard}
               ${yellowCard && styles.yellowCard}}
              ${styles.activeSlot} ${styles.ghostCard}

              `}
            data-ghost-overlay="true"
            style={{
              position: 'fixed',
              ...(() => {
                const leftPos = dragPosition.x - dragOffset.x;
                const topPos = dragPosition.y - dragOffset.y;
                const clamped = getClampedGhostCardPosition(leftPos, topPos, ghostCard.rect.width, ghostCard.rect.height);
                return {
                  left: `${clamped.left}px`,
                  top: `${clamped.top}px`,
                };
              })(),
              width: `${ghostCard.rect.width}px`,
              height: `${ghostCard.rect.height}px`,
              zIndex: 1001,
              opacity: 1,
              pointerEvents: 'none',
              cursor: 'grabbing',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
            }}
          >
            <p style={{ 
              color: '#000',
              background:'transparent',
              display: 'inline-block',
              fontSize: '10px'
            }}>
              {dropTime?.value
                ? formatTimelineTimeLabel(dropTime.value, dropTime.absoluteMinutes)
                : getDraftCardTimeLabel(slot)}
            </p>
            {/* Show service name if available */}
            {(slot.serviceName || matchingService?.name || matchingService?.service_name) &&
              <p className={styles.serviceName}>{duration} mins {slot.serviceName || matchingService?.name || matchingService?.service_name}</p>
            }

            {/* Show customer details or "New Appointment" */}
            {slot?.customer && Object.keys(slot?.customer).length > 0 ? (
              <div className={styles.customerDetails}>
                <p className={`${styles.customerNumber} ${styles.bold}`}>{slot?.customer?.contact_number}</p>
                {duration > 30 && <p className={styles.customerName}>{slot?.customer?.name || slot?.customer?.lastname}</p>}
              </div>
            ) : (
              <>
                <p>New Appointment</p>
              </>
            )}

            <div className={styles.iconRow}>
              {requestIconTags.map((icon, i) => (
                <img
                  key={i}
                  src={icon.icon}
                  alt={icon.title}
                  title={icon.title}
                  className={styles.iconImage}
                />
              ))}

              {roomIconTags.map((icon, i) => (
                <img
                  key={i}
                  src={icon.icon}
                  alt={icon.title}
                  title={icon.title}
                  className={styles.iconImage}
                />
              ))}

              {notesIconTags.map((icon, i) => (
                <img
                  key={`notes-${i}`}
                  src={icon.icon}
                  alt={icon.title}
                  title={icon.title}
                  className={styles.iconImage}
                />
              ))}
            </div>
          </div>
        );
      })()}
      
      <div className={`
        ${styles.content} 
        ${rightSidebarOpen ? styles.rightSidebarOpen : ''}
        `} ref={pageRef}>
        <ToastContainer position="bottom-center" autoClose={1500} />
        
        {/* Header - always at the top */}
        <Header 
          onHardQueueToggle={() => setIsHardQueueSidebarOpen(prev => !prev)} 
          isHardQueueSidebarOpen={isHardQueueSidebarOpen}
          onManualReload={handleManualReload}
          isOutletExtended={isOutletExtended}
          onDateContainerChange={handleDateContainerChange}
          isTableLoading={isTableLoading}
          isRoomFilterTransitionLoading={isRoomFilterTransitionLoading}
        />
        
        {/* Main layout below header - contains sidebar and content */}
        <div className={`${styles.mainLayout} ${isHardQueueSidebarOpen ? styles.withSidebar : ""}`}>
          {/* Hard Queue Sidebar */}
          <HardQueueSidebar 
            isOpen={isHardQueueSidebarOpen} 
            onClose={() => setIsHardQueueSidebarOpen(false)} 
          />
          
          <div className={styles.contentWrapper}>
          <div className={`
            ${styles.bookingWrapper} 
            prevent-outside-click
            ${rightSidebarOpen ? styles.rightSidebarOpen : ''}
            ${isCustomerDetailsOpen ? styles.scrollLocked : ''}
            ${
              // Show noData when "No room selected" placeholder is showing
              (isRoomFilterActive && hasRoomsLoaded && homeRoomList.length > 0 && filteredRoomIds.length === 0) ||
              // Show noData when "No rooms found!" placeholder is showing
              (isRoomFilterActive && hasRoomsLoaded && homeRoomList.length === 0) ||
              // Show noData when "No therapist selected" placeholder is showing
              (!isRoomFilterActive && hasStaffLoaded && homeStaffList.length > 0 && filteredRoomHeaders.length === 0)
                ? styles.noData 
                : ''
            }
            `} ref={bookingWrapperRef}>
            {(isTableLoading || isRoomFilterTransitionLoading) ? (
              <div className={styles.centeredLoader}>
                <Loader />
              </div>
            ) : (
              <table ref={bookingTableRef} className={`${styles.bookingTable} ${isRoomFilterActive ? styles.roomTable : styles.therapistTable} table ${styles[intervalClass]} ${isDraggingCard ? styles.isDragging : ''}`} onMouseUp={() => setIsDragging(false)}>
                <thead>
                  <tr>
                    <th>Time</th>
                    {isRoomFilterActive ? (
                      !hasRoomsLoaded ? (
                        <th></th>
                      ) : homeRoomList.length === 0 ? (
                        <th className={styles.noTherapistSelectedTH}>
                          <div className={styles.noTherapistSelected}>
                            <Placeholder
                              icon={
                                <img
                                  src={BOOKING_TAG_ICONS.hasRequestedRoom}
                                  alt="Room"
                                  width="24"
                                  height="24"
                                  style={{ filter: "brightness(0) invert(1)" }}
                                />
                              }
                              heading="No rooms found!"
                              para="No rooms available for this outlet"
                            />
                          </div>
                        </th>
                      ) : filteredRoomIds.length > 0 ? (
                        homeRoomList
                          .filter(room => filteredRoomIds.includes(room.room_id))
                          .map(room => (
                            <th
                              key={room.room_id}
                              data-room-id={room.room_id}
                              colSpan={room.items.length}
                              className={getRoomClasses(room)}
                            >
                              <RoomCard room={room} />
                            </th>
                          ))
                      ) : (
                        <th className={styles.noTherapistSelectedTH}>
                          <div className={styles.noTherapistSelected}>
                            <Placeholder
                              icon={
                                <img
                                  src={BOOKING_TAG_ICONS.hasRequestedRoom}
                                  alt="Room"
                                  width="24"
                                  height="24"
                                  style={{ filter: "brightness(0) invert(1)" }}
                                />
                              }
                              heading="No room selected"
                              para="No room selected in filter"
                            />
                          </div>
                        </th>
                      )
                    ) : !hasStaffLoaded ? (
                      <th></th>
                    ) : filteredRoomHeaders.length > 0 ? (
                      filteredRoomHeaders.map(header => (
                        <th key={header.id} data-therapist-id={header.id}>
                          <TherapistCard 
                            header={header} 
                            staffTiming={staffTiming}
                            onAttendanceChange={fetchBreaks}
                            activeDropdownId={activeDropdownId}
                            setActiveDropdownId={setActiveDropdownId}
                          />
                        </th>
                      ))
                    ) : homeStaffList.length > 0 ? (
                      <th className={styles.noTherapistSelectedTH}>
                        <div className={styles.noTherapistSelected}>
                          <Placeholder
                            icon={<StaffIcon width="24" height="24" color="white" />}
                            heading="No therapist selected"
                            para="No therapists are selected in filter"
                          />
                        </div>
                      </th>
                    ) : (
                      <th className={styles.noTherapistSelectedTH}>
                        <div className={styles.noTherapistSelected}>
                          <Placeholder
                            icon={<StaffIcon width="24" height="24" color="white" />}
                            heading="No therapists found!"
                            para="No therapists available for this outlet"
                          />
                        </div>
                      </th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {(() => {
                    let rowspanMap = {}; // Track merged rows
                    const now = new Date();
                    const currentHour = now.getHours();
                    const currentMinutes = now.getMinutes();
                    const currentTime24 = `${currentHour < 10 ? "0" : ""}${currentHour}:${currentMinutes < 10 ? "0" : ""
                      }${currentMinutes}`;
                    const nowMins = getSlotAbsoluteMinutes({ time24: currentTime24 });
                    return timeSlots.map((slot, rowIndex) => {
                      const nextSlot = timeSlots[rowIndex + 1]
                        ? timeSlots[rowIndex + 1]
                        : null;
                      const isLastRow = rowIndex === timeSlots.length - 1;
                      const slotStartMins = getSlotAbsoluteMinutes(slot);
                      const isCurrentTimeSlot =
                        slot.time24 &&
                        currentTime24 &&
                        (nextSlot
                          ? slot.time24 <= currentTime24 && currentTime24 < nextSlot.time24
                          : isLastRow &&
                            nowMins >= slotStartMins &&
                            nowMins < slotStartMins + tableInterval);
                      return (
                        <tr
                          id={isCurrentTimeSlot ? "current-time-row" : ""}
                          ref={isCurrentTimeSlot ? currentTimeRowRef : null}
                          key={rowIndex}
                          className={slot.isBold ? styles.boldHour : styles.normalSlot}
                        >
                          <td className={!slot.isBold ? styles.hideTime : ""}>
                            {slot.isBold && (
                              <span className={styles.timeText}>
                                {getSlotTimeLabel(slot)}
                              </span>
                            )}
                            {linePosition !== null && linePosition >= rowIndex * getCellHeight(tableInterval) && linePosition < (rowIndex + 1) * getCellHeight(tableInterval) &&
                              (
                                <div className={styles.currentTimeline} style={{ top: `${Math.round(linePosition - rowIndex * getCellHeight(tableInterval))}px` }} />
                              )
                            }
                          </td>
                          {(() => {
                            const currentHeaders = filteredRoomHeaders;
                            // If headers are empty or still loading, render a placeholder td to maintain table structure
                            if (!currentHeaders || currentHeaders.length === 0) {
                              return <td key="placeholder" className={styles.placeholderTD}></td>;
                            }
                            return currentHeaders.map((header, colIndex) => {
                            // const cellKey = getCellKey(rowIndex, colIndex);
                            const slotTime = getSlotAbsoluteMinutes(slot);
                            const headerId = header.id;
                            const formattedDate = formatDateToDDMMYYYY(selectedDate);
                            const therapistDayData = staffTiming?.[headerId]?.[formattedDate]?.[0];
                            const slotTimeInMinutes = getSlotAbsoluteMinutes(slot);

                            let therapistIsOnLeave = true;
                            let therapistIsAvailable = false;

                            // For room mode, we don't check therapist availability
                            if (!isRoomFilterActive && therapistDayData) {
                              const availabilityState = isSlotWithinTherapistAvailability(slotTimeInMinutes, therapistDayData);
                              therapistIsOnLeave = availabilityState.therapistIsOnLeave;
                              therapistIsAvailable = availabilityState.therapistIsAvailable;
                            } else if (isRoomFilterActive) {
                              // In room mode, always consider available (no therapist timing restrictions)
                              therapistIsOnLeave = false;
                              therapistIsAvailable = true;
                            }
                            
                            const shouldIgnoreOutletDisabled = shouldIgnoreOutletDisabledForExpandedSchedule(
                              slotTimeInMinutes,
                              therapistDayData,
                              therapistIsAvailable
                            );
                            const isGreyedOut =
                              (slot.disabled && !shouldIgnoreOutletDisabled) ||
                              therapistIsOnLeave ||
                              !therapistIsAvailable;
                            
                            // Check if this column belongs to a "couples" room when in room filter mode
                            let isCouplesRoom = false;
                            let isFirstInCouplesRoom = false;
                            let isLastInCouplesRoom = false;
                            if (isRoomFilterActive && header.id) {
                              const room = homeRoomList.find((r) =>
                                Array.isArray(r.items) &&
                                r.items.some((item) => Number(item.item_id) === Number(header.id))
                              );
                              if (room) {
                                const roomClasses = getRoomClasses(room);
                                isCouplesRoom = roomClasses.includes('couples');
                                
                                if (isCouplesRoom) {
                                  // Find all columns that belong to this same parent room
                                  const roomColumns = currentHeaders
                                    .map((h, idx) => ({ header: h, index: idx }))
                                    .filter(({ header: h }) => {
                                      const hParent = homeRoomList.find((r) =>
                                        Array.isArray(r.items) &&
                                        r.items.some((item) => Number(item.item_id) === Number(h.id))
                                      );
                                      return hParent?.room_id === room.room_id;
                                    });
                                  
                                  // Check if this is the first column in the room group
                                  isFirstInCouplesRoom = roomColumns[0]?.index === colIndex;
                                  
                                  // Check if this is the last column in the room group
                                  isLastInCouplesRoom = roomColumns[roomColumns.length - 1]?.index === colIndex;
                                }
                              }
                            }
                            
                            // Filter bookings based on current mode
                            const columnBookings = bookingsByColumnIndex.get(colIndex) || [];
                            const therapistBreaks = !isRoomFilterActive
                              ? (breaksByTherapistId.get(Number(headerId)) || [])
                              : [];

                            const currentSlotTime = getSlotAbsoluteMinutes(slot);
                            const nextSlotTime = nextSlot
                              ? getSlotAbsoluteMinutes(nextSlot)
                              : currentSlotTime + tableInterval;

                            // Anchor existing bookings to the row that contains their actual start time.
                            // This keeps them aligned with draft cards that start at off-grid times
                            // such as 12:05 or 12:25, so overlap width splitting uses the same cell.
                            const bookingsStartingInThisCell = columnBookings.filter((booking) => {
                              const bookingStartMins = getBookingAbsoluteStartMinutes(booking);
                              return bookingStartMins >= currentSlotTime && bookingStartMins < nextSlotTime;
                            });
                            
                            // STEP 2: Use Transitive Grouping to find ALL connected overlaps
                            let overlappingBookedSlotsInThisCell = [];
                            
                            if (bookingsStartingInThisCell.length > 0) {
                              // If there are any bookings in this cell, find their entire connected group
                              // This ensures that A overlapping B and B overlapping C are treated as one group {A,B,C}
                              overlappingBookedSlotsInThisCell = getTransitiveOverlapGroup(bookingsStartingInThisCell, columnBookings);
                            }

                            const isGroupOverlap = overlappingBookedSlotsInThisCell.length > 1;
                            
                            const bookingDetails = (!isGroupOverlap && bookingsStartingInThisCell.length === 1)
                              ? bookingsStartingInThisCell[0] 
                              : null;

                            // const isSelected = selectedCells.has(cellKey);
                            const isSelected = bookingDetails?.status.toLowerCase().replace(/\s+/g, "").replace(/-/g, "").replace(/[()]/g, "") === 'holding';

                            // Check if this booking is being dragged
                            const isDraggingThisBooking = isDraggingCard && (
                              (bookingDetails && dragKey === `${bookingDetails.booking_id}:${bookingDetails?.item_id}`) ||
                              (overlappingBookedSlotsInThisCell.length > 0 && overlappingBookedSlotsInThisCell.some(b => dragKey === `${b.booking_id}:${b?.item_id}`))
                            );

                            
                            const shouldRenderGroup =
                              overlappingBookedSlotsInThisCell.length > 0 &&
                              bookingsStartingInThisCell.length > 0;
                            const currentRenderedBookings = shouldRenderGroup
                              ? overlappingBookedSlotsInThisCell
                              : (bookingDetails ? [bookingDetails] : []);
                            const nextExistingOwnerSlotStartMinutes = currentRenderedBookings.length > 0
                              ? getNextColumnBookingOwnerSlotStartMinutes(currentRenderedBookings, columnBookings)
                              : null;
                            if (rowspanMap[rowIndex]?.[header.id]) {
                              return null;
                            }

                            let rowspan = 1;

                            // Calculate rowspan for overlapping booked slots.
                            // Only skip the group that contains the dragged card — other columns/cards
                            // must keep rowspan or the grid collapses for the whole drag and flickers on drop.
                            if (overlappingBookedSlotsInThisCell.length > 0 && !isDraggingThisBooking) {
                              const latestEnd = Math.max(...overlappingBookedSlotsInThisCell.map((booking) => getBookingAbsoluteEndMinutes(booking)));
                              const cappedLatestEnd = typeof nextExistingOwnerSlotStartMinutes === "number"
                                ? Math.min(latestEnd, nextExistingOwnerSlotStartMinutes)
                                : latestEnd;

                              // Calculate rowspan based on the full overlapping range
                              let nextIndex = rowIndex + 1;
                              while (
                                nextIndex < timeSlots.length &&
                                getSlotAbsoluteMinutes(timeSlots[nextIndex]) < cappedLatestEnd
                              ) {
                                if (!rowspanMap[nextIndex]) {
                                  rowspanMap[nextIndex] = {};
                                }
                                rowspanMap[nextIndex][header.id] = true;
                                rowspan++;
                                nextIndex++;
                              }
                            } else if (bookingDetails && !isDraggingThisBooking) {
                               // Original logic for non-overlapping bookings (skip only the dragged card)
                               const adjustedEndMins = getBookingAbsoluteEndMinutes(bookingDetails);
                               const cappedAdjustedEndMins = typeof nextExistingOwnerSlotStartMinutes === "number"
                                 ? Math.min(adjustedEndMins, nextExistingOwnerSlotStartMinutes)
                                 : adjustedEndMins;
                               let nextIndex = rowIndex + 1;
                               while (
                                 nextIndex < timeSlots.length &&
                                 getSlotAbsoluteMinutes(timeSlots[nextIndex]) < cappedAdjustedEndMins
                               ) {
                                 if (!rowspanMap[nextIndex]) {
                                   rowspanMap[nextIndex] = {};
                                 }
                                 rowspanMap[nextIndex][header.id] = true;
                                 rowspan++;
                                 nextIndex++;
                               }
                             }
                            const primaryVisibleBookings = [
                              ...overlappingBookedSlotsInThisCell,
                              ...(bookingDetails && !overlappingBookedSlotsInThisCell.some(
                                (booking) => String(booking.booking_id) === String(bookingDetails.booking_id) &&
                                  String(booking.item_id || '') === String(bookingDetails.item_id || '')
                              ) ? [bookingDetails] : []),
                            ];
                            // Always expand the same way during drag and after drop so overlap width /
                            // indices do not jump when isDraggingCard flips false.
                            const visibleExistingBookings = (() => {
                              const sortedColumnBookings = [...columnBookings].sort((a, b) => {
                                const startDiff = getBookingAbsoluteStartMinutes(a) - getBookingAbsoluteStartMinutes(b);
                                if (startDiff !== 0) return startDiff;
                                return `${a.booking_id}:${a.item_id || "main"}`.localeCompare(`${b.booking_id}:${b.item_id || "main"}`);
                              });
                              const visibleMap = new Map(
                                primaryVisibleBookings.map((booking) => [`${booking.booking_id}:${booking.item_id || "main"}`, booking])
                              );
                              let expanded = true;

                              while (expanded) {
                                expanded = false;
                                const currentVisibleBookings = Array.from(visibleMap.values());
                                if (currentVisibleBookings.length === 0) break;

                                const currentVisibleEndMinutes = Math.max(
                                  ...currentVisibleBookings.map((booking) => getBookingAbsoluteEndMinutes(booking))
                                );

                                sortedColumnBookings.forEach((booking) => {
                                  const bookingId = `${booking.booking_id}:${booking.item_id || "main"}`;
                                  if (visibleMap.has(bookingId)) return;
                                  const bookingStartMins = getBookingAbsoluteStartMinutes(booking);
                                  // Only include bookings that truly overlap the currently visible range.
                                  // Adjacent bookings should keep their own owner row instead of being merged.
                                  if (
                                    bookingStartMins >= currentSlotTime &&
                                    bookingStartMins < currentVisibleEndMinutes
                                  ) {
                                    visibleMap.set(bookingId, booking);
                                    expanded = true;
                                  }
                                });
                              }

                              return Array.from(visibleMap.values());
                            })();

                            if (visibleExistingBookings.length > 0 && !isDraggingThisBooking) {
                              const latestVisibleExistingBookingEnd = Math.max(
                                ...visibleExistingBookings.map((booking) => getBookingAbsoluteEndMinutes(booking))
                              );
                              const cappedLatestVisibleExistingBookingEnd = typeof nextExistingOwnerSlotStartMinutes === "number"
                                ? Math.min(latestVisibleExistingBookingEnd, nextExistingOwnerSlotStartMinutes)
                                : latestVisibleExistingBookingEnd;
                              let nextIndex = rowIndex + rowspan;
                              while (
                                nextIndex < timeSlots.length &&
                                getSlotAbsoluteMinutes(timeSlots[nextIndex]) < cappedLatestVisibleExistingBookingEnd
                              ) {
                                if (!rowspanMap[nextIndex]) {
                                  rowspanMap[nextIndex] = {};
                                }
                                rowspanMap[nextIndex][header.id] = true;
                                rowspan++;
                                nextIndex++;
                              }
                            }

                            const visibleCellEndMinutes = rowIndex + rowspan < timeSlots.length
                              ? getSlotAbsoluteMinutes(timeSlots[rowIndex + rowspan])
                              : currentSlotTime + (rowspan * tableInterval);
                            const primaryVisibleBookingIds = new Set(
                              primaryVisibleBookings.map((booking) => `${booking.booking_id}:${booking.item_id || "main"}`)
                            );
                            const sequentialBookedSlotsInVisibleCell = visibleExistingBookings.filter((booking) => {
                              const bookingId = `${booking.booking_id}:${booking.item_id || "main"}`;
                              return !primaryVisibleBookingIds.has(bookingId);
                            });
                            const contentToRender = bookingDetails || (shouldRenderGroup ? overlappingBookedSlotsInThisCell : []);
                            const hasContent = (Array.isArray(contentToRender) ? contentToRender.length > 0 : !!contentToRender) || sequentialBookedSlotsInVisibleCell.length > 0;
                            // Use the same rowspan the <td> will render with so partial schedule
                            // greying (e.g. available from 11:05 inside an 11:00 cell) works for empty cells too.
                            const effectiveRowspan = (bookingDetails || overlappingBookedSlotsInThisCell.length > 0) ? rowspan : 1;
                            const therapistTimingCellStyle = getMergedCellBackgroundStyle({
                              rowIndex,
                              rowspan: effectiveRowspan,
                              therapistDayData,
                              therapistIsOnLeave,
                            });
                            return (
                              <td
                                key={header.id}
                                className={`
                                  ${styles.slot}
                                  ${isSelected ? styles.selected : ""}
                                    ${isGreyedOut && !therapistTimingCellStyle ? styles.greyedOut : ""}
                                    ${isDraggingCard && isGreyedOut && !bookingDetails ? styles.dragOverride : ""}
                                  // ${slot.partial ? styles.partialSlot : ""}
                                  ${!bookingDetails ? `${styles.emptySlot} emptySlot` : ""}
                                  ${!bookingDetails && isViewOnlyOutlet ? styles.disabledSlot : ""}
                                  ${isCouplesRoom ? styles.couplesRoomCell : ""}
                                  ${isFirstInCouplesRoom ? styles.couplesRoomCellFirst : ""}
                                  ${isLastInCouplesRoom ? styles.couplesRoomCellLast : ""}
                                  `}
                                style={therapistTimingCellStyle || undefined}
                                rowSpan={effectiveRowspan}
                                onMouseDown={(e) => handleMouseDown(e, rowIndex, colIndex, isGreyedOut)}
                                onMouseOver={() => handleMouseOver(rowIndex, colIndex)}
                                ref={isSelected ? newBookingCardRef : null}
                                data-row={rowIndex}
                                data-col={colIndex}
                              >
                                {hasContent // Only render content if we determined we should (based on start of group and drag state)
                                  ? (() => {
                                    // Handle overlapping booked slots
                                    if (overlappingBookedSlotsInThisCell.length > 0) {
                                      // FIXED: Split width equally and maintain stable positioning for overlapping existing bookings
                                      // Split width equally between existing overlapped bookings (in this cell) and any overlapping drafts and breaks
                                      const cellStartMinutes = getSlotAbsoluteMinutes(slot);
                                      const mergedStartMinutes = Math.min(...overlappingBookedSlotsInThisCell.map((b) => getBookingAbsoluteStartMinutes(b)));
                                      const mergedEndMinutes = Math.max(...overlappingBookedSlotsInThisCell.map((b) => getBookingAbsoluteEndMinutes(b)));
                                      const draftSlotsInThisCellFlat = (!isViewOnlyOutlet && (isNewBookingSlotActive || (isEditBookingActive && (draftSlotsForUI.length || 0) > 0)))
                                        ? (draftSlotsForUI?.filter(s => {
                                            if (s.colIndex !== colIndex) return false;
                                            if (isDraggingCard && draggedCard?.counterKey === s.counterKey) return false;
                                            if (!isDraftSlotOnSelectedTimeline(s)) return false;
                                            
                                            const sStart = getDraftSlotAbsoluteStartMinutes(s);
                                            const adjustedSEnd = getDraftSlotAbsoluteEndMinutes(s);
                                            return sStart < mergedEndMinutes && adjustedSEnd > mergedStartMinutes;
                                          }) || [])
                                        : [];
                                      
                                      // Count breaks in this cell
                                      const breaksInThisCellCount = !isRoomFilterActive ? therapistBreaks.filter(breakItem => {
                                        const breakStart = getBreakAbsoluteStartMinutes(breakItem);
                                        const breakEnd = getBreakAbsoluteEndMinutes(breakItem);
                                        return breakStart < mergedEndMinutes && breakEnd > mergedStartMinutes;
                                      }).length : 0;
                                      
                                      // Keep the dragged card in the width count so siblings do not
                                      // expand during drag and collapse on drop (flicker).
                                      const combinedCount = overlappingBookedSlotsInThisCell.length + draftSlotsInThisCellFlat.length + breaksInThisCellCount;
                                      const groupWidth = 100 / Math.max(combinedCount, 1); // Each card takes equal width (existing first, then drafts, then breaks)
                                      
                                      // Sort overlapping bookings for stable positioning (by start time, then booking_id)
                                      const sortedOverlappingBookings = [...overlappingBookedSlotsInThisCell].sort((a, b) => {
                                        const aStart = getBookingAbsoluteStartMinutes(a);
                                        const bStart = getBookingAbsoluteStartMinutes(b);
                                        if (aStart !== bStart) return aStart - bStart;
                                        return String(a.booking_id).localeCompare(String(b.booking_id));
                                      });
                                      
                                      return sortedOverlappingBookings.map((booking, originalIndex) => {
                                        const cellHeight = getCellHeight(tableInterval);
                                        const topOffset = ((getBookingAbsoluteStartMinutes(booking) - getSlotAbsoluteMinutes(slot)) / tableInterval) * cellHeight;
                                        const bookingHeight = getBookingHeight(booking.duration, tableInterval);
                                        const status = booking.status.toLowerCase().replace(/\s+/g, "").replace(/-/g, "").replace(/[()]/g, "");
                                        
                                        const iconTags = buildTagIcons(
                                          booking.tags,
                                          BOOKING_TAG_ICONS,
                                          {
                                            therapist: booking?.therapistName,
                                            note: booking?.tags?.note,
                                            room: booking?.tags?.roomName,
                                          }
                                        );

                                     

                                        // Stable lane index (same during drag and after drop)
                                        const isDraggingThisCard = isDraggingCard && dragKey === `${booking.booking_id}:${booking?.item_id}`;
                                        const displayIndex = originalIndex;
                                        const shouldHighlightAllForBooking = selectedBookingId != null && String(selectedBookingId) === String(booking.booking_id);
                                        const hasIcon = booking?.tags?.isRequested || booking?.tags?.hasRequestedRoom || booking?.tags?.male || booking?.tags?.female || booking?.tags?.natal || booking?.tags?.slimming || booking?.tags?.haveNotes || booking?.tags?.indonesian || booking?.tags?.malaysian || booking?.tags?.singapore || booking?.tags?.China || booking?.tags?.thai || booking?.tags?.repeat || booking?.tags?.hasWebNotes;
                                        
                                        const requestedCoupleRoom = booking?.tags?.coupleRoom && booking?.tags?.hasRequestedRoom;
                                        const requestedShowerRoom = booking?.tags?.showerRoom && booking?.tags?.hasRequestedRoom;
                                        const scrubService = booking?.tags?.scrubService;

                                        const pinkCard = booking?.tags?.isRequested;
                                        const orangeCard = (requestedCoupleRoom) || (pinkCard && requestedCoupleRoom);
                                        const yellowCard = scrubService || (scrubService && requestedShowerRoom) || (pinkCard && requestedShowerRoom) || requestedShowerRoom;
                                        
                                        return (
                                          <div
                                            key={`exist-${booking.booking_id}-${booking.item_id || 'main'}-${rowIndex}-${colIndex}`}
                                            className={`
                                              ${styles.bookingInfo} 
                                              ${styles.bookedSlot} 
                                              ${styles["status" + status]}
                                              ${booking?.isVoided && styles.voidedSlot}
                                              ${hasIcon && styles.cardHasIcon}
                                              ${booking.duration <= 45 && styles.hoverCard} 
                                              ${booking.duration <= 20 && styles.card15}
                                              ${booking.duration <= 45 && styles.card30}
                                              ${pinkCard && styles.pinkCard}
                                              ${orangeCard && styles.orangeCard}
                                              ${yellowCard && styles.yellowCard}
                                              `}
                                            style={{
                                              position: "absolute",
                                              top: `${topOffset}px`,
                                              height: `${bookingHeight}px`,
                                              width: groupWidth === 100 ? 'calc(100% - 8px)' : `calc(${groupWidth}% - 2px)`,
                                              left: `${displayIndex * groupWidth}%`,
                                              cursor: (isDraggingCard && dragKey === `${booking.booking_id}:${booking?.item_id}`) ? 'grabbing' : 'grab',
                                              display: (deletedBookingID === booking.booking_id && (!booking.item_id || deletedBookingID !== draggedCard?.booking_id)) && "none",
                                              pointerEvents: booking.status === 'Holding' ? 'none' : 'auto',
                                              zIndex: 4,
                                              border: (!isDraggingCard && (String(activeCardKey) === String(`${booking.booking_id}:${booking?.item_id ?? ""}`) || shouldHighlightAllForBooking)) && '2px solid #000',
                                              boxSizing: 'border-box',
                                              opacity: (isDraggingCard && dragKey === `${booking.booking_id}:${booking?.item_id}`) ? 0 : 1,
                                              touchAction: 'none',
                                            }}
                                            data-booking-id={booking.booking_id}
                                            data-item-id={booking?.item_id || ""}
                                            onClick={(event) => booking && handleBookingCardClick(event, booking?.booking_id, booking?.item_id)}
                                            onMouseDown={stopCardMouseDown}
                                            onPointerDown={(e) => {
                                              if (booking.status === 'Holding') return;
                                              const startTime24 = (() => {
                                                const t = booking?.service_at?.split(" ")?.[1];
                                                if (t) return String(t).slice(0, 5);
                                                const st = booking?.startTime;
                                                if (st && /(AM|PM)/i.test(String(st))) {
                                                  try {
                                                    return convertTo24HourFormat(String(st));
                                                  } catch {}
                                                }
                                                if (typeof st === "string" && st.includes(":")) return st.slice(0, 5);
                                                return "";
                                              })();
                                              const endTime24 = (() => {
                                                if (startTime24) return calculateEndTime(startTime24, booking.duration || 15);
                                                const et = booking?.endTime;
                                                if (et && /(AM|PM)/i.test(String(et))) {
                                                  try {
                                                    return convertTo24HourFormat(String(et));
                                                  } catch {}
                                                }
                                                if (typeof et === "string" && et.includes(":")) return et.slice(0, 5);
                                                return "";
                                              })();


                                              const slotLike = {
                                                booking_id: booking.booking_id,
                                                item_id: booking.item_id,
                                                service_id: booking.service_id,
                                                service_at: booking.service_at,
                                                start_time: startTime24,
                                                end_time: endTime24,
                                                duration: booking.duration,
                                                therapistId: booking.therapistId,
                                                therapistName: booking.therapistName,
                                                therapistPager: booking.therapistPager,
                                                therapistGender: booking.therapistGender,
                                                roomId: booking.room_id,
                                                status: booking.status,
                                                payment_status: booking.payment_status,
                                                payment_status_id: booking.payment_status_id,
                                                user: booking.user,
                                                serviceName: booking.serviceName,
                                                service_category: booking.service_category,
                                                tags: booking.tags,
                                                therapist_type: booking.therapist_type,
                                                rowIndex,
                                                colIndex,
                                                isPlaceholder: false,
                                                requested_person: booking?.tags?.isRequested ? 1 : 0,
                                                requested_therapist: booking?.tags?.isRequested ? true : false,
                                                natal: booking?.tags?.natal || false,
                                                slimming: booking?.tags?.slimming || false,
                                                isMemberRateApplied: booking?.isMemberRateApplied || false,
                                                isMembershipApplied: booking?.isMembershipApplied || false,
                                                price_rate: booking?.price_rate || null,
                                              };
                                              handleCardMouseDown(e, slotLike);
                                            }}
                                          >
                                            
                                            {booking.status === 'Holding' ?
                                              <div className={styles.newBookingFromWeb}>
                                                {booking.panel === 'outlet' && <p className={styles.createdBy}>{booking?.createdBy}</p>}
                                                <p className={styles.bookingSource}>{
                                                  booking.panel === 'customer' ? 'Web' 
                                                  : booking.panel === 'widget' ? 'Widget' 
                                                  : booking.panel === 'outlet' && booking.duration >= 20 ? 'outlet' : ''} 
                                                </p>
                                                {booking.duration >= 40 ? <p>Booking in progress</p> : ''}
                                                {booking.panel === 'outlet' && <
                                                  HoldingCardTimer 
                                                    key={`timer-${booking.booking_id}-${booking.lastUpdated || 0}`}
                                                    holdingCardTime={booking.holdingCardTime}
                                                    bookingId={booking.booking_id}
                                                    onTimerExpired={handleTimerExpired} />}
                                              </div>
                                              :
                                              <div className={styles.upperBlock}>
                                                <div>
                                                  {/* Show drop time when dragging - REMOVED from original card, moved to Ghost Card */}
                                                  
                                                  <p 
                                                    className={styles.serviceName}
                                                  >
                                                    {`${booking.totalDuration} mins ${booking.serviceName} `}
                                                  </p>

                                                  <div 
                                                    className={styles.customerInfo}
                                                  >
                                                    <p className={`${styles.customerNumber} ${styles.bold}`}>{booking.user?.contact}</p>
                                                    {booking.guestIndex !==
                                                      null ? (
                                                      <>
                                                        <p className={styles.customerName}>{booking.user?.name}</p>
                                                        <p className={styles.paxName}>
                                                          {booking.memberName?.includes("→") ? booking.memberName.split("→")[1]?.trim() : ""}
                                                        </p>
                                                      </>
                                                    ) : (
                                                      <p className={styles.customerName}>{booking.user?.name}</p>
                                                    )}
                                                  <p className={`${styles.timeRange} ${styles.bold}`}>{getBookingCardTimeRangeLabel(booking)}</p>
                                                    
                                                  </div>
                                                </div>


                                                <div className={styles.iconRow}>
                                                  {iconTags.map((icon, i) => (
                                                    <img
                                                      key={i}
                                                      src={icon.icon}
                                                      alt={icon.title}
                                                      title={icon.title}
                                                      className={styles.iconImage}
                                                    />
                                                  ))}
                                                </div>

                                              </div>
                                            }
                                          </div>
                                        );
                                      });
                                    }

                                    // logic for non-overlapping bookings

                                    // CRITICAL FIX: Don't render bookingDetails if it's already in the overlapping group
                                    const isBookingInOverlapGroup = overlappingBookedSlotsInThisCell.some(
                                      b => String(b.booking_id) === String(bookingDetails?.booking_id) && 
                                           String(b.item_id || '') === String(bookingDetails?.item_id || '')
                                    );
                                    const renderSingleBookedSlot = (singleBooking, keySuffix) => {
                                      const cellHeight = getCellHeight(tableInterval);
                                      const topOffset = ((getBookingAbsoluteStartMinutes(singleBooking) - getSlotAbsoluteMinutes(slot)) / tableInterval) * cellHeight;
                                      const bookingHeight = getBookingHeight(singleBooking.duration, tableInterval);
                                      const status = singleBooking.status.toLowerCase().replace(/\s+/g, "").replace(/-/g, "").replace(/[()]/g, "");
                                      const iconTags = buildTagIcons(
                                        singleBooking.tags,
                                        BOOKING_TAG_ICONS,
                                        {
                                          therapist: singleBooking?.therapistName,
                                          note: singleBooking?.tags?.note,
                                          room: singleBooking?.tags?.roomName,
                                          userName: singleBooking?.user?.name,
                                          userNumber: singleBooking?.user?.contact,
                                        }
                                      );

                                      const assignedItem = isRoomFilterActive ? singleBooking?.therapistName : singleBooking?.tags?.roomName;
                                      const shouldHighlightAllForBooking = selectedBookingId != null && String(selectedBookingId) === String(singleBooking.booking_id);
                                      const hasIcon = singleBooking?.tags?.isRequested || singleBooking?.tags?.hasRequestedRoom || singleBooking?.tags?.male || singleBooking?.tags?.female || singleBooking?.tags?.natal || singleBooking?.tags?.slimming || singleBooking?.tags?.haveNotes || singleBooking?.tags?.indonesian || singleBooking?.tags?.malaysian || singleBooking?.tags?.singapore || singleBooking?.tags?.China || singleBooking?.tags?.thai || singleBooking?.tags?.repeat;
                                      
                                      const requestedCoupleRoom = singleBooking?.tags?.coupleRoom && singleBooking?.tags?.hasRequestedRoom;
                                      const requestedShowerRoom = singleBooking?.tags?.showerRoom && singleBooking?.tags?.hasRequestedRoom;
                                      const scrubService = singleBooking?.tags?.scrubService;
                                      const pinkCard = singleBooking?.tags?.isRequested;
                                      const orangeCard = (requestedCoupleRoom) || (pinkCard && requestedCoupleRoom);
                                      const yellowCard = scrubService || (scrubService && requestedShowerRoom) || (pinkCard && requestedShowerRoom) || requestedShowerRoom;
                                      
                                      return (
                                        <div className={`
                                          ${styles.bookingInfo}
                                          ${styles.bookedSlot}
                                          ${styles["status" + status]} 
                                          ${singleBooking?.duration <= 45 && styles.hoverCard} 
                                          ${singleBooking?.duration <= 20 && styles.card15}
                                          ${singleBooking?.duration <= 45 && styles.card30}
                                          ${pinkCard && styles.pinkCard}
                                          ${orangeCard && styles.orangeCard}
                                          ${yellowCard && styles.yellowCard}
                                          ${hasIcon && styles.cardHasIcon}
                                          `}
                                          key={`single-${singleBooking.booking_id}-${singleBooking.item_id || 'main'}-${keySuffix}`}
                                          style={{
                                            position: "absolute",
                                            top: `${topOffset}px`,
                                            height: `${bookingHeight}px`,
                                            ...(() => {
                                              const bookingStart = getBookingAbsoluteStartMinutes(singleBooking);
                                              const bookingEnd = getBookingAbsoluteEndMinutes(singleBooking);
                                              
                                              const draftSlotsInThisCellFlat = (!isViewOnlyOutlet && (isNewBookingSlotActive || (isEditBookingActive && (draftSlotsForUI.length || 0) > 0)))
                                                ? (draftSlotsForUI?.filter(s => {
                                                    if (s.colIndex !== colIndex) return false;
                                                    if (isDraggingCard && draggedCard?.counterKey === s.counterKey) return false;
                                                    if (!isDraftSlotOnSelectedTimeline(s)) return false;
                                                    
                                                    const sStart = getDraftSlotAbsoluteStartMinutes(s);
                                                    const adjustedSEnd = getDraftSlotAbsoluteEndMinutes(s);
                                                    return sStart < bookingEnd && adjustedSEnd > bookingStart;
                                                  }) || [])
                                                : [];
                                              
                                              const breaksInThisCellCount = !isRoomFilterActive ? therapistBreaks.filter(breakItem => {
                                                const breakStart = getBreakAbsoluteStartMinutes(breakItem);
                                                const breakEnd = getBreakAbsoluteEndMinutes(breakItem);
                                                return breakStart < bookingEnd && breakEnd > bookingStart;
                                              }).length : 0;
                                              
                                              const numDraftInCell = draftSlotsInThisCellFlat.length;
                                              const numOtherItems = numDraftInCell + breaksInThisCellCount;
                                              
                                              if (numOtherItems > 0) {
                                                const combinedCountForSingle = 1 + numOtherItems;
                                                return { width: `${100 / combinedCountForSingle}%`, left: `0%` };
                                              }
                                              return {};
                                            })(),
                                            zIndex: 4,
                                            cursor: (isDraggingCard && dragKey === `${singleBooking.booking_id}:${singleBooking?.item_id}`) ? 'grabbing' : 'grab',
                                            display: (deletedBookingID === singleBooking.booking_id && (!singleBooking.item_id || deletedBookingID !== draggedCard?.booking_id)) && "none",
                                            pointerEvents: singleBooking.status === 'Holding' ? 'none' : 'auto',
                                            border: (!isDraggingCard && (String(activeCardKey) === String(`${singleBooking.booking_id}:${singleBooking?.item_id ?? ""}`) || shouldHighlightAllForBooking)) && '2px solid #000',
                                            opacity: (isDraggingCard && dragKey === `${singleBooking.booking_id}:${singleBooking?.item_id}`) ? 0 : 1,
                                            touchAction: 'none',
                                          }}
                                          data-booking-id={singleBooking.booking_id}
                                          data-item-id={singleBooking?.item_id || ""}
                                          onClick={(event) => singleBooking && handleBookingCardClick(event, singleBooking?.booking_id, singleBooking?.item_id)}
                                          onMouseDown={stopCardMouseDown}
                                          onPointerDown={(e) => {
                                            if (singleBooking.status === 'Holding') return;
                                            const startTime24 = (() => {
                                              const t = singleBooking?.service_at?.split(" ")?.[1];
                                              if (t) return String(t).slice(0, 5);
                                              const st = singleBooking?.startTime;
                                              if (st && /(AM|PM)/i.test(String(st))) {
                                                try {
                                                  return convertTo24HourFormat(String(st));
                                                } catch {}
                                              }
                                              if (typeof st === "string" && st.includes(":")) return st.slice(0, 5);
                                              return "";
                                            })();
                                            const endTime24 = (() => {
                                              if (startTime24) return calculateEndTime(startTime24, singleBooking.duration || 15);
                                              const et = singleBooking?.endTime;
                                              if (et && /(AM|PM)/i.test(String(et))) {
                                                try {
                                                  return convertTo24HourFormat(String(et));
                                                } catch {}
                                              }
                                              if (typeof et === "string" && et.includes(":")) return et.slice(0, 5);
                                              return "";
                                            })();
                                            const slotLike = {
                                              booking_id: singleBooking.booking_id,
                                              item_id: singleBooking.item_id,
                                              service_id: singleBooking.service_id,
                                              service_at: singleBooking.service_at,
                                              start_time: startTime24,
                                              end_time: endTime24,
                                              duration: singleBooking.duration,
                                              therapistId: singleBooking.therapistId,
                                              therapistName: singleBooking.therapistName,
                                              roomId: singleBooking.room_id,
                                              status: singleBooking.status,
                                              payment_status: singleBooking.payment_status,
                                              payment_status_id: singleBooking.payment_status_id,
                                              user: singleBooking.user,
                                              serviceName: singleBooking.serviceName,
                                              service_category: singleBooking.service_category,
                                              tags: singleBooking.tags,
                                              therapist_type: singleBooking.therapist_type,
                                              rowIndex,
                                              colIndex,
                                              isPlaceholder: false,
                                              requested_person: singleBooking?.tags?.isRequested ? 1 : 0,
                                              requested_therapist: singleBooking?.tags?.isRequested ? true : false,
                                              natal: singleBooking?.tags?.natal || false,
                                              slimming: singleBooking?.tags?.slimming || false,
                                              isMemberRateApplied: singleBooking?.isMemberRateApplied || false,
                                              isMembershipApplied: singleBooking?.isMembershipApplied || false,
                                              price_rate: singleBooking?.price_rate || null,
                                            };
                                            handleCardMouseDown(e, slotLike);
                                          }}
                                        >
                                          {singleBooking.status === 'Holding' ?
                                            <div className={styles.newBookingFromWeb}>
                                              {singleBooking.panel === 'outlet' && <p className={styles.createdBy}>{singleBooking?.createdBy}</p>}
                                              <p className={styles.bookingSource}>{
                                                  singleBooking.panel === 'customer' ? 'Web' 
                                                  : singleBooking.panel === 'widget' ? 'Widget' 
                                                  : singleBooking.panel === 'outlet' && singleBooking.duration >= 20 ? 'outlet' : ''} 
                                                </p>
                                              {singleBooking.duration >= 40 ? <p>Booking in progress</p> : '' }
                                              {singleBooking.panel === 'outlet' && <HoldingCardTimer 
                                                key={`timer-${singleBooking.booking_id}-${singleBooking.lastUpdated || 0}`}
                                                holdingCardTime={singleBooking.holdingCardTime}
                                                bookingId={singleBooking.booking_id}
                                                onTimerExpired={handleTimerExpired} />}
                                            </div>
                                            :
                                              <div className={`${styles.upperBlock}`}>
                                              <div>
                                                  <p 
                                                    className={styles.serviceName}
                                                  >
                                                    {`${singleBooking.totalDuration} mins ${singleBooking.serviceName} `}
                                                  </p>
                                                <div 
                                                  className={styles.customerInfo}
                                                  >
                                                    <p className={`${styles.customerNumber} ${styles.bold}`}>{singleBooking.user?.contact}</p>
                                                  
                                                  {singleBooking.guestIndex !== null ? (
                                                    <>
                                                      <p className={styles.customerName}>{singleBooking.user?.name}</p>
                                                      <p className={styles.paxName}>
                                                        {singleBooking.memberName?.includes("→") ? singleBooking.memberName.split("→")[1]?.trim() : ""}
                                                      </p>
                                                    </>
                                                  ) : (
                                                    <p className={styles.customerName}>{singleBooking.user?.name}</p>
                                                  )}
                                                <p className={`${styles.timeRange} ${styles.bold}`}>{getBookingCardTimeRangeLabel(singleBooking)}</p>
                                                </div>


                                                <p 
                                                  className={styles.assignedItem}
                                                  style={isDraggingCard && dragKey === `${singleBooking.booking_id}:${singleBooking?.item_id}` && singleBooking.duration <= 45 ? { display: 'block' } : {}}
                                                >
                                                  {assignedItem}
                                                </p>
                                                
                                              </div>
                                              <div className={styles.iconRow}>
                                                {iconTags.map((icon, i) => (
                                                  <img
                                                    key={i}
                                                    src={icon.icon}
                                                    alt={icon.title}
                                                    title={icon.title}
                                                    className={styles.iconImage}
                                                  />
                                                ))}
                                              </div>
                                            </div>
                                          }
                                        </div>
                                      );
                                    };

                                    const primarySingleBooking = bookingDetails && !isBookingInOverlapGroup ? bookingDetails : null;
                                    const singleBookingsToRender = [
                                      ...(primarySingleBooking ? [primarySingleBooking] : []),
                                      ...sequentialBookedSlotsInVisibleCell,
                                    ];

                                    if (singleBookingsToRender.length > 0) {
                                      return singleBookingsToRender.map((singleBooking, singleIndex) =>
                                        renderSingleBookedSlot(singleBooking, singleIndex)
                                      );
                                    }

                                    return null; // No booking to render
                                  })()
                                  : null
                                }
                                {!isViewOnlyOutlet && (isNewBookingSlotActive || (isEditBookingActive && (draftSlotsForUI.length || 0) > 0)) && (() => {
                                  // Find all draft slots that should be rendered inside the current visible td.
                                  // When an existing off-grid booking spans multiple rows, the rows underneath are
                                  // hidden by rowSpan, so drafts that start exactly after that booking still need
                                  // to render inside this merged cell.
                                  const rangeStartMins = getSlotAbsoluteMinutes(timeSlots[rowIndex]);
                                  const tdVisibleEndMins = rowIndex + rowspan < timeSlots.length
                                    ? getSlotAbsoluteMinutes(timeSlots[rowIndex + rowspan])
                                    : rangeStartMins + (rowspan * tableInterval);
                                  let rangeEndMins = tdVisibleEndMins;
                                  if (overlappingBookedSlotsInThisCell.length > 0) {
                                    rangeEndMins = Math.max(tdVisibleEndMins, ...overlappingBookedSlotsInThisCell.map((b) => getBookingAbsoluteEndMinutes(b)));
                                  } else if (bookingDetails) {
                                    rangeEndMins = Math.max(tdVisibleEndMins, getBookingAbsoluteEndMinutes(bookingDetails));
                                  }
                                  
                                  const slotsInThisCell = draftSlotsForUI?.filter(s => {
                                    if (s.colIndex !== colIndex) return false;
                                    if (isDraggingCard && draggedCard?.counterKey === s.counterKey) return false;
                                    if (!isDraftSlotOnSelectedTimeline(s)) return false;
                                    
                                    const sStart = getDraftSlotAbsoluteStartMinutes(s);
                                    const adjustedSEnd = getDraftSlotAbsoluteEndMinutes(s);
                                    return sStart < rangeEndMins && adjustedSEnd > rangeStartMins;
                                  }) || [];

                                  if (slotsInThisCell.length === 0) return null;

                                  // Build overlapping groups per therapist with stable ordering (clustered per overlap set)
                                  const allSlotsForThisTherapist = draftSlotsForUI?.filter(slot => {
                                    if (slot.colIndex !== colIndex) return false;
                                    if (isDraggingCard && draggedCard?.counterKey === slot.counterKey) return false;
                                    if (!isDraftSlotOnSelectedTimeline(slot)) return false;
                                    return true;
                                  }) || [];

                                  const processedKeys = new Set();
                                  const overlapGroups = [];

                                  const overlaps = (a, b) => {
                                    const aStart = getDraftSlotAbsoluteStartMinutes(a);
                                    const aEnd = getDraftSlotAbsoluteEndMinutes(a);
                                    const bStart = getDraftSlotAbsoluteStartMinutes(b);
                                    const bEnd = getDraftSlotAbsoluteEndMinutes(b);
                                    return aStart < bEnd && aEnd > bStart;
                                  };

                                  // Use BFS-style grouping to include transitive overlaps
                                  for (let i = 0; i < allSlotsForThisTherapist.length; i++) {
                                    const seed = allSlotsForThisTherapist[i];
                                    if (processedKeys.has(seed.counterKey)) continue;

                                    const group = [];
                                    const queue = [seed];
                                    processedKeys.add(seed.counterKey);

                                    while (queue.length > 0) {
                                      const current = queue.shift();
                                      group.push(current);
                                      for (let j = 0; j < allSlotsForThisTherapist.length; j++) {
                                        const candidate = allSlotsForThisTherapist[j];
                                        if (processedKeys.has(candidate.counterKey)) continue;
                                        if (overlaps(current, candidate)) {
                                          processedKeys.add(candidate.counterKey);
                                          queue.push(candidate);
                                        }
                                      }
                                    }

                                    // Stable ordering within a group: by start time, then counterKey
                                    group.sort((a, b) => {
                                      const aStart = getDraftSlotAbsoluteStartMinutes(a);
                                      const bStart = getDraftSlotAbsoluteStartMinutes(b);
                                      if (aStart !== bStart) return aStart - bStart;
                                      return String(a.counterKey).localeCompare(String(b.counterKey));
                                    });
                                    overlapGroups.push(group);
                                  }

                                  // Find groups that affect this specific cell
                                  const overlapsRange = (s) => {
                                    const sStart = getDraftSlotAbsoluteStartMinutes(s);
                                    const adjustedSEnd = getDraftSlotAbsoluteEndMinutes(s);
                                    return s.colIndex === colIndex && sStart < rangeEndMins && adjustedSEnd > rangeStartMins;
                                  };
                                  const groupsForThisCell = overlapGroups.filter(group =>
                                    group.some(s => overlapsRange(s))
                                  );

                                  if (groupsForThisCell.length > 0) {
                                    // Render each group's slots for this cell with stable indices within the group
                                    // FIXED: Properly calculate width/position for overlapping slots with different start times
                                    return groupsForThisCell.flatMap(group => {
                                      // Find the earliest start time in this group
                                      const groupEarliestStart = Math.min(...group.map((s) => getDraftSlotAbsoluteStartMinutes(s)));
                                      
                                      // Only render this group in the cell where the earliest card starts
                                      // This prevents duplicate rendering in multiple cells
                                      if (groupEarliestStart < rangeStartMins || groupEarliestStart >= rangeEndMins) {
                                        return []; // Skip rendering in this cell
                                      }
                                      
                                      // When drafts overlap and there are existing bookings in the same cell, split width across both and breaks
                                      const groupStart = Math.min(...group.map((s) => getDraftSlotAbsoluteStartMinutes(s)));
                                      const groupEnd = Math.max(...group.map((s) => getDraftSlotAbsoluteEndMinutes(s)));
                                      const numExistingInCell = columnBookings.filter((booking) => {
                                        const bookingStart = getBookingAbsoluteStartMinutes(booking);
                                        const bookingEnd = getBookingAbsoluteEndMinutes(booking);
                                        return bookingStart < groupEnd && bookingEnd > groupStart;
                                      }).length;
                                      
                                      // Use the FULL group count for width calculation (all overlapping cards)
                                      const numDraftInCell = group.length;
                                      
                                      // Count breaks that overlap with any slot in this group
                                      const breaksInThisCellCount = !isRoomFilterActive ? therapistBreaks.filter(breakItem => {
                                        const breakStart = getBreakAbsoluteStartMinutes(breakItem);
                                        const breakEnd = getBreakAbsoluteEndMinutes(breakItem);
                                        // Check if break overlaps with any slot in the group
                                        return group.some(slot => {
                                          const slotStart = getDraftSlotAbsoluteStartMinutes(slot);
                                          const adjustedSlotEnd = getDraftSlotAbsoluteEndMinutes(slot);
                                          return breakStart < adjustedSlotEnd && breakEnd > slotStart;
                                        });
                                      }).length : 0;
                                      
                                      const shouldCombineExistingAndDraft = numExistingInCell > 0 && numDraftInCell > 0;
                                      // Always include breaks in the count, even if no existing bookings
                                      const combinedCount = shouldCombineExistingAndDraft 
                                        ? (numExistingInCell + numDraftInCell + breaksInThisCellCount) 
                                        : (numDraftInCell + breaksInThisCellCount);
                                      const groupWidth = 100 / combinedCount;
                                      
                                      // Build draft index map for deterministic left ordering
                                      // This is needed ALWAYS when we have overlapping drafts, not just when combining with existing
                                      let draftIndexMap = new Map();
                                      // Sort ALL slots in the group (not filtered by cell range)
                                      const draftsSortedForGroup = [...group].sort((a, b) => {
                                        const aStart = getDraftSlotAbsoluteStartMinutes(a);
                                        const bStart = getDraftSlotAbsoluteStartMinutes(b);
                                        if (aStart !== bStart) return aStart - bStart;
                                        return String(a.counterKey).localeCompare(String(b.counterKey));
                                      });
                                      draftIndexMap = new Map(draftsSortedForGroup.map((s, idx) => [s.counterKey, idx]));
                                      
                                      // Render ALL cards in the group (don't filter by overlapsRange)
                                      return group.map((slot, indexInVisibleSlots) => {
                                      // Get the full service details from both main and pax services
                                      const matchingService = selectedServices?.find((service) => service.counterKey === slot.counterKey) ||
                                        paxes?.flatMap((p) => p.selectedServices)?.find((s) => s.counterKey === slot.counterKey);

                                      const duration = Number(matchingService?.duration || slot.duration || 15);
                                      const height = getBookingHeight(duration, tableInterval);
                                      // Get request data from matching service instead of slot.services
                                      const baseRequest = normalizeTags(matchingService?.service_request || "");
                                      // Check service name for natal and slimming tags
                                      const serviceNameForTags = matchingService?.name || matchingService?.service_name || slot.serviceName || "";
                                      const serviceNameForTagsStr = typeof serviceNameForTags === 'string' ? serviceNameForTags : String(serviceNameForTags || "");
                                      const rawRequest = {
                                        ...baseRequest,
                                        ...(matchingService?.requested_person === 1 ? { isRequested: true } : {}),
                                        ...(serviceNameForTagsStr.toLowerCase().includes("natal") ? { natal: true } : {}),
                                        ...(serviceNameForTagsStr.toLowerCase().includes("slimming") ? { slimming: true } : {})
                                      };
                                      const roomName = matchingService?.roomName || null;
                                      const hasRequestedRoom = matchingService?.requested_room === 1;
                                      const currentNote = isEditBookingActive ? editedItemData?.note : itemData?.note;
                                      const hasNotes = !!(currentNote && currentNote.trim() && currentNote.length > 0);
                                      const overlappingRequestIconTags = buildTagIcons(rawRequest, BOOKING_TAG_ICONS);
                                      const overlappingRoomIconTags = buildTagIcons(hasRequestedRoom ? { hasRequestedRoom: true } : {}, BOOKING_TAG_ICONS, { room: roomName });
                                      const overlappingNotesIconTags = buildTagIcons(hasNotes ? { haveNotes: true } : {}, BOOKING_TAG_ICONS, { note: currentNote });

                                      // Calculate the top position relative to the current (merged) cell start time
                                      const topPosition = ((
                                        getDraftSlotAbsoluteStartMinutes(slot) - getSlotAbsoluteMinutes(timeSlots[rowIndex])
                                      ) / tableInterval) * getCellHeight(tableInterval);
                                      const hasIcon = overlappingRequestIconTags.length > 0 || overlappingRoomIconTags.length > 0 || overlappingNotesIconTags.length > 0;
                                      
                                      const {
                                        pinkCard,
                                        orangeCard,
                                        yellowCard,
                                      } = getDraftCardColorTags({
                                        matchingService,
                                        slot,
                                        selectedRooms,
                                        homeRoomList,
                                      });
                                      return (
                                        <div
                                          key={`cal-new-${slot.counterKey}-${slot.rowIndex}-${slot.colIndex}`}
                                          className={`
                                            ${styles.newAppointmentBlock} lol3 
                                            ${styles.activeSlot} 
                                            overlapping 
                                            ${isDraggingCard && draggedCard?.counterKey === slot.counterKey ? styles.dragging : ''}
                                            ${duration <= 30 ? styles.hoverCard : ''}
                                            ${duration <= 15 ? styles.card15 : ''}
                                            ${duration <= 30 ? styles.card30 : ''}
                                            ${pinkCard && styles.pinkCard}
                                            ${orangeCard && styles.orangeCard}
                                            ${yellowCard && styles.yellowCard}
                                            ${hasIcon && styles.cardHasIcon}
                                          `}
                                          data-counter-key={slot.counterKey}
                                          data-item-id={slot.counterKey}
                                          style={{
                                            height: `${height}px`,
                                                width: combinedCount === 1 ? 'calc(100% - 8px)' : `calc(${100 / combinedCount}% - 2px)`,
                                                left: shouldCombineExistingAndDraft 
                                                  ? `${(numExistingInCell + (draftIndexMap.get(slot.counterKey) ?? 0)) * (100 / combinedCount)}%` 
                                                  : `${(draftIndexMap.get(slot.counterKey) ?? 0) * groupWidth}%`,
                                            position: 'absolute',
                                            top: `${topPosition}px`,
                                                zIndex: isDraggingCard && draggedCard?.counterKey === slot.counterKey ? 1000 : 1,
                                                boxSizing: 'border-box',
                                            cursor: isDraggingCard && draggedCard?.counterKey === slot.counterKey ? 'grabbing' : 'grab',
                                            transform: 'none',
                                            opacity: 1,
                                            border: shouldShowMultiServiceBorder ? '1px solid #000000' : undefined,
                                            touchAction: 'none',
                                          }}
                                          onMouseDown={stopCardMouseDown}
                                          onPointerDown={(e) => handleCardMouseDown(e, slot)}
                                        >
                                          <p className={styles.appointmentTime}>{getDraftCardTimeLabel(slot)}</p>
                                          {(slot.serviceName || matchingService?.name || matchingService?.service_name) &&
                                            <p className={styles.serviceName}>{duration} mins {slot.serviceName || matchingService?.name || matchingService?.service_name}</p>
                                          }

                                          {slot?.customer && Object.keys(slot.customer).length > 0 ? (
                                            <div className={styles.customerDetails}>
                                              <p className={`${styles.customerNumber} ${styles.bold}`}>{slot?.customer?.contact_number}</p>
                                              {duration > 30 && <p className={styles.customerName}>{slot?.customer?.name || slot?.customer?.lastname}</p>}
                                              {/* key debug removed */}
                                              
                                            </div>
                                          ) : (
                                            !(slot.serviceName || matchingService?.name || matchingService?.service_name) && (
                                              <>
                                                <p>New Appointment</p>
                                                {/* key debug removed */}
                                              </>
                                            )
                                          )}

                                          <div className={styles.iconRow}>
                                            {overlappingRequestIconTags.map((icon, i) => (
                                              <img
                                                key={i}
                                                src={icon.icon}
                                                alt={icon.title}
                                                title={icon.title}
                                                className={styles.iconImage}
                                              />
                                            ))}

                                            {overlappingRoomIconTags.map((icon, i) => (
                                              <img
                                                key={`room-${i}`}
                                                src={icon.icon}
                                                alt={icon.title}
                                                title={icon.title}
                                                className={styles.iconImage}
                                              />
                                            ))}

                                            {overlappingNotesIconTags.map((icon, i) => (
                                              <img
                                                key={`notes-${i}`}
                                                src={icon.icon}
                                                alt={icon.title}
                                                title={icon.title}
                                                className={styles.iconImage}
                                              />
                                            ))}
                                          </div>
                                        </div>
                                      );
                                        });
                                    });
                                  }

                                  // If no overlapping slots, render normal slots
                                  return slotsInThisCell.map((slot, slotIndex) => {
                                    // Get the full service details from both main and pax services
                                    const matchingService = selectedServices?.find((service) => service.counterKey === slot.counterKey) ||
                                      paxes?.flatMap((p) => p.selectedServices)?.find((s) => s.counterKey === slot.counterKey);

                                    const duration = Number(matchingService?.duration || slot.duration || 15);
                                    const height = getBookingHeight(duration, tableInterval);
                                    // Get request data from matching service instead of slot.services
                                    const baseRequest = normalizeTags(matchingService?.service_request || "");
                                    // Check service name for natal and slimming tags
                                    const serviceNameForTags = matchingService?.name || matchingService?.service_name || slot.serviceName || "";
                                    const serviceNameForTagsStr = typeof serviceNameForTags === 'string' ? serviceNameForTags : String(serviceNameForTags || "");
                                    const rawRequest = {
                                      ...baseRequest,
                                      ...(matchingService?.requested_person === 1 ? { isRequested: true } : {}),
                                      ...(serviceNameForTagsStr.toLowerCase().includes("natal") ? { natal: true } : {}),
                                      ...(serviceNameForTagsStr.toLowerCase().includes("slimming") ? { slimming: true } : {})
                                    };
                                    const roomName = matchingService?.roomName || null;
                                    const hasRequestedRoom = matchingService?.requested_room === 1;
                                    const currentNote = isEditBookingActive ? editedItemData?.note : itemData?.note;
                                    const hasNotes = !!(currentNote && currentNote.trim() && currentNote.length > 0);
                                    const currentWebNotes = isEditBookingActive ? editedItemData?.web_notes : itemData?.web_notes;
                                    const hasWebNotes = !!(currentWebNotes && currentWebNotes.trim() && currentWebNotes.length > 0);
                                    const singleRequestIconTags = buildTagIcons(rawRequest, BOOKING_TAG_ICONS);
                                    const singleRoomIconTags = buildTagIcons(hasRequestedRoom ? { hasRequestedRoom: true } : {}, BOOKING_TAG_ICONS, { room: roomName });
                                    const singleNotesIconTags = buildTagIcons(hasNotes ? { haveNotes: true } : {}, BOOKING_TAG_ICONS, { note: currentNote });
                                    const singleWebNotesIconTags = buildTagIcons(hasWebNotes ? { hasWebNotes: true } : {}, BOOKING_TAG_ICONS, { webNotes: currentWebNotes });
                                    const hasIcon = singleRequestIconTags.length > 0 || singleRoomIconTags.length > 0 || singleNotesIconTags.length > 0 || singleWebNotesIconTags.length > 0;
                                    
                                    const {
                                      pinkCard,
                                      orangeCard,
                                      yellowCard,
                                    } = getDraftCardColorTags({
                                      matchingService,
                                      slot,
                                      selectedRooms,
                                      homeRoomList,
                                    });
                                    
                                    return (
                                      <div
                                        key={`cal-new-${slot.counterKey}-${slot.rowIndex}-${slot.colIndex}`}
                                        className={`
                                          ${styles.newAppointmentBlock} 
                                          ${styles.activeSlot} 
                                          ${isDraggingCard && draggedCard?.counterKey === slot.counterKey ? styles.dragging : ''}
                                          ${duration <= 30 ? styles.hoverCard : ''}
                                          ${duration <= 15 ? styles.card15 : ''}
                                          ${duration <= 30 ? styles.card30 : ''}
                                          ${pinkCard && styles.pinkCard}
                                          ${orangeCard && styles.orangeCard}
                                          ${yellowCard && styles.yellowCard}
                                          ${hasIcon && styles.cardHasIcon}
                                          `}
                                        data-counter-key={slot.counterKey}
                                        data-item-id={slot.counterKey}
                                        style={{
                                          height: `${height}px`,
                                          // Split width with existing bookings and breaks in this cell if present
                                          ...(() => {
                                            const slotStart = getDraftSlotAbsoluteStartMinutes(slot);
                                            const slotEnd = getDraftSlotAbsoluteEndMinutes(slot);
                                            const numExistingInCell = columnBookings.filter((booking) => {
                                              const bookingStart = getBookingAbsoluteStartMinutes(booking);
                                              const bookingEnd = getBookingAbsoluteEndMinutes(booking);
                                              return bookingStart < slotEnd && bookingEnd > slotStart;
                                            }).length;
                                            const numDraftInCell = slotsInThisCell.length;
                                            const breaksInThisCellCount = !isRoomFilterActive ? therapistBreaks.filter(breakItem => {
                                              const breakStart = getBreakAbsoluteStartMinutes(breakItem);
                                              const breakEnd = getBreakAbsoluteEndMinutes(breakItem);
                                              // Check if break overlaps with this draft slot's time range
                                              const slotStart = getDraftSlotAbsoluteStartMinutes(slot);
                                              const adjustedSlotEnd = getDraftSlotAbsoluteEndMinutes(slot);
                                              return breakStart < adjustedSlotEnd && breakEnd > slotStart;
                                            }).length : 0;
                                            
                                            // Always divide if there are breaks, existing bookings, or multiple drafts
                                            const totalItems = numExistingInCell + numDraftInCell + breaksInThisCellCount;
                                            if (totalItems > 1) {
                                              const combinedCountNormal = totalItems;
                                              // Stable draft index ordering by time then key
                                              const draftsSortedForCell = [...slotsInThisCell].sort((a, b) => {
                                                const aStart = convertToMinutes(a.start_time);
                                                const bStart = convertToMinutes(b.start_time);
                                                if (aStart !== bStart) return aStart - bStart;
                                                return String(a.counterKey).localeCompare(String(b.counterKey));
                                              });
                                              const draftIndex = Math.max(0, draftsSortedForCell.findIndex(s => s.counterKey === slot.counterKey));
                                              return { width: `calc(${100 / combinedCountNormal}% - 2px)`, left: `${(numExistingInCell + draftIndex) * (100 / combinedCountNormal)}%` };
                                            }
                                            // If only one item and no breaks, render at full width
                                            return { width: '100%', left: '0%' };
                                          })(),
                                          position: 'absolute',
                                          // Position based on actual start time within the slot
                                          top: `${((getDraftSlotAbsoluteStartMinutes(slot) - getSlotAbsoluteMinutes(timeSlots[rowIndex])) / tableInterval) * getCellHeight(tableInterval)}px`,
                                          zIndex: isDraggingCard && draggedCard?.counterKey === slot.counterKey ? 1000 : 1,
                                          cursor: isDraggingCard && draggedCard?.counterKey === slot.counterKey ? 'grabbing' : 'grab',
                                          transform: 'none',
                                          opacity: 1,
                                          border: shouldShowMultiServiceBorder ? '1px solid #000000' : undefined,
                                          touchAction: 'none',
                                        }}
                                        onMouseDown={stopCardMouseDown}
                                        onPointerDown={(e) => handleCardMouseDown(e, slot)}
                                      >
                                        <p className={styles.appointmentTime}>{getDraftCardTimeLabel(slot)}</p>
                                        {/* Show service name if available */}
                                        {(slot.serviceName || matchingService?.name || matchingService?.service_name) &&
                                          <p className={styles.serviceName}>{duration} mins {slot.serviceName || matchingService?.name || matchingService?.service_name}</p>
                                        }

                                        {/* Show customer details or time + "New Appointment" */}
                                        {slot?.customer && Object.keys(slot.customer).length > 0 ? (
                                          <div className={styles.customerDetails}>
                                            <p className={`${styles.customerNumber} ${styles.bold}`}>{slot?.customer?.contact_number}</p>
                                            {duration > 30 && <p className={styles.customerName}>{slot?.customer?.name || slot?.customer?.lastname}</p>}
                                          </div>
                                        ) : (
                                          <>
                                            <p>New Appointments</p>
                                          </>
                                        )}

                                        <div className={styles.iconRow}>
                                          {singleRequestIconTags.map((icon, i) => (
                                            <img
                                              key={i}
                                              src={icon.icon}
                                              alt={icon.title}
                                              title={icon.title}
                                              className={styles.iconImage}
                                            />
                                          ))}

                                          {singleRoomIconTags.map((icon, i) => (
                                            <img
                                              key={`room-${i}`}
                                              src={icon.icon}
                                              alt={icon.title}
                                              title={icon.title}
                                              className={styles.iconImage}
                                            />
                                          ))}

                                          {singleNotesIconTags.map((icon, i) => (
                                            <img
                                              key={`notes-${i}`}
                                              src={icon.icon}
                                              alt={icon.title}
                                              title={icon.title}
                                              className={styles.iconImage}
                                            />
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  });
                                })()}
                                
                                {/* Render Pending Booking Placeholders */}
                                {(() => {
                                  if (!pendingBookings || pendingBookings.length === 0) return null;
                                  
                                  // Filter placeholders for this cell (rowIndex, colIndex)
                                  const placeholdersInThisCell = pendingBookings.filter((placeholder) => {
                                    if (placeholder.colIndex !== colIndex) return false;
                                    if (placeholder.rowIndex !== rowIndex) return false;
                                    
                                    // Check if placeholder date matches selectedDate
                                    // Placeholders use the same date as selectedDate
                                    return true;
                                  });
                                  
                                  if (placeholdersInThisCell.length === 0) return null;
                                  
                                  return placeholdersInThisCell.map((placeholder, idx) => {
                                    const duration = placeholder.duration || 15;
                                    const height = getBookingHeight(duration, tableInterval);
                                    const cellHeight = getCellHeight(tableInterval);
                                    
                                    // Calculate top position based on start time
                                    const startMinutes = convertToMinutes(placeholder.startTime || slot.time24);
                                    const cellStartMinutes = getSlotAbsoluteMinutes(slot);
                                    const topOffset = ((startMinutes - cellStartMinutes) / tableInterval) * cellHeight;
                                    
                                    // Calculate width - similar to booking cards
                                    const cellStart = getSlotAbsoluteMinutes(slot);
                                    const cellEnd = convertToMinutes(placeholder.endTime || slot.time24) + (tableInterval);
                                    
                                    // Check for overlapping bookings, drafts, and breaks
                                    const overlappingBookingsInCell = overlappingBookedSlotsInThisCell.length > 0 ? overlappingBookedSlotsInThisCell : (bookingDetails ? [bookingDetails] : []);
                                    const draftSlotsInThisCellFlat = (!isViewOnlyOutlet && (isNewBookingSlotActive || (isEditBookingActive && (draftSlotsForUI.length || 0) > 0)))
                                      ? (draftSlotsForUI?.filter(s => {
                                          if (s.colIndex !== colIndex) return false;
                                          if (isDraggingCard && draggedCard?.counterKey === s.counterKey) return false;
                                          if (!isDraftSlotOnSelectedTimeline(s)) return false;
                                          const sStart = getDraftSlotAbsoluteStartMinutes(s);
                                          const adjustedSEnd = getDraftSlotAbsoluteEndMinutes(s);
                                          return sStart < cellEnd && adjustedSEnd > cellStart;
                                        }) || [])
                                      : [];
                                    
                                    const breaksInThisCellCount = !isRoomFilterActive ? therapistBreaks.filter(breakItem => {
                                      const breakStart = convertToMinutes(breakItem.start_time);
                                      const breakEnd = convertToMinutes(breakItem.end_time);
                                      return breakStart < cellEnd && breakEnd > cellStart;
                                    }).length : 0;
                                    
                                    const totalItems = overlappingBookingsInCell.length + draftSlotsInThisCellFlat.length + placeholdersInThisCell.length + breaksInThisCellCount;
                                    const groupWidth = totalItems > 1 ? 100 / totalItems : 100;
                                    
                                    // Calculate display index (placeholders come after existing bookings and drafts)
                                    const displayIndex = overlappingBookingsInCell.length + draftSlotsInThisCellFlat.length + idx;

                                    const hasIcon = placeholder?.tags?.isRequested || placeholder?.tags?.hasRequestedRoom || placeholder?.tags?.male || placeholder?.tags?.female || placeholder?.tags?.natal || placeholder?.tags?.slimming || placeholder?.tags?.haveNotes || placeholder?.tags?.indonesian || placeholder?.tags?.malaysian || placeholder?.tags?.singapore || placeholder?.tags?.China || placeholder?.tags?.thai || placeholder?.tags?.repeat || placeholder?.tags?.hasWebNotes;
                                    
                                    const requestedCoupleRoom = placeholder?.tags?.coupleRoom && placeholder?.tags?.hasRequestedRoom;
                                    const requestedShowerRoom = placeholder?.tags?.showerRoom && placeholder?.tags?.hasRequestedRoom;
                                    const scrubService = placeholder?.tags?.scrubService;
                                    const pinkCard = placeholder?.tags?.isRequested;
                                    const orangeCard = (requestedCoupleRoom) || (pinkCard && requestedCoupleRoom);
                                    const yellowCard = scrubService || (scrubService && requestedShowerRoom) || (pinkCard && requestedShowerRoom) || requestedShowerRoom;
                                    
                                    return (
                                      <div
                                        key={`pending-${placeholder.booking_id}-${idx}-${rowIndex}-${colIndex}`}
                                        className={`
                                          ${styles.bookingInfo} 
                                          ${styles.bookedSlot} 
                                          ${styles.statusHolding}
                                          ${duration <= 45 && styles.hoverCard} 
                                          ${duration <= 20 && styles.card15}
                                          ${duration <= 45 && styles.card30}
                                          ${pinkCard && styles.pinkCard}
                                          ${orangeCard && styles.orangeCard}
                                          ${yellowCard && styles.yellowCard}
                                          ${hasIcon && styles.cardHasIcon}
                                        `}
                                        style={{
                                          position: "absolute",
                                          top: `${topOffset}px`,
                                          height: `${height}px`,
                                          width: totalItems > 1 ? `calc(${groupWidth}% - 2px)` : 'calc(100% - 8px)',
                                          left: totalItems > 1 ? `${displayIndex * groupWidth}%` : '0%',
                                          zIndex: 3,
                                          boxSizing: 'border-box',
                                          opacity: 0.9, // Slightly transparent to indicate it's a placeholder
                                        }}
                                        data-booking-id={placeholder.booking_id}
                                        data-is-placeholder="true"
                                      >
                                        <div className={styles.newBookingFromWeb}>
                                          <p className={styles.bookingSource}>Creating booking...</p>
                                        </div>
                                      </div>
                                    );
                                  });
                                })()}
                                
                                {/* Render Break Cards */}
                                {(() => {
                                  // In room mode, don't show breaks
                                  if (isRoomFilterActive) return null;
                                  
                                  // Use the same merged time range calculation as overlapping bookings
                                  const cellStartMinutes = getSlotAbsoluteMinutes(slot);
                                  let mergedEndMinutes;
                                  
                                  if (overlappingBookedSlotsInThisCell.length > 0) {
                                    // For overlapping bookings, use the merged end time
                                    mergedEndMinutes = Math.max(...overlappingBookedSlotsInThisCell.map((b) => getBookingAbsoluteEndMinutes(b)));
                                  } else if (bookingDetails) {
                                    // For single booking, use its end time
                                    mergedEndMinutes = getBookingAbsoluteEndMinutes(bookingDetails);
                                  } else {
                                    // No bookings, use cell end time
                                    mergedEndMinutes = convertToMinutes(timeSlots[rowIndex].time24) + tableInterval;
                                  }

                                  // Get draft slots in this cell (same logic as bookings)
                                  const draftSlotsInThisCellFlat = (!isViewOnlyOutlet && (isNewBookingSlotActive || (isEditBookingActive && (draftSlotsForUI.length || 0) > 0)))
                                    ? (draftSlotsForUI?.filter(s => {
                                        if (s.colIndex !== colIndex) return false;
                                        if (isDraggingCard && draggedCard?.counterKey === s.counterKey) return false;
                                        if (!isDraftSlotOnSelectedTimeline(s)) return false;
                                        
                                        const sStart = getDraftSlotAbsoluteStartMinutes(s);
                                        const adjustedSEnd = getDraftSlotAbsoluteEndMinutes(s);
                                        return sStart < mergedEndMinutes && adjustedSEnd > cellStartMinutes;
                                      }) || [])
                                    : [];

                                  const breaksInThisCell = therapistBreaks.filter(breakItem => {
                                    const breakStart = getBreakAbsoluteStartMinutes(breakItem);
                                    const breakEnd = getBreakAbsoluteEndMinutes(breakItem);
                                    
                                    // Check if break overlaps with any draft slot in this cell
                                    const overlapsDraft = draftSlotsInThisCellFlat.some(draft => {
                                      const draftStart = convertToMinutes(draft.start_time);
                                      const draftEnd = convertToMinutes(draft.end_time);
                                      const adjustedDraftEnd = adjustEndTimeForMidnightWrap(draftStart, draftEnd);
                                      return breakStart < adjustedDraftEnd && breakEnd > draftStart;
                                    });
                                    
                                    // Include breaks that start or fall within the visible rowspan cell range
                                    const overlapsVisibleCell = breakStart < visibleCellEndMinutes && breakEnd > cellStartMinutes;
                                    
                                    return overlapsDraft || overlapsVisibleCell;
                                  });

                                  if (breaksInThisCell.length === 0) return null;

                                  return breaksInThisCell.map((breakItem, breakIndex) => {
                                    const breakStart = getBreakAbsoluteStartMinutes(breakItem);
                                    const breakEnd = getBreakAbsoluteEndMinutes(breakItem);
                                    const duration = breakEnd - breakStart;

                                    const shouldRenderBreakHere = breakStart >= cellStartMinutes && breakStart < visibleCellEndMinutes;
                                    if (!shouldRenderBreakHere) return null;

                                    // Compute overlap group widths based on ALL bookings/drafts that overlap this break interval.
                                    // This fixes the case where the booking starts later than the break start row (booking renders in a later row),
                                    // causing break to stay full width while booking shrinks.
                                    const overlappingBookingsForBreak = (columnBookings || []).filter((b) => {
                                      const bStart = getBookingAbsoluteStartMinutes(b);
                                      const bEnd = getBookingAbsoluteEndMinutes(b);
                                      return bStart < breakEnd && bEnd > breakStart;
                                    });

                                    const draftSlotsOverlappingBreak = (!isViewOnlyOutlet && (isNewBookingSlotActive || (isEditBookingActive && (draftSlotsForUI.length || 0) > 0)))
                                      ? (draftSlotsForUI?.filter((s) => {
                                          if (s.colIndex !== colIndex) return false;
                                          if (isDraggingCard && draggedCard?.counterKey === s.counterKey) return false;
                                          if (!isDraftSlotOnSelectedTimeline(s)) return false;
                                          const sStart = getDraftSlotAbsoluteStartMinutes(s);
                                          const sAdjEnd = getDraftSlotAbsoluteEndMinutes(s);
                                          return sStart < breakEnd && sAdjEnd > breakStart;
                                        }) || [])
                                      : [];

                                    const overlappingBreaksForBreak = breaksData
                                      .filter((b) => Number(b.therapist_id) === Number(headerId))
                                      .filter((b) => {
                                        const bs = getBreakAbsoluteStartMinutes(b);
                                        const be = getBreakAbsoluteEndMinutes(b);
                                        return bs < breakEnd && be > breakStart;
                                      })
                                      .sort((a, b) => {
                                        const as = getBreakAbsoluteStartMinutes(a);
                                        const bs = getBreakAbsoluteStartMinutes(b);
                                        if (as !== bs) return as - bs;
                                        return String(a.id || '').localeCompare(String(b.id || ''));
                                      });

                                    const combinedCount =
                                      overlappingBookingsForBreak.length +
                                      draftSlotsOverlappingBreak.length +
                                      overlappingBreaksForBreak.length;
                                    const safeCombinedCount = Math.max(1, combinedCount);
                                    const groupWidth = 100 / safeCombinedCount;
                                    const breakIndexInGroup = Math.max(
                                      0,
                                      overlappingBreaksForBreak.findIndex((b) => String(b.id) === String(breakItem.id))
                                    );
                                    const leftPosition =
                                      (overlappingBookingsForBreak.length + draftSlotsOverlappingBreak.length + breakIndexInGroup) * groupWidth;
                                    
                                    const cellHeight = getCellHeight(tableInterval);
                                    const topOffset = ((breakStart - cellStartMinutes) / tableInterval) * cellHeight;
                                    const breakHeight = (duration / tableInterval) * cellHeight;

                                    return (
                                      <div
                                        key={`break-${breakItem.id}`}
                                        className={styles.breakCard}
                                        style={{
                                          position: 'absolute',
                                          top: `${topOffset}px`,
                                          height: `${breakHeight}px`,
                                          width: `${groupWidth}%`,
                                          left: `${leftPosition}%`,
                                        }}
                                        onClick={(e) => {
                                          // Prevent creating new appointment card
                                          e.stopPropagation();
                                          e.preventDefault();

                                          // Dispatch custom event to open attendance popup
                                          const event = new CustomEvent('openAttendancePopup', {
                                            detail: {
                                              therapist_id: breakItem.therapist_id,
                                              therapist_name: breakItem.therapist_name,
                                            }
                                          });
                                          window.dispatchEvent(event);
                                        }}
                                        onMouseDown={(e) => {
                                          // Prevent handleMouseDown from creating new appointment
                                          e.stopPropagation();
                                          e.preventDefault();
                                        }}
                                        onMouseOver={(e) => {
                                          // Prevent handleMouseOver from triggering
                                          e.stopPropagation();
                                        }}
                                        title={`Break: ${breakItem.start_time_display} - ${breakItem.end_time_display}${breakItem.status_name ? '\n' + breakItem.status_name : ''}${breakItem.remark ? '\n' + breakItem.remark : ''}`}
                                      >
                                        <div className={styles.breakCardTime}>
                                          {breakItem.start_time_display} - {breakItem.end_time_display}
                                        </div>
                                        <div className={styles.breakCardStatus}>
                                          {breakItem.status_name && <span>{breakItem.status_name}</span>}
                                        </div>
                                        {breakItem.remark && (
                                          <div className={styles.breakCardRemark}>
                                            {breakItem.remark}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  });
                                })()}
                              </td>
                            );
                          });
                          })()}
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            )}
            {/* <PrimaryButton
			text="Book Appointment"
			onClick={() => !isViewOnlyOutlet && setIsSidebarVisible(true)}
			className={`${styles.CheckAvailableBtn} ${isViewOnlyOutlet ? styles.disabledBtn : ""}`}
			disabled={isViewOnlyOutlet}
		/> */}

            {/* Ghost card for drag preview with correct status color and details (only for non-placeholder new bookings) */}
            {/* Ghost card for drag preview with correct status color and details (only for non-placeholder new bookings) */}
            {isDraggingCard && ghostCard && ghostCard?.isPlaceholder !== true && (() => {
               // Prepare icons for ghost card
               const ghostIcons = buildTagIcons(
                ghostCard?.tags,
                BOOKING_TAG_ICONS,
                {
                  therapist: ghostCard?.therapistName,
                  note: ghostCard?.tags?.note,
                  room: ghostCard?.tags?.roomName,
                  userName: ghostCard?.user?.name,
                  userNumber: ghostCard?.user?.contact,
                }
              );
              const requestedCoupleRoom = ghostCard?.tags?.coupleRoom && ghostCard?.tags?.hasRequestedRoom;
              const requestedShowerRoom = ghostCard?.tags?.showerRoom && ghostCard?.tags?.hasRequestedRoom;
              const scrubService = ghostCard?.tags?.scrubService;
              const pinkCard = ghostCard?.tags?.isRequested;
              const orangeCard = (requestedCoupleRoom) || (pinkCard && requestedCoupleRoom);
              const yellowCard = scrubService || (scrubService && requestedShowerRoom) || (pinkCard && requestedShowerRoom) || requestedShowerRoom;
              return (
              <div
                data-ghost-overlay="true"
                style={{
                  position: 'fixed',
                  ...(() => {
                    const leftPos = dragPosition.x - (ghostCard?.offset?.x || 0);
                    const topPos = dragPosition.y - (ghostCard?.offset?.y || 0);
                    const cardWidth = ghostCard?.rect?.width || 160;
                    const cardHeight = ghostCard?.rect?.height || 60;
                    const clamped = getClampedGhostCardPosition(leftPos, topPos, cardWidth, cardHeight);
                    return {
                      left: `${clamped.left}px`,
                      top: `${clamped.top}px`,
                    };
                  })(),
                  width: `${ghostCard?.rect?.width || 160}px`,
                  minHeight: (ghostCard?.duration || 0) <= 20 ? '84px' : `${ghostCard?.rect?.height || 60}px`,
                  height: (ghostCard?.duration || 0) <= 20 ? 'auto' : `${ghostCard?.rect?.height || 60}px`,
                  pointerEvents: 'none',
                  zIndex: 10000,
                }}
              >
                <div
                  className={`
                    ${styles.bookingInfo} 
                    ${styles.bookedSlot} 
                    ${ghostCard?.ghostMeta?.statusClass || ''}
                    ${pinkCard && styles.pinkCard}
                    ${orangeCard && styles.orangeCard}
                    ${yellowCard && styles.yellowCard}
                    `}
                  style={{
                    width: '100%',
                    height: '100%',
                    opacity: 1,
                    boxSizing: 'border-box',
                    cursor: 'grabbing',
                  }}
                >
                  <div className={styles.upperBlock}>
                    <div>
                      {dropTime?.value && (
                        <p style={{ 
                          color: '#000',
                          display: 'inline-block',
                          fontSize: '10px'
                        }}>
                          {formatTimelineTimeLabel(dropTime.value, dropTime.absoluteMinutes)}
                        </p>
                      )}
                      
                        {/* Always show service name in ghost card */}
                        <p className={styles.serviceName} style={{ display: 'block' }}>
                          {ghostCard?.duration ? `${ghostCard.duration} mins ` : ''}{ghostCard?.ghostMeta?.serviceName || ghostCard?.serviceName || ''}
                        </p>
                        
                        {/* Always show customer info in ghost card */}
                        <div className={styles.customerInfo} style={{ display: 'block' }}>
                          {ghostCard?.ghostMeta?.userContact && (
                            <p className={`${styles.customerNumber} ${styles.bold}`}>{ghostCard.ghostMeta.userContact}</p>
                          )}
                          {ghostCard?.ghostMeta?.userName && (
                            <p className={styles.customerName}>{ghostCard.ghostMeta.userName}</p>
                          )}
                        <p className={`${styles.timeRange} ${styles.bold}`}>{getBookingCardTimeRangeLabel(ghostCard)}</p>
                        </div>
                        
                    </div>

                    <div className={styles.iconRow}>
                      {ghostIcons.map((icon, i) => (
                        <img
                          key={i}
                          src={icon.icon}
                          alt={icon.title}
                          title={icon.title}
                          className={styles.iconImage}
                        />
                      ))}
                    </div>

                  </div>
                </div>
              </div>
            );
            })()}

            <RightSidebarWrapper
              ref={sidebarRef}
              show={isSidebarVisible && !(isDraggingCard && isMobileViewport)}
              isBookingSidebar={isBookingSidebar}
              bookingId={selectedBookingId}
              onClose={handleCloseSidebar}
              onClearSidebarSelection={clearSidebarVisualSelection}
              bookingsList={bookingsList}
              staffTiming={staffTiming}
              interval={tableInterval}
              createdBooking={createdBooking}
              setCreatedBooking={setCreatedBooking}
              setShowBookingSummary={setShowBookingSummary}
              setSelectedCells={setSelectedCells}
              editPrefill={editPrefill}
              breaksData={breaksData}
              setIsBookingSubmitting={setIsBookingSubmitting}
              isOutletExtended={isOutletExtended}
              mainContentRef={bookingWrapperRef}
            />

            {showConflictModal && (
              <DiscardPopup
                onCancel={onCancel}
                onDiscard={onDiscard}
              />
            )}

            {/* Customer Details Modal */}
            <CustomerDetailsSidebar
              customer={selectedCustomerForDetails}
              isOpen={isCustomerDetailsOpen}
              onClose={() => {
                setIsCustomerDetailsOpen(false);
                setSelectedCustomerForDetails(null);
              }}
            />

          </div>
        </div>

        <EventListner 
          bookingsList={bookingsList} 
          setBookingsList={setBookingsList} 
          setNewAppointmentSlots={setNewAppointmentSlots}
          onBreakCreated={fetchBreaks}
          onScheduleUpdated={handleScheduleUpdated}
          onCloseSidebar={handleCloseSidebar}
        />

      </div>


      </div>


      {/* Confirmation popup removed: edits happen via Edit sidebar */}

    </div>
  );
};

export default Home;
